/**
 * `userInput.country` stores lowercase ISO 3166-1 alpha-2 from the location search (Google Places).
 * Map geometry comes from the CRM maps API; optional `countries[]` on the product element overrides pin/bbox only.
 */
/** Legacy slug keys for pin `bbox` fallback when only ISO is stored ( west/east/south/north WGS84 ). */
const COUNTRY_BBOX_FALLBACK = {
    ireland: { west: -10.5, east: -5.6, south: 51.4, north: 55.4 },
    australia: { west: 113.0, east: 154.0, south: -44.0, north: -9.0 },
    usa: { west: -125.0, east: -66.0, south: 24.0, north: 49.5 },
    uk: { west: -8.2, east: 2.0, south: 49.5, north: 61.0 },
    "new-zealand": { west: 166.0, east: 179.0, south: -47.5, north: -34.0 },
    canada: { west: -141.0, east: -52.0, south: 41.5, north: 83.5 },
};

const COUNTRY_ISO_BBOX_FALLBACK = {
    ie: COUNTRY_BBOX_FALLBACK.ireland,
    au: COUNTRY_BBOX_FALLBACK.australia,
    us: COUNTRY_BBOX_FALLBACK.usa,
    gb: COUNTRY_BBOX_FALLBACK.uk,
    nz: COUNTRY_BBOX_FALLBACK["new-zealand"],
    ca: COUNTRY_BBOX_FALLBACK.canada,
};

/** `mapMarkers` index for `#101010` / black marker assets. Applied when entering Country Map (not edit-design). */
const DEFAULT_COUNTRY_MAP_MARKER_COLOR_INDEX = 3;
const DEFAULT_COUNTRY_MAP_MARKER_INDEX = 2;

/** A5 portrait at 300 PPI. Export multiplier is derived from the live canvas width so every device hits this. */
const PRINT_EXPORT_WIDTH_PX = 1748;

function getDefaultLosAngelesCountryMapLocation() {
    return {
        position: { lat: 34.052235, lng: -118.243683 },
        title: "Los Angeles, CA, USA",
        address: {
            label: "Los Angeles, CA, USA",
            city: "Los Angeles",
            countryName: "United States",
            district: "California",
            countryCode: "US",
            state: "California",
            stateCode: "CA",
        },
        resultType: "locality",
    };
}

const ROUTE_PREVIEW_MIN_METERS = 80000;
const ROUTE_PREVIEW_COUNTRIES = {
    AU: ["Australia", [
        ["Sydney", "NSW", -33.8688, 151.2093], ["Melbourne", "VIC", -37.8136, 144.9631],
        ["Brisbane", "QLD", -27.4698, 153.0251], ["Perth", "WA", -31.9523, 115.8613],
        ["Adelaide", "SA", -34.9285, 138.6007], ["Canberra", "ACT", -35.2809, 149.13],
        ["Gold Coast", "QLD", -28.0167, 153.4], ["Cairns", "QLD", -16.9186, 145.7781],
        ["Hobart", "TAS", -42.8821, 147.3272], ["Darwin", "NT", -12.4634, 130.8456],
    ]],
    GB: ["United Kingdom", [
        ["London", "", 51.5074, -0.1278], ["Manchester", "", 53.4808, -2.2426],
        ["Birmingham", "", 52.4862, -1.8904], ["Edinburgh", "", 55.9533, -3.1883],
        ["Glasgow", "", 55.8642, -4.2518], ["Bristol", "", 51.4545, -2.5879],
        ["Liverpool", "", 53.4084, -2.9916], ["Leeds", "", 53.8008, -1.5491],
        ["Cardiff", "", 51.4816, -3.1791], ["Belfast", "", 54.5973, -5.9301],
    ]],
    US: ["United States", [
        ["New York", "NY", 40.7128, -74.006], ["Los Angeles", "CA", 34.0522, -118.2437],
        ["Chicago", "IL", 41.8781, -87.6298], ["Houston", "TX", 29.7604, -95.3698],
        ["Miami", "FL", 25.7617, -80.1918], ["Seattle", "WA", 47.6062, -122.3321],
        ["San Francisco", "CA", 37.7749, -122.4194], ["Denver", "CO", 39.7392, -104.9903],
        ["Boston", "MA", 42.3601, -71.0589], ["Atlanta", "GA", 33.749, -84.388],
        ["Las Vegas", "NV", 36.1699, -115.1398], ["Dallas", "TX", 32.7767, -96.797],
    ]],
    CA: ["Canada", [
        ["Toronto", "ON", 43.6532, -79.3832], ["Vancouver", "BC", 49.2827, -123.1207],
        ["Montreal", "QC", 45.5019, -73.5674], ["Calgary", "AB", 51.0447, -114.0719],
        ["Ottawa", "ON", 45.4215, -75.6972], ["Edmonton", "AB", 53.5461, -113.4938],
        ["Winnipeg", "MB", 49.8951, -97.1384], ["Halifax", "NS", 44.6488, -63.5752],
        ["Quebec City", "QC", 46.8139, -71.208], ["Victoria", "BC", 48.4284, -123.3656],
    ]],
};

function buildRoutePreviewLocation(countryCode, countryName, entry) {
    const city = entry[0];
    const stateCode = entry[1];
    const label = city + (stateCode ? ", " + stateCode : "") + ", " + countryName;
    return {
        position: { lat: entry[2], lng: entry[3] },
        title: city,
        address: {
            label: label,
            city: city,
            countryName: countryName,
            district: "",
            countryCode: countryCode,
            state: "",
            stateCode: stateCode,
        },
        resultType: "locality",
    };
}

/** Sample second location for the route preview: the closest listed city that is far enough from the first pin. */
function pickRoutePreviewCity(firstLoc) {
    const pos = firstLoc && firstLoc.position;
    const lat = pos ? Number(pos.lat) : NaN;
    const lng = pos ? Number(pos.lng) : NaN;
    const code = firstLoc && firstLoc.address && firstLoc.address.countryCode
        ? String(firstLoc.address.countryCode).trim().toUpperCase()
        : "";
    const codes = ROUTE_PREVIEW_COUNTRIES[code] ? [code] : Object.keys(ROUTE_PREVIEW_COUNTRIES);
    const candidates = [];
    codes.forEach(function (c) {
        const country = ROUTE_PREVIEW_COUNTRIES[c];
        country[1].forEach(function (entry) {
            candidates.push({
                code: c,
                name: country[0],
                entry: entry,
                distance: haversineMeters(lat, lng, entry[2], entry[3]),
            });
        });
    });
    if (!candidates.length) {
        return null;
    }
    let best = null;
    let farthest = null;
    candidates.forEach(function (cand) {
        if (Number.isFinite(cand.distance) && cand.distance >= ROUTE_PREVIEW_MIN_METERS) {
            if (!best || cand.distance < best.distance) {
                best = cand;
            }
        }
        if (!farthest || (Number.isFinite(cand.distance) && cand.distance > farthest.distance)) {
            farthest = cand;
        }
    });
    const chosen = best || farthest || candidates[0];
    return buildRoutePreviewLocation(chosen.code, chosen.name, chosen.entry);
}

/** Legacy lat/lng strings when no search selection exists (non-country-map products without JSON default). */
function getDefaultGoldCoastLatLngStrings() {
    return {
        latlong: "-27.9985041, 153.4227099",
        longlat: "153.4227099,-27.9985041",
        latStr: "-27.9985041",
        lngStr: "153.4227099",
    };
}

/** Round every decimal numeric run inside a string to at most `maxDp` decimals. Negatives + integers preserved. */
function limitDecimalPlacesInString(input, maxDp) {
    if (input == null) return input;
    const s = String(input);
    const dp = Number.isFinite(maxDp) ? Math.max(0, Math.floor(maxDp)) : 5;
    return s.replace(/-?\d+\.\d+/g, function (match) {
        const num = Number(match);
        if (!Number.isFinite(num)) return match;
        const fixed = num.toFixed(dp);
        return fixed.indexOf(".") === -1 ? fixed : fixed.replace(/\.?0+$/, "");
    });
}

function getMergedDefaultMapLocationFromStyleProduct(productType, styleIndex) {
    if (!productType || !productType.styles) {
        return null;
    }
    const style = productType.styles[styleIndex];
    if (style && style.default_map_location != null) {
        return style.default_map_location;
    }
    if (productType.default_map_location != null) {
        return productType.default_map_location;
    }
    return null;
}

/**
 * WKWebView / iOS Safari often ignores CanvasRenderingContext2D.filter hue-rotate when drawing images.
 * Pixel-path rotates hue in HSL so texture tint tracks Map Style colours on mobile.
 */
let countryMapCanvasHueRotateFilterWorksCache = null;

function countryMapDetectCanvasHueRotateFilterWorks() {
    if (countryMapCanvasHueRotateFilterWorksCache !== null) {
        return countryMapCanvasHueRotateFilterWorksCache;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 8;
    canvas.height = 8;
    const ctx = canvas.getContext("2d");
    if (!ctx || typeof ctx.filter === "undefined") {
        countryMapCanvasHueRotateFilterWorksCache = false;
        return false;
    }
    try {
        ctx.filter = "hue-rotate(120deg)";
        ctx.fillStyle = "rgb(255,0,0)";
        ctx.fillRect(0, 0, 8, 8);
        ctx.filter = "none";
        const d = ctx.getImageData(0, 0, 1, 1).data;
        countryMapCanvasHueRotateFilterWorksCache = d[1] > 160 && d[0] < 120;
        return countryMapCanvasHueRotateFilterWorksCache;
    } catch (e) {
        countryMapCanvasHueRotateFilterWorksCache = false;
        return false;
    }
}

function rgbToHslCountryMap(r, g, b) {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            default:
                h = (r - g) / d + 4;
        }
        h /= 6;
    }
    return [h, s, l];
}

function hslToRgbCountryMap(h, s, l) {
    let r;
    let g;
    let b;
    if (s === 0) {
        r = g = b = l;
    } else {
        const hue2rgb = function (p, q, t) {
            let tt = t;
            if (tt < 0) tt += 1;
            if (tt > 1) tt -= 1;
            if (tt < 1 / 6) return p + (q - p) * 6 * tt;
            if (tt < 1 / 2) return q;
            if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return [r, g, b];
}

function hueRotateCountryMapImageData(imageData, angleDeg) {
    const data = imageData.data;
    const delta = angleDeg / 360;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) {
            continue;
        }
        const r = data[i] / 255;
        const g = data[i + 1] / 255;
        const b = data[i + 2] / 255;
        let hsl = rgbToHslCountryMap(r, g, b);
        hsl[0] = (hsl[0] + delta + 1) % 1;
        const rgb = hslToRgbCountryMap(hsl[0], hsl[1], hsl[2]);
        data[i] = Math.round(Math.min(255, Math.max(0, rgb[0] * 255)));
        data[i + 1] = Math.round(Math.min(255, Math.max(0, rgb[1] * 255)));
        data[i + 2] = Math.round(Math.min(255, Math.max(0, rgb[2] * 255)));
    }
}

function isGrayscaleHexForCountryMap(hex) {
    if (!hex) {
        return false;
    }
    const s = String(hex)
        .trim()
        .replace(/^#/, "")
        .toLowerCase();
    let r;
    let g;
    let b;
    if (/^[0-9a-f]{6}$/.test(s)) {
        const n = parseInt(s, 16);
        r = (n >> 16) & 255;
        g = (n >> 8) & 255;
        b = n & 255;
    } else if (/^[0-9a-f]{3}$/.test(s)) {
        r = parseInt(s[0] + s[0], 16);
        g = parseInt(s[1] + s[1], 16);
        b = parseInt(s[2] + s[2], 16);
    } else {
        return false;
    }
    return r === g && g === b;
}

function grayscaleCountryMapImageData(imageData) {
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) {
            continue;
        }
        const y = Math.round(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
        data[i] = y;
        data[i + 1] = y;
        data[i + 2] = y;
    }
}

/** Full map image outline + optional `pin_regions` rects (same % space as marker_x/marker_y). Set false for production. */
const SHOW_COUNTRY_MAP_DEBUG_BOUNDS = false;

/** Console traces for country vs state SVG resolution (`[CountryMap]`). Set false for production. */
const DEBUG_COUNTRY_MAP_STATE = true;

function logCountryMapDebug() {
    if (!DEBUG_COUNTRY_MAP_STATE) {
        return;
    }
    const parts = Array.prototype.slice.call(arguments);
    parts.unshift("[CountryMap]");
    console.log.apply(console, parts);
}

/** Lowercase US state/territory name → USPS code (for geocoders that only return full names). */
const US_STATE_NAME_TO_CODE = {
    alabama: "AL",
    alaska: "AK",
    arizona: "AZ",
    arkansas: "AR",
    california: "CA",
    colorado: "CO",
    connecticut: "CT",
    delaware: "DE",
    "district of columbia": "DC",
    florida: "FL",
    georgia: "GA",
    hawaii: "HI",
    idaho: "ID",
    illinois: "IL",
    indiana: "IN",
    iowa: "IA",
    kansas: "KS",
    kentucky: "KY",
    louisiana: "LA",
    maine: "ME",
    maryland: "MD",
    massachusetts: "MA",
    michigan: "MI",
    minnesota: "MN",
    mississippi: "MS",
    missouri: "MO",
    montana: "MT",
    nebraska: "NE",
    nevada: "NV",
    "new hampshire": "NH",
    "new jersey": "NJ",
    "new mexico": "NM",
    "new york": "NY",
    "north carolina": "NC",
    "north dakota": "ND",
    ohio: "OH",
    oklahoma: "OK",
    oregon: "OR",
    pennsylvania: "PA",
    "rhode island": "RI",
    "south carolina": "SC",
    "south dakota": "SD",
    tennessee: "TN",
    texas: "TX",
    utah: "UT",
    vermont: "VT",
    virginia: "VA",
    washington: "WA",
    "west virginia": "WV",
    wisconsin: "WI",
    wyoming: "WY",
};

/** Lowercase AU state/territory name → common abbrev (Places often returns “Queensland”, not “QLD”). */
const AU_STATE_NAME_TO_CODE = {
    "australian capital territory": "ACT",
    "new south wales": "NSW",
    "northern territory": "NT",
    queensland: "QLD",
    "south australia": "SA",
    tasmania: "TAS",
    victoria: "VIC",
    "western australia": "WA",
};

/** Adjust Pin keeps a business name while the pin stays within this distance of the original place. */
const LOCATION_PIN_KEEP_PLACE_METERS = 200;
/** Confirming Adjust Pin without a real move does not reverse-geocode. */
const LOCATION_PIN_MOVE_EPSILON_METERS = 1;

function formatCoordinateDegrees(value) {
    const abs = Math.abs(Number(value));
    const trimmed = abs.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
    return trimmed.indexOf(".") === -1 ? trimmed + ".0" : trimmed;
}

/** Display form: `27.4698° S, 153.0251° E`. */
function formatLocationCoordinatesLabel(lat, lng) {
    const latHem = Number(lat) < 0 ? "S" : "N";
    const lngHem = Number(lng) < 0 ? "W" : "E";
    return formatCoordinateDegrees(lat) + "° " + latHem + ", " + formatCoordinateDegrees(lng) + "° " + lngHem;
}

/**
 * Printed title for a coordinate or area pick.
 * City + country, otherwise state + country, otherwise the country, otherwise the formatted coordinates.
 */
function locationAreaTitle(address, lat, lng) {
    const city = address && address.city ? String(address.city).trim() : "";
    const state = address && address.state ? String(address.state).trim() : "";
    const country = address && address.countryName ? String(address.countryName).trim() : "";
    if (city && country) {
        return city + ", " + country;
    }
    if (city) {
        return city;
    }
    if (state && country) {
        return state + ", " + country;
    }
    if (state) {
        return state;
    }
    if (country) {
        return country;
    }
    return formatLocationCoordinatesLabel(lat, lng);
}

function pairToLatLng(first, second) {
    if (!Number.isFinite(first) || !Number.isFinite(second)) {
        return null;
    }
    let lat = first;
    let lng = second;
    if (Math.abs(first) > 90 && Math.abs(second) <= 90 && Math.abs(first) <= 180) {
        lng = first;
        lat = second;
    }
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        return null;
    }
    return { lat: lat, lng: lng };
}

function hemisphereToSigned(value, hemisphere) {
    const n = Math.abs(Number(value));
    if (!Number.isFinite(n)) {
        return null;
    }
    const h = String(hemisphere || "").toUpperCase();
    if (h === "S" || h === "W") {
        return -n;
    }
    if (h === "N" || h === "E") {
        return n;
    }
    return null;
}

function latLngFromHemisphereParts(firstValue, firstHem, secondValue, secondHem) {
    const a = hemisphereToSigned(firstValue, firstHem);
    const b = hemisphereToSigned(secondValue, secondHem);
    if (a == null || b == null) {
        return null;
    }
    const aKind = String(firstHem).toUpperCase();
    const bKind = String(secondHem).toUpperCase();
    const aIsLat = aKind === "N" || aKind === "S";
    const bIsLat = bKind === "N" || bKind === "S";
    if (aIsLat === bIsLat) {
        return null;
    }
    return pairToLatLng(aIsLat ? a : b, aIsLat ? b : a);
}

function dmsToDecimal(degrees, minutes, seconds) {
    const deg = Number(degrees);
    const min = minutes == null || minutes === "" ? 0 : Number(minutes);
    const sec = seconds == null || seconds === "" ? 0 : Number(seconds);
    if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec)) {
        return null;
    }
    if (min < 0 || min >= 60 || sec < 0 || sec >= 60) {
        return null;
    }
    return Math.abs(deg) + min / 60 + sec / 3600;
}

function looksLikeMapsUrl(input) {
    return /https?:\/\//i.test(input)
        || /google\.[^/\s]+\/maps/i.test(input)
        || /maps\.google\./i.test(input)
        || /^www\./i.test(input);
}

function parseMapsUrlCoordinates(input) {
    let decoded = input;
    try {
        decoded = decodeURIComponent(input);
    } catch (err) {
        decoded = input;
    }
    const at = decoded.match(/@(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (at) {
        return pairToLatLng(Number(at[1]), Number(at[2]));
    }
    const query = decoded.match(/[?&#](?:q|query)=(?:loc:)?\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i);
    if (query) {
        return pairToLatLng(Number(query[1]), Number(query[2]));
    }
    return null;
}

function parseDmsPair(input) {
    const part = "([+-]?\\d+(?:\\.\\d+)?)\\s*[°º]\\s*(?:(\\d+(?:\\.\\d+)?)\\s*['′’]\\s*)?(?:(\\d+(?:\\.\\d+)?)\\s*(?:[\"″]|'')\\s*)?\\s*([NnSsEeWw])";
    const match = input.match(new RegExp("^" + part + "(?:\\s*,\\s*|\\s+)" + part + "$"));
    if (!match) {
        return null;
    }
    const first = dmsToDecimal(match[1], match[2], match[3]);
    const second = dmsToDecimal(match[5], match[6], match[7]);
    if (first == null || second == null) {
        return null;
    }
    return latLngFromHemisphereParts(first, match[4], second, match[8]);
}

var TT_SUGGEST_ICON_SVG = {
    pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21c.4 0 6.25-5.2 6.25-10.1a6.25 6.25 0 1 0-12.5 0C5.75 15.8 11.6 21 12 21z"/><circle cx="12" cy="10.9" r="1.9"/></svg>',
    briefcase: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="8" width="17" height="11" rx="2"/><path d="M8.5 8V6.4A1.4 1.4 0 0 1 9.9 5h4.2a1.4 1.4 0 0 1 1.4 1.4V8"/><path d="M3.5 13h17"/></svg>',
    food: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3.5v6.2"/><path d="M4.2 3.5v5.2a1.8 1.8 0 0 0 3.6 0V3.5"/><path d="M6 9.7V20.5"/><path d="M16.2 3.5c1.7 2 2.1 3.6 2.1 5.6 0 1.5-.9 2.6-2.1 2.6V20.5"/><path d="M16.2 3.5v8.2"/></svg>',
    bed: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 18.5V8.5"/><path d="M4 14.5h16"/><path d="M20 18.5V11"/><path d="M4 18.5h16"/><path d="M8 14.5v-1.2A1.6 1.6 0 0 1 9.6 11.7h2.2a1.6 1.6 0 0 1 1.6 1.6v1.2"/></svg>',
    city: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20V10.5h5.5V20"/><path d="M9.5 20V6.5H16V20"/><path d="M16 20v-7h4V20"/><path d="M3 20h18"/></svg>',
    plane: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 12.2 20.5 4.5l-6.6 15.2-2.2-6.3-8.2-1.2z"/><path d="M11.7 13.4 20.5 4.5"/></svg>',
    tree: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21v-4.5"/><path d="M12 3.5 6.2 13h3.1L6.8 17.2h10.4L14.7 13h3.1L12 3.5z"/></svg>',
    school: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.2 12 5.5l9 4.7-9 4.7-9-4.7z"/><path d="M7 12.2v4.2c1.7 1.5 8.3 1.5 10 0v-4.2"/><path d="M21 10.2v5.5"/></svg>',
    medical: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.5" y="4.5" width="15" height="15" rx="3.5"/><path d="M12 8.2v7.6M8.2 12h7.6"/></svg>',
    transit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="3.5" width="12" height="13" rx="3"/><path d="M6 10.5h12"/><circle cx="9.2" cy="13.6" r="0.9" fill="currentColor" stroke="none"/><circle cx="14.8" cy="13.6" r="0.9" fill="currentColor" stroke="none"/><path d="M8.2 20.5 9.2 16.5M15.8 20.5 14.8 16.5"/></svg>'
};

function locationSuggestionIconSvg(suggestion) {
    var types = suggestion && suggestion.types ? suggestion.types : [];
    function has(list) {
        for (var i = 0; i < list.length; i++) {
            if (types.indexOf(list[i]) !== -1) {
                return true;
            }
        }
        return false;
    }
    var name = "pin";
    if (!suggestion || suggestion.source === "coordinates") {
        name = "pin";
    } else if (has(["airport"])) {
        name = "plane";
    } else if (has(["park", "national_park", "campground", "natural_feature"])) {
        name = "tree";
    } else if (has(["school", "primary_school", "secondary_school", "university"])) {
        name = "school";
    } else if (has(["hospital", "doctor", "dentist", "pharmacy", "physiotherapist"])) {
        name = "medical";
    } else if (has(["train_station", "transit_station", "bus_station", "subway_station", "light_rail_station"])) {
        name = "transit";
    } else if (has(["lodging"])) {
        name = "bed";
    } else if (has(["restaurant", "cafe", "bar", "bakery", "meal_takeaway", "meal_delivery", "food"])) {
        name = "food";
    } else if (has(["establishment", "point_of_interest", "store", "shopping_mall", "supermarket", "gym", "spa", "bank", "atm"])) {
        name = "briefcase";
    } else if (has(["locality", "postal_code", "neighborhood", "sublocality", "administrative_area_level_1", "administrative_area_level_2", "administrative_area_level_3", "colloquial_area", "political", "country"])) {
        name = "city";
    }
    return TT_SUGGEST_ICON_SVG[name] || TT_SUGGEST_ICON_SVG.pin;
}

function parseHemispherePair(input) {
    const match = input.match(/^([+-]?\d+(?:\.\d+)?)\s*[°º]?\s*([NnSsEeWw])(?:\s*,\s*|\s+)([+-]?\d+(?:\.\d+)?)\s*[°º]?\s*([NnSsEeWw])$/);
    if (!match) {
        return null;
    }
    return latLngFromHemisphereParts(match[1], match[2], match[3], match[4]);
}

function parseDecimalPair(input) {
    if (input.indexOf(".") === -1 || /[A-Za-z°º]/.test(input)) {
        return null;
    }
    const match = input.match(/^([+-]?\d+(?:\.\d+)?)(?:\s*,\s*|\s+)([+-]?\d+(?:\.\d+)?)$/);
    if (!match) {
        return null;
    }
    return pairToLatLng(Number(match[1]), Number(match[2]));
}

/**
 * Returns `{ lat, lng }` only when the entire string is a coordinate pair or a Google Maps
 * link that contains one. Anything else returns null so place search stays unchanged.
 */
function parseLocationCoordinates(raw) {
    if (raw == null) {
        return null;
    }
    const input = String(raw).trim().replace(/\s+/g, " ");
    if (!input) {
        return null;
    }
    if (looksLikeMapsUrl(input)) {
        return parseMapsUrlCoordinates(input);
    }
    if (/[°º]/.test(input) && /['′’"″]/.test(input)) {
        const dms = parseDmsPair(input);
        if (dms) {
            return dms;
        }
    }
    if (/[NnSsEeWw]/.test(input)) {
        const hemisphere = parseHemispherePair(input);
        if (hemisphere) {
            return hemisphere;
        }
    }
    return parseDecimalPair(input);
}

function haversineMeters(lat1, lng1, lat2, lng2) {
    if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) {
        return Infinity;
    }
    const toRad = function (deg) {
        return deg * Math.PI / 180;
    };
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * City, state, and country from Geocoding `address_components`.
 * Establishment and point-of-interest names are ignored.
 */
function addressFieldsFromComponents(components) {
    let city = "";
    let stateName = "";
    let stateCode = "";
    let country = "";
    let countryCode = "";
    const list = components || [];
    for (let i = 0; i < list.length; i++) {
        const c = list[i];
        const typesArr = c.types || [];
        const longName = c.longText || c.long_name || "";
        if (typesArr.indexOf("locality") !== -1 && !city) {
            city = longName;
        }
        if (typesArr.indexOf("postal_town") !== -1 && !city) {
            city = longName;
        }
        if (typesArr.indexOf("administrative_area_level_1") !== -1) {
            if (!stateName) {
                stateName = longName;
            }
            const shortSt = (c.shortText || c.short_name || "").trim();
            if (
                !stateCode &&
                shortSt.length >= 2 &&
                shortSt.length <= 3 &&
                /^[A-Za-z]+$/.test(shortSt)
            ) {
                stateCode = shortSt.toUpperCase();
            }
        }
        if (typesArr.indexOf("country") !== -1 && !country) {
            country = longName;
            countryCode = (c.shortText || c.short_name || "").toUpperCase();
        }
    }
    return {
        city: city,
        stateName: stateName,
        stateCode: stateCode,
        country: country,
        countryCode: countryCode,
    };
}

function addressFieldsFromGeocodeResults(results) {
    const merged = {
        city: "",
        stateName: "",
        stateCode: "",
        country: "",
        countryCode: "",
    };
    const rows = results || [];
    for (let i = 0; i < rows.length; i++) {
        const fields = addressFieldsFromComponents(rows[i].address_components || rows[i].addressComponents || []);
        if (!merged.city && fields.city) merged.city = fields.city;
        if (!merged.stateName && fields.stateName) merged.stateName = fields.stateName;
        if (!merged.stateCode && fields.stateCode) merged.stateCode = fields.stateCode;
        if (!merged.country && fields.country) merged.country = fields.country;
        if (!merged.countryCode && fields.countryCode) merged.countryCode = fields.countryCode;
    }
    return merged;
}

function buildCoordinateLocation(lat, lng, area) {
    const address = {
        label: "",
        city: area && area.city ? area.city : "",
        countryName: area && area.country ? area.country : "",
        district: area && area.stateName ? area.stateName : "",
        countryCode: area && area.countryCode ? area.countryCode : "",
        state: area && area.stateName ? area.stateName : "",
        stateCode: area && area.stateCode ? area.stateCode : "",
    };
    const title = locationAreaTitle(address, lat, lng);
    address.label = title;
    return {
        position: { lat: lat, lng: lng },
        title: title,
        address: address,
        resultType: "coordinate",
    };
}

/**
 * WGS84 box from CRM maps JSON: top-level `geo_bounds` or the same keys on `meta`.
 * Used for marker_x/y when the raster matches this geographic framing.
 */
function normalizeMapsApiGeoBounds(metaOrGeo) {
    if (!metaOrGeo || typeof metaOrGeo !== "object") {
        return null;
    }
    const g =
        metaOrGeo.geo_bounds != null && typeof metaOrGeo.geo_bounds === "object"
            ? metaOrGeo.geo_bounds
            : metaOrGeo;
    const west = Number(g.west);
    const east = Number(g.east);
    const south = Number(g.south);
    const north = Number(g.north);
    if (![west, east, south, north].every(Number.isFinite)) {
        return null;
    }
    if (!(east > west && north > south)) {
        return null;
    }
    return { west: west, east: east, south: south, north: north };
}

const DEFAULT_MAPS_API_BASE = "https://app.agentallen.ai";
const GPS_ROUTES_API_URL = DEFAULT_MAPS_API_BASE + "/api/gps-routes";

/** @type {Map<string, { etag: string, body: object }>} */
const mapsSvgRenderResultCache = new Map();

function clampMapsWidth(w) {
    const n = Math.round(Number(w));
    if (!Number.isFinite(n)) {
        return 1000;
    }
    return Math.min(4096, Math.max(16, n));
}

function escapeSvgAttr(s) {
    if (s == null) {
        return "";
    }
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function svgRenderResultToSvgString(data) {
    if (!data || data.view_box == null || !Array.isArray(data.paths)) {
        return "";
    }
    const pathMarkup = data.paths
        .map(function (p) {
            if (!p || p.d == null) {
                return "";
            }
            const idAttr = p.id != null ? " id=\"" + escapeSvgAttr(p.id) + "\"" : "";
            return "<path class=\"country-shape\"" + idAttr + ' d="' + escapeSvgAttr(p.d) + '"/>';
        })
        .join("");
    return (
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' +
        escapeSvgAttr(data.view_box) +
        '">' +
        pathMarkup +
        "</svg>"
    );
}

function normalizeMapsIso(iso) {
    if (iso == null || String(iso).trim() === "") {
        return "";
    }
    const s = String(iso).trim();
    if (!/^[A-Za-z]{2,3}$/.test(s)) {
        return "";
    }
    return s.toLowerCase();
}

/** When `maps_iso` is omitted, map product slug → CRM alpha-2 (extend as backend adds regions). */
const MAPS_SLUG_TO_ISO = {
    ireland: "ie",
    australia: "au",
    usa: "us",
    uk: "gb",
    "new-zealand": "nz",
    canada: "ca",
};

const LINE_ART_GENERATION_HISTORY_MAX = 8;
const FALLBACK_DESIGN_URL = "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/fallback_design.png?v=1787195159";
const TT_DESIGN_DB = "tt-designs";
const TT_DESIGN_STORE = "designs";
const TT_DESIGN_GENERATED_INPUTS = {
    "country-map-image": true,
    "map-image": true,
    "sky_map": true,
    "moon_image": true,
};

function ttOpenDesignDb() {
    return new Promise((resolve, reject) => {
        if (!window.indexedDB) {
            reject(new Error("IndexedDB unavailable"));
            return;
        }
        const request = indexedDB.open(TT_DESIGN_DB, 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(TT_DESIGN_STORE)) {
                db.createObjectStore(TT_DESIGN_STORE);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
    });
}

function ttPutDesign(key, record) {
    return ttOpenDesignDb().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction(TT_DESIGN_STORE, "readwrite");
        tx.objectStore(TT_DESIGN_STORE).put(record, key);
        tx.oncomplete = () => {
            db.close();
            resolve();
        };
        tx.onerror = () => {
            db.close();
            reject(tx.error || new Error("IndexedDB write failed"));
        };
    }));
}

function ttGetDesign(key) {
    return ttOpenDesignDb().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction(TT_DESIGN_STORE, "readonly");
        const request = tx.objectStore(TT_DESIGN_STORE).get(key);
        request.onsuccess = () => {
            db.close();
            resolve(request.result || null);
        };
        request.onerror = () => {
            db.close();
            reject(request.error || new Error("IndexedDB read failed"));
        };
    }));
}

const TT_RENDER_API = DEFAULT_MAPS_API_BASE + "/api";
const TT_RENDER_RESERVE_TIMEOUT_MS = 8000;
const TT_RENDER_RESERVE_ATTEMPTS = 2;
const TT_RENDER_UPLOAD_TIMEOUT_MS = 60000;
const TT_RENDER_UPLOAD_ATTEMPTS = 3;
const TT_RENDER_PREVIEWS_KEY = "tt-render-previews";
const TT_RENDER_PREVIEWS_MAX = 6;
const ttRenderEventBatches = {};
let ttRenderEventTimer = null;
const ttRenderUploads = {
    pending: {},
    previews: ttReadRenderPreviews(),
};

function ttFetchWithTimeout(url, options, timeoutMs) {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    return fetch(url, Object.assign({}, options, controller ? { signal: controller.signal } : {}))
        .finally(() => clearTimeout(timer));
}

function ttErrorText(error) {
    if (!error) return "unknown";
    if (error.name === "AbortError") return "timeout";
    return String(error.message || error).slice(0, 300);
}

function ttDelay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Resolves after the browser has painted, so UI set before blocking work is visible. */
function ttNextPaint() {
    return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
}

function ttFlushRenderEvents() {
    clearTimeout(ttRenderEventTimer);
    ttRenderEventTimer = null;
    Object.keys(ttRenderEventBatches).forEach((attemptId) => {
        const batch = ttRenderEventBatches[attemptId];
        if (!batch.events.length) return;
        const body = JSON.stringify({ render_id: batch.render_id, attempt_id: attemptId, events: batch.events.splice(0, 20) });
        const url = TT_RENDER_API + "/renders/events";
        try {
            if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
            fetch(url, { method: "POST", body: body, keepalive: true, headers: { "Content-Type": "text/plain" } }).catch(() => {});
        } catch (error) {}
    });
}

/** Records an add-to-cart / upload stage; batches are sent with sendBeacon. */
function ttLogRenderEvent(attemptId, stage, extra, renderId) {
    if (!attemptId) return;
    const batch = ttRenderEventBatches[attemptId] || (ttRenderEventBatches[attemptId] = { render_id: null, events: [] });
    if (renderId) batch.render_id = renderId;
    const event = { stage: stage };
    if (extra && Number.isFinite(extra.ms)) event.ms = Math.max(0, Math.round(extra.ms));
    if (extra && extra.error) event.error = String(extra.error).slice(0, 300);
    if (extra && extra.detail) event.detail = String(extra.detail).slice(0, 300);
    batch.events.push(event);
    if (batch.events.length >= 20) {
        ttFlushRenderEvents();
    } else if (!ttRenderEventTimer) {
        ttRenderEventTimer = setTimeout(ttFlushRenderEvents, 1500);
    }
}

function ttReadRenderPreviews() {
    try {
        return JSON.parse(sessionStorage.getItem(TT_RENDER_PREVIEWS_KEY) || "{}") || {};
    } catch (error) {
        return {};
    }
}

function ttSaveRenderPreviews() {
    const urls = Object.keys(ttRenderUploads.previews);
    const kept = {};
    urls.slice(-TT_RENDER_PREVIEWS_MAX).forEach((url) => {
        kept[url] = ttRenderUploads.previews[url];
    });
    ttRenderUploads.previews = kept;
    try {
        sessionStorage.setItem(TT_RENDER_PREVIEWS_KEY, JSON.stringify(kept));
    } catch (error) {
        try {
            sessionStorage.removeItem(TT_RENDER_PREVIEWS_KEY);
        } catch (ignored) {}
    }
}

/** Tells the cart drawer which uploads are pending and which local previews to show. */
function ttPublishRenderUploads() {
    window.__ttRenderUploads = {
        pending: Object.keys(ttRenderUploads.pending).length,
        previews: Object.assign({}, ttRenderUploads.previews),
    };
    window.dispatchEvent(new CustomEvent("tt-render-uploads", { detail: window.__ttRenderUploads }));
}

function ttStartPendingRender(renderId, fileUrl, previewDataUrl, attemptId) {
    ttRenderUploads.pending[renderId] = { attemptId: attemptId, startedAt: Date.now() };
    if (fileUrl && previewDataUrl) {
        ttRenderUploads.previews[fileUrl] = previewDataUrl;
        ttSaveRenderPreviews();
    }
    ttPublishRenderUploads();
}

function ttFinishPendingRender(renderId) {
    delete ttRenderUploads.pending[renderId];
    ttPublishRenderUploads();
}

window.addEventListener("pagehide", () => {
    Object.keys(ttRenderUploads.pending).forEach((renderId) => {
        const pending = ttRenderUploads.pending[renderId];
        ttLogRenderEvent(pending.attemptId, "upload_abandoned", { ms: Date.now() - pending.startedAt }, renderId);
    });
    ttFlushRenderEvents();
});

ttPublishRenderUploads();

/** Dominant hue of the orange aura artwork, in degrees. */
const AURA_ARTWORK_BASE_HUE = 27;
const auraTintedArtworkCache = {};

const angelAuraConfig = {
    presets: {
        "111": {
            title: "New Beginnings",
            titleSuggestions: ["New Beginnings", "Make It Happen", "Trust Yourself", "Your Time Is Now", "Create Your Reality"],
            keywords: ["Manifestation", "Intuition", "Alignment"],
            wordSuggestions: ["Beginnings", "Intention", "Opportunity"],
            recommended: { shape: "heart", palette: "love" },
        },
        "222": {
            title: "Balance",
            titleSuggestions: ["Balance", "Trust the Timing", "In Alignment", "Keep the Faith", "Trust the Journey"],
            keywords: ["Persistence", "Positivity", "Growth"],
            wordSuggestions: ["Trust", "Patience", "Connection"],
            recommended: { shape: "circle", palette: "ocean" },
        },
        "333": {
            title: "Growth",
            titleSuggestions: ["Growth", "You Are Supported", "Keep Going", "Step Into Your Power", "Guided & Growing"],
            keywords: ["Support", "Guidance", "Creativity"],
            wordSuggestions: ["Inspiration", "Expression", "Confidence"],
            recommended: { shape: "flower", palette: "lavender" },
        },
        "444": {
            title: "Protected",
            titleSuggestions: ["Protected", "You Are Safe", "Strong Foundations", "Trust the Process", "Stay Grounded"],
            keywords: ["Protection", "Strength", "Foundation"],
            wordSuggestions: ["Stability", "Security", "Grounding"],
            recommended: { shape: "star", palette: "earth" },
        },
        "555": {
            title: "Embrace Change",
            titleSuggestions: ["Embrace Change", "Transformation", "Change Is Coming", "Move Forward", "A New Chapter"],
            keywords: ["Freedom", "Adventure", "Transformation"],
            wordSuggestions: ["Change", "Discovery", "Evolution"],
            recommended: { shape: "circle", palette: "sunset" },
        },
        "666": {
            title: "Find Your Balance",
            titleSuggestions: ["Find Your Balance", "Realign", "Come Back to You", "Inner Peace", "Reset"],
            keywords: ["Balance", "Harmony", "Healing"],
            wordSuggestions: ["Peace", "Reflection", "Compassion"],
            recommended: { shape: "heart", palette: "love" },
        },
        "777": {
            title: "Lucky You",
            titleSuggestions: ["Lucky You", "Trust Your Intuition", "Divine Timing", "Follow Your Path", "Good Things Are Coming"],
            keywords: ["Wisdom", "Spirituality", "Insight"],
            wordSuggestions: ["Awakening", "Purpose", "Awareness"],
            recommended: { shape: "star", palette: "lavender" },
        },
        "888": {
            title: "Abundance",
            titleSuggestions: ["Abundance", "Success Is Coming", "Prosperity", "Know Your Worth", "Endless Abundance"],
            keywords: ["Success", "Prosperity", "Power"],
            wordSuggestions: ["Abundance", "Achievement", "Reward"],
            recommended: { shape: "star", palette: "earth" },
        },
        "999": {
            title: "New Chapter",
            titleSuggestions: ["New Chapter", "Let Go", "Full Circle", "Release & Renew", "Ready for What's Next"],
            keywords: ["Closure", "Release", "Renewal"],
            wordSuggestions: ["Completion", "Acceptance", "Rebirth"],
            recommended: { shape: "circle", palette: "sunset" },
        },
        "1111": {
            title: "Alignment",
            titleSuggestions: ["Alignment"],
            keywords: ["Intuition", "Awakening", "Purpose"],
            wordSuggestions: ["Intuition", "Awakening", "Purpose"],
            recommended: { shape: "flower", palette: "ocean" },
        },
    },
    palettes: {
        ocean: "#43BED8",
        love: "#FF2E78",
        sunset: "#FF8E52",
        lavender: "#8F83E8",
        earth: "#C4A46A",
        sky: "#5C8DFF",
        rose: "#F472B6",
        sun: "#F0C14B",
        moss: "#7DAF6A",
        jade: "#3CB89A",
    },
    panels: {
        panel: { title: "Gradient", icon: "gradient" },
        transparent: { title: "Transparent", icon: "checker" },
    },
    background: {
        url: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Rectangle_1.png?v=1790073359",
        x: 104,
        y: 175,
        width: 792,
        height: 1026,
    },
    shapes: {
        circle: {
            title: "Circle",
            url: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Circle.png?v=1790073358",
            x: 104,
            y: 293,
            width: 792,
            height: 792,
        },
        flower: {
            title: "Flower",
            url: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Flower.png?v=1790073358",
            x: 106,
            y: 293,
            width: 789,
            height: 792,
        },
        star: {
            title: "Star",
            url: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Star.png?v=1790073358",
            x: 119,
            y: 321,
            width: 764,
            height: 734,
        },
        heart: {
            title: "Heart",
            url: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart_ac75527c-13b0-4c13-84d9-10a46b7fa890.png?v=1790073358",
            x: 116,
            y: 366,
            width: 768,
            height: 668,
        },
    },
};

function auraHumanizeKey(key) {
    return String(key || "")
        .replace(/([A-Z])/g, " $1")
        .replace(/[_-]/g, " ")
        .replace(/^\s+/, "")
        .replace(/\b\w/g, function (ch) { return ch.toUpperCase(); });
}

function buildAngelNumberSelectorOptions() {
    return Object.keys(angelAuraConfig.presets).map(function (num) {
        var preset = angelAuraConfig.presets[num];
        return {
            title: num,
            values: [
                { id: "title", value: preset.title },
                { id: "keywords", value: (preset.keywords || []).slice() },
            ],
        };
    });
}

const AURA_PALETTE_ORDER = ["love", "ocean", "sunset", "lavender", "earth", "sky", "rose", "sun", "moss", "jade"];

function buildAuraShapePickerOptions() {
    return Object.keys(angelAuraConfig.shapes).map(function (id) {
        const shape = angelAuraConfig.shapes[id];
        return {
            id: id,
            title: shape.title || auraHumanizeKey(id),
            image: shape.url,
        };
    });
}

function buildAuraPalettePickerOptions() {
    const ids = AURA_PALETTE_ORDER.filter(function (id) {
        return Object.prototype.hasOwnProperty.call(angelAuraConfig.palettes, id);
    });
    Object.keys(angelAuraConfig.palettes).forEach(function (id) {
        if (ids.indexOf(id) === -1) {
            ids.push(id);
        }
    });
    return ids.map(function (id) {
        return { id: id, title: auraHumanizeKey(id), color: angelAuraConfig.palettes[id] };
    });
}

function buildAuraPanelPickerOptions() {
    return Object.keys(angelAuraConfig.panels).map(function (id) {
        const panel = angelAuraConfig.panels[id];
        return {
            id: id,
            title: panel.title || auraHumanizeKey(id),
            icon: panel.icon || id,
        };
    });
}

const PLAYING_CARD_RED = "#E72020";
const PLAYING_CARD_BLACK = "#000000";
const PLAYING_CARD_SUITS = {
    hearts: {
        title: "Hearts",
        picker: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart_590c5896-4a98-43f6-b55a-39defb60eb87.png?v=1790915556",
        pip: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart_044b95d1-4466-4eaa-bada-37e4f5fce8a4.png?v=1790915910",
    },
    spades: {
        title: "Spades",
        picker: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Spades_e29b378d-4846-4b8a-90c7-8e39c969bdb0.png?v=1790915538",
        pip: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Spades_6546109d-504a-4480-882a-d2a1d90feb0d.png?v=1790915910",
    },
    clubs: {
        title: "Clubs",
        picker: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Clubs_2dce998f-aac9-4ce6-86e8-76758da2a3ff.png?v=1790915558",
        pip: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Clubs_b1dd637b-a9ca-41b2-9397-a6a3570e1be1.png?v=1790915909",
    },
    diamonds: {
        title: "Diamonds",
        picker: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Diamond_e306627c-006d-4ffb-b3a4-f991789dbe8f.png?v=1790915558",
        pip: "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Diamond_a5a5a565-243a-494a-8a81-7f5265704c86.png?v=1790915910",
    },
};

function playingCardSuitUrl(suitId) {
    const suit = PLAYING_CARD_SUITS[suitId] || PLAYING_CARD_SUITS.hearts;
    return suit.pip;
}

function playingCardDefaultColor(suitId) {
    return suitId === "spades" || suitId === "clubs" ? "black" : "red";
}

function playingCardColorHex(colorId) {
    return colorId === "black" ? PLAYING_CARD_BLACK : PLAYING_CARD_RED;
}

function buildPlayingCardValueOptions() {
    const labels = {
        A: "A - Ace",
        J: "J - Jack",
        Q: "Q - Queen",
        K: "K - King",
    };
    const options = ["A", "K", "Q", "J", "10", "9", "8", "7", "6", "5", "4", "3", "2"].map(function (title) {
        const option = { title: title };
        if (labels[title]) {
            option.label = labels[title];
        }
        return option;
    });
    options.push({ title: "custom", label: "Add Custom Letter" });
    return options;
}

function playingCardCustomFieldId(fieldId) {
    return fieldId + "_custom";
}

function playingCardCustomLetter(userInput, fieldId) {
    const typed = userInput ? userInput[playingCardCustomFieldId(fieldId)] : "";
    return typed == null ? "" : String(typed).slice(0, 1).toUpperCase();
}

function buildPlayingCardSuitOptions() {
    return ["hearts", "spades", "clubs", "diamonds"].map(function (id) {
        const suit = PLAYING_CARD_SUITS[id];
        return { id: id, title: suit.title, image: suit.picker };
    });
}

function buildPlayingCardColorOptions() {
    return [
        { id: "red", title: "Red", icon: "red" },
        { id: "black", title: "Black", icon: "black" },
    ];
}

function auraPaletteHex(paletteId) {
    const hex = angelAuraConfig.palettes[paletteId] || angelAuraConfig.palettes.love;
    return typeof hex === "string" ? hex : angelAuraConfig.palettes.love;
}

/** 8×8 Bayer matrix, 0–63. Breaks 8-bit gradient steps into fine grain. */
const AURA_BAYER_8 = new Uint8Array([
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21,
]);

/**
 * Aura PNGs are soft radial gradients stored in 8-bit, so each step sits on the
 * same colour for many pixels and reads as rings after a hue spin. Blur the
 * colour (alpha unchanged) so those stairs become a ramp, then dither back to 8-bit.
 */
const AURA_DEBAND_RADIUS = 4;

function boxBlurAuraPremultiplied(channel, weight, width, height, radius) {
    const n = width * height;
    const horiz = new Float32Array(n);
    const horizWeight = new Float32Array(n);
    const straight = new Float32Array(n);
    const span = Math.max(width, height) + 1;
    const prefix = new Float64Array(span);
    const prefixWeight = new Float64Array(span);

    for (let y = 0; y < height; y++) {
        const row = y * width;
        for (let x = 0; x < width; x++) {
            prefix[x + 1] = prefix[x] + channel[row + x];
            prefixWeight[x + 1] = prefixWeight[x] + weight[row + x];
        }
        for (let x = 0; x < width; x++) {
            const x0 = x - radius > 0 ? x - radius : 0;
            const x1 = x + radius < width ? x + radius : width - 1;
            horiz[row + x] = prefix[x1 + 1] - prefix[x0];
            horizWeight[row + x] = prefixWeight[x1 + 1] - prefixWeight[x0];
        }
    }

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            prefix[y + 1] = prefix[y] + horiz[y * width + x];
            prefixWeight[y + 1] = prefixWeight[y] + horizWeight[y * width + x];
        }
        for (let y = 0; y < height; y++) {
            const y0 = y - radius > 0 ? y - radius : 0;
            const y1 = y + radius < height ? y + radius : height - 1;
            const summedWeight = prefixWeight[y1 + 1] - prefixWeight[y0];
            const i = y * width + x;
            straight[i] = summedWeight > 0 ? (prefix[y1 + 1] - prefix[y0]) / summedWeight : 0;
        }
    }
    return straight;
}

function debandAuraImageData(imageData, angleDeg) {
    const width = imageData.width;
    const height = imageData.height;
    const src = imageData.data;
    const n = width * height;
    const premR = new Float32Array(n);
    const premG = new Float32Array(n);
    const premB = new Float32Array(n);
    const weight = new Float32Array(n);
    for (let i = 0, o = 0; i < n; i++, o += 4) {
        const alpha = src[o + 3] / 255;
        weight[i] = alpha;
        premR[i] = (src[o] / 255) * alpha;
        premG[i] = (src[o + 1] / 255) * alpha;
        premB[i] = (src[o + 2] / 255) * alpha;
    }
    const red = boxBlurAuraPremultiplied(premR, weight, width, height, AURA_DEBAND_RADIUS);
    const green = boxBlurAuraPremultiplied(premG, weight, width, height, AURA_DEBAND_RADIUS);
    const blue = boxBlurAuraPremultiplied(premB, weight, width, height, AURA_DEBAND_RADIUS);
    const delta = (((Number(angleDeg) % 360) + 360) % 360) / 360;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            const o = i * 4;
            if (src[o + 3] === 0) {
                continue;
            }
            let r = red[i];
            let g = green[i];
            let b = blue[i];
            if (delta !== 0) {
                const hsl = rgbToHslCountryMap(r, g, b);
                hsl[0] = (hsl[0] + delta + 1) % 1;
                const rgb = hslToRgbCountryMap(hsl[0], hsl[1], hsl[2]);
                r = rgb[0];
                g = rgb[1];
                b = rgb[2];
            }
            const threshold = (AURA_BAYER_8[((y & 7) << 3) + (x & 7)] + 0.5) / 64;
            const channels = [r, g, b];
            for (let c = 0; c < 3; c++) {
                let value = Math.floor(channels[c] * 255 + threshold);
                if (value < 0) {
                    value = 0;
                } else if (value > 255) {
                    value = 255;
                }
                src[o + c] = value;
            }
        }
    }
}

function tintAuraArtwork(image, angleDeg) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    debandAuraImageData(imageData, angleDeg);
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

function stackAuraArtwork(canvas) {
    if (!canvas || typeof canvas.sendToBack !== "function") {
        return;
    }
    const objects = canvas.getObjects();
    const panel = objects.find(function (obj) { return obj.canvasElementId === "aura_panel"; });
    const shape = objects.find(function (obj) { return obj.canvasElementId === "aura"; });
    if (panel) {
        canvas.sendToBack(panel);
    }
    if (shape) {
        canvas.sendToBack(shape);
        if (panel && typeof canvas.bringForward === "function") {
            canvas.bringForward(shape);
        }
    }
    if (typeof canvas.requestRenderAll === "function") {
        canvas.requestRenderAll();
    }
}

function placeTintedAuraImage(vm, frame, paletteId, canvasId, canvasElementId) {
    if (!frame || !frame.url || !vm || !vm.canvasCreator) {
        return;
    }
    const hex = auraPaletteHex(paletteId);
    const cacheKey = frame.url + "|" + hex;
    const place = function (source) {
        if (canvasId != null && canvasId !== vm.canvasBuildVersion) {
            return;
        }
        const sourceWidth = source.width || source.naturalWidth;
        const sourceHeight = source.height || source.naturalHeight;
        if (!sourceWidth || !sourceHeight) {
            return;
        }
        const img = new fabric.Image(source, {
            left: frame.x,
            top: frame.y,
            originX: "left",
            originY: "top",
            scaleX: frame.width / sourceWidth,
            scaleY: frame.height / sourceHeight,
            selectable: false,
            evented: false,
            objectCaching: false,
            imageSmoothing: true,
            canvasElementId: canvasElementId,
        });
        vm.canvasCreator.add(img);
        stackAuraArtwork(vm.canvasCreator);
    };
    if (auraTintedArtworkCache[cacheKey]) {
        place(auraTintedArtworkCache[cacheKey]);
        return;
    }
    vm.beginCanvasAssetLoad(canvasId);
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.onload = function () {
        try {
            if (!auraTintedArtworkCache[cacheKey]) {
                const targetHue = vm.hexColorToHueDegrees(hex);
                const angle = vm.countryMapTextureHueRotateDegrees(targetHue, AURA_ARTWORK_BASE_HUE);
                auraTintedArtworkCache[cacheKey] = tintAuraArtwork(image, angle);
            }
            place(auraTintedArtworkCache[cacheKey]);
        } finally {
            vm.finishCanvasAssetLoad(canvasId);
        }
    };
    image.onerror = function () {
        vm.finishCanvasAssetLoad(canvasId);
    };
    image.src = frame.url;
}

function resolveMapsIsoForCountryRow(countryRow, slug) {
    if (countryRow && countryRow.maps_iso != null && String(countryRow.maps_iso).trim() !== "") {
        const fromField = normalizeMapsIso(countryRow.maps_iso);
        if (fromField) {
            return fromField;
        }
    }
    const s = slug != null ? String(slug).trim().toLowerCase() : "";
    if (!s) {
        return "";
    }
    return (
        (MAPS_SLUG_TO_ISO[s] || "") ||
        normalizeMapsIso(s) ||
        (s.length === 2 && /^[a-z]{2}$/.test(s) ? s : "")
    );
}

function buildMapsQueryParams(query) {
    const q = query || {};
    const params = new URLSearchParams();
    params.set("width", String(clampMapsWidth(q.width != null ? q.width : 1000)));
    if (q.height != null && Number.isFinite(Number(q.height))) {
        const h = Math.round(Number(q.height));
        if (h >= 16 && h <= 4096) {
            params.set("height", String(h));
        }
    }
    if (q.padding != null && Number.isFinite(Number(q.padding))) {
        const pad = Math.round(Number(q.padding));
        if (pad >= 0 && pad <= 512) {
            params.set("padding", String(pad));
        }
    }
    if (q.projection != null && String(q.projection).trim() !== "") {
        params.set("projection", String(q.projection).trim());
    }
    if (q.precision != null && Number.isFinite(Number(q.precision))) {
        const prec = Math.round(Number(q.precision));
        if (prec >= 0 && prec <= 6) {
            params.set("precision", String(prec));
        }
    }
    const qs = params.toString();
    return qs ? "?" + qs : "";
}

function buildMapsCountryUrl(base, iso, query) {
    const b = String(base || "").replace(/\/$/, "");
    const code = normalizeMapsIso(iso);
    if (!b || !code) {
        return "";
    }
    return b + "/api/maps/countries/" + encodeURIComponent(code) + buildMapsQueryParams(query);
}

/**
 * Path segment for GET .../countries/{country}/states/{segment} (e.g. ak).
 * Accepts USPS (AK), lowercase (ak), or list iso ({country}-{code}, e.g. us-ak).
 */
function normalizeMapsStatePathSegment(stateInput, countryIso) {
    let s = stateInput != null ? String(stateInput).trim().toLowerCase() : "";
    if (!s) {
        return "";
    }
    const c = normalizeMapsIso(countryIso);
    if (c) {
        const prefix = `${c}-`;
        if (s.startsWith(prefix)) {
            const rest = s.slice(prefix.length);
            s = rest.includes("-") ? (rest.split("-").pop() || rest) : rest;
        }
    }
    if (!/^[a-z0-9]{1,12}$/.test(s)) {
        return "";
    }
    return s;
}

function buildMapsStateUrl(base, countryIso, stateCode, query) {
    const b = String(base || "").replace(/\/$/, "");
    const c = normalizeMapsIso(countryIso);
    const st = normalizeMapsStatePathSegment(stateCode, countryIso);
    if (!b || !c || !st) {
        return "";
    }
    return (
        b +
        "/api/maps/countries/" +
        encodeURIComponent(c) +
        "/states/" +
        encodeURIComponent(st) +
        buildMapsQueryParams(query)
    );
}

async function fetchMapsSvgRenderResult(url, etag, signal) {
    if (!url) {
        throw new Error("missing maps URL");
    }
    const headers = {};
    if (etag) {
        headers["If-None-Match"] = etag;
    }
    const res = await fetch(url, {
        credentials: "omit",
        mode: "cors",
        headers: headers,
        signal: signal,
    });
    if (res.status === 304) {
        const cached = mapsSvgRenderResultCache.get(url);
        if (!cached || !cached.body) {
            throw new Error("maps API 304 without cache");
        }
        return { ok: true, notModified: true, data: cached.body, etag: cached.etag };
    }
    const newEtag = res.headers.get("ETag") || "";
    if (res.status === 422) {
        const err = new Error("maps API validation failed");
        err.status = 422;
        try {
            err.body = await res.json();
        } catch (e) {}
        throw err;
    }
    if (res.status === 404) {
        const err = new Error("maps API not found");
        err.status = 404;
        throw err;
    }
    if (!res.ok) {
        const err = new Error("maps API HTTP " + res.status);
        err.status = res.status;
        throw err;
    }
    const data = await res.json();
    mapsSvgRenderResultCache.set(url, { etag: newEtag, body: data });
    return { ok: true, notModified: false, data: data, etag: newEtag };
}

async function fetchCountryMapFromApi(options) {
    const opts = options || {};
    const baseUrl = (opts.baseUrl || DEFAULT_MAPS_API_BASE).replace(/\/$/, "");
    const slotW = opts.slotWidth != null ? opts.slotWidth : 1000;
    const query = {
        width: clampMapsWidth(slotW),
        height: opts.height,
        padding: opts.padding,
        projection: opts.projection,
        precision: opts.precision,
    };
    const stateRaw = opts.stateCode != null ? String(opts.stateCode).trim() : "";
    const url = stateRaw
        ? buildMapsStateUrl(baseUrl, opts.countryIso, stateRaw, query)
        : buildMapsCountryUrl(baseUrl, opts.countryIso, query);
    if (!url) {
        throw new Error("invalid maps API URL");
    }
    const cached = mapsSvgRenderResultCache.get(url);
    const prevEtag = cached && cached.etag ? cached.etag : "";
    const result = await fetchMapsSvgRenderResult(url, prevEtag, opts.signal);
    return {
        svgString: svgRenderResultToSvgString(result.data),
        meta: result.data,
        etag: result.etag,
    };
}

const CountryMapApiSvg = {
    props: {
        apiBase: { type: String, default: DEFAULT_MAPS_API_BASE },
        country: { type: String, default: "" },
        state: { type: String, default: "" },
        width: { type: [Number, String], default: null },
        height: { type: [Number, String], default: null },
        padding: { type: [Number, String], default: null },
        projection: { type: String, default: "" },
        precision: { type: [Number, String], default: null },
        pathFill: { type: String, default: "currentColor" },
        pathClass: { type: String, default: "" },
        /** When true, SVG fills the parent box without clipping (xMidYMid meet + explicit height). */
        fitToContainer: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: false,
            error: null,
            data: null,
            loadSeq: 0,
            abortController: null,
        };
    },
    computed: {
        pathClassList() {
            const parts = ["country-shape"];
            if (this.pathClass && String(this.pathClass).trim() !== "") {
                parts.push(String(this.pathClass).trim());
            }
            return parts.join(" ");
        },
        countryMapApiSvgInnerStyle() {
            if (this.fitToContainer) {
                return "display:block;width:100%;height:100%;max-width:100%;max-height:100%";
            }
            return "display:block;width:100%;height:auto";
        },
        mapsUrl() {
            const base = this.apiBase || DEFAULT_MAPS_API_BASE;
            const iso = normalizeMapsIso(this.country);
            if (!iso) {
                return "";
            }
            const w = this.width != null && this.width !== "" ? this.width : 1000;
            const query = {
                width: w,
                height: this.height,
                padding: this.padding,
                projection: this.projection ? this.projection : undefined,
                precision: this.precision,
            };
            const st = this.state != null && String(this.state).trim() !== "" ? String(this.state).trim() : "";
            if (st) {
                return buildMapsStateUrl(base, iso, st, query);
            }
            return buildMapsCountryUrl(base, iso, query);
        },
    },
    watch: {
        mapsUrl: {
            handler() {
                this.load();
            },
            immediate: true,
        },
    },
    methods: {
        async load() {
            const seq = ++this.loadSeq;
            if (this.abortController) {
                this.abortController.abort();
            }
            const url = this.mapsUrl;
            if (!url) {
                this.abortController = null;
                this.loading = false;
                this.error = null;
                this.data = null;
                return;
            }
            const controller = new AbortController();
            this.abortController = controller;
            const signal = controller.signal;
            this.loading = true;
            this.error = null;
            try {
                const cached = mapsSvgRenderResultCache.get(url);
                const prevEtag = cached && cached.etag ? cached.etag : "";
                const res = await fetchMapsSvgRenderResult(url, prevEtag, signal);
                if (seq !== this.loadSeq || signal.aborted) {
                    return;
                }
                this.data = res.data;
            } catch (e) {
                if (e.name === "AbortError" || seq !== this.loadSeq || signal.aborted) {
                    return;
                }
                this.data = null;
                if (e.status === 404) {
                    this.error = "not found";
                } else if (e.status === 422) {
                    this.error = "invalid request";
                } else {
                    this.error = e.message || String(e);
                }
            } finally {
                if (seq === this.loadSeq) {
                    this.loading = false;
                }
                if (this.abortController === controller) {
                    this.abortController = null;
                }
            }
        },
    },
    template:
        '<div class="country-map-api-svg" style="width:100%">' +
        '<div v-if="loading" class="country-map-api-svg__loading">Loading…</div>' +
        '<div v-else-if="error" class="country-map-api-svg__error">{{ error }}</div>' +
        '<svg v-else-if="data && data.paths && data.paths.length" ' +
        ':viewBox="data.view_box" xmlns="http://www.w3.org/2000/svg" role="img" ' +
        ':aria-label="data.name" preserveAspectRatio="xMidYMid meet" ' +
        ':style="countryMapApiSvgInnerStyle">' +
        '<path v-for="p in data.paths" :key="p.id" :d="p.d" :class="pathClassList" :fill="pathFill" />' +
        "</svg>" +
        '<div v-else class="country-map-api-svg__empty">No map</div>' +
        "</div>",
};

function updateHeaderCartCount(count) {
    var itemCount = Number(count);
    if (!Number.isFinite(itemCount) || itemCount < 0) itemCount = 0;
    document.querySelectorAll(
        ".header-cart-icon-pop-up .bigcounter, .bigcounter, .tt-nav__cart-badge"
    ).forEach(function (el) {
        el.textContent = itemCount;
    });
}
function refreshHeaderCartCount() {
    return fetch("/cart.js")
        .then(function (res) { return res.json(); })
        .then(function (cart) {
            updateHeaderCartCount(cart.item_count);
            return cart;
        });
}

const lazyScriptPromises = {};

function loadScriptOnce(src) {
    if (lazyScriptPromises[src]) {
        return lazyScriptPromises[src];
    }
    lazyScriptPromises[src] = new Promise(function (resolve, reject) {
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.onload = function () {
            resolve();
        };
        script.onerror = function () {
            delete lazyScriptPromises[src];
            reject(new Error("Failed to load " + src));
        };
        document.head.appendChild(script);
    });
    return lazyScriptPromises[src];
}

function loadStyleOnce(href) {
    if (document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) {
        return Promise.resolve();
    }
    return new Promise(function (resolve, reject) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = function () {
            resolve();
        };
        link.onerror = function () {
            reject(new Error("Failed to load " + href));
        };
        document.head.appendChild(link);
    });
}

/**
 * Flight route drawn on top of a Mapbox Static image. Projection follows the Static Images API:
 * Web Mercator, 512px tiles, bearing 0, pitch 0. `view` holds the exact centre, zoom and request
 * size sent in the URL, and `slot` is the design box the image is stretched into.
 */
const TTRouteOverlay = (function () {
    const TILE_SIZE = 512;
    const MAX_LAT = 85.0511287798066;
    const DEG = Math.PI / 180;

    const defaultStyle = {
        color: "#d54a39",
        lineStyle: "dashed",
        lineWidth: 0.0045,
        dashLength: 0.016,
        dashGap: 0.012,
        plane: true,
        planeColor: null,
        planeSize: 0.06,
        planePosition: 0.5,
        loop: false,
        loopSize: 0.075,
        loopPosition: 0.5,
        loopSide: "auto",
        loopPlanePosition: 0.75,
        startMarker: "heart",
        endMarker: "heart",
        markerColor: null,
        markerSize: 0.05,
        labels: true,
        labelColor: "#3b2f2a",
        labelHalo: "#ffffff",
        labelFontFamily: "Georgia",
        labelFontSize: 0.036,
        labelFontWeight: "normal",
        labelGap: 0.012,
        padding: 0.14,
        maskMargin: 0.035,
    };

    // `anchor` is the point in the path's own units that sits on the location. `heading` is the
    // direction the artwork faces, in canvas degrees.
    const shapes = {
        heart: {
            path: "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z",
            width: 20,
            height: 18.35,
            anchor: [12, 21.35],
            heading: 0,
        },
        plane: {
            path: "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z",
            width: 20,
            height: 20,
            anchor: [11.5, 12],
            heading: -90,
        },
    };

    function clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
    }
    function mercX(lng) {
        return (lng + 180) / 360;
    }
    function mercY(lat) {
        const s = Math.sin(clamp(lat, -MAX_LAT, MAX_LAT) * DEG);
        return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
    }
    function unmercY(y) {
        return (2 * Math.atan(Math.exp(Math.PI * (1 - 2 * y))) - Math.PI / 2) / DEG;
    }
    function wrapLng(lng) {
        return ((((lng + 180) % 360) + 360) % 360) - 180;
    }
    function round(value, places) {
        const f = Math.pow(10, places);
        return Math.round(value * f) / f;
    }

    function toVector(p) {
        const lat = p.lat * DEG;
        const lng = p.lng * DEG;
        return [Math.cos(lat) * Math.cos(lng), Math.cos(lat) * Math.sin(lng), Math.sin(lat)];
    }
    function fromVector(v) {
        return {
            lat: Math.atan2(v[2], Math.hypot(v[0], v[1])) / DEG,
            lng: Math.atan2(v[1], v[0]) / DEG,
        };
    }
    function slerp(a, b, steps) {
        const dot = clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
        const omega = Math.acos(dot);
        const sinOmega = Math.sin(omega);
        const out = [];
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            let wa = 1 - t;
            let wb = t;
            if (sinOmega > 1e-9) {
                wa = Math.sin((1 - t) * omega) / sinOmega;
                wb = Math.sin(t * omega) / sinOmega;
            }
            out.push(fromVector([
                wa * a[0] + wb * b[0],
                wa * a[1] + wb * b[1],
                wa * a[2] + wb * b[2],
            ]));
        }
        return out;
    }

    /** Geodesic samples from start to end. Longitudes are unwrapped so the path never jumps 360°. */
    function greatCircle(start, end, samples) {
        const steps = Math.max(2, samples || 128);
        const a = toVector(start);
        const b = toVector(end);
        const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
        let points;
        if (dot < -0.999999) {
            let mid = [a[1], -a[0], 0];
            if (Math.hypot(mid[0], mid[1]) < 1e-6) {
                mid = [0, a[2], -a[1]];
            }
            const len = Math.hypot(mid[0], mid[1], mid[2]);
            mid = [mid[0] / len, mid[1] / len, mid[2] / len];
            const half = Math.ceil(steps / 2);
            points = slerp(a, mid, half).concat(slerp(mid, b, half).slice(1));
        } else {
            points = slerp(a, b, steps);
        }
        points[0] = { lat: Number(start.lat), lng: Number(start.lng) };
        points[points.length - 1] = { lat: Number(end.lat), lng: Number(end.lng) };
        for (let i = 1; i < points.length; i++) {
            const prev = points[i - 1].lng;
            let lng = points[i].lng;
            while (lng - prev > 180) lng -= 360;
            while (lng - prev < -180) lng += 360;
            points[i].lng = lng;
        }
        return points;
    }

    /**
     * Centre and zoom that fit `path` inside a reqW x reqH static image with `padding` (fraction of
     * each side) left clear. Values are rounded the way they are sent to Mapbox, so the URL and
     * the overlay share one view.
     */
    function fitView(path, reqW, reqH, options) {
        const opts = options || {};
        const padding = opts.padding != null ? opts.padding : defaultStyle.padding;
        const bounds = routeBounds(path);
        const zoom = boundsZoom(bounds, reqW * (1 - 2 * padding), reqH * (1 - 2 * padding), opts.maxZoom);
        return viewAt(bounds, zoom, CENTRE, reqW, reqH);
    }

    const CENTRE = { u: 0.5, v: 0.5 };

    /** Mercator box of a path, in world fractions (x may run past 0..1 for unwrapped paths). */
    function routeBounds(path) {
        const b = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
        path.forEach(function (p) {
            const x = mercX(p.lng);
            const y = mercY(p.lat);
            b.minX = Math.min(b.minX, x);
            b.maxX = Math.max(b.maxX, x);
            b.minY = Math.min(b.minY, y);
            b.maxY = Math.max(b.maxY, y);
        });
        return b;
    }

    function boundsZoom(bounds, availW, availH, maxZoom) {
        const spanX = Math.max((bounds.maxX - bounds.minX) * TILE_SIZE, 1e-9);
        const spanY = Math.max((bounds.maxY - bounds.minY) * TILE_SIZE, 1e-9);
        const zoom = Math.min(Math.log2(availW / spanX), Math.log2(availH / spanY));
        return Math.floor(clamp(zoom, 0, maxZoom != null ? maxZoom : 22) * 100) / 100;
    }

    /**
     * View at `zoom` that puts the centre of `bounds` at `target` (fractions of the image).
     * Rounded the way it is sent to Mapbox, so the URL and the overlay share one view.
     */
    function viewAt(bounds, zoom, target, reqW, reqH) {
        const z = Math.max(0, Math.round(zoom * 100) / 100);
        const world = TILE_SIZE * Math.pow(2, z);
        const cx = ((bounds.minX + bounds.maxX) / 2) * world - (target.u - 0.5) * reqW;
        const cy = ((bounds.minY + bounds.maxY) / 2) * world - (target.v - 0.5) * reqH;
        return {
            lng: round(wrapLng((cx / world) * 360 - 180), 6),
            lat: round(unmercY(clamp(cy / world, 0, 1)), 6),
            zoom: z,
            width: reqW,
            height: reqH,
        };
    }

    /**
     * Alpha of a shape mask on a size x size grid stretched over the slot, with each cell's
     * distance (in cells) to the nearest transparent cell or slot edge. `centre` is the most
     * interior point of the shape.
     */
    function maskGrid(image, size) {
        const n = size || 128;
        const canvas = document.createElement("canvas");
        canvas.width = n;
        canvas.height = n;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, n, n);
        const alpha = ctx.getImageData(0, 0, n, n).data;
        const w = n + 2;
        const dist = new Float32Array(w * w);
        for (let y = 0; y < w; y++) {
            for (let x = 0; x < w; x++) {
                const inside = x > 0 && y > 0 && x <= n && y <= n && alpha[((y - 1) * n + (x - 1)) * 4 + 3] > 127;
                dist[y * w + x] = inside ? Infinity : 0;
            }
        }
        const d = Math.SQRT2;
        for (let y = 1; y < w - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
                const i = y * w + x;
                dist[i] = Math.min(dist[i], dist[i - 1] + 1, dist[i - w] + 1, dist[i - w - 1] + d, dist[i - w + 1] + d);
            }
        }
        for (let y = w - 2; y > 0; y--) {
            for (let x = w - 2; x > 0; x--) {
                const i = y * w + x;
                dist[i] = Math.min(dist[i], dist[i + 1] + 1, dist[i + w] + 1, dist[i + w + 1] + d, dist[i + w - 1] + d);
            }
        }
        const cells = new Float32Array(n * n);
        let best = -1;
        let centre = CENTRE;
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                const v = dist[(y + 1) * w + (x + 1)];
                cells[y * n + x] = v;
                if (v > best) {
                    best = v;
                    centre = { u: (x + 0.5) / n, v: (y + 0.5) / n };
                }
            }
        }
        return { size: n, cells: cells, centre: centre };
    }

    /** True when (u, v) is inside the mask with at least `margin` (fraction of the slot) to spare. */
    function maskContains(mask, u, v, margin) {
        if (!(u >= 0 && u < 1 && v >= 0 && v < 1)) {
            return false;
        }
        const n = mask.size;
        return mask.cells[Math.floor(v * n) * n + Math.floor(u * n)] / n >= (margin || 0) + 1 / n;
    }

    let measureCtx = null;
    /** Label box estimate before Fabric draws it; matches fabric.Text closely for one line. */
    function measureLabel(text, style, unit) {
        const fontSize = style.labelFontSize * unit;
        let w = String(text).length * fontSize * 0.55;
        if (typeof document !== "undefined") {
            measureCtx = measureCtx || document.createElement("canvas").getContext("2d");
            measureCtx.font = style.labelFontWeight + " " + fontSize + 'px "' + style.labelFontFamily + '"';
            w = measureCtx.measureText(String(text)).width;
        }
        return { w: w + fontSize * 0.16, h: fontSize * 1.16 };
    }

    /**
     * Top-left of a label box near a marker: below, then above, right, left. `fits(box)` rejects
     * spots outside a shaped map. Returns null when nothing fits and `fallback` is false.
     */
    function placeLabel(point, size, extent, gap, slot, fits, fallback) {
        const w = size.w;
        const h = size.h;
        const above = extent.above || 0;
        const below = extent.below || 0;
        const markerW = extent.w || 0;
        const minX = slot.left + gap;
        const maxX = slot.left + slot.width - gap - w;
        const centredX = minX > maxX ? slot.left + (slot.width - w) / 2 : clamp(point.x - w / 2, minX, maxX);
        const bodyMid = point.y + (below - above) / 2;
        const candidates = [
            { left: centredX, top: point.y + below + gap },
            { left: centredX, top: point.y - above - gap - h },
            { left: point.x + markerW / 2 + gap, top: bodyMid - h / 2 },
            { left: point.x - markerW / 2 - gap - w, top: bodyMid - h / 2 },
        ];
        for (let i = 0; i < candidates.length; i++) {
            const c = candidates[i];
            const box = { left: c.left, top: c.top, width: w, height: h };
            const inSlot = c.left >= slot.left && c.top >= slot.top &&
                c.left + w <= slot.left + slot.width && c.top + h <= slot.top + slot.height;
            if (inSlot && (!fits || fits(box))) {
                return c;
            }
        }
        if (fallback === false) {
            return null;
        }
        return point.y + below + gap + h > slot.top + slot.height - gap ? candidates[1] : candidates[0];
    }

    /** How far a marker reaches around its point. Image pins are centred, like Mapbox's url- overlay. */
    function markerExtent(style, unit, marker) {
        if (marker) {
            const w = marker.width || 0;
            const h = marker.height || 0;
            const above = marker.anchorY === "bottom" ? h : h / 2;
            return { w: w, h: h, above: above, below: h - above };
        }
        const shape = shapes[style.startMarker] || shapes[style.endMarker] || shapes.heart;
        const w = style.markerSize * unit;
        const h = w * (shape.height / shape.width);
        return { w: w, h: h, above: h, below: 0 };
    }

    /** `fits(box)` for placeLabel: box corners and edge midpoints all inside the mask. */
    function boxFitsMask(mask, slot, margin) {
        return function (box) {
            const xs = [box.left, box.left + box.width / 2, box.left + box.width];
            const ys = [box.top, box.top + box.height / 2, box.top + box.height];
            for (let i = 0; i < 3; i++) {
                for (let j = 0; j < 3; j++) {
                    if (!maskContains(mask, (xs[i] - slot.left) / slot.width, (ys[j] - slot.top) / slot.height, margin)) {
                        return false;
                    }
                }
            }
            return true;
        };
    }

    /** Route line, plane, both markers and both labels all land inside the mask for `view`. */
    function layoutFitsMask(path, view, slot, mask, style, labels, marker) {
        const unit = slot.width;
        const points = withLoop(projectPath(path, view, slot), style, unit);
        const uv = function (x, y, margin) {
            return maskContains(mask, (x - slot.left) / slot.width, (y - slot.top) / slot.height, margin);
        };
        const margin = style.maskMargin;
        for (let i = 0; i < points.length; i += 2) {
            if (!uv(points[i].x, points[i].y, margin * 0.6)) {
                return false;
            }
        }
        const extent = markerExtent(style, unit, marker);
        const ends = [points[0], points[points.length - 1]];
        if (extent.h > 0) {
            for (let i = 0; i < ends.length; i++) {
                const p = ends[i];
                const top = p.y - extent.above;
                const bot = p.y + extent.below;
                const left = p.x - extent.w / 2;
                const right = p.x + extent.w / 2;
                if (!uv(p.x, p.y, margin) || !uv(left, top, margin) || !uv(right, top, margin) ||
                    !uv(left, bot, margin) || !uv(right, bot, margin)) {
                    return false;
                }
            }
        }
        if (style.plane) {
            const at = pointAlong(points, planeFraction(style));
            if (!uv(at.x, at.y, Math.max(margin, style.planeSize / 2))) {
                return false;
            }
        }
        if (style.labels && labels) {
            const fits = boxFitsMask(mask, slot, margin);
            const gap = style.labelGap * unit;
            for (let i = 0; i < ends.length; i++) {
                if (labels[i] && !placeLabel(ends[i], measureLabel(labels[i], style, unit), extent, gap, slot, fits, false)) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Zoom and target for a route. Rectangular maps use the padded box fit. Shaped maps step the
     * zoom out from a tight fit, trying the slot centre and then the shape's most interior point,
     * until the whole layout sits inside the mask.
     */
    function fitRoute(path, reqW, reqH, opts) {
        const style = opts.style || defaultStyle;
        const bounds = routeBounds(path);
        const mask = opts.mask;
        if (!mask) {
            const padding = style.padding;
            return {
                bounds: bounds,
                target: CENTRE,
                zoom: boundsZoom(bounds, reqW * (1 - 2 * padding), reqH * (1 - 2 * padding), opts.maxZoom),
            };
        }
        const start = boundsZoom(bounds, reqW * 0.94, reqH * 0.94, opts.maxZoom);
        const targets = [CENTRE, mask.centre];
        for (let zoom = start; zoom >= 0; zoom = Math.round((zoom - 0.05) * 100) / 100) {
            for (let t = 0; t < targets.length; t++) {
                const view = viewAt(bounds, zoom, targets[t], reqW, reqH);
                if (layoutFitsMask(path, view, opts.slot, mask, style, opts.labels, opts.marker)) {
                    return { bounds: bounds, target: targets[t], zoom: zoom };
                }
            }
        }
        return { bounds: bounds, target: mask.centre, zoom: 0 };
    }

    function projector(view, slot) {
        const world = TILE_SIZE * Math.pow(2, view.zoom);
        const cx = mercX(view.lng) * world;
        const cy = mercY(view.lat) * world;
        const sx = slot.width / view.width;
        const sy = slot.height / view.height;
        return function (lat, lng) {
            return {
                x: slot.left + (mercX(lng) * world - cx + view.width / 2) * sx,
                y: slot.top + (mercY(lat) * world - cy + view.height / 2) * sy,
            };
        };
    }

    /** One location to design coordinates, using the world copy nearest the view centre. */
    function project(lat, lng, view, slot) {
        const shift = 360 * Math.round((view.lng - lng) / 360);
        return projector(view, slot)(lat, lng + shift);
    }

    /** A whole path, shifted by one whole-turn offset so it stays continuous. */
    function projectPath(path, view, slot) {
        const toCanvas = projector(view, slot);
        const midLng = path[Math.floor(path.length / 2)].lng;
        const shift = 360 * Math.round((view.lng - midLng) / 360);
        return path.map(function (p) {
            return toCanvas(p.lat, p.lng + shift);
        });
    }

    /** Point and travel direction (canvas degrees) at `fraction` of the polyline's length. */
    function pointAlong(points, fraction) {
        const lengths = [0];
        for (let i = 1; i < points.length; i++) {
            lengths.push(lengths[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
        }
        const total = lengths[lengths.length - 1];
        const target = total * clamp(fraction, 0, 1);
        let i = 1;
        while (i < points.length - 1 && lengths[i] < target) {
            i++;
        }
        const a = points[i - 1];
        const b = points[i];
        const seg = lengths[i] - lengths[i - 1];
        const t = seg > 0 ? (target - lengths[i - 1]) / seg : 0;
        return {
            x: a.x + (b.x - a.x) * t,
            y: a.y + (b.y - a.y) * t,
            angle: Math.atan2(b.y - a.y, b.x - a.x) / DEG,
            length: total,
        };
    }

    function planeFraction(style) {
        return style.loop ? style.loopPlanePosition : style.planePosition;
    }

    /** Cubic Bezier samples from `a` to `d`, including both ends. */
    function bezier(a, b, c, d, steps) {
        const out = [];
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const u = 1 - t;
            out.push({
                x: u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * d.x,
                y: u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * d.y,
            });
        }
        return out;
    }

    /**
     * Loop piece in its own frame: x along the route, y toward the loop, radius `r`. A round loop
     * sits on one side of the route. Two symmetric cubics leave the route flat and cross once,
     * tight against the bottom of the loop.
     */
    function loopLocal(r) {
        const lift = 1.08;
        const half = 1.75;
        const centreY = r * lift;
        const halfW = r * half;
        const p = Math.asin(Math.min(0.98, 1 / lift));
        const sinP = Math.sin(p);
        const cosP = Math.cos(p);
        const t1 = { x: r * cosP, y: centreY - r * sinP };
        const t2 = { x: -t1.x, y: t1.y };
        const start = { x: -halfW, y: 0 };
        const end = { x: halfW, y: 0 };
        const dist = Math.hypot(t1.x - start.x, t1.y - start.y);
        const h0 = dist * 0.5;
        const h1 = dist * 0.4;
        const entry = bezier(
            start,
            { x: start.x + h0, y: 0 },
            { x: t1.x - h1 * sinP, y: t1.y - h1 * cosP },
            t1,
            24
        );
        const sweep = Math.PI + 2 * p;
        const arc = [];
        const arcSteps = 48;
        for (let i = 1; i <= arcSteps; i++) {
            const ang = -p + sweep * (i / arcSteps);
            arc.push({ x: r * Math.cos(ang), y: centreY + r * Math.sin(ang) });
        }
        const exit = bezier(
            t2,
            { x: t2.x + h1 * sinP, y: t2.y - h1 * cosP },
            { x: end.x - h0, y: 0 },
            end,
            24
        ).slice(1);
        return { halfW: halfW, points: entry.concat(arc, exit) };
    }

    /**
     * Splices a self-crossing loop into projected route points at `loopPosition`. The curve leaves
     * and rejoins the route along its direction of travel, so the join has no kink. `loopSide` is
     * "left" or "right" of travel; "auto" bulges toward the top of the canvas. Short routes are
     * returned unchanged.
     */
    function withLoop(points, style, unit) {
        if (!style.loop || points.length < 2) {
            return points;
        }
        const shape = loopLocal(style.loopSize * unit);
        const span = shape.halfW * 2;
        const lengths = [0];
        for (let i = 1; i < points.length; i++) {
            lengths.push(lengths[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
        }
        const total = lengths[lengths.length - 1];
        if (total < span * 1.8) {
            return points;
        }
        const centre = clamp(total * style.loopPosition, shape.halfW, total - shape.halfW);
        const at = function (d) {
            let i = 1;
            while (i < points.length - 1 && lengths[i] < d) {
                i++;
            }
            const seg = lengths[i] - lengths[i - 1];
            const t = seg > 0 ? (d - lengths[i - 1]) / seg : 0;
            return {
                x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
                y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
                i: i,
            };
        };
        const from = at(centre - shape.halfW);
        const to = at(centre + shape.halfW);
        const chord = Math.hypot(to.x - from.x, to.y - from.y);
        if (chord <= 0) {
            return points;
        }
        const ux = (to.x - from.x) / chord;
        const uy = (to.y - from.y) / chord;
        let side = style.loopSide === "left" ? -1 : 1;
        if (style.loopSide === "auto") {
            side = ux > 1e-6 ? -1 : 1;
        }
        const nx = -uy * side;
        const ny = ux * side;
        const along = chord / span;
        const curve = shape.points.map(function (p) {
            const lx = (p.x + shape.halfW) * along;
            return {
                x: from.x + ux * lx + nx * p.y,
                y: from.y + uy * lx + ny * p.y,
            };
        });
        return points.slice(0, from.i).concat(curve, points.slice(to.i));
    }

    function resolveStyle(overrides, fontFamily) {
        const style = Object.assign({}, defaultStyle);
        if (fontFamily) {
            style.labelFontFamily = fontFamily;
        }
        return Object.assign(style, overrides || {});
    }

    function dashArray(style, unit) {
        if (style.lineStyle === "solid") {
            return null;
        }
        if (style.lineStyle === "dotted") {
            return [0.001, Math.max(style.dashGap * unit, style.lineWidth * unit * 2.5)];
        }
        return [style.dashLength * unit, style.dashGap * unit];
    }

    const inert = { selectable: false, evented: false, objectCaching: false };

    function drawShape(fabric, name, x, y, size, color, angle) {
        const shape = shapes[name];
        if (!shape) {
            return null;
        }
        const scale = size / shape.width;
        const obj = new fabric.Path(shape.path, Object.assign({
            fill: color,
            stroke: null,
            strokeWidth: 0,
            scaleX: scale,
            scaleY: scale,
            originX: "center",
            originY: "center",
            angle: angle,
        }, inert));
        const ox = (obj.pathOffset.x - shape.anchor[0]) * scale;
        const oy = (obj.pathOffset.y - shape.anchor[1]) * scale;
        const r = angle * DEG;
        obj.set({
            left: x + ox * Math.cos(r) - oy * Math.sin(r),
            top: y + ox * Math.sin(r) + oy * Math.cos(r),
        });
        return obj;
    }

    function drawRoute(fabric, points, style, unit) {
        return new fabric.Polyline(points.map(function (p) {
            return { x: p.x, y: p.y };
        }), Object.assign({
            fill: "",
            stroke: style.color,
            strokeWidth: style.lineWidth * unit,
            strokeDashArray: dashArray(style, unit),
            strokeLineCap: "round",
            strokeLineJoin: "round",
        }, inert));
    }

    function drawPlane(fabric, points, style, unit) {
        const at = pointAlong(points, planeFraction(style));
        if (at.length < unit * 0.08) {
            return null;
        }
        return drawShape(fabric, "plane", at.x, at.y, style.planeSize * unit, style.planeColor || style.color, at.angle - shapes.plane.heading);
    }

    function drawMarker(fabric, kind, point, style, unit) {
        if (!kind) {
            return null;
        }
        return drawShape(fabric, kind, point.x, point.y, style.markerSize * unit, style.markerColor || style.color, 0);
    }

    /** The customer's pin image, centred on the point the way Mapbox centres a url- overlay. */
    function drawMarkerImage(fabric, image, point, marker) {
        const natW = image && (image.naturalWidth || image.width);
        const natH = image && (image.naturalHeight || image.height);
        if (!natW || !natH || !marker.width || !marker.height) {
            return null;
        }
        return new fabric.Image(image, Object.assign({
            left: point.x,
            top: point.y,
            originX: "center",
            originY: marker.anchorY === "bottom" ? "bottom" : "center",
            scaleX: marker.width / natW,
            scaleY: marker.height / natH,
        }, inert));
    }

    /** Label under the marker, kept inside the slot (and the mask, when given); otherwise above or beside it. */
    function drawLocationLabel(fabric, text, point, style, unit, slot, mask, extent) {
        if (!text) {
            return null;
        }
        const fontSize = style.labelFontSize * unit;
        const label = new fabric.Text(String(text), Object.assign({
            fontFamily: style.labelFontFamily,
            fontWeight: style.labelFontWeight,
            fontSize: fontSize,
            fill: style.labelColor,
            stroke: style.labelHalo || null,
            strokeWidth: style.labelHalo ? fontSize * 0.16 : 0,
            strokeLineJoin: "round",
            paintFirst: "stroke",
            originX: "left",
            originY: "top",
        }, inert));
        const box = extent || markerExtent(style, unit, null);
        const fits = mask ? boxFitsMask(mask, slot, style.maskMargin) : null;
        const at = placeLabel(point, { w: label.width, h: label.height }, box, style.labelGap * unit, slot, fits, true);
        label.set({ left: at.left, top: at.top });
        return label;
    }

    /**
     * Route, plane, markers and labels as one inert group in design coordinates. Pass the map's
     * mask element as `clipElement` so shaped maps clip the overlay the same way.
     */
    function build(fabric, opts) {
        const slot = opts.slot;
        const style = opts.style || defaultStyle;
        const unit = slot.width;
        const path = opts.path || greatCircle(opts.start, opts.end, 128);
        const points = withLoop(projectPath(path, opts.view, slot), style, unit);
        const first = points[0];
        const last = points[points.length - 1];
        const marker = opts.marker || null;
        const extent = markerExtent(style, unit, marker);
        const objects = [drawRoute(fabric, points, style, unit)];
        if (style.plane) {
            objects.push(drawPlane(fabric, points, style, unit));
        }
        const startMarker = marker
            ? drawMarkerImage(fabric, marker.image, first, marker)
            : drawMarker(fabric, style.startMarker, first, style, unit);
        const endMarker = marker
            ? drawMarkerImage(fabric, marker.image, last, marker)
            : drawMarker(fabric, style.endMarker, last, style, unit);
        objects.push(startMarker, endMarker);
        if (style.labels) {
            objects.push(
                drawLocationLabel(fabric, opts.start.label, first, style, unit, slot, opts.mask, extent),
                drawLocationLabel(fabric, opts.end.label, last, style, unit, slot, opts.mask, extent)
            );
        }
        const group = new fabric.Group(objects.filter(Boolean), inert);
        group.customType = "route_overlay";
        const clipEl = opts.clipElement;
        const clipW = clipEl && (clipEl.naturalWidth || clipEl.width);
        const clipH = clipEl && (clipEl.naturalHeight || clipEl.height);
        if (clipW && clipH) {
            group.clipPath = new fabric.Image(clipEl, {
                left: slot.left + slot.width / 2,
                top: slot.top + slot.height / 2,
                originX: "center",
                originY: "center",
                absolutePositioned: true,
                scaleX: slot.width / clipW,
                scaleY: slot.height / clipH,
            });
        }
        return group;
    }

    return {
        defaultStyle: defaultStyle,
        shapes: shapes,
        resolveStyle: resolveStyle,
        greatCircle: greatCircle,
        fitView: fitView,
        fitRoute: fitRoute,
        viewAt: viewAt,
        maskGrid: maskGrid,
        project: project,
        projectPath: projectPath,
        pointAlong: pointAlong,
        withLoop: withLoop,
        loopLocal: loopLocal,
        drawRoute: drawRoute,
        drawPlane: drawPlane,
        drawMarker: drawMarker,
        drawLocationLabel: drawLocationLabel,
        build: build,
    };
})();

const MAPBOX_GL_CSS = "https://api.mapbox.com/mapbox-gl-js/v3.17.0/mapbox-gl.css";
const MAPBOX_GL_JS = "https://api.mapbox.com/mapbox-gl-js/v3.17.0/mapbox-gl.js";
const HEIC2ANY_JS = "https://cdn.jsdelivr.net/npm/heic2any@latest/dist/heic2any.min.js";
const CELESTIAL_D3_JS = "https://cdn.jsdelivr.net/npm/d3-celestial@0.7.35/lib/d3.min.js";
const CELESTIAL_GEO_JS = "https://cdn.jsdelivr.net/npm/d3-celestial@0.7.35/lib/d3.geo.projection.min.js";
const CELESTIAL_JS = "https://cdn.jsdelivr.net/npm/d3-celestial@0.7.35/celestial.min.js";
const CELESTIAL_CSS = "https://cdn.jsdelivr.net/npm/d3-celestial@0.7.35/celestial.css";
const ASTRONOMY_JS = "https://cdn.jsdelivr.net/npm/astronomy-engine@2.1.19/astronomy.browser.min.js";
const TZ_LOOKUP_JS = "https://cdn.jsdelivr.net/npm/tz-lookup@6.1.25/tz.js";
const MOON_ASTRONOMY_JS = window.TT_MOON_ASTRONOMY_JS || "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/moon-phase-astronomy_b3be3078-867e-4acb-b8ef-24886555c8ba.js?v=1789390452";
const MOON_RENDERER_JS = window.TT_MOON_RENDERER_JS || "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/moon-phase-renderer_31d812b3-50f1-4eb8-ad25-df32adcb1164.js?v=1789440005";

function ensureMapboxGl() {
    return loadStyleOnce(MAPBOX_GL_CSS).then(function () {
        return loadScriptOnce(MAPBOX_GL_JS);
    });
}

/**
 * Maps JavaScript API geocoder. A referrer-restricted key is accepted here.
 * The Geocoding web-service URL is not.
 */
function ensureGoogleMapsGeocoder(apiKey) {
    if (window.google && window.google.maps && typeof window.google.maps.Geocoder === "function") {
        return Promise.resolve();
    }
    const src = "https://maps.googleapis.com/maps/api/js?v=weekly&callback=__ttGoogleMapsGeocoderReady&key="
        + encodeURIComponent(apiKey);
    if (lazyScriptPromises[src]) {
        return lazyScriptPromises[src];
    }
    lazyScriptPromises[src] = new Promise(function (resolve, reject) {
        window.__ttGoogleMapsGeocoderReady = function () {
            resolve();
            try {
                delete window.__ttGoogleMapsGeocoderReady;
            } catch (err) {
                window.__ttGoogleMapsGeocoderReady = undefined;
            }
        };
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onerror = function () {
            delete lazyScriptPromises[src];
            reject(new Error("Failed to load Google Maps"));
        };
        document.head.appendChild(script);
    });
    return lazyScriptPromises[src];
}

function ensureCelestial() {
    return loadStyleOnce(CELESTIAL_CSS).then(function () {
        return loadScriptOnce(CELESTIAL_D3_JS);
    }).then(function () {
        return loadScriptOnce(CELESTIAL_GEO_JS);
    }).then(function () {
        return loadScriptOnce(CELESTIAL_JS);
    });
}

function ensureMoonPhaseLibs() {
    return loadScriptOnce(ASTRONOMY_JS).then(function () {
        return loadScriptOnce(TZ_LOOKUP_JS);
    }).then(function () {
        return loadScriptOnce(MOON_ASTRONOMY_JS);
    }).then(function () {
        return loadScriptOnce(MOON_RENDERER_JS);
    });
}

function ensureHeic2any() {
    return loadScriptOnce(HEIC2ANY_JS);
}

let cachedNightSky = null;

function warmCelestialLibrary() {
    ensureCelestial().catch(function () {});
}

function warmDeferredLibraries() {
    warmCelestialLibrary();
    ensureMapboxGl().catch(function () {});
    ensureHeic2any().catch(function () {});
    ensureMoonPhaseLibs().catch(function () {});
}

const PRODUCT_DATA_BUILDERS = {
    buildPlayingCardValueOptions: buildPlayingCardValueOptions,
    buildPlayingCardSuitOptions: buildPlayingCardSuitOptions,
    buildPlayingCardColorOptions: buildPlayingCardColorOptions,
    buildAngelNumberSelectorOptions: buildAngelNumberSelectorOptions,
    buildAuraPanelPickerOptions: buildAuraPanelPickerOptions,
    buildAuraShapePickerOptions: buildAuraShapePickerOptions,
    buildAuraPalettePickerOptions: buildAuraPalettePickerOptions,
};

function hydrateProductData(node) {
    if (Array.isArray(node)) {
        return node.map(hydrateProductData);
    }
    if (node && typeof node === "object") {
        const keys = Object.keys(node);
        if (keys.length === 1 && keys[0] === "$build" && PRODUCT_DATA_BUILDERS[node.$build]) {
            return PRODUCT_DATA_BUILDERS[node.$build]();
        }
        const out = {};
        keys.forEach(function (key) {
            out[key] = hydrateProductData(node[key]);
        });
        return out;
    }
    return node;
}

function loadProductData() {
    const url = window.TT_PRODUCT_DATA_URL;
    if (!url) {
        return Promise.reject(new Error("Missing TT_PRODUCT_DATA_URL"));
    }
    return fetch(url).then(function (res) {
        if (!res.ok) {
            throw new Error("product data " + res.status);
        }
        return res.json();
    }).then(hydrateProductData);
}

function designerCartCurrency() {
    const confirmed = window.__ttConfirmedCurrency;
    if (typeof confirmed === "string" && confirmed) {
        return confirmed;
    }
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (typeof active === "string" && active) {
        return active;
    }
    return "USD";
}

function designerCurrencyCode(detail) {
    if (typeof detail === "string" && detail) {
        return detail;
    }
    if (detail && typeof detail.currency === "string" && detail.currency) {
        return detail.currency;
    }
    return "";
}

function designerManualCurrency() {
    let stored = null;
    if (typeof $cookies !== "undefined" && $cookies && typeof $cookies.get === "function") {
        stored = $cookies.get("lk_currency_manual");
    }
    if (stored == null || stored === "") {
        const match = document.cookie.match(/(?:^|; )lk_currency_manual=([^;]*)/);
        stored = match ? decodeURIComponent(match[1]) : null;
    }
    return typeof stored === "string" && stored ? stored : "";
}

loadProductData().then(function (loadedProductData) {
const createPage =  createApp({
    components: {
        Cropper: VueAdvancedCropper.Cropper,
        Datepicker: VueDatePicker,
        draggable: window.vuedraggable,
        CountryMapApiSvg: CountryMapApiSvg,
    },
    data() {
        return {
            awaitingDesignUpdate: false,
            showUpdateDesignPopUp: false,
            showLongLat: true,
            showPinAdjustModal: false,
            pinAdjustMap: null,
            pinAdjustMarker: null,
            pinAdjustStartLat: null,
            pinAdjustStartLng: null,
            showDate: true,
            showTransparent: true,
            showMoment: false,
            fullPrintEdgeToEdge: false,
            showMultiImageGrayscale: false,
            selectedProductTypeAndSizeUpsell: 0,
            showTopBlur: false,
            showBottomBlur: true,
            showScrollHint: true,
            /** Set `true` in devtools to show a fixed corner preview of `<country-map-api-svg>`. */
            showCountryMapApiSvgPreview: false,
            pendingFirstRemoveBackgroundCrop: false,
            showAiImageGenerationModal: false,
            aiImageProcessingMessage: 'Creating your image...',
            aiImageProcessingInterval: null,
            aiImageProgress: 0,
            lineArtColor: '#000000',
            lineArtGeneratedUrlRaw: null,
            lineArtTintedUrl: null,
            lineArtGenerating: false,
            lineArtProgress: 0,
            lineArtProcessingInterval: null,
            lineArtGenerationLimit: 5,
            lineArtGenerationsUsed: 0,
            lineArtGenerationLimitReached: false,
            showLineArtGenerationModal: false,
            lineArtGenerationError: '',
            lineArtLimitModalTimer: null,
            lineArtGenerationHistory: [],
            lineArtSelectedGenerationId: null,
            lineArtTintSeq: 0,
            // remote AI URLs → in-memory data URLs (avoids canvas CORS failures)
            lineArtSafeUrlCache: {},
            nightSkyDate: new Date(),
            replaceNightSkyImageCount: 1,
            moonPhaseUpdateSeq: 0,
            lastMoonPhaseKey: "",
            timeSelector: {
                showTime: false,
                hour: "",
                minute: "",
                period: "AM"
            },
            nightSkyGrid: false,
            nightSkyMilkyWay: true,
            nightSkyConstellations: true,
            selectedSkyColor: "#151726",
            skyColors: [
                "#151726", // Deep Navy
                "#161514", // Charcoal Black
                "#436f9d", // Night Sky Blue
                "#3c4150", // Graphite Gray
                "#33525b", // Ocean Blue
                "#a17c9b", // Dusty Orchid
                "#d33c6e", // Roseberry
                "#e16028"  // Balanced Coral
            ],
            globalLat: "",
            globalLon: "",
            showSizeGuide: false,
            designSaveVisible: false,
            designSaveLoading: false,
            designSaveType: "loading", // "loading" | "success" | "error"
            designSaveMessage: "",
            designSaveHideTimer: null,
            updateDesignAssetId: null,
            updateDesignPreview: null,
            updateDesignPreviewLoading: false,
            updateDesignPreviewError: '',
            lastSavedDesignUrl: null,
            designPreviewCacheKey: Date.now(),
            showViewCurrentDesignPopUp: false,
            showSaveDesignConfirmPopUp: false,
            userHash: "",
            showBrowseExamples: false,
            multiImageUploader: false,
            curCropImage: 0,
            multiUploadImagesArray: [],
            multiUploadImagesArrayOriginal: [],
            curAutoAddressLocation: null,
            mapboxMapSearchField: "",
            mapboxMapSearchFieldSuggestions: [],
            showMapboxSearchFieldSuggestions: false,
            locationMapboxSearchLoading: false,
            lastSuccessfulMapboxSearchTimestamp: "",
            mapboxLocationSearchLatestRequestAt: 0,
            googlePlacesSearchSessionToken: null,
            /** Last Autocomplete request time for Google Places session idle timeout (ms since epoch). */
            googlePlacesSessionLastAutocompleteAt: 0,
            /** Debounce timer for Google Places Autocomplete while typing in the location field. */
            googlePlacesSearchDebounceTimer: null,
            awaitingATC: false,
            atcInFlight: false,
            cartEditActive: false,
            cartEditLineKey: "",
            cartEditDesignKey: "",
            cartEditProductType: null,
            cartEditStyle: null,
            cartEditMissing: false,
            cartEditRestoring: false,
            fillCanvasDebounceTimer: null,
            previewWarmupStarted: false,
            previewWarmupQueue: [],
            previewWarmupActive: 0,
            previewWarmupSeen: null,
            loadingCanvasImage: false,
            pendingCanvasAssets: 0,
            canvasLoaderTopPos: "",
            canvasLoader: false,
            showMarkerColors: false,
            selectedMapMarkerColor: 0,
            defaultMap: "",
            mapDrawnAtHalfSize: null,
            mapPrintSrc: "",
            isDefaultMap: true,
            /** True while canvas uses product JSON default_map_location; search + location inputs stay empty until user picks a place. */
            usingDefaultLocation: false,
            /** True after the user types in Enter Location Title; skip Places overwrites until a new place is selected. */
            locationTitleManuallyEdited: false,
            locationEditSeq: 0,
            /** "place" keeps a business name on a small pin move; "coordinate" and "area" refresh the area title. */
            locationSelectionKind: null,
            /** Original business coordinates for the Adjust Pin name rule. */
            locationPlaceAnchor: null,
            coordinateGeocodeSeq: 0,
            coordinateCommitInFlight: false,
            /** Second location for the flight route; same shape as curAutoAddressLocation. */
            routeEndLocation: null,
            /** True while routeEndLocation is a sample city shown before the customer picks their own. */
            routeEndIsPreview: false,
            routeEndSearchField: "",
            routeEndSuggestions: [],
            showRouteEndSuggestions: false,
            routeEndSearchLoading: false,
            routeEndSearchRequestAt: 0,
            routeEndSearchTimer: null,
            showRouteEndField: false,
            /** Zoom relative to the fitted route view; 0 is the fit, negative zooms out. */
            routeZoomOffset: 0,
            /** Customer choice: draw a loop-de-loop in the middle of the flight route. */
            routeLoop: false,
            routeFitZoom: 0,
            /** Centre, zoom and request size sent to Mapbox for the current route map. */
            routeView: null,
            /** Normalised GPS route from the Agent Allen route API, for products with has_gps_route. */
            gpsRoute: null,
            /** True while gpsRoute is the product's sample route; the customer must still choose their own. */
            gpsRouteIsPreview: false,
            /** Subtitle parts for GPS routes, joined into userInput.location. */
            routeStats: { distance: "", time: "", pace: "" },
            routeStatsEdited: { distance: false, time: false, pace: false },
            selectedRouteColor: "#FC5200",
            gpsRouteTab: "search",
            gpsRouteSearchField: "",
            gpsRouteResults: [],
            showGpsRouteResults: false,
            gpsRouteLoading: false,
            gpsRouteError: "",
            gpsRouteSearchTimer: null,
            gpsRouteRequestAt: 0,
            /** Opaque Agent Allen session id for a connected Strava account; the token never reaches the browser. */
            stravaSession: "",
            stravaActivities: [],
            stravaLoaded: false,
            stravaPage: 0,
            stravaHasMore: false,
            stravaLoadingMore: false,
            stravaListOpen: false,
            /** Signed Strava authorize link, fetched ahead so a tap on a phone can open the Strava app. */
            stravaConnectUrl: "",
            stravaConnectUrlAt: 0,
            distanceUnit: "km",
            pinAdjustConfirmInFlight: false,
            auraVisualsTouched: false,
            auraWordSuggestionIndex: null,
            /** Label from default_map_location for canvas preview when userInput.location is empty. */
            defaultPreviewLocationLabel: "",
            selectedMapMarker: 0,
            mapMarkers:[
                {
                    "color":"#d8503f",
                    "route_color":"#d54a39",
                    "stroke":"#a4291f",
                    "markers": [
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/PIN-thumb_39fbebee-efa7-48bc-8871-73c02ee39590.png?v=1790814728",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Normal-Pin-168px.png?v=1790814646",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NEW-google-pin-84px.png?v=1722206648",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/HEART-01.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-2-168px.png?v=1790814553",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/heart1-pin-84px.png?v=1721947494",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/HEART-02.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-1-168px.png?v=1790814553",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/heart2-pin-84px.png?v=1721947494",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/DROP-PIN.png?v=1721886111",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-168px.png?v=1790814553",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/drop-pin-84px.png?v=1721947494",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/HOME-2.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/House-Pin-168px.png?v=1790814553",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/home-pin-84px.png?v=1721947494",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/PIN.png?v=1721886111",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-168px.png?v=1790814553",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/pin-pin-84px.png?v=1721947494",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NONE.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/None-Pin-32.png?v=1721888032",
                        }
                    ]
                },
                {
                    "color":"#3c3f97",
                    "stroke":"#1c2797",
                    "markers": [
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Blue-100px.png?v=1722389813",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Normal-Pin-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Blue-84px.png?v=1722389708",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Blue-100px.png?v=1722389813",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-2-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Blue-84px.png?v=1722389708",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-Blue-100px.png?v=1722389812",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-1-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-Blue-84px.png?v=1722389709",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Blue-100px.png?v=1722389813",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Blue-84px.png?v=1722389708",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Blue-100px.png?v=1722389813",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/House-Pin-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Blue-84px.png?v=1722389708",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Blue-100px.png?v=1722389813",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Blue-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Blue-84px.png?v=1722389709",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NONE.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/None-Pin-32.png?v=1721888032",
                        }
                    ]
                },
                {
                    "color":"#ddb553",
                    "stroke":"#96721b",
                    "markers": [
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Gold-100px.png?v=1722391356",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Normal-Pin-Gold-168px.png?v=1790817710",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Gold-100px.png?v=1722391356",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-2-Gold-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-Gold-100px.png?v=1722391356",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-1-Gold-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Gold-100px.png?v=1722391356",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Gold-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Gold-100px.png?v=1722391355",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/House-Pin-Gold-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Gold-100px.png?v=1722391356",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Gold-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Gold-84px.png?v=1722391466",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NONE.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/None-Pin-32.png?v=1721888032",
                        }
                    ]
                },
                {
                    "color":"#101010",
                    "stroke":"#323232",
                    "markers": [
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Black-100px.png?v=1722391690",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Normal-Pin-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-Black-84px.png?v=1722391554",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Black-100px.png?v=1722391691",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-2-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-Black-84px.png?v=1722391555",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-Black-100px.png?v=1722391691",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-1-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-Black-84px.png?v=1722391555",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Black-100px.png?v=1722391691",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-Black-84px.png?v=1722391555",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Black-100px.png?v=1722391691",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/House-Pin-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-Black-84px.png?v=1722391554",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Black-100px.png?v=1722391691",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Black-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-Black-84px.png?v=1722391555",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NONE.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/None-Pin-32.png?v=1721888032",
                        }
                    ]
                },
                {
                    "color":"#ffffff",
                    "stroke":"#cecece",
                    "markers": [
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Normal-Pin-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Google-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-2-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart1-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart-Pin-1-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Heart2-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Drop-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/House-Pin-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Home-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-White-100px.png?v=1722391769",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-White-168px.png?v=1790817709",
                            "imgPreview":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/Pin-Pin-White-84px.png?v=1722391850",
                        },
                        {
                            "thumbnail":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/NONE.png?v=1721886112",
                            "img":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/None-Pin-32.png?v=1721888032",
                        }
                    ]
                }
            ],
            carouselBreakpoints: {
                700: {
                    itemsToShow: 4,
                    snapAlign: 'start',
                },
                1024: {
                    itemsToShow: 5,
                    snapAlign: 'start',
                },
            },
            imageSelector: [{"title":"0","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0030_0.png?v=1710718858"},{"title":"1","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0029_1.png?v=1710718857"},{"title":"2","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0028_2.png?v=1710718858"},{"title":"3","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0027_3.png?v=1710718859"},{"title":"4","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0026_4.png?v=1710718858"},{"title":"5","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0025_5.png?v=1710718859"},{"title":"6","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0024_6.png?v=1710718858"},{"title":"7","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0023_7.png?v=1710718858"},{"title":"8","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0022_8.png?v=1710718859"},{"title":"9","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0021_9.png?v=1710718858"},{"title":"10","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0020_10.png?v=1710718859"},{"title":"11","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0019_11.png?v=1710718860"},{"title":"12","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0018_12.png?v=1710718860"},{"title":"13","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0017_13.png?v=1710718860"},{"title":"14","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0016_14.png?v=1710718860"},{"title":"15","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0015_15.png?v=1710718860"},{"title":"16","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0014_16.png?v=1710718860"},{"title":"17","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0013_17.png?v=1710718860"},{"title":"18","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0012_18.png?v=1710718860"},{"title":"19","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0011_19.png?v=1710718860"},{"title":"20","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0010_20.png?v=1710718860"},{"title":"21","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0009_21.png?v=1710718860"},{"title":"22","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0008_22.png?v=1710718860"},{"title":"23","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0007_23.png?v=1710718860"},{"title":"24","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0006_24.png?v=1710718860"},{"title":"25","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0005_25.png?v=1710718860"},{"title":"26","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0004_26.png?v=1710718860"},{"title":"27","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0003_27.png?v=1710718860"},{"title":"28","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0002_28.png?v=1710718860"},{"title":"29","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0001_29.png?v=1710718861"},{"title":"30","src":"https://cdn.shopify.com/s/files/1/0431/4156/2528/files/TT_Anniversary_Plaque_TEST_A4_1000px-_0000s_0000_30.png?v=1710718860"}],
            canvasBuildVersion: 0,
            dateFormats: [
                "dd-MM-yyyy",
                "MM-dd-yyyy",
                "MMMM do, yyyy",
                "d MMMM yyyy",
                "dd/MM/yyyy",
                "MM/dd/yyyy",
                "dd.MM.yyyy",
                "MM.dd.yyyy",
                "MMMM yyyy",
                "yyyy"
            ],
            availableFonts: ["Arial", "Abril Fatface", "Alex Brush", "Alfa Slab One","Amatic SC", "Arizonia", "Arvo", "expressway", "Fjalla One", "Fredoka One", "Indie Flower", "Lobster", "Moon Dance", "Nothing You Could Do", "Pacifico", "Permanent Marker", "Playfair Display", "Poppins", "Roboto", "Rouge Script", "Rowdies", "Sail", "Satisfy","Spectral"],
            productsWithoutScriptFonts: ["Map", "Moon Phase", "Watercolor", "Night Sky", "Testimony"],
            scriptFontsToHide: ["Alex Brush", "Arizonia", "Moon Dance", "Rouge Script", "Amatic SC"],
            selectedFont: "",
            showFontSelector: false,
            imageSelectorShow: false,
            imageSelectorValue: "",
            freeShippingCutoff: {
                "USD": 100,
                "AUD": 140,
                "GBP": 75,
                "NZD": 150,
                "CAD": 140,
                "JPY": 15555
            },
            selectedProductTypeStyle:0,
            cartCurrency: designerCartCurrency(),
            curFormattedDate: "",
            isMobile: true,
            viewMoreProductsPopUp: false,
            mapStyleThumbnails: {},
            userDateFormat: "MM-dd-yyyy",
            showMapZoom: false,
            dateFormatShow: false,
            activeDateFieldId: "date_from",
            showExtraDate: false,
            userSelectedDateFormat: 'MM-dd-yyyy',
            dateFieldInstance: '',
            queryArray: {},
            curSelectedSearch: '',
            uploadPercentage: 1,
            curSelectedSearchValue: '',
            // Blocks iOS focus-restoration from reopening Suggested after keyboard dismiss / outside tap
            suppressSearchFocusUntil: 0,
            _vvLastHeight: null,
            exampleTextOptions: [],
            requiredProductFields: [],
            prefillableFields: [],
            errorRequiredFields: [],
            requiredValidationActive: false,
            showCrop: false,
            showImageSrc: '',
            showImageIndex: 0,
            showImage: false,
            upsellComplete: false,
            cropping: false,
            cropError: "",
            imageUploadError: "",
            curCroppingImageSRC: "",
            curCroppingImageID: "",
            curCropAspect: 1 / 1,
            cropSession: 0,
            datePickerValue: null,
            curRenderedImage: null,
            curRenderedImageThumb: "",
            curRenderedImageID: null,
            productTypeSelected: 0,
            productSizeSelected: 0,
            userInput: {},
            quantity: 1,
            atcAvailable: true,
            showSuccessBox: false,
            preLoaderShow: true,
            preLoaderSettled: false,
            preLoaderCanvasReady: false,
            preLoaderSafetyTimer: null,
            cartEmpty: true,
            cartItemCount: 0,
            previewPillVisible: false,
            mapZoom: 13,
            yearSelectorShow: false,
            productData: loadedProductData,
            canvas: null,
        }
    },
    computed: {
        productViewableCount(){
            if (this.isMobile === true){
                return 3;
            }else{
                return 4;
            }
        },
        /** Design picker order. Route Map is shown where Coordinates sits. The types array stays put so saved designs and product links keep their indexes. */
        productTypeDisplayIndexes() {
            const types = (this.productData && this.productData.types) || [];
            const route = types.findIndex((type) => type && type.title === "Route Map");
            const coordinates = types.findIndex((type) => type && type.title === "Coordinates");
            const order = types.map((_, index) => index).filter((index) => index !== route);
            const insertAt = coordinates === -1 ? order.length : order.indexOf(coordinates);
            if (route !== -1) {
                order.splice(insertAt < 0 ? order.length : insertAt, 0, route);
            }
            return order;
        },
        years(){
            var startYear = 1900;
            var currentYear = new Date().getFullYear(), years = [];
            var endYear = currentYear + 2;
            startYear = startYear || 1980;
            while ( startYear <= endYear ) {
                years.push(startYear++);
            }
            return years.reverse();
        },
        atcCanShow() {
            return true;
        },
        exampleImageCount() {
            const images = this.selectedProductType && this.selectedProductType['images'];
            return images ? images.length : 0;
        },
        currentDesignPreviewUrl() {
            const url = this.updateDesignPreview && this.updateDesignPreview.design_url;
            if (!url) {
                return '';
            }
            const separator = url.indexOf('?') === -1 ? '?' : '&';
            return url + separator + 't=' + this.designPreviewCacheKey;
        },
        dateFormatsCal() {
            const tempFormats = [];
            if (!this.curFormattedDate || !this.userSelectedDateFormat) return tempFormats;

            try {
                // Parse current date from selected format (assumed to be date-fns style now)
                const parsedDate = dateFns.parse(this.curFormattedDate, this.userSelectedDateFormat, new Date());

                this.dateFormats.forEach((format) => {
                    const tempFormat = {
                        id: format,
                        title: dateFns.format(parsedDate, format)
                    };
                    tempFormats.push(tempFormat);
                });
            } catch (err) {
                console.warn("Error parsing date:", err);
            }

            return tempFormats;
        },
        selectedProductType() {
            return this.productData.types[this.productTypeSelected];
        },
        currentFonts() {
            const title = this.selectedProductType && this.selectedProductType.title;
            const hidden = this.productsWithoutScriptFonts.indexOf(title) !== -1
                ? this.scriptFontsToHide
                : [];
            return this.availableFonts.filter((font) => hidden.indexOf(font) === -1);
        },
        selectedProductTypeAndSize() {
            if (this.productData.types[this.productTypeSelected]['type'] === "standard") {
                return this.productData.types[this.productTypeSelected]['products'][0];
            } else {
                return this.productData.types[this.productTypeSelected]['products'][this.productSizeSelected];
            }
        },
        filteredAndSortedExampleOptions() {
            return this.exampleTextOptions
                .filter((example) => {
                    const exampleString = (example || '').toString().toLowerCase();
                    const searchValueString = (this.curSelectedSearchValue || '').toString().toLowerCase();
                    return exampleString.includes(searchValueString);
                });
        },
        canvasBackgroundImage() {
            let selectedProductTypeAndSize = "";
            if (this.productData.types[this.productTypeSelected]["type"] === "standard") {
                selectedProductTypeAndSize = this.productData.types[this.productTypeSelected]['products'][0];
            } else {
                selectedProductTypeAndSize = this.productData.types[this.productTypeSelected]['products'][this.productSizeSelected];
            }
            const addonIndex = this.userInput['upsell_addon'];
            const hasAddonIndex = addonIndex !== undefined && addonIndex !== null && addonIndex !== '';
            if (selectedProductTypeAndSize["upsell_addon"] && hasAddonIndex) {
                return this.shopifyFrameUrl(selectedProductTypeAndSize['background-image-' + addonIndex]);
            }
            return this.shopifyFrameUrl(selectedProductTypeAndSize['background-image-0']);
        },
        canvasMaskImage() {
            let selectedProductTypeAndSize = "";
            if (this.productData.types[this.productTypeSelected]["type"] === "standard") {
                selectedProductTypeAndSize = this.productData.types[this.productTypeSelected]['products'][0];
            } else {
                selectedProductTypeAndSize = this.productData.types[this.productTypeSelected]['products'][this.productSizeSelected];
            }
            const addonIndex = this.userInput['upsell_addon'];
            const hasAddonIndex = addonIndex !== undefined && addonIndex !== null && addonIndex !== '';
            if (selectedProductTypeAndSize["upsell_addon"] && hasAddonIndex) {
                return this.shopifyFrameUrl(selectedProductTypeAndSize['canvas-mask-image-' + addonIndex]);
            }
            return this.shopifyFrameUrl(selectedProductTypeAndSize['canvas-mask-image-0']);
        },
        isOverlayModalOpen() {
            return this.showCrop
                || this.showAiImageGenerationModal
                || this.showLineArtGenerationModal
                || this.showPinAdjustModal
                || this.showUpdateDesignPopUp
                || this.showSaveDesignConfirmPopUp
                || this.showViewCurrentDesignPopUp
                || this.viewMoreProductsPopUp
                || this.showImage
                || this.showBrowseExamples
                || this.showSizeGuide
                || this.atcAvailable === false;
        },
    },
    created() {
        //this.isMobile();
        if (screen.width <= 760) {
            this.isMobile = true;
            //console.log("Mobile");
        } else {
            this.isMobile = false;
            //console.log("Not Mobile");
        }
    },
    mounted() {

        this.userHash = this.makeid(5);

        this.loadCurrency();
        this.checkClearAiLimitHash();
        this.loadLineArtGenerationCount();
        this.loadLineArtGenerationHistory();
        window.addEventListener('hashchange', this.checkClearAiLimitHash);

            this.userDateFormat = this.getDefaultDateFnsFormat();
            this.userSelectedDateFormat = this.getDefaultDateFnsFormat();

            this.canvasCreator = new fabric.StaticCanvas('canvas', {
                selection: true,
                controlsAboveOverlay: true,
                centeredScaling: true,
                allowTouchScrolling: true,
                enableRetinaScaling: false

            });

            this.fillViaQuery();

            const cartEditQuery = this.readCartEditQuery();
            if (cartEditQuery) {
                this.cartEditActive = true;
                this.cartEditLineKey = cartEditQuery.lineKey;
                this.cartEditDesignKey = cartEditQuery.designKey;
                this.cartEditRestoring = true;
            }

            if (!this.cartEditActive && this.queryArray['product_type']) {
                if (this.queryArray['product_type'] === "Map") {
                    this.productTypeSelected = 0;
                }
                if (this.queryArray['product_type'] === "Album") {
                    this.productTypeSelected = 1;
                }
                if (this.queryArray['product_type'] === "Country") {
                    this.productTypeSelected = 2;
                }
                if (this.queryArray['product_type'] === "ai-line-art") {
                    this.productTypeSelected = 3;
                }
                if (this.queryArray['product_type'] === "night-sky") {
                    this.productTypeSelected = 6;
                }
                if (this.queryArray['product_type'] === "Sign") {
                    this.productTypeSelected = 5;
                }
                if (this.queryArray['product_type'] === "retro-photo-plaque") {
                    this.productTypeSelected = 4;
                }
                if (this.queryArray['product_type'] === "Memorial") {
                    this.productTypeSelected = 13;
                }
                if (this.queryArray['product_type'] === "perfect-match") {
                    this.productTypeSelected = 14;
                }
                if (this.queryArray['product_type'] === "Coordinates") {
                    this.productTypeSelected = 11;
                }
                if (this.queryArray['product_type'] === "calendar") {
                    this.productTypeSelected = 12;
                }
                if (this.queryArray['product_type'] === "cutout-portrait") {
                    this.productTypeSelected = 10;
                }
                if (this.queryArray['product_type'] === "photo-plaque") {
                    this.productTypeSelected = 15;
                }
                if (this.queryArray['product_type'] === "Affirmation") {
                    this.productTypeSelected = 16;
                }
                if (this.queryArray['product_type'] === "full-print") {
                    this.productTypeSelected = 17;
                }
                if (this.queryArray['product_type'] === "anniversary-plaque") {
                    this.productTypeSelected = 18;
                }
                if (this.queryArray['product_type'] === "newspaper-print") {
                    this.productTypeSelected = 7;
                }
                if (this.queryArray['product_type'] === "moon-phase") {
                    this.productTypeSelected = 8;
                }
                if (this.queryArray['product_type'] === "angel-number") {
                    this.productTypeSelected = 19;
                }
                if (this.queryArray['product_type'] === "playing-card") {
                    this.productTypeSelected = 9;
                }
                if (this.queryArray['product_type'] === "route-map") {
                    this.productTypeSelected = 20;
                }
                if (!this.queryArray['product_fork']) {
                    this.selectedProductTypeStyle = this.defaultStyleIndex(this.productTypeSelected);
                }

                this.updateSelectedProductType();
            }

            if (!this.cartEditActive && this.queryArray['product_size']) {
                if (this.queryArray['product_size'] === "A5") {
                    this.productSizeSelected = 0;
                }
                if (this.queryArray['product_size'] === "A4") {
                    this.productSizeSelected = 1;
                }
                if (this.queryArray['product_size'] === "A3") {
                    this.productSizeSelected = 2;
                }
                if (this.queryArray['product_size'] === "Keychain") {
                    this.productSizeSelected = 3;
                }
                if (this.queryArray['product_size'] === "Block") {
                    this.productSizeSelected = 4;
                }
                if (this.queryArray['product_size'] === "Magnet") {
                    this.productSizeSelected = 5;
                }
                if (this.queryArray['product_size'] === "Ornament") {
                    this.productSizeSelected = 6;
                }
                this.updateSelectedProductType();
            }

            if (!this.cartEditActive && this.queryArray['product_fork']) {
                this.updateSelectedProductTypeStyle(parseInt(this.queryArray['product_fork']));
            }

            if (!this.cartEditActive && !this.queryArray['product_type'] && !this.queryArray['product_size']) {
                this.setUpUserJson();
                if (this.selectedProductType && this.selectedProductType["has_country_map"] === true) {
                    this.applyCountryMapProductDefaults();
                    void this.fillInAddressNew();
                } else if (
                    this.selectedProductType &&
                    this.selectedProductType["has_map_field"] === true &&
                    this.selectedProductType["has_country_map"] !== true
                ) {
                    this.applyMapProductDefaults({ forkSwitch: false });
                    void this.fillInAddressNew();
                }
            }

            if (!this.cartEditActive) {
                this.applyProductDateFormatDefault();
                this.reloadLineArtGenerationState();
                this.preloadCanvasFonts();
                if (this.gpsRouteMode()) {
                    this.consumeStravaReturn();
                }
            }

            window.addEventListener("resize", this.rescale_canvas_if_needed);
            document.addEventListener('mousedown', (event) => this.handleClickOutside(event));
            this.bindSearchInputDismissHandlers();
            this.rescale_canvas_if_needed();
            const canvasHost = this.$refs.canvas;
            if (!canvasHost || !canvasHost.clientWidth) {
                requestAnimationFrame(() => this.rescale_canvas_if_needed());
            }

            // fillInAddressNew and updateSelectedProductType already call fillCanvas
            // for Map and for ?product_type= links. A second call clears that canvas
            // and starts the same image load again. Products that have not built yet
            // still need this one.
            if (!this.cartEditActive && this.canvasBuildVersion === 0) {
                if (this.getLineArtGeneratorElement()) {
                    if (!(this.lineArtGeneratedUrlRaw || this.lineArtGenerationHistory.length > 0)) {
                        void this.refreshLineArtTint();
                    }
                } else {
                    this.fillCanvas();
                }
            }

            this.preLoaderSafetyTimer = setTimeout(() => {
                this.forceHidePreLoader();
            }, 8000);

            this.$nextTick(() => {
                this.setupPreviewPillObserver();
            });
            this.syncCartFromServer();
            if (this.cartEditActive) {
                void this.restoreCartDesign();
            }

    },
    beforeDestroy() {
        this.clearTextareaCanvasSyncTimer();
        this.unbindSearchInputDismissHandlers();
        this.teardownPreviewPillObserver();
        document.removeEventListener('keydown', this.onExampleLightboxKeydown);
    },
    beforeUnmount() {
        this.teardownPreviewPillObserver();
        document.removeEventListener('keydown', this.onExampleLightboxKeydown);
    },
    watch: {
        designSaveType(newVal) {
            if (newVal === 'success' && this.showSaveDesignConfirmPopUp) {
                setTimeout(() => {
                    this.showSaveDesignConfirmPopUp = false;
                }, 2500);
            }
        },
        userInput: {
            deep: true,
            handler() {
                if (this.requiredValidationActive) {
                    this.validateRequiredFields();
                }
            }
        },
        usingDefaultLocation() {
            if (this.requiredValidationActive) {
                this.validateRequiredFields();
            }
        },
        routeEndIsPreview() {
            if (this.requiredValidationActive) {
                this.validateRequiredFields();
            }
        },
        isOverlayModalOpen: {
            immediate: true,
            handler(open) {
                document.body.classList.toggle('tt-modal-open', !!open);
                var host = document.getElementById('tt-support-widget');
                if (host) {
                    host.classList.toggle('tt-modal-open', !!open);
                }
            }
        },
        showImage(open) {
            if (open) {
                document.addEventListener('keydown', this.onExampleLightboxKeydown);
                this.$nextTick(() => {
                    const el = this.$refs.exampleLightbox;
                    if (el && el.focus) el.focus();
                });
            } else {
                document.removeEventListener('keydown', this.onExampleLightboxKeydown);
            }
        },
    },
    methods: {

        collectMissingRequiredFields() {
            const missing = [];
            this.requiredProductFields.forEach((item) => {
                if (this.showDate === false && (item === 'date' || item === 'date_from')) {
                    return;
                }
                if (this.userInput[item] === "") {
                    missing.push(item);
                }
            });
            if (
                this.usingDefaultLocation === true &&
                this.selectedProductType &&
                this.selectedProductType["has_map_field"] === true
            ) {
                const searchEl = this.getMapLocationSearchElement();
                if (searchEl && searchEl.id && missing.indexOf(searchEl.id) === -1) {
                    missing.push(searchEl.id);
                }
                if (missing.indexOf("location") === -1) {
                    missing.push("location");
                }
            }
            if (this.gpsRouteMode() && (!this.routeActive() || this.gpsRouteIsPreview)) {
                const searchEl = this.getMapLocationSearchElement();
                if (searchEl && searchEl.id && missing.indexOf(searchEl.id) === -1) {
                    missing.push(searchEl.id);
                }
            }
            if (!this.gpsRouteMode() && this.routeEndIsPreview && this.routeEndLocation && this.showRouteEndField) {
                if (missing.indexOf("route_end") === -1) {
                    missing.push("route_end");
                }
            }
            const rankElements = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            rankElements.forEach((el) => {
                if (!el || el["editable_type"] !== "option_selector" || this.userInput[el["id"]] !== "custom") {
                    return;
                }
                const letter = this.userInput[playingCardCustomFieldId(el["id"])];
                if (letter == null || String(letter).trim() === "") {
                    if (missing.indexOf(el["id"]) === -1) {
                        missing.push(el["id"]);
                    }
                }
            });
            rankElements.forEach((el) => {
                if (!el || el["editable_type"] !== "multi_image" || el["required"] !== true) {
                    return;
                }
                if (Array.isArray(el["photo-source-options"]) && this.cutoutPhotoSource() !== "upload") {
                    return;
                }
                const limit = Number(el["multi-image-limit"]) || 1;
                const uploaded = (this.multiUploadImagesArray || []).filter((img) => img && img.src && !img.loading).length;
                if (uploaded < limit) {
                    if (missing.indexOf(el["id"]) === -1) {
                        missing.push(el["id"]);
                    }
                }
            });
            return missing;
        },

        validateRequiredFields() {
            this.errorRequiredFields = this.collectMissingRequiredFields();
            return this.errorRequiredFields.length === 0;
        },

        updateMapMarker(marker){
            this.selectedMapMarker = marker;
            this.fillInAddressNew();
        },
        updateMapMarkerColor(color){
            this.selectedMapMarkerColor = color;
            this.fillInAddressNew();
        },
        getPhotoShapeElement() {
            const elems = this.selectedProductType?.["styles"]?.[this.selectedProductTypeStyle]?.["elements"];
            if (!elems) return null;
            return elems.find((el) => el && el["editable_type"] === "photo_shape_selector") || null;
        },
        photoShapeIndex(element) {
            const el = element || this.getPhotoShapeElement();
            if (!el) return 0;
            if (this.cutoutPhotoSource() === "sky" || this.cutoutPhotoSource() === "moon") {
                return 0;
            }
            const raw = this.userInput ? this.userInput[el["id"]] : "";
            if (raw === "" || raw === undefined || raw === null || Number.isNaN(Number(raw))) {
                return Number(el["initial"]) || 0;
            }
            const idx = Number(raw);
            const count = Array.isArray(el["options"]) ? el["options"].length : 0;
            if (count && (idx < 0 || idx >= count)) return 0;
            return idx;
        },
        getSelectedPhotoShape() {
            const el = this.getPhotoShapeElement();
            if (!el || !Array.isArray(el["options"]) || !el["options"].length) return null;
            return el["options"][this.photoShapeIndex(el)] || el["options"][0];
        },
        updatePhotoShape(index) {
            const el = this.getPhotoShapeElement();
            if (!el) return;
            this.userInput[el["id"]] = index;
            if (this.routeActive()) {
                this.fillInAddressNew();
                return;
            }
            this.fillCanvas();
        },
        getPhotoSourceElement() {
            const elems = this.selectedProductType?.["styles"]?.[this.selectedProductTypeStyle]?.["elements"];
            if (!elems) return null;
            return elems.find((el) => el && Array.isArray(el["photo-source-options"]) && el["photo-source-options"].length) || null;
        },
        cutoutPhotoSource() {
            const el = this.getPhotoSourceElement();
            if (!el) return "upload";
            const raw = this.userInput ? this.userInput.photo_source : "";
            return el["photo-source-options"].indexOf(raw) === -1 ? "upload" : raw;
        },
        photoSourceLabel(src) {
            if (src === "sky") return "Night Sky";
            if (src === "moon") return "Moon";
            return "Upload Photo";
        },
        wantsSky() {
            return (this.selectedProductType && this.selectedProductType["has_sky_field"] === true)
                || this.cutoutPhotoSource() === "sky";
        },
        wantsMoon() {
            return (this.selectedProductType && this.selectedProductType["has_moon_field"] === true)
                || this.cutoutPhotoSource() === "moon";
        },
        ensurePhotoSourceState() {
            if (!this.userInput) return;
            const el = this.getPhotoSourceElement();
            if (!el) {
                return;
            }
            if (el["photo-source-options"].indexOf(this.userInput.photo_source) === -1) {
                this.userInput.photo_source = "upload";
            }
        },
        setPhotoSource(src) {
            const el = this.getPhotoSourceElement();
            if (!el || el["photo-source-options"].indexOf(src) === -1) return;
            if (this.userInput.photo_source === src) return;
            this.userInput.photo_source = src;
            if (src === "sky") {
                this.updateNightSky();
            } else if (src === "moon") {
                this.updateMoonPhase();
            } else {
                this.fillCanvas();
            }
        },
        /** Default ON for editable_type toggleable when key missing (e.g. after fork switch without setUpUserJson). */
        ensureToggleableDefaultsForCurrentStyle() {
            const elems = this.selectedProductType?.["styles"]?.[this.selectedProductTypeStyle]?.["elements"];
            if (!elems || !this.userInput) return;
            for (const el of elems) {
                if (el["editable_type"] === "toggleable" && this.userInput[el["id"]] === undefined) {
                    this.userInput[el["id"]] = true;
                }
            }
        },
        resetShowSectionDefaultsForCurrentStyle() {
            this.showDate = true;
            this.showTransparent = true;
            this.showMoment = false;
            this.showLongLat = true;
            this.showExtraDate = false;
            this.fullPrintEdgeToEdge = false;
            this.applyFullPrintEdgeLayout({ fill: false });

            const elems = this.selectedProductType?.["styles"]?.[this.selectedProductTypeStyle]?.["elements"];
            if (!elems) {
                return;
            }

            this.requiredProductFields = [];
            for (const el of elems) {
                if (el && el["required"] === true) {
                    this.requiredProductFields.push(el["id"]);
                }
            }
        },
        updateSelectedProductTypeStyle(newStyle){
            const preserveUploads = this.selectedProductType['fork-selector-preserve-uploads'] === true;
            if (!preserveUploads) {
                this.multiUploadImagesArray = [];
                this.multiImageUploader = false;
                this.resetLineArtState();
            }

            const nextStyle = this.selectedProductType?.styles?.[newStyle];
            const firstDoubleCardVisit = nextStyle && nextStyle.fork === "double-card"
                && (!this.userInput || this.userInput.back_card_value === undefined);
            this.selectedProductTypeStyle = newStyle;
            this.fillMissingStyleInitials();
            if (firstDoubleCardVisit) {
                this.userInput.card_value = "Q";
                this.userInput.suit = "hearts";
                this.userInput.suit_color = "red";
            }
            if (this.previewWarmupStarted) {
                this.enqueuePreviewUrls(this.collectStyleCanvasUrls(this.productTypeSelected, newStyle), true);
            }
            this.ensureToggleableDefaultsForCurrentStyle();
            this.ensurePlayingCardCustomFields();
            this.ensurePhotoSourceState();
            this.resetShowSectionDefaultsForCurrentStyle();
            if (this.selectedProductType['has_map_field'] === true) {
                if (this.selectedProductType["has_country_map"] !== true) {
                    this.applyMapProductDefaults({ forkSwitch: true });
                }
                this.fillInAddressNew();
            }else{
                this.fillCanvas();
            }

            if (preserveUploads && this.getLineArtGeneratorElement()) {
                void this.refreshLineArtTint();
            }

            if (this.selectedProductType['fork-selector-reset-change'] === true) {
                this.setUpUserJson();
            }

        },
        handleClickOutside(event) {
            if (this.showFontSelector && !this.$refs.fontSelector.includes(event.target)) {
                this.showFontSelector = false;
            }
        },
        openYearSelector(){
            this.yearSelectorShow = true;
            this.$nextTick(() => {
                const list = this.$refs.yearList;
                const box = Array.isArray(list) ? list[0] : list;
                if (!box) return;
                const current = String(new Date().getFullYear());
                const row = Array.prototype.find.call(box.querySelectorAll('li'), function (li) {
                    return li.textContent.trim() === current;
                });
                if (row) box.scrollTop = row.offsetTop - box.offsetTop;
            });
        },
        selectYearInput(year,id){
            //console.log(this.userInput);
            this.userInput[id] = year.toString();
            this.fillCanvas();
            this.yearSelectorShow = false;
        },
        selectImageInput(image,id){
            //console.log(this.userInput);
            this.userInput[id] = image.src;
            this.fillCanvas();
            this.imageSelectorValue = image.title
            this.imageSelectorShow = false;
        },
        changeFont(font) {
            this.showFontSelector = false;
            this.selectedFont = font;
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
            this.scrollToCanvas();
            this.preloadCanvasFonts().then(() => {
                if (this.selectedFont !== font) {
                    return;
                }
                this.clearCanvasFontMeasureCache();
                this.refreshCanvasTextObjects();
            });
            /*if(this.availableFonts[font]['type'] != "standard"){
                  var f = new FontFace(font, 'url('+this.availableFonts[font]['url']+')');
                  f.load().then(function(font) {
                    document.fonts.add(font);
                    this.fillCanvas();
                  });
                }else{
                  this.fillCanvas();
                }*/

        },
        loadCurrency() {
            this.cartCurrency = designerCartCurrency();
            const apply = (detail, fromPrices) => {
                const currency = designerCurrencyCode(detail);
                if (!currency) {
                    return;
                }
                const manual = designerManualCurrency();
                if (manual && currency !== manual) {
                    return;
                }
                if (fromPrices && window.__ttConfirmedCurrency && currency !== window.__ttConfirmedCurrency) {
                    return;
                }
                if (this.cartCurrency !== currency) {
                    this.cartCurrency = currency;
                }
                this.tryHidePreLoader();
            };
            window.addEventListener("tt-currency-updated", (e) => {
                apply(e && e.detail, false);
            });
            window.addEventListener("tt-prices-updated", (e) => {
                apply(e && e.detail, true);
            });
            if (
                typeof window.__ttResolveCurrency === "function" &&
                !window.__ttCurrencySwitching &&
                !window.__ttConfirmedCurrency
            ) {
                window.__ttResolveCurrency();
            }
            this.tryHidePreLoader();
        },
        updateCurrency() {},
        editDateFormat(tempDateNewFormat, newDateString) {
            const prevFormat = this.userSelectedDateFormat;
            this.userSelectedDateFormat = tempDateNewFormat;
            this.userDateFormat = tempDateNewFormat;
            this.curFormattedDate = newDateString;
            this.dateFormatShow = false;

            if (this.userInput && "date_from" in this.userInput) {
                const tryParse = (str, fmt) => {
                    try {
                        const d = dateFns.parse(str, fmt, new Date());
                        return d instanceof Date && !isNaN(d) ? d : null;
                    } catch (e) {
                        return null;
                    }
                };
                const reformatDateString = (str) => {
                    if (!str) return str;
                    const parsed =
                        tryParse(str, prevFormat) ||
                        tryParse(str, "dd/MM/yyyy") ||
                        tryParse(str, "MM/dd/yyyy") ||
                        tryParse(str, "dd-MM-yyyy") ||
                        tryParse(str, "MM-dd-yyyy");
                    return parsed ? dateFns.format(parsed, tempDateNewFormat) : str;
                };
                ["date_from", "date_to"].forEach((key) => {
                    if (this.userInput[key]) {
                        this.userInput[key] = reformatDateString(this.userInput[key]);
                    }
                });
            } else {
                this.userInput['date'] = newDateString;
            }

            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        getDatePickerValue(fieldId) {
            const raw = this.userInput && this.userInput[fieldId] ? String(this.userInput[fieldId]).trim() : "";
            if (!raw) return null;
            try {
                const parsed = dateFns.parse(raw, this.userDateFormat, new Date());
                return parsed instanceof Date && !isNaN(parsed) ? parsed : null;
            } catch (e) {
                return null;
            }
        },
        openDateFormatPicker(fieldId) {
            this.activeDateFieldId = fieldId;
            this.curFormattedDate = this.userInput && this.userInput[fieldId] ? this.userInput[fieldId] : "";
            this.dateFormatShow = true;
        },
        returnLength(value) {
            if (value) {
                return value.length;
            } else {
                return 0;
            }
        },
        async downloadDesignPNG() {
            var outputImage = await this.withPrintMap(() => this.renderCanvas("png", this.getPrintExportMultiplier()));
            var a = document.createElement("a"); //Create <a>
            a.href = outputImage; //Image Base64 Goes here
            a.download = "Image.png"; //File name Here
            a.click(); //Downloaded file
        },
        fillViaQuery() {
            this.queryArray = decodeURI(window.location.search)
                .replace('?', '')
                .split('&')
                .map(param => param.split('='))
                .reduce((values, [key, value]) => {
                    values[key] = value
                    return values
                }, {});

                if(this.queryArray['update-design'] === 'true'  ){

                   this.showUpdateDesignPopUp = true;

                   this.updateDesignAssetId = this.queryArray['asset_id'] || null;
                   if (this.updateDesignAssetId) {
                       this.fetchUpdateDesignPreview();
                   }
                }

        },
        closeSearchInput() {
            this.curSelectedSearch = '';
            this.curSelectedSearchValue = '';
        },
        isSearchableTextInput(el) {
            return !!(
                el &&
                el.tagName === 'INPUT' &&
                el.classList &&
                el.classList.contains('input-main')
            );
        },
        dismissSearchableInput() {
            this.suppressSearchFocusUntil = Date.now() + 500;
            this.closeSearchInput();
            const active = document.activeElement;
            if (this.isSearchableTextInput(active) && typeof active.blur === 'function') {
                active.blur();
            }
        },
        bindSearchInputDismissHandlers() {
            if (this._searchOutsidePointerDownBound) {
                return;
            }
            this._searchOutsidePointerDownBound = (event) => this.onSearchOutsidePointerDown(event);
            this._onVisualViewportResizeBound = () => this.onSearchVisualViewportResize();
            document.addEventListener('pointerdown', this._searchOutsidePointerDownBound, true);
            // Older iOS: pointer events may be missing; touchstart still runs before focus restore
            document.addEventListener('touchstart', this._searchOutsidePointerDownBound, { capture: true, passive: true });
            if (window.visualViewport) {
                this._vvLastHeight = window.visualViewport.height;
                window.visualViewport.addEventListener('resize', this._onVisualViewportResizeBound);
            }
        },
        unbindSearchInputDismissHandlers() {
            if (this._searchOutsidePointerDownBound) {
                document.removeEventListener('pointerdown', this._searchOutsidePointerDownBound, true);
                document.removeEventListener('touchstart', this._searchOutsidePointerDownBound, true);
                this._searchOutsidePointerDownBound = null;
            }
            if (this._onVisualViewportResizeBound && window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this._onVisualViewportResizeBound);
                this._onVisualViewportResizeBound = null;
            }
        },
        onSearchOutsidePointerDown(event) {
            const target = event && event.target;
            if (!target || typeof target.closest !== 'function') {
                return;
            }
            // Keep open when choosing a suggestion. Route results use the map suggestion
            // list, which is not an input, so this must run before the field is blurred.
            if (target.closest('.input-example-search-outer, .mapbox-location-search-mapbox-suggestions-outer')) {
                return;
            }
            // Intentional tap on a text field — allow focus (including quick re-edit after keyboard dismiss)
            if (target.closest('input.input-main')) {
                this.suppressSearchFocusUntil = 0;
                return;
            }
            const activeIsSearchable = this.isSearchableTextInput(document.activeElement);
            if (!this.curSelectedSearch && !activeIsSearchable) {
                return;
            }
            this.dismissSearchableInput();
        },
        onSearchVisualViewportResize() {
            if (!window.visualViewport) {
                return;
            }
            const height = window.visualViewport.height;
            const prev = this._vvLastHeight;
            this._vvLastHeight = height;
            // Keyboard dismissed: viewport grows. Force-blur so Suggested cannot reopen on the next tap.
            if (prev != null && height - prev > 80 && (this.curSelectedSearch || this.isSearchableTextInput(document.activeElement))) {
                this.dismissSearchableInput();
            }
        },
        shuffleArray(array) {
            array.sort(() => Math.random() - 0.5);
        },
        setSearchableInput(id, textArray) {
            // iOS often restores focus after keyboard-down + outside tap; ignore that restoration
            if (Date.now() < this.suppressSearchFocusUntil) {
                this.$nextTick(() => {
                    const active = document.activeElement;
                    if (this.isSearchableTextInput(active) && typeof active.blur === 'function') {
                        active.blur();
                    }
                });
                return;
            }
            // Keep filter in sync with current text so a refocus does not flash the full Suggested list
            const currentValue = (this.userInput && this.userInput[id] != null)
                ? String(this.userInput[id])
                : '';
            this.curSelectedSearchValue = currentValue;
            this.curSelectedSearch = id;
            if (textArray && textArray.length > 0) {
                this.shuffleArray(textArray);
                this.exampleTextOptions = textArray;
            } else {
                this.exampleTextOptions = [];
            }
        },
        updateExampleText(text, id) {
            this.userInput[id] = text;
            this.curSelectedSearchValue = text != null ? String(text) : '';
            if (!this.patchTextField(id)) {
                this.fillCanvas();
            }
        },
        closeUpsellPopUp() {
            this.atcAvailable = true;
            this.showSuccessBox = false;
            this.upsellComplete = false;
        },
        updateAndShowImage(index) {
            const images = this.selectedProductType && this.selectedProductType['images'];
            if (!images || !images.length) {
                return;
            }
            const next = ((index % images.length) + images.length) % images.length;
            this.showImageIndex = next;
            this.showImageSrc = images[next];
            this.showImage = true;
            const preload = new Image();
            preload.src = images[(next + 1) % images.length];
        },
        stepExampleImage(delta) {
            if (!this.showImage) {
                return;
            }
            this.updateAndShowImage(this.showImageIndex + delta);
        },
        closeExampleLightbox() {
            this.showImage = false;
        },
        onExampleLightboxKeydown(event) {
            if (!this.showImage) {
                return;
            }
            if (event.key === 'Escape') {
                this.closeExampleLightbox();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.stepExampleImage(1);
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.stepExampleImage(-1);
            }
        },
        onExampleTouchStart(event) {
            const touch = event.changedTouches && event.changedTouches[0];
            this._exampleTouchStartX = touch ? touch.clientX : null;
        },
        onExampleTouchEnd(event) {
            if (this._exampleTouchStartX == null) {
                return;
            }
            const touch = event.changedTouches && event.changedTouches[0];
            const startX = this._exampleTouchStartX;
            this._exampleTouchStartX = null;
            if (!touch) {
                return;
            }
            const dx = touch.clientX - startX;
            if (Math.abs(dx) < 40) {
                return;
            }
            this.stepExampleImage(dx < 0 ? 1 : -1);
        },
        addUpsell() {
            var properties = this.ornamentUpsellProperties();
            if (!properties) {
                return;
            }
            var shopifyJson = {
                quantity: 1,
                id: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['variant_id'],
                properties: properties
            }
            fetch("/cart/add.js", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(shopifyJson)
            }).then(response => {
                this.upsellComplete = true;
                this.syncCartFromServer();
            });
        },
        preLoadedImage() {
            this.preLoaderCanvasReady = true;
            this.tryHidePreLoader();
        },
        currencyReadyForReveal() {
            const confirmed = window.__ttConfirmedCurrency;
            return typeof confirmed === "string" && confirmed !== "" && this.cartCurrency === confirmed;
        },
        tryHidePreLoader() {
            if (this.preLoaderSettled || !this.preLoaderCanvasReady || !this.currencyReadyForReveal()) {
                return;
            }
            this.$nextTick(() => {
                this.hidePreLoader();
            });
        },
        forceHidePreLoader() {
            this.hidePreLoader();
        },
        hidePreLoader() {
            if (this.preLoaderSettled) {
                return;
            }
            this.preLoaderSettled = true;
            if (this.preLoaderSafetyTimer) {
                clearTimeout(this.preLoaderSafetyTimer);
                this.preLoaderSafetyTimer = null;
            }
            this.preLoaderShow = false;
            var bootCover = document.getElementById('tt-boot-cover');
            if (bootCover) {
                bootCover.classList.add('tt-boot-hide');
                setTimeout(function () {
                    if (bootCover.parentNode) {
                        bootCover.parentNode.removeChild(bootCover);
                    }
                }, 400);
            }
        },
        changeCurrentProduct(index){
            this.multiUploadImagesArray = [];
            this.resetLineArtState();
            var tempSelection = this.productData.types[index]['products'][this.productSizeSelected]["route_selection"];
            this.timeSelector.showTime = false;

            if(tempSelection !== undefined){
                this.productSizeSelected = tempSelection;
            }

            if(this.productData.types[index]['onload_scroll'] !== undefined){
                this.scrollToCanvas();
            }

            this.canvasLoader = false;
            this.clearRouteEnd();
            this.selectedProductTypeStyle = this.defaultStyleIndex(index);
            this.productTypeSelected = index;
            this.updateSelectedProductType();
            this.viewMoreProductsPopUp = false;
            this.prioritizeProductPreview(index);

            this.mapboxMapSearchField = "";
            this.mapboxMapSearchFieldSuggestions = [];
            this.resetGooglePlacesAutocompleteSession();

            this.showDate = true;
            this.showTransparent = true;
            this.datePickerValue = "";
            this.curFormattedDate = "";

        },
        viewMoreProducts(){
            this.viewMoreProductsPopUp = true;
        },
        shopifyFrameUrl(url) {
            if (typeof url !== "string" || url.indexOf("cdn.shopify.com") === -1) {
                return url;
            }
            try {
                const parsed = new URL(url);
                parsed.searchParams.set("width", "800");
                return parsed.toString();
            } catch (e) {
                return url;
            }
        },
        productRecordForType(type, sizeIndex) {
            if (!type || !Array.isArray(type.products) || !type.products.length) {
                return null;
            }
            if (type.type === "standard") {
                return type.products[0];
            }
            return type.products[sizeIndex] || type.products[0];
        },
        collectFrameUrlsForType(typeIndex, mode) {
            const type = this.productData && this.productData.types ? this.productData.types[typeIndex] : null;
            if (!type || !Array.isArray(type.products)) {
                return [];
            }
            const urls = [];
            if (mode === "default") {
                const product = this.productRecordForType(type, this.productSizeSelected);
                if (product) {
                    urls.push(this.shopifyFrameUrl(product["background-image-0"]));
                    urls.push(this.shopifyFrameUrl(product["canvas-mask-image-0"]));
                }
            } else {
                type.products.forEach((product) => {
                    if (!product) {
                        return;
                    }
                    Object.keys(product).forEach((key) => {
                        if (key.indexOf("background-image-") === 0 || key.indexOf("canvas-mask-image-") === 0) {
                            urls.push(this.shopifyFrameUrl(product[key]));
                        }
                    });
                });
            }
            return urls.filter((url) => typeof url === "string" && url !== "");
        },
        isStaticCanvasImageUrl(value) {
            if (typeof value !== "string") {
                return false;
            }
            if (value.indexOf("http") !== 0 && value.indexOf("//") !== 0) {
                return false;
            }
            if (value.indexOf("api.mapbox.com") !== -1 || value.indexOf("data:") === 0) {
                return false;
            }
            return /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(value);
        },
        collectStyleCanvasUrls(typeIndex, styleIndex) {
            const type = this.productData && this.productData.types ? this.productData.types[typeIndex] : null;
            const style = type && type.styles ? type.styles[styleIndex] : null;
            const urls = [];
            if (!style || !Array.isArray(style.elements)) {
                return urls;
            }
            let hasImageSelector = false;
            style.elements.forEach((el) => {
                if (!el) {
                    return;
                }
                if (el["editable_type"] === "image_selector") {
                    hasImageSelector = true;
                }
                ["initial", "clipping-mask-image", "image", "src"].forEach((key) => {
                    if (this.isStaticCanvasImageUrl(el[key])) {
                        urls.push(el[key]);
                    }
                });
            });
            if (hasImageSelector && Array.isArray(this.imageSelector)) {
                this.imageSelector.forEach((item) => {
                    if (item && this.isStaticCanvasImageUrl(item.src)) {
                        urls.push(item.src);
                    }
                });
            }
            return urls;
        },
        enqueuePreviewUrls(urls, front) {
            if (!this.previewWarmupSeen) {
                this.previewWarmupSeen = {};
            }
            const fresh = [];
            (urls || []).forEach((url) => {
                if (typeof url !== "string" || url === "" || this.previewWarmupSeen[url]) {
                    return;
                }
                this.previewWarmupSeen[url] = true;
                fresh.push(url);
            });
            if (!fresh.length) {
                return;
            }
            if (front) {
                this.previewWarmupQueue = fresh.concat(this.previewWarmupQueue);
            } else {
                this.previewWarmupQueue = this.previewWarmupQueue.concat(fresh);
            }
            this.pumpPreviewWarmup();
        },
        pumpPreviewWarmup() {
            const concurrency = 2;
            while (this.previewWarmupActive < concurrency && this.previewWarmupQueue.length) {
                const url = this.previewWarmupQueue.shift();
                this.previewWarmupActive += 1;
                const img = new Image();
                const done = () => {
                    this.previewWarmupActive = Math.max(0, this.previewWarmupActive - 1);
                    this.pumpPreviewWarmup();
                };
                img.onload = done;
                img.onerror = done;
                img.src = url;
            }
        },
        collectAndQueuePreviewWarmup() {
            const current = this.productTypeSelected;
            const urls = this.collectFrameUrlsForType(current, "all")
                .concat(this.collectStyleCanvasUrls(current, this.selectedProductTypeStyle));
            const types = this.productData && this.productData.types ? this.productData.types : [];
            for (let i = 0; i < types.length; i++) {
                if (i === current) {
                    continue;
                }
                urls.push.apply(urls, this.collectFrameUrlsForType(i, "default"));
            }
            this.enqueuePreviewUrls(urls, false);
        },
        schedulePreviewWarmup() {
            if (this.previewWarmupStarted) {
                return;
            }
            this.previewWarmupStarted = true;
            const run = () => {
                this.collectAndQueuePreviewWarmup();
                this.warmFontList();
                warmDeferredLibraries();
            };
            if (typeof requestIdleCallback === "function") {
                requestIdleCallback(run, { timeout: 2000 });
            } else {
                setTimeout(run, 300);
            }
        },
        warmFontList() {
            if (this.previewFontWarmStarted) {
                return;
            }
            this.previewFontWarmStarted = true;
            const families = (this.availableFonts || []).slice();
            let index = 0;
            const loadNext = () => {
                if (index >= families.length) {
                    return;
                }
                const batch = families.slice(index, index + 2);
                index += batch.length;
                const loads = batch.map((family) => {
                    if (!family || !document.fonts || typeof document.fonts.load !== "function") {
                        return Promise.resolve();
                    }
                    return document.fonts.load('80px "' + family + '"').catch(function () {});
                });
                Promise.all(loads).then(() => {
                    if (index >= families.length) {
                        return;
                    }
                    if (typeof requestIdleCallback === "function") {
                        requestIdleCallback(loadNext, { timeout: 1000 });
                    } else {
                        setTimeout(loadNext, 40);
                    }
                });
            };
            loadNext();
        },
        prioritizeProductPreview(typeIndex) {
            if (!this.previewWarmupStarted) {
                return;
            }
            const urls = this.collectFrameUrlsForType(typeIndex, "all")
                .concat(this.collectStyleCanvasUrls(typeIndex, this.selectedProductTypeStyle || 0));
            this.enqueuePreviewUrls(urls, true);
        },
        returnConvertedCurrency(price, type) {
            var output = price;
            if (this.cartCurrency != "JPY") {
                output = output.toFixed(2);
            }

            if (this.cartCurrency == "JPY") {
                output = "¥" + output;
            } else if (this.cartCurrency == "GBP") {
                output = "£" + output;
            } else {
                output = "$" + output;
            }

            if (type == "currencyFormat" || type == "") {
                output = output + " " + this.cartCurrency;
            }

            return output;
        },
        formatDate(dateObj) {
            return dateFns.format(dateObj, this.userDateFormat);
        },
        getDefaultDateFnsFormat() {
            const testDate = new Date(2013, 11, 31); // Dec 31, 2013
            const localeDate = testDate.toLocaleDateString();

            // Use pattern to guess format
            if (/31.*12.*2013/.test(localeDate)) return "dd/MM/yyyy"; // e.g. Australia, UK
            if (/12.*31.*2013/.test(localeDate)) return "MM/dd/yyyy"; // e.g. US
            if (/2013.*12.*31/.test(localeDate)) return "yyyy/MM/dd"; // e.g. China, Japan

            // Fallback
            return "MM-dd-yyyy";
        },
        applyProductDateFormatDefault() {
            const pt = this.selectedProductType;
            const raw = pt && pt["default-date-format"] ? String(pt["default-date-format"]).trim() : "";
            const next = raw !== "" ? raw : this.getDefaultDateFnsFormat();
            if (next === this.userDateFormat && next === this.userSelectedDateFormat) {
                return;
            }
            const prev = this.userDateFormat;
            this.userDateFormat = next;
            this.userSelectedDateFormat = next;
            if (this.curFormattedDate && prev && prev !== next) {
                const tryParse = (str, fmt) => {
                    try {
                        const d = dateFns.parse(str, fmt, new Date());
                        return d instanceof Date && !isNaN(d) ? d : null;
                    } catch (e) {
                        return null;
                    }
                };
                const parsed =
                    tryParse(this.curFormattedDate, prev) ||
                    tryParse(this.curFormattedDate, "dd/MM/yyyy") ||
                    tryParse(this.curFormattedDate, "MM/dd/yyyy") ||
                    tryParse(this.curFormattedDate, "dd-MM-yyyy") ||
                    tryParse(this.curFormattedDate, "MM-dd-yyyy");
                if (parsed) {
                    const formatted = dateFns.format(parsed, next);
                    this.curFormattedDate = formatted;
                    if (this.userInput && "date" in this.userInput) {
                        this.userInput["date"] = formatted;
                    }
                }
            }
        },
        handleDate(selectedDate, fieldId) {
            if (!selectedDate) {
                console.log("No Date Change");
                return;
            }

            console.log(selectedDate);

            const dateStr = this.formatDate(selectedDate);

            if (fieldId === "date_from" || fieldId === "date_to") {
                this.userInput[fieldId] = dateStr;
                if (fieldId === "date_from") {
                    this.curFormattedDate = dateStr;
                }
            } else {
                this.userInput['date'] = dateStr;
                this.curFormattedDate = dateStr;
                this.dateFieldInstance = selectedDate;
            }

            if (this.cartEditRestoring) {
                return;
            }

            this.refreshLocationVisual({ textOnly: true });

            console.log("Date Change");
        },
        multiImageCropAspect() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            const shaped = elems.find((el) => el && el.type === "multi_image" && el["use-photo-shape"]);
            if (shaped) {
                const shape = this.getSelectedPhotoShape();
                const width = Number(shape && shape["photo-width"]);
                const height = Number(shape && shape["photo-height"]);
                if (width > 0 && height > 0) {
                    return width / height;
                }
            }
            const upload = elems.find((el) => el && (el.editable_type === "multi_image" || el.editable_type === "line_art_generator"));
            const ratio = Number(upload && upload["aspect-ratio"]);
            return ratio > 0 ? ratio : (this.curCropAspect || 1);
        },
        cropMultiImage(id){
            this.multiImageUploader = true;
            this.curCropAspect = this.multiImageCropAspect();
            this.curCroppingImageSRC = this.multiUploadImagesArray[id]['original'];
            this.curCropImage = id;
            this.showCropModal();
        },
        resetLineArtState() {
            this.lineArtColor = '#000000';
            this.lineArtGeneratedUrlRaw = null;
            this.lineArtTintedUrl = null;
            this.lineArtSelectedGenerationId = null;
            this.lineArtGenerating = false;
            this.lineArtGenerationError = '';
            this.stopLineArtProcessingUI();
            this.closeLineArtGenerationModal();
            if (this.userInput) {
                this.userInput['line_art_generator'] = '';
            }
            if (this.getLineArtGeneratorElement()) {
                void this.refreshLineArtTint();
            }
        },
        getLineArtDefaultImageUrl() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements;
            const slot = elems?.find((el) => el.type === 'line_art_image');
            return slot?.initial || null;
        },
        getLineArtActiveSourceUrl() {
            const raw = this.lineArtGeneratedUrlRaw || this.getLineArtDefaultImageUrl();
            if (!raw) {
                return null;
            }
            return this.lineArtSafeUrlCache[raw] || raw;
        },
        isLineArtDataOrBlobUrl(url) {
            return typeof url === 'string' && (url.startsWith('data:') || url.startsWith('blob:'));
        },
        readBlobAsDataUrl(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },
        /**
         * Best-effort: turn a remote line-art URL into a data URL so Fabric/canvas
         * can load it with CORS. Returns the data URL on success, or null if the
         * remote host blocks reading the pixels (caller should still display via
         * a non-CORS <img> load).
         */
        async ensureLineArtCanvasSafeUrl(url) {
            if (!url) {
                return null;
            }
            if (this.isLineArtDataOrBlobUrl(url)) {
                return url;
            }
            if (this.lineArtSafeUrlCache[url]) {
                return this.lineArtSafeUrlCache[url];
            }

            try {
                const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
                if (res.ok) {
                    const dataUrl = await this.readBlobAsDataUrl(await res.blob());
                    this.lineArtSafeUrlCache[url] = dataUrl;
                    return dataUrl;
                }
            } catch (error) {
                // fall through to Image + canvas attempt
            }

            try {
                const dataUrl = await new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = 'Anonymous';
                    img.onload = () => {
                        try {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.naturalWidth || img.width;
                            canvas.height = img.naturalHeight || img.height;
                            canvas.getContext('2d').drawImage(img, 0, 0);
                            resolve(canvas.toDataURL('image/png'));
                        } catch (err) {
                            reject(err);
                        }
                    };
                    img.onerror = reject;
                    img.src = url;
                });
                this.lineArtSafeUrlCache[url] = dataUrl;
                return dataUrl;
            } catch (error) {
                console.warn('Could not create canvas-safe line art URL (will load without CORS):', url);
                return null;
            }
        },
        getLineArtGeneratorElement() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements;
            if (!elems) {
                return null;
            }
            return elems.find((el) => el['editable_type'] === 'line_art_generator') || null;
        },
        getLineArtGenerationKey() {
            const title = this.selectedProductType?.title || 'default';
            return String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        },
        closeLineArtGenerationModal() {
            this.showLineArtGenerationModal = false;
            this.lineArtGenerationError = '';
            if (this.lineArtLimitModalTimer) {
                clearTimeout(this.lineArtLimitModalTimer);
                this.lineArtLimitModalTimer = null;
            }
        },
        retryLineArtGeneration() {
            if (this.lineArtGenerating) {
                return;
            }
            const element = this.getLineArtGeneratorElement();
            if (!element || !this.multiUploadImagesArray[0]?.src) {
                this.closeLineArtGenerationModal();
                return;
            }
            void this.generateLineArt(element);
        },
        maybeAutoGenerateLineArt() {
            const element = this.getLineArtGeneratorElement();
            if (!element || !this.multiUploadImagesArray[0]?.src || this.lineArtGenerating) {
                return;
            }

            this.loadLineArtGenerationCount(
                element['ai-image-generation'] ? element['ai-image-generation']['generation-limit'] : null
            );
            if (this.lineArtGenerationLimitReached) {
                this.lineArtGenerationError = '';
                this.showLineArtGenerationModal = true;
                this.lineArtLimitModalTimer = setTimeout(() => {
                    this.closeLineArtGenerationModal();
                }, 4000);
                return;
            }
            void this.generateLineArt(element);
        },
        startLineArtProcessingUI(messages) {
            if (!messages || messages.length === 0) {
                return;
            }
            this.stopLineArtProcessingUI();
            let index = 0;
            this.aiImageProcessingMessage = messages[index];
            this.lineArtProgress = Math.round(((index + 1) / messages.length) * 95);
            this.lineArtProcessingInterval = setInterval(() => {
                if (index < messages.length - 1) {
                    index += 1;
                    this.aiImageProcessingMessage = messages[index];
                    this.lineArtProgress = Math.round(((index + 1) / messages.length) * 95);
                }
            }, 3500);
        },
        stopLineArtProcessingUI() {
            if (this.lineArtProcessingInterval) {
                clearInterval(this.lineArtProcessingInterval);
                this.lineArtProcessingInterval = null;
            }
            this.aiImageProcessingMessage = 'Creating your image...';
            this.lineArtProgress = 0;
        },
        lineArtColorMatches(color) {
            return (this.lineArtColor || '').toLowerCase() === (color || '').toLowerCase();
        },
        updateLineArtColor(color) {
            this.lineArtColor = color;
            this.refreshLineArtTint();
        },
        recolorLineArtPng(imageUrl, hexColor) {
            if (!imageUrl) {
                return Promise.resolve(null);
            }
            const normalized = (hexColor || '#000000').toLowerCase();
            if (normalized === '#000000') {
                return Promise.resolve(imageUrl);
            }
            const hex = normalized.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);

            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;
                    for (let i = 0; i < data.length; i += 4) {
                        if (data[i + 3] > 0) {
                            data[i] = r;
                            data[i + 1] = g;
                            data[i + 2] = b;
                        }
                    }
                    ctx.putImageData(imageData, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = reject;
                img.src = imageUrl;
            });
        },
        async refreshLineArtTint() {
            const seq = ++this.lineArtTintSeq;
            const sourceUrl = this.getLineArtActiveSourceUrl();
            if (!sourceUrl) {
                if (seq !== this.lineArtTintSeq) {
                    return;
                }
                this.lineArtTintedUrl = null;
                this.fillCanvas();
                return;
            }
            try {
                const tinted = await this.recolorLineArtPng(sourceUrl, this.lineArtColor);
                if (seq !== this.lineArtTintSeq) {
                    return;
                }
                this.lineArtTintedUrl = tinted;
            } catch (error) {
                if (seq !== this.lineArtTintSeq) {
                    return;
                }
                console.error('Line art recolor failed:', error);
                this.lineArtTintedUrl = sourceUrl;
            }
            if (seq !== this.lineArtTintSeq) {
                return;
            }
            this.fillCanvas();
        },
        loadLineArtGenerationCount(limit) {
            if (limit != null && !Number.isNaN(Number(limit))) {
                this.lineArtGenerationLimit = Number(limit);
            }
            try {
                const key = 'tt_line_art_generation_count_' + this.getLineArtGenerationKey();
                const stored = parseInt(localStorage.getItem(key) || '0', 10);
                this.lineArtGenerationsUsed = Number.isNaN(stored) ? 0 : stored;
            } catch (error) {
                this.lineArtGenerationsUsed = 0;
            }
            this.lineArtGenerationLimitReached = this.lineArtGenerationsUsed >= this.lineArtGenerationLimit;
        },
        clearLineArtGenerationLimit() {
            try {
                localStorage.removeItem('tt_line_art_generation_count');
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.indexOf('tt_line_art_generation_count_') === 0) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach((key) => localStorage.removeItem(key));
            } catch (error) {
                console.warn('Could not clear line art generation count:', error);
            }
            this.lineArtGenerationsUsed = 0;
            this.lineArtGenerationLimitReached = false;
        },
        checkClearAiLimitHash() {
            const hash = (window.location.hash || '').replace(/^#/, '');
            if (hash !== 'clearAiLimit') {
                return;
            }
            this.clearLineArtGenerationLimit();
            if (window.history && window.history.replaceState) {
                const cleanUrl = window.location.pathname + window.location.search;
                window.history.replaceState(null, '', cleanUrl);
            }
        },
        incrementLineArtGenerationCount() {
            this.lineArtGenerationsUsed += 1;
            try {
                const key = 'tt_line_art_generation_count_' + this.getLineArtGenerationKey();
                localStorage.setItem(key, String(this.lineArtGenerationsUsed));
            } catch (error) {
                console.warn('Could not save line art generation count:', error);
            }
            this.lineArtGenerationLimitReached = this.lineArtGenerationsUsed >= this.lineArtGenerationLimit;
        },
        resetLineArtGenerationCount() {
            this.lineArtGenerationsUsed = 0;
            this.lineArtGenerationLimitReached = false;
            try {
                const key = 'tt_line_art_generation_count_' + this.getLineArtGenerationKey();
                localStorage.setItem(key, '0');
            } catch (error) {
                console.warn('Could not reset line art generation count:', error);
            }
        },
        lineArtGenerationsRemainingText() {
            const remaining = this.lineArtGenerationLimit - this.lineArtGenerationsUsed;
            return remaining + ' generation(s) remaining';
        },
        normalizeLineArtGenerationHistory(entries) {
            if (!Array.isArray(entries)) {
                return [];
            }
            return entries
                .filter((entry) => entry && entry.id && entry.rawUrl)
                .sort((a, b) => Number(b.id) - Number(a.id))
                .slice(0, LINE_ART_GENERATION_HISTORY_MAX);
        },
        loadLineArtGenerationHistory() {
            try {
                const key = 'tt_line_art_generation_history_' + this.getLineArtGenerationKey();
                const stored = JSON.parse(localStorage.getItem(key) || '[]');
                this.lineArtGenerationHistory = this.normalizeLineArtGenerationHistory(stored);
            } catch (error) {
                this.lineArtGenerationHistory = [];
            }
        },
        saveLineArtGenerationHistory() {
            try {
                this.lineArtGenerationHistory = this.normalizeLineArtGenerationHistory(
                    this.lineArtGenerationHistory
                );
                localStorage.setItem(
                    'tt_line_art_generation_history_' + this.getLineArtGenerationKey(),
                    JSON.stringify(this.lineArtGenerationHistory)
                );
            } catch (error) {
                console.warn('Could not save line art generation history:', error);
            }
        },
        addLineArtGenerationToHistory(rawUrl) {
            if (!rawUrl) {
                return;
            }
            this.lineArtGenerationHistory.unshift({
                id: String(Date.now()),
                rawUrl: rawUrl,
            });
            this.lineArtGenerationHistory = this.normalizeLineArtGenerationHistory(
                this.lineArtGenerationHistory
            );
            this.lineArtSelectedGenerationId = this.lineArtGenerationHistory[0].id;
            this.saveLineArtGenerationHistory();
        },
        async selectLineArtGeneration(id) {
            const entry = this.lineArtGenerationHistory.find((gen) => gen.id === id);
            if (!entry) {
                return;
            }
            ++this.lineArtTintSeq;
            this.lineArtSelectedGenerationId = id;
            this.lineArtGeneratedUrlRaw = entry.rawUrl;
            // Satisfy required-field validation (same marker as upload path)
            if (this.userInput) {
                this.userInput['line_art_generator'] = "Uploaded";
            }
            // Clear stale tint so fillCanvas doesn't keep showing the previous generation
            this.lineArtTintedUrl = null;
            await this.ensureLineArtCanvasSafeUrl(entry.rawUrl);
            this.fillCanvas();
            await this.refreshLineArtTint();
        },
        lineArtGenerationIsSelected(id) {
            return this.lineArtSelectedGenerationId === id;
        },
        reloadLineArtGenerationState() {
            const element = this.getLineArtGeneratorElement();
            this.loadLineArtGenerationCount(
                element && element['ai-image-generation']
                    ? element['ai-image-generation']['generation-limit']
                    : null
            );
            this.loadLineArtGenerationHistory();
            const selectedEntry = this.lineArtSelectedGenerationId
                ? this.lineArtGenerationHistory.find((gen) => gen.id === this.lineArtSelectedGenerationId)
                : null;
            if (selectedEntry) {
                this.lineArtGeneratedUrlRaw = selectedEntry.rawUrl;
                if (this.userInput) {
                    this.userInput['line_art_generator'] = "Uploaded";
                }
                void (async () => {
                    await this.ensureLineArtCanvasSafeUrl(selectedEntry.rawUrl);
                    await this.refreshLineArtTint();
                })();
                return;
            }
            this.lineArtSelectedGenerationId = null;
            this.lineArtGeneratedUrlRaw = null;
            if (element && this.lineArtGenerationHistory.length > 0) {
                const latest = this.lineArtGenerationHistory[0];
                void this.selectLineArtGeneration(latest.id);
            }
        },
        async generateLineArt(element) {
            const upload = this.multiUploadImagesArray[0];
            if (!upload || !upload.src || this.lineArtGenerating) {
                return;
            }

            this.loadLineArtGenerationCount(
                element && element['ai-image-generation']
                    ? element['ai-image-generation']['generation-limit']
                    : null
            );
            if (this.lineArtGenerationLimitReached) {
                return;
            }

            this.clearImageUploadError();
            this.lineArtGenerationError = '';
            this.lineArtGenerating = true;
            this.lineArtProgress = 0;
            this.showLineArtGenerationModal = true;
            const messages = element && element['ai-image-generation']
                ? element['ai-image-generation']['processing-messages']
                : [];
            this.startLineArtProcessingUI(messages);

            let generationSucceeded = false;
            try {
                const blob = await fetch(upload.src).then((r) => r.blob());
                const config = Object.assign({}, element['ai-image-generation']);
                const blobForApi = await this.prepareImageFileForUpload(
                    new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' }),
                    config
                );
                const remoteUrl = await this.generateAiImage(blobForApi, config);
                await this.ensureLineArtCanvasSafeUrl(remoteUrl);
                this.lineArtGeneratedUrlRaw = remoteUrl;
                this.incrementLineArtGenerationCount();
                this.addLineArtGenerationToHistory(remoteUrl);
                this.lineArtProgress = 100;
                await this.refreshLineArtTint();
                generationSucceeded = true;
            } catch (error) {
                console.error('Line art generation failed:', error);
                const message = (error && error.message) ? String(error.message) : '';
                if (/429|too many requests|rate limit/i.test(message)) {
                    this.lineArtGenerationError = 'Line art is busy right now. Please wait a moment and try again.';
                } else {
                    this.lineArtGenerationError = "Couldn't create line art from this photo. Please try again.";
                }
                // Keep any previous successful generation on the canvas
                if (!this.lineArtGeneratedUrlRaw && this.lineArtGenerationHistory.length > 0) {
                    const latest = this.lineArtGenerationHistory[0];
                    this.lineArtSelectedGenerationId = latest.id;
                    this.lineArtGeneratedUrlRaw = latest.rawUrl;
                    await this.refreshLineArtTint();
                } else {
                    this.fillCanvas();
                }
            } finally {
                this.stopLineArtProcessingUI();
                this.lineArtGenerating = false;
                if (generationSucceeded) {
                    this.closeLineArtGenerationModal();
                } else {
                    // Keep modal open so the error state is visible
                    this.showLineArtGenerationModal = true;
                }
            }

            if (generationSucceeded) {
                this.scrollToCanvas();
            }
        },
        deleteMultiImage(id,limit){
            this.multiUploadImagesArray = this.removeFromArray(this.multiUploadImagesArray,id);
            this.userInput['multi_image_selector'] = "";
            this.userInput['line_art_generator'] = "";
            this.lineArtGeneratedUrlRaw = null;
            this.lineArtTintedUrl = null;
            this.lineArtSelectedGenerationId = null;
            if (this.getLineArtGeneratorElement()) {
                void this.refreshLineArtTint();
            } else {
                this.fillCanvas();
            }
        },
        removeFromArray(arr, item) {
            var newArray = [];
            for(var i = arr.length; i--;) {
                if(i !== item) {
                    newArray.push(arr[i]);
                }
            }
            return newArray.reverse();
        },
        async removeBackgroundFromBinary(blob) {
            const formData = new FormData();
            formData.append('image', blob);

            const response = await fetch('https://app.agentallen.ai/api/remove-background', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error("Laravel proxy request failed");
            }

            const result = await response.json();
            return result.cleaned_image_url; // your backend should return this
        },
        async parseApiJsonResponse(response, fallbackMessage) {
            const contentType = (response.headers.get('content-type') || '').toLowerCase();
            let data = null;
            let rawText = '';

            if (contentType.includes('application/json')) {
                try {
                    data = await response.json();
                } catch (error) {
                    data = null;
                }
            } else {
                try {
                    rawText = await response.text();
                    if (rawText && rawText.trim().charAt(0) === '{') {
                        data = JSON.parse(rawText);
                    }
                } catch (error) {
                    data = null;
                }
            }

            if (!response.ok) {
                const apiMessage = data && (data.message || data.error);
                if (response.status === 429) {
                    throw new Error(apiMessage || 'Too many requests (429)');
                }
                throw new Error(apiMessage || fallbackMessage || `Request failed (${response.status})`);
            }

            if (!data || typeof data !== 'object') {
                throw new Error(fallbackMessage || 'Unexpected API response');
            }

            return data;
        },
        async generateAiImage(blob, config) {
            const baseUrl = 'https://app.agentallen.ai/api';
            const maxSubmitAttempts = 4;
            let submitData = null;

            for (let attempt = 0; attempt < maxSubmitAttempts; attempt++) {
                const formData = new FormData();
                formData.append('image', blob, 'photo.jpg');
                if (config['prompt']) {
                    formData.append('prompt', config['prompt']);
                }

                try {
                    const submitRes = await fetch(`${baseUrl}/${config['submit-endpoint']}`, {
                        method: 'POST',
                        body: formData,
                        headers: { Accept: 'application/json' },
                    });
                    submitData = await this.parseApiJsonResponse(
                        submitRes,
                        `AI image submit failed (${submitRes.status})`
                    );
                    break;
                } catch (error) {
                    const isRateLimited = /429|too many requests|rate limit/i.test(
                        (error && error.message) ? String(error.message) : ''
                    );
                    const isLastAttempt = attempt >= maxSubmitAttempts - 1;
                    if (!isRateLimited || isLastAttempt) {
                        throw error;
                    }
                    const delayMs = Math.min(12000, 1500 * Math.pow(2, attempt));
                    await new Promise((r) => setTimeout(r, delayMs));
                }
            }

            if (!submitData || !submitData.id) {
                throw new Error('AI image submit did not return a job id');
            }

            const jobId = submitData.id;
            const pollPath = config['poll-endpoint'] || 'ai-images';
            let interval = submitData.poll_interval_ms || 3000;

            for (let attempt = 0; attempt < 60; attempt++) {
                await new Promise((r) => setTimeout(r, interval));
                const pollRes = await fetch(`${baseUrl}/${pollPath}/${jobId}`, {
                    headers: { Accept: 'application/json' },
                });
                const pollData = await this.parseApiJsonResponse(
                    pollRes,
                    `AI image poll failed (${pollRes.status})`
                );
                if (pollData.status === 'complete') {
                    if (!pollData.url) {
                        throw new Error('AI image completed without a result URL');
                    }
                    return pollData.url;
                }
                if (pollData.status === 'failed') {
                    throw new Error(pollData.message || 'AI image generation failed');
                }
                interval = pollData.poll_interval_ms || interval;
            }
            throw new Error('Timed out waiting for AI image generation');
        },
        startAiImageProcessingMessages(messages) {
            this.stopAiImageProcessingMessages();
            const list = messages && messages.length ? messages : ['Creating your image...'];
            let index = 0;
            this.aiImageProcessingMessage = list[index];
            this.aiImageProgress = Math.round(((index + 1) / list.length) * 95);
            this.aiImageProcessingInterval = setInterval(() => {
                if (index < list.length - 1) {
                    index += 1;
                    this.aiImageProgress = Math.round(((index + 1) / list.length) * 95);
                } else {
                    index = (index + 1) % list.length;
                }
                this.aiImageProcessingMessage = list[index];
            }, 3500);
        },
        stopAiImageProcessingMessages() {
            if (this.aiImageProcessingInterval) {
                clearInterval(this.aiImageProcessingInterval);
                this.aiImageProcessingInterval = null;
            }
            this.aiImageProcessingMessage = 'Creating your image...';
            this.aiImageProgress = 0;
        },
        closeAiImageGenerationModal() {
            this.stopAiImageProcessingMessages();
            this.showAiImageGenerationModal = false;
        },
        async prepareImageFileForUpload(file, config) {
            const maxDimension = config['max-upload-dimension'] || 2048;
            const quality = config['upload-quality'] || 0.88;
            const objectUrl = URL.createObjectURL(file);

            try {
                const img = await new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = reject;
                    image.src = objectUrl;
                });

                const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
                const width = Math.round(img.width * scale);
                const height = Math.round(img.height * scale);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);

                return await new Promise((resolve) => {
                    canvas.toBlob(resolve, 'image/jpeg', quality);
                });
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        },
        async runAiImageGeneration(index, file, aiImageGeneration, limit) {
            this.showAiImageGenerationModal = true;

            try {
                this.startAiImageProcessingMessages(aiImageGeneration['processing-messages']);
                const blob = await this.prepareImageFileForUpload(file, aiImageGeneration);
                const finalImageURL = await this.generateAiImage(blob, aiImageGeneration);
                this.multiUploadImagesArray[index]['src'] = finalImageURL;
            } catch (error) {
                console.error("AI image generation failed:", error);
                if (this.multiUploadImagesArray[index]['original']) {
                    this.multiUploadImagesArray[index]['src'] = this.multiUploadImagesArray[index]['original'];
                }
            } finally {
                this.multiUploadImagesArray[index]['loading'] = false;
                this.closeAiImageGenerationModal();
                this.fillCanvas();

                const doneCount = this.multiUploadImagesArray.filter(img => !img.loading).length;
                if (doneCount === limit) {
                    this.userInput['multi_image_selector'] = "Uploaded";
                }
            }
        },
        isHeicOrHeifFile(file) {
            if (!file) {
                return false;
            }
            const type = String(file.type || "").toLowerCase();
            const name = String(file.name || "").toLowerCase();
            return type === "image/heic" || type === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
        },
        heicFileToJpegName(name) {
            return String(name || "photo.heic").replace(/\.(heic|heif)$/i, ".jpg");
        },
        async convertHeicViaServer(file) {
            const form = new FormData();
            form.append("file", file);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);

            try {
                const res = await fetch("https://app.agentallen.ai/api/convert-heic", {
                    method: "POST",
                    body: form,
                    signal: controller.signal,
                });

                if (!res.ok) {
                    const detail = await res.text().catch(() => "");
                    throw new Error(
                        `Server HEIC conversion failed (${res.status})${detail ? `: ${detail}` : ""}`
                    );
                }

                const blob = await res.blob();
                if (!blob || !blob.size) {
                    throw new Error("Server HEIC conversion produced an empty result");
                }

                return new File([blob], this.heicFileToJpegName(file.name), { type: "image/jpeg" });
            } finally {
                clearTimeout(timeoutId);
            }
        },
        async normalizeImageFileForPipeline(file) {
            if (!file || !this.isHeicOrHeifFile(file)) {
                return file;
            }

            if (typeof heic2any !== "function") {
                try {
                    await ensureHeic2any();
                } catch (loadError) {
                    console.warn("heic2any failed to load, trying server HEIC conversion", loadError);
                }
            }

            if (typeof heic2any === "function") {
                try {
                    const converted = await heic2any({
                        blob: file,
                        toType: "image/jpeg",
                        quality: 1
                    });
                    const blob = Array.isArray(converted) ? converted[0] : converted;
                    if (!blob) {
                        throw new Error("HEIC conversion produced an empty result");
                    }
                    return new File([blob], this.heicFileToJpegName(file.name), { type: "image/jpeg" });
                } catch (localError) {
                    console.warn("Local HEIC/HEIF conversion failed, trying server fallback", localError);
                }
            } else {
                console.warn("heic2any unavailable, trying server HEIC conversion");
            }

            try {
                return await this.convertHeicViaServer(file);
            } catch (serverError) {
                console.error("Server HEIC/HEIF conversion failed", serverError);
                throw serverError;
            }
        },
        showImageUploadError(message) {
            this.imageUploadError = message || "This photo couldn't be processed. Please try a JPEG or PNG.";
        },
        clearImageUploadError() {
            this.imageUploadError = "";
        },
        multiImageButtonLabel(element) {
            const limit = Number(element['multi-image-limit']);
            let label = element['placeholder'] || '';
            if (limit === 1) {
                label = label.replace(/\bImages\b/, 'Image');
            }
            if (limit > 1) {
                label += ' <span>(' + this.multiUploadImagesArray.length + '/' + element['multi-image-limit'] + ')</span>';
            }
            return label;
        },
        async selectMultiImages(event, id, aspectRatio, limit, element) {
            const selectedFiles = Array.from(event.target.files);
            this.clearImageUploadError();
            this.curCropAspect = aspectRatio;
            this.curCroppingImageID = id;
            this.multiImageUploader = true;

            if (limit === 1 && this.multiUploadImagesArray.length > 0) {
                this.multiUploadImagesArray = [];
                if (element && element['editable_type'] === 'line_art_generator') {
                    this.lineArtGeneratedUrlRaw = null;
                    this.lineArtTintedUrl = null;
                }
            }

            const completedCount = this.multiUploadImagesArray.filter(img => !img.loading).length;
            const remainingSlots = limit - completedCount;

            // Only take files within the limit
            const filesToProcess = selectedFiles.slice(0, remainingSlots);

            let removeBackground = false;
            if (element && element['remove-background']) {
                removeBackground = true;
            }

            const aiImageGeneration = element && element['editable_type'] !== 'line_art_generator' && element['ai-image-generation']
                ? element['ai-image-generation']
                : null;

            // Process each image in sequence. Only add a placeholder after HEIC/HEIF conversion succeeds.
            for (let i = 0; i < filesToProcess.length; i++) {
                let file = filesToProcess[i];
                try {
                    file = await this.normalizeImageFileForPipeline(file);
                } catch (error) {
                    console.error("HEIC/HEIF conversion failed", error);
                    this.showImageUploadError("This photo couldn't be processed. Please try a JPEG or PNG.");
                    continue;
                }

                const placeholderIndex = this.multiUploadImagesArray.length;
                this.multiUploadImagesArray.push({
                    src: '',
                    original: '',
                    loading: true,
                    removeBackground: removeBackground,
                    aiImageGeneration: aiImageGeneration,
                });

                if (aiImageGeneration) {
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        this.multiUploadImagesArray[placeholderIndex].original = reader.result;
                        this.multiUploadImagesArray[placeholderIndex].src = reader.result;
                        this.multiUploadImagesArray[placeholderIndex].loading = true;
                        await this.runAiImageGeneration(placeholderIndex, file, aiImageGeneration, limit);
                    };
                    reader.readAsDataURL(file);
                    continue;
                }

                // Display image
                const reader = new FileReader();
                reader.onloadend = async () => {
                    this.multiUploadImagesArray[placeholderIndex].src = reader.result;
                    this.multiUploadImagesArray[placeholderIndex].original = reader.result;
                    this.multiUploadImagesArray[placeholderIndex].loading = false;

                    const doneCount = this.multiUploadImagesArray.filter(img => !img.loading).length;
                    if (doneCount === limit) {
                        if (element && element['editable_type'] === 'line_art_generator') {
                            this.userInput['line_art_generator'] = "Uploaded";
                        } else {
                            this.userInput['multi_image_selector'] = "Uploaded";
                        }
                    }

                    if (i === 0 && removeBackground) {
                        this.curCropImage = placeholderIndex;
                        this.curCroppingImageSRC = reader.result;
                        this.showCrop = true;
                        this.cropping = false;
                        this.pendingFirstRemoveBackgroundCrop = true;
                    } else if (element && element['editable_type'] === 'line_art_generator') {
                        this.maybeAutoGenerateLineArt();
                    } else {
                        this.fillCanvas();
                    }
                };

                reader.readAsDataURL(file);
            }
        },
        closeCropModal(){
            if (this.pendingFirstRemoveBackgroundCrop) {
                // Remove the uploaded image
                this.multiUploadImagesArray = [];
                this.pendingFirstRemoveBackgroundCrop = false;
                this.fillCanvas();
            }

            this.showCrop = false;
            this.cropping = false;
        },
        async selectImage(event, id, aspectRatio) {
            var file = event.target.files[0];
            this.clearImageUploadError();
            if (!file) {
                return;
            }

            try {
                file = await this.normalizeImageFileForPipeline(file);
            } catch (error) {
                console.error("HEIC/HEIF conversion failed", error);
                this.showImageUploadError("This photo couldn't be processed. Please try a JPEG or PNG.");
                return;
            }

            var reader = new FileReader();
            var baseString;
            reader.onloadend = () => {
                baseString = reader.result;
                this.curCropAspect = aspectRatio;
                this.curCroppingImageSRC = baseString;
                this.curCroppingImageID = id;
                this.showCropModal();
            };
            reader.readAsDataURL(file);
        },
        showCropModal(){
            this.cropError = "";
            this.cropSession = (this.cropSession || 0) + 1;
            this.showCrop = true;
            this.$nextTick(() => {
                const refresh = () => {
                    const cropper = this.$refs.cropper;
                    if (cropper && typeof cropper.refresh === "function") {
                        cropper.refresh();
                    }
                };
                refresh();
                setTimeout(refresh, 50);
            });
        },
        async cropImage() {
            this.cropError = "";
            let cropSucceeded = false;
            let canvas = null;
            try {
                const cropper = this.$refs.cropper;
                if (!cropper || typeof cropper.getResult !== "function") {
                    throw new Error("Cropper is not ready");
                }
                const result = cropper.getResult();
                canvas = result && result.canvas;
                if (!canvas) {
                    throw new Error("Cropper did not return an image");
                }
            } catch (error) {
                console.error("Crop failed:", error);
                this.cropError = "Couldn't crop this image. Please try again.";
                return;
            }

            this.cropping = true;
            try {
                const croppedDataURL = canvas.toDataURL();
                const blob = await new Promise((resolve, reject) => {
                    canvas.toBlob((result) => {
                        if (result) {
                            resolve(result);
                        } else {
                            reject(new Error("Could not create cropped image"));
                        }
                    }, "image/png");
                });

                if (this.multiImageUploader === true) {
                    const index = this.curCropImage;
                    const shouldRemoveBackground = this.multiUploadImagesArray[index]['removeBackground'];

                    this.pendingFirstRemoveBackgroundCrop = false;

                    try {
                        let finalImageURL = croppedDataURL;

                        if (shouldRemoveBackground) {
                            finalImageURL = await this.removeBackgroundFromBinary(blob);
                        }

                        this.multiUploadImagesArray[index]['src'] = finalImageURL;
                        this.multiUploadImagesArray[index]['original'] = this.multiUploadImagesArray[index]['original'];
                    } catch (error) {
                        console.error("Image processing after cropping failed:", error);
                        this.multiUploadImagesArray[index]['src'] = croppedDataURL;
                        this.multiUploadImagesArray[index]['original'] = this.multiUploadImagesArray[index]['original'];
                    }

                    this.showCrop = false;
                    cropSucceeded = true;

                    if (this.getLineArtGeneratorElement()) {
                        this.maybeAutoGenerateLineArt();
                    } else {
                        this.fillCanvas();
                    }
                } else {
                    let finalImage = croppedDataURL;

                    try {
                        this.userInput[this.curCroppingImageID] = finalImage;

                        if (this.curCroppingImageID === "post_url") {
                            this.userInput["profile_image"] = finalImage;
                        }
                    } catch (error) {
                        console.error("Background removal failed on single image:", error);
                        this.userInput[this.curCroppingImageID] = croppedDataURL;
                    }

                    this.fillCanvas();
                    this.showCrop = false;
                    cropSucceeded = true;
                }
            } catch (error) {
                console.error("Crop failed:", error);
                this.cropError = "Couldn't crop this image. Please try again.";
            } finally {
                this.cropping = false;
                if (!cropSucceeded && this.showCrop) {
                    this.cropping = false;
                }
            }
        },
        returnPercentageDiff(oldNumber, newNumber){
            var decreaseValue = oldNumber - newNumber;
            var tempPercentage = Math.round((decreaseValue / oldNumber) * 100);
            return Math.ceil(tempPercentage / 5) * 5;
        },
        setUpUserJson() {
            this.resetShowSectionDefaultsForCurrentStyle();
            this.requiredValidationActive = false;
            this.errorRequiredFields = [];
            this.userInput = {};
            this.auraVisualsTouched = false;
            this.requiredProductFields = [];
            this.selectedProductType["styles"][this.selectedProductTypeStyle]['elements'].forEach((element, key) => {
                var tempInitialVal = "";
                if (element['json_initial']) {
                    this.userInput[element['id']] = Array.isArray(element['json_initial'])
                        ? element['json_initial'].slice()
                        : element['json_initial'];
                } else {
                    if (element['editable_type'] === "toggleable") {
                        this.userInput[element['id']] = true;
                        tempInitialVal = true;
                    } else if (element['editable_type'] === "tag_input") {
                        this.userInput[element['id']] = [];
                        tempInitialVal = [];
                    } else {
                        this.userInput[element['id']] = "";
                        tempInitialVal = "";
                    }
                }
                if (element['required'] === true) {
                    this.requiredProductFields.push(element['id']);
                }
                if (element['editable_type'] === 'date_to_form') {
                    this.userInput['date_to'] = '';
                    this.showExtraDate = !!(this.userInput['date_to'] && String(this.userInput['date_to']).trim());
                }
            });
            this.ensurePhotoSourceState();

            //console.log(this.selectedProductTypeAndSize,"test");
            if (this.selectedProductTypeAndSize['upsell_addon']){
                //console.log("selectedProductTypeAndSize",this.selectedProductTypeAndSize["upsell_addon"]);
                if (this.queryArray && this.queryArray['update-design'] === 'true') {
                    const upsellOptions = this.selectedProductTypeAndSize['upsell_addon']['options'];
                    const blankIndex = upsellOptions.findIndex(function(o){ return o && o['type'] === 'blank'; });
                    this.userInput['upsell_addon'] = blankIndex !== -1 ? blankIndex : upsellOptions.length - 1;
                } else {
                    this.userInput['upsell_addon'] = this.selectedProductTypeAndSize["upsell_addon"]["default"];
                }
                //this.userInput['upsell_addon'] = 0;
                //this.userInput['upsell_addon'] = this.selectedProductTypeAndSize['upsell_addon']['option1'];
            }else{
                this.userInput['upsell_addon'] = 0;
            }

            if (this.selectedProductType["has_country_map"] === true) {
                const defs = {
                    country_color: "#9fd4ea",
                    country_map_fill_style: "texture",
                    country_map_texture_id: "",
                    marker_x: 50,
                    marker_y: 50,
                    country: "",
                    design_type: "country_map",
                    state_code: "",
                    country_map_pin_visible: true,
                    country_map_geo_bounds: "",
                };
                for (const k in defs) {
                    if (k === "country") {
                        if (this.userInput[k] === undefined) {
                            this.userInput[k] = defs[k];
                        }
                        continue;
                    }
                    if (this.userInput[k] === "" || this.userInput[k] === undefined) {
                        this.userInput[k] = defs[k];
                    }
                }
                const cmCfg = this.findCountryMapSvgElement();
                if (this.userInput["show_mode"] === "" || this.userInput["show_mode"] === undefined) {
                    const dm =
                        cmCfg &&
                        cmCfg.default_show_mode != null &&
                        String(cmCfg.default_show_mode).trim() !== ""
                            ? String(cmCfg.default_show_mode).trim()
                            : "country";
                    const opts = cmCfg && cmCfg.show_mode_options;
                    this.userInput["show_mode"] =
                        opts && opts.length && opts.indexOf(dm) === -1 ? opts[0] : dm;
                }
                if (cmCfg && cmCfg.textures && cmCfg.textures.length) {
                    this.userInput["country_map_fill_style"] = "texture";
                } else {
                    if (
                        this.userInput["country_map_fill_style"] === "texture" ||
                        this.userInput["country_map_fill_style"] === "" ||
                        this.userInput["country_map_fill_style"] === undefined
                    ) {
                        this.userInput["country_map_fill_style"] = "solid";
                    }
                }
                if (
                    cmCfg &&
                    cmCfg.textures &&
                    cmCfg.textures.length &&
                    (this.userInput["country_map_texture_id"] === "" ||
                        this.userInput["country_map_texture_id"] === undefined)
                ) {
                    const dt =
                        cmCfg.default_texture_id != null && String(cmCfg.default_texture_id).trim() !== ""
                            ? String(cmCfg.default_texture_id).trim()
                            : cmCfg.textures[0].id;
                    const ok = cmCfg.textures.some(function (t) {
                        return t && String(t.id) === dt;
                    });
                    this.userInput["country_map_texture_id"] = ok ? dt : cmCfg.textures[0].id;
                }
                [
                    "country",
                    "country_color",
                    "country_map_fill_style",
                    "country_map_texture_id",
                    "country_map_geo_bounds",
                    "marker_x",
                    "marker_y",
                    "design_type",
                    "subtitle",
                    "country-map-image",
                    "country-map-content-w",
                    "country-map-content-h",
                    "show_mode",
                    "state_code",
                    "country_map_pin_visible",
                ].forEach(
                    function (fid) {
                        if (this.prefillableFields.indexOf(fid) === -1) {
                            this.prefillableFields.push(fid);
                        }
                    }.bind(this)
                );
            }
            this.ensurePlayingCardCustomFields();
        },
        applyCountryMapProductDefaults() {
            if (!this.selectedProductType || this.selectedProductType["has_country_map"] !== true) {
                return;
            }
            if (this.queryArray && this.queryArray["update-design"] === "true") {
                return;
            }
            this.selectedMapMarkerColor = DEFAULT_COUNTRY_MAP_MARKER_COLOR_INDEX;
            this.selectedMapMarker = DEFAULT_COUNTRY_MAP_MARKER_INDEX;
            const locJson = getMergedDefaultMapLocationFromStyleProduct(
                this.selectedProductType,
                this.selectedProductTypeStyle
            );
            this.curAutoAddressLocation =
                locJson && locJson.position ? locJson : getDefaultLosAngelesCountryMapLocation();
            this.locationSelectionKind = null;
            this.locationPlaceAnchor = null;
            this.usingDefaultLocation = true;
            this.defaultPreviewLocationLabel =
                (this.curAutoAddressLocation.address && this.curAutoAddressLocation.address.label) || "";
            this.mapboxMapSearchField = "";
        },

        /** First location search element (`map-mapbox`) in the active fork — drives which Vue search field to sync. */
        getMapLocationSearchElement() {
            const pt = this.selectedProductType;
            if (!pt || !pt.styles || !pt.styles[this.selectedProductTypeStyle]) {
                return null;
            }
            const els = pt.styles[this.selectedProductTypeStyle].elements;
            return els.find(function (e) {
                return e && e.editable_type === "map-mapbox";
            }) || null;
        },

        /**
         * Classic Map + Night Sky: optional JSON on product type / style fork.
         * @param {{ forkSwitch?: boolean }} options forkSwitch: only apply location if style defines `default_map_location`.
         */
        applyMapProductDefaults(options) {
            const opts = options || {};
            const forkSwitch = opts.forkSwitch === true;
            const pt = this.selectedProductType;
            if (!pt || pt["has_country_map"] === true || pt["has_map_field"] !== true) {
                return;
            }
            if (this.queryArray && this.queryArray["update-design"] === "true") {
                return;
            }
            this.selectedMapMarker = 0;
            const style = pt.styles && pt.styles[this.selectedProductTypeStyle] ? pt.styles[this.selectedProductTypeStyle] : null;
            const mapMarkersLen =
                this.mapMarkers && this.mapMarkers.length ? this.mapMarkers.length : 1;

            let markerIdx;
            if (style && style.default_map_marker_color_index !== undefined && style.default_map_marker_color_index !== null) {
                markerIdx = style.default_map_marker_color_index;
            } else if (pt.default_map_marker_color_index !== undefined && pt.default_map_marker_color_index !== null) {
                markerIdx = pt.default_map_marker_color_index;
            } else {
                markerIdx = undefined;
            }

            if (pt["has_map_image"] === true && markerIdx !== undefined && markerIdx !== null) {
                const n = Number(markerIdx);
                if (!isNaN(n)) {
                    const clamped = Math.max(0, Math.min(Math.floor(n), mapMarkersLen - 1));
                    this.selectedMapMarkerColor = clamped;
                }
            }

            const userHasSelectedLocation =
                this.usingDefaultLocation !== true &&
                this.curAutoAddressLocation != null &&
                this.curAutoAddressLocation.position != null;

            if (!userHasSelectedLocation) {
                let locationObj = null;
                if (forkSwitch) {
                    if (style && style.default_map_location != null) {
                        locationObj = style.default_map_location;
                    }
                } else {
                    if (style && style.default_map_location != null) {
                        locationObj = style.default_map_location;
                    } else if (pt.default_map_location != null) {
                        locationObj = pt.default_map_location;
                    }
                }

                if (locationObj && locationObj.position) {
                    this.curAutoAddressLocation = locationObj;
                    this.usingDefaultLocation = true;
                    this.defaultPreviewLocationLabel =
                        (locationObj.address && locationObj.address.label) || "";
                } else {
                    this.usingDefaultLocation = true;
                    this.defaultPreviewLocationLabel = "";
                }
                this.locationTitleManuallyEdited = false;
                this.locationSelectionKind = null;
                this.locationPlaceAnchor = null;
                this.mapboxMapSearchField = "";
            }
        },

        mapRequestUsesHalfSize() {
            return this.productSizeSelected === 3 || this.productSizeSelected === 5 || this.productSizeSelected === 6;
        },
        styleHasStaticMapImage() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            return elems.some((el) => el && (
                el.type === "map_image" ||
                el.type === "map_image_mask" ||
                el.type === "map_image_cutout"
            ));
        },
        updateSelectedProductSize() {
            const half = this.mapRequestUsesHalfSize();
            if (this.styleHasStaticMapImage() && half !== this.mapDrawnAtHalfSize) {
                this.fillInAddressNew();
            }
            this.scheduleCanvasRescale();
        },
        scheduleCanvasRescale() {
            requestAnimationFrame(() => {
                this.rescale_canvas_if_needed();
                setTimeout(() => {
                    this.rescale_canvas_if_needed();
                }, 50);
            });
        },
        updateRandonUpsell(){
            this.selectedProductTypeAndSizeUpsell = Math.floor(Math.random() * ((this.selectedProductTypeAndSize['upsell'].length-1) - 0 + 1) + 0);
        },
        updateUpsellItem(item,upsellOption){
            if(upsellOption['sold_out'] !== true){
                this.userInput['upsell_addon'] = item;
                this.fillCanvas();
            }

        },
        updateSelectedProductType() {
            this.showDate = true;
            this.showTransparent = true;
            this.applyProductDateFormatDefault();
            this.selectedFont = "";
            this.rescale_canvas_if_needed();
            this.reloadLineArtGenerationState();
            if (this.selectedProductType['type'] === "standard") {
                this.productSizeSelected = 0;
            } else {
                this.setUpUserJson();
                if (this.selectedProductType["has_country_map"] === true) {
                    this.applyCountryMapProductDefaults();
                    void this.fillInAddressNew();
                } else if (
                    this.selectedProductType["has_map_field"] === true &&
                    this.selectedProductType["has_country_map"] !== true
                ) {
                    this.applyMapProductDefaults({ forkSwitch: false });
                    if (this.gpsRouteMode() && this.gpsRoute) {
                        void this.applyGpsRoute(this.gpsRoute, { preview: this.gpsRouteIsPreview, keepText: true });
                    } else {
                        void this.fillInAddressNew();
                        void this.loadDefaultGpsRoute();
                    }
                } else {
                    this.fillCanvas();
                    if (this.getLineArtGeneratorElement()) {
                        void this.refreshLineArtTint();
                    }
                }
            }

            setTimeout(() => {
                this.rescale_canvas_if_needed();
            }, 500);

        },
        fillMissingStyleInitials() {
            const elements = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            if (!this.userInput) {
                this.userInput = {};
            }
            elements.forEach((element) => {
                if (!element || element["id"] == null || element["json_initial"] == null) {
                    return;
                }
                if (this.userInput[element["id"]] !== undefined) {
                    return;
                }
                this.userInput[element["id"]] = Array.isArray(element["json_initial"])
                    ? element["json_initial"].slice()
                    : element["json_initial"];
            });
        },
        getStyleElementById(fieldId) {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            return elems.find((el) => el && el.id === fieldId) || null;
        },
        clampLocationTitle(value) {
            const text = value == null ? "" : String(value);
            const element = this.getStyleElementById("location");
            const max = element && element["max_char"] != null ? Number(element["max_char"]) : 0;
            if (!max || text.length <= max) {
                return text;
            }
            return text.slice(0, max);
        },
        formatElementCanvasText(element, value) {
            let tempText = value == null ? "" : String(value);
            if (!element) {
                return tempText;
            }
            if (element['lowercase'] === true && element['lowercase'] !== undefined) {
                tempText = tempText.toLowerCase();
            }
            if (element['uppercase'] !== "" && element['uppercase'] !== undefined) {
                tempText = tempText.toUpperCase();
            }
            return tempText;
        },
        getCanvasTextFontFamily(element) {
            if (this.selectedFont) {
                return this.selectedFont;
            }
            return element && element['font-family'] ? element['font-family'] : "";
        },
        fitCanvasTextFontSize(element, text, designedSize) {
            const baseSize = designedSize != null ? designedSize : (element && element['font-size']);
            if (!element || element['fit_to_width'] !== true) {
                return baseSize;
            }
            const maxWidth = element['max_text_width'] != null ? element['max_text_width'] : 925;
            const minFontSize = element['min_font_size'] != null ? element['min_font_size'] : 48;
            const maxFontSize = element['max_font_size'] != null ? element['max_font_size'] : baseSize;
            const minFitChars = element['min_fit_chars'] != null ? element['min_fit_chars'] : 0;
            const canGrow = element['fit_grow'] === true;
            if (baseSize == null || maxWidth <= 0) {
                return baseSize;
            }
            const probeText = text == null ? "" : String(text);
            const probeOptions = {
                fontFamily: this.getCanvasTextFontFamily(element),
                fontWeight: element['font-weight'],
                fontStyle: element['font-style'] || 'normal',
                charSpacing: element['char-spacing'] || 0,
            };
            const scaleX = element['scaleX'] != null ? element['scaleX'] : 1;
            const measureWidth = (size) => {
                const probe = new fabric.Text(probeText, Object.assign({}, probeOptions, { fontSize: size }));
                return probe.width * scaleX;
            };
            const designedWidth = measureWidth(baseSize);
            if (designedWidth <= maxWidth) {
                if (!canGrow || probeText.length < minFitChars) {
                    return baseSize;
                }
                if (designedWidth <= 0) {
                    return baseSize;
                }
                let grown = Math.floor(baseSize * maxWidth / designedWidth);
                grown = Math.max(baseSize, Math.min(maxFontSize, grown));
                const grownWidth = measureWidth(grown);
                if (grownWidth < maxWidth && grown < maxFontSize) {
                    grown = Math.min(maxFontSize, grown + 1);
                }
                return grown;
            }
            let fitted = Math.floor(baseSize * maxWidth / designedWidth);
            fitted = Math.max(minFontSize, Math.min(baseSize, fitted));
            const fittedWidth = measureWidth(fitted);
            if (fittedWidth > maxWidth && fitted > minFontSize) {
                fitted = Math.floor(fitted * maxWidth / fittedWidth);
                fitted = Math.max(minFontSize, Math.min(baseSize, fitted));
            }
            return fitted;
        },
        resolveCanvasTextTop(element) {
            if (!element) {
                return 0;
            }
            const yPos = element['y-pos'];
            const originY = element['originY'] || 'top';
            if (originY === 'center' && element['y_pos_is_center'] !== true) {
                const designedSize = element['font-size'] || 0;
                return yPos + Math.round(designedSize / 2);
            }
            return yPos;
        },
        getLocationDateCanvasText(currentValue) {
            const locationRaw = this.userInput && this.userInput["location"] != null
                ? String(this.userInput["location"])
                : "";
            const locationFallback = locationRaw.trim() !== "" ? locationRaw : "Gold Coast, Australia";
            const dateRaw = this.userInput && this.userInput["date"] != null
                ? String(this.userInput["date"])
                : "";

            if (this.showDate === false) {
                if (currentValue === "" || currentValue == null) {
                    return locationFallback;
                }
                return locationRaw;
            }

            let value = dateRaw === "" ? locationRaw : locationRaw + " – " + dateRaw;
            if (value === "") {
                return locationFallback + " – 20/10/2019";
            }
            return value;
        },
        requestCanvasRender() {
            if (!this.canvasCreator) {
                return;
            }
            if (typeof this.canvasCreator.requestRenderAll === "function") {
                this.canvasCreator.requestRenderAll();
            } else {
                this.canvasCreator.renderAll();
            }
        },
        applyPlayingCardCustomRank(element, value) {
            if (!element) {
                return value;
            }
            const fieldId = element["value_from"] || element["id"];
            if (fieldId !== "card_value" && fieldId !== "back_card_value") {
                return value;
            }
            const stored = this.userInput ? this.userInput[fieldId] : "";
            if (stored !== "custom") {
                return value;
            }
            return playingCardCustomLetter(this.userInput, fieldId);
        },
        updatePlayingCardCustomLetter(fieldId, event) {
            const raw = event && event.target ? event.target.value : "";
            const letter = String(raw).slice(0, 1).toUpperCase();
            if (event && event.target && event.target.value !== letter) {
                event.target.value = letter;
            }
            const customId = playingCardCustomFieldId(fieldId);
            this.userInput[customId] = letter;
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        ensurePlayingCardCustomFields() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            if (!this.userInput) {
                this.userInput = {};
            }
            elems.forEach((el) => {
                if (!el || el["editable_type"] !== "option_selector" || !Array.isArray(el["options"])) {
                    return;
                }
                const hasCustom = el["options"].some((opt) => opt && opt["title"] === "custom");
                if (!hasCustom) {
                    return;
                }
                const customId = playingCardCustomFieldId(el["id"]);
                if (this.userInput[customId] === undefined) {
                    this.userInput[customId] = "";
                }
                if (this.prefillableFields.indexOf(customId) === -1) {
                    this.prefillableFields.push(customId);
                }
            });
        },
        resolveCanvasElementValue(element) {
            if (!element) {
                return "";
            }
            const isToggleable = element["editable_type"] === "toggleable";
            let value;
            if (isToggleable) {
                value = element["initial"];
            } else if (this.userInput[element["id"]] === "" || this.userInput[element["id"]] === undefined) {
                value = element["initial"];
            } else {
                value = this.userInput[element["id"]];
            }

            if (
                element["id"] === "location" &&
                this.selectedProductType &&
                this.selectedProductType["has_map_field"] === true &&
                (this.userInput["location"] === "" || this.userInput["location"] === undefined) &&
                this.defaultPreviewLocationLabel
            ) {
                value = this.defaultPreviewLocationLabel;
            }

            if (element["suit_pip"] === true) {
                const suitField = element["suit_from"] || "suit";
                value = playingCardSuitUrl(this.userInput && this.userInput[suitField]);
            }
            if (element["value_from"]) {
                const source = this.userInput ? this.userInput[element["value_from"]] : "";
                value = (source === "" || source === undefined || source === null)
                    ? (element["initial"] || "")
                    : source;
            }
            value = this.applyPlayingCardCustomRank(element, value);

            if (element["id"] === "location-date") {
                value = this.getLocationDateCanvasText(value);
            }

            if (element["id"] === "date_from") {
                const fromRaw = String(this.userInput["date_from"] || "").trim();
                const toRaw = this.showExtraDate ? String(this.userInput["date_to"] || "").trim() : "";
                const fromPart = fromRaw || element["initial"] || "";
                value = toRaw ? fromPart + " – " + toRaw : fromPart;
            }

            if (this.selectedProductType && this.selectedProductType["has_country_map"] === true && element["id"] === "date") {
                const dateRaw = this.userInput && this.userInput["date"] != null ? String(this.userInput["date"]).trim() : "";
                const momentRaw = this.userInput && this.userInput["moment"] != null ? String(this.userInput["moment"]).trim() : "";
                const datePart = this.showDate === true
                    ? (dateRaw !== "" ? dateRaw : element["initial"] || "")
                    : "";
                const momentPart = this.showMoment === true && momentRaw !== "" ? momentRaw : "";
                if (datePart && momentPart) {
                    value = datePart + " – " + momentPart;
                } else if (datePart) {
                    value = datePart;
                } else if (momentPart) {
                    value = momentPart;
                } else {
                    value = "";
                }
            }

            return value;
        },
        composeCanvasTextSpec(element) {
            const value = this.resolveCanvasElementValue(element);
            let tempText = value == null ? "" : value;

            if (element["Limit_decimal"] === true || typeof element["Limit_decimal"] === "number") {
                const maxDp = typeof element["Limit_decimal"] === "number" ? element["Limit_decimal"] : 5;
                tempText = limitDecimalPlacesInString(tempText, maxDp);
            }
            if (element["append_text"] !== "" && element["append_text"] !== undefined) {
                tempText = tempText + element["append_text"];
            }
            tempText = this.formatElementCanvasText(element, tempText);
            if (element["append-time"] !== "" && element["append-time"] !== undefined) {
                if (element["append-time"] === true && this.timeSelector.showTime) {
                    tempText = tempText + " - " + this.timeSelector.hour + ":" + this.timeSelector.minute + " " + this.timeSelector.period;
                }
            }
            if (element["text_type"] === "number") {
                tempText = tempText.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }

            let fontSize = element["font-size"];
            let xPos = element["x-pos"];
            let yPos = element["y-pos"];
            if (element["char_count_styles"] !== undefined) {
                const charCountStyle = element["char_count_styles"][String(tempText).length];
                if (charCountStyle) {
                    fontSize = charCountStyle["font-size"];
                    xPos = charCountStyle["x-pos"];
                    yPos = charCountStyle["y-pos"];
                }
            }
            const designedFontSize = element["font-size"];
            fontSize = this.fitCanvasTextFontSize(element, tempText, fontSize);
            if (element["center_on_fit"] === true && designedFontSize != null) {
                yPos += Math.round((designedFontSize - fontSize) / 2);
            }
            yPos = this.resolveCanvasTextTop(Object.assign({}, element, {
                "y-pos": yPos,
                "font-size": designedFontSize,
            }));

            let textColor = element["text-color"];
            if (this.userInput["white_text_toggle"] === true) {
                textColor = "#ffffff";
            }
            if (element["color_from"]) {
                textColor = playingCardColorHex(this.userInput && this.userInput[element["color_from"]]);
            }
            if (
                element["id"] === "subtitle" &&
                this.selectedProductType &&
                this.selectedProductType["has_country_map"] === true &&
                this.userInput["country_color"]
            ) {
                textColor = this.userInput["country_color"];
            }

            if (element["editable_type"] === "date" || element["id"] === "date") {
                if (this.showDate === false && this.selectedProductType && this.selectedProductType["has_country_map"] === true) {
                    // Country map already composed the date/moment line above.
                } else if (this.showDate === false) {
                    tempText = "";
                }
            }
            if (element["id"] == "long-lat" && this.showLongLat === false) {
                tempText = "";
            }

            const fontFamily = element["lock_font"] === true
                ? element["font-family"]
                : (this.selectedFont != "" ? this.selectedFont : element["font-family"]);
            return {
                text: tempText,
                fontFamily: fontFamily,
                fontWeight: element["font-weight"],
                fontStyle: element["font-style"] || "normal",
                fontSize: fontSize,
                left: xPos,
                top: yPos,
                textAlign: element["text-align"],
                originX: element["text-align"],
                originY: element["originY"] || "top",
                fill: textColor,
                skewY: element["skewY"] !== undefined ? element["skewY"] : 0,
                skewX: element["skewX"] !== undefined ? element["skewX"] : 0,
                scaleX: element["scaleX"] !== undefined ? element["scaleX"] : 1,
                angle: element["angle"] !== undefined ? element["angle"] : 0,
                lineHeight: element["line-height"] != null ? element["line-height"] : undefined,
                charSpacing: element["char-spacing"] !== "" && element["char-spacing"] !== undefined ? element["char-spacing"] : 0,
            };
        },
        getKeywordRowElement() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            return elems.find((el) => el && el.type === "keyword_row") || null;
        },
        composeKeywordRowSpec(element) {
            if (!element) {
                return null;
            }
            if (element["display"] !== "block" && element["display"] !== undefined) {
                return null;
            }
            const words = this.getAuraKeywords().filter(function (word) {
                return String(word || "").trim() !== "";
            });
            if (!words.length) {
                return null;
            }
            const line = words.map((word) => this.formatElementCanvasText(element, String(word).trim())).join("  \u00b7  ");
            const rowRight = element["x-end"] != null ? element["x-end"] : 896;
            const rowTop = element["y-pos"] != null ? element["y-pos"] : 1258;
            const titleElement = this.getStyleElementById("title");
            let maxWidth = rowRight;
            if (titleElement && titleElement.type === "text") {
                const titleSpec = this.composeCanvasTextSpec(titleElement);
                const titleProbe = new fabric.Text(titleSpec.text, {
                    fontFamily: titleSpec.fontFamily,
                    fontWeight: titleSpec.fontWeight,
                    fontStyle: titleSpec.fontStyle,
                    fontSize: titleSpec.fontSize,
                });
                const titleLeft = titleElement["x-pos"] != null ? titleElement["x-pos"] : 0;
                maxWidth = Math.max(40, rowRight - (titleLeft + (titleProbe.width || 0)) - 24);
            }
            const fontSize = this.fitCanvasTextFontSize(Object.assign({}, element, {
                fit_to_width: true,
                max_text_width: maxWidth,
            }), line, element["font-size"]);
            return {
                text: line,
                fontFamily: this.getCanvasTextFontFamily(element) || element["font-family"],
                fontWeight: element["font-weight"],
                fontStyle: element["font-style"] || "normal",
                left: rowRight,
                top: rowTop,
                fontSize: fontSize,
                textAlign: "right",
                originX: "right",
                originY: element["originY"] || "top",
                fill: element["text-color"] || "#111111",
                id: element.id,
            };
        },
        findCanvasObject(fieldId, types) {
            if (!this.canvasCreator || !fieldId) {
                return null;
            }
            return this.canvasCreator.getObjects().find((obj) => {
                if (!obj || obj.canvasElementId !== fieldId) {
                    return false;
                }
                if (!types) {
                    return true;
                }
                return types.indexOf(obj.type) !== -1;
            }) || null;
        },
        applyTextSpec(obj, spec) {
            obj.set({
                text: spec.text,
                fontFamily: spec.fontFamily,
                fontWeight: spec.fontWeight,
                fontStyle: spec.fontStyle,
                fontSize: spec.fontSize,
                fill: spec.fill,
                angle: spec.angle || 0,
                lineHeight: spec.lineHeight != null ? spec.lineHeight : obj.lineHeight,
            });
            if (typeof obj.initDimensions === "function") {
                obj.initDimensions();
            }
            obj.set({
                left: spec.left,
                top: spec.top,
                originX: spec.originX,
                originY: spec.originY,
            });
            if (typeof obj.setCoords === "function") {
                obj.setCoords();
            }
            obj.dirty = true;
        },
        phoneticCanvasLayout(element) {
            const value = this.resolveCanvasElementValue(element);
            const letterPlace = this.userInput ? this.userInput["word-class"] : "";
            const tempText = letterPlace === "" ? value : "[" + value + "] " + letterPlace;
            let fontSize = element["font-size"];
            let left = element["x-pos"];
            let top = element["y-pos"];
            if (element["char_count_styles"] !== undefined && tempText.length > 0) {
                const charCountStyle = element["char_count_styles"][tempText.length];
                if (charCountStyle) {
                    fontSize = charCountStyle["font-size"];
                    left = charCountStyle["x-pos"];
                    top = charCountStyle["y-pos"];
                }
            }
            return {
                text: tempText,
                fontFamily: this.selectedFont ? this.selectedFont : element["font-family"],
                fontSize: fontSize,
                left: left,
                top: top,
            };
        },
        patchPhoneticSpelling(element, options) {
            if (!element || element.display !== "block") {
                return true;
            }
            const layout = this.phoneticCanvasLayout(element);
            const obj = this.findCanvasObject(element.id, ["text", "i-text", "textbox"]);
            if (!obj) {
                return false;
            }
            obj.set({
                text: layout.text,
                fontFamily: layout.fontFamily,
                fontSize: layout.fontSize,
                left: layout.left,
                top: layout.top,
            });
            if (typeof obj.initDimensions === "function") {
                obj.initDimensions();
            }
            if (typeof obj.setCoords === "function") {
                obj.setCoords();
            }
            obj.dirty = true;
            if (!options || options.render !== false) {
                this.requestCanvasRender();
            }
            return true;
        },
        patchTextBox2(element, options) {
            if (!element || element.display !== "block") {
                return true;
            }
            const value = this.resolveCanvasElementValue(element);
            const obj = this.findCanvasObject(element.id, ["text", "i-text", "textbox"]);
            if (!obj) {
                return false;
            }
            obj.set({
                text: value == null ? "" : String(value),
                fontFamily: this.selectedFont ? this.selectedFont : element["font-family"],
            });
            if (typeof obj.initDimensions === "function") {
                obj.initDimensions();
            }
            if (typeof obj.setCoords === "function") {
                obj.setCoords();
            }
            obj.dirty = true;
            if (!options || options.render !== false) {
                this.requestCanvasRender();
            }
            return true;
        },
        patchCanvasText(fieldId, options) {
            if (!fieldId || !this.canvasCreator) {
                return false;
            }
            const element = this.getStyleElementById(fieldId);
            if (!element) {
                return false;
            }
            if (element.type === "text_box2") {
                return this.patchTextBox2(element, options);
            }
            if (element.type === "phonetic-spelling-word-class") {
                return this.patchPhoneticSpelling(element, options);
            }
            if (element.type !== "text") {
                return false;
            }
            if (element.display !== "block") {
                return true;
            }
            const spec = this.composeCanvasTextSpec(element);
            const obj = this.findCanvasObject(fieldId, ["text", "i-text", "textbox"]);
            if (!obj) {
                return false;
            }
            this.applyTextSpec(obj, spec);
            if (!options || options.render !== false) {
                this.requestCanvasRender();
            }
            return true;
        },
        patchKeywordRow(options) {
            const element = this.getKeywordRowElement();
            if (!element || !this.canvasCreator) {
                return true;
            }
            const spec = this.composeKeywordRowSpec(element);
            const existing = this.findCanvasObject(element.id, ["text", "i-text", "textbox"]);
            if (!spec) {
                if (existing) {
                    this.canvasCreator.remove(existing);
                }
                if (!options || options.render !== false) {
                    this.requestCanvasRender();
                }
                return true;
            }
            if (!existing) {
                this.canvasCreator.add(new fabric.Text(spec.text, {
                    fontFamily: spec.fontFamily,
                    fontWeight: spec.fontWeight,
                    fontStyle: spec.fontStyle,
                    left: spec.left,
                    top: spec.top,
                    fontSize: spec.fontSize,
                    textAlign: spec.textAlign,
                    originX: spec.originX,
                    originY: spec.originY,
                    fill: spec.fill,
                    selectable: false,
                    canvasElementId: element.id,
                }));
            } else {
                this.applyTextSpec(existing, spec);
            }
            if (!options || options.render !== false) {
                this.requestCanvasRender();
            }
            return true;
        },
        patchTextField(fieldId) {
            this.clearFillCanvasDebounceTimer();
            if (!this.patchCanvasText(fieldId, { render: false })) {
                return false;
            }
            if ((fieldId === "location" || fieldId === "date") && this.getStyleElementById("location-date")) {
                if (!this.patchCanvasText("location-date", { render: false })) {
                    return false;
                }
            }
            if (fieldId === "title" && this.getKeywordRowElement()) {
                if (!this.patchKeywordRow({ render: false })) {
                    return false;
                }
            }
            if (fieldId === "word-class") {
                const phonetic = this.getStyleElementById("phonetic-spelling");
                if (phonetic && !this.patchPhoneticSpelling(phonetic, { render: false })) {
                    return false;
                }
            }
            this.requestCanvasRender();
            return true;
        },
        refreshCanvasTextObjects() {
            if (!this.canvasCreator) {
                return false;
            }
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            let missing = false;
            elems.forEach((el) => {
                if (!el) {
                    return;
                }
                if (el.type === "phonetic-spelling-word-class") {
                    if (!this.patchPhoneticSpelling(el, { render: false })) {
                        missing = true;
                    }
                    return;
                }
                if (el.type !== "text" && el.type !== "text_box2") {
                    return;
                }
                if (!this.patchCanvasText(el.id, { render: false })) {
                    missing = true;
                }
            });
            if (this.getKeywordRowElement() && !this.patchKeywordRow({ render: false })) {
                missing = true;
            }
            if (!missing) {
                this.requestCanvasRender();
            }
            return !missing;
        },
        applyIconColor() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            const element = elems.find((el) => el && el.type === "icon");
            if (!element) {
                return true;
            }
            if (!this.canvasCreator) {
                return false;
            }
            const icon = this.canvasCreator.getObjects().find((obj) => obj && obj.customType === "icon");
            if (!icon) {
                return false;
            }
            const bgEnabled = !!element["background_color_picker_enabled"];
            const selectedColor = this.userInput["icon_color"] || element["default_color"] || "#000000";
            const iconColor = bgEnabled
                ? (selectedColor === "#f9f9f9" ? "#000" : "#ffffff")
                : selectedColor;
            const paint = (obj, color) => {
                if (!obj) {
                    return;
                }
                if (obj._objects) {
                    obj._objects.forEach((child) => paint(child, color));
                }
                if (obj.fill && obj.fill !== "none") {
                    obj.set("fill", color);
                }
                if (obj.stroke && obj.stroke !== "none") {
                    obj.set({ stroke: color, strokeUniform: true });
                }
                obj.dirty = true;
            };
            if (bgEnabled && icon._objects && icon._objects.length) {
                icon._objects[0].set({ fill: selectedColor, dirty: true });
                for (let i = 1; i < icon._objects.length; i++) {
                    paint(icon._objects[i], iconColor);
                }
            } else {
                paint(icon, iconColor);
            }
            icon.dirty = true;
            icon.objectCaching = false;
            this.requestCanvasRender();
            return true;
        },
        setLayerVisible(fieldId, visible) {
            const obj = this.findCanvasObject(fieldId);
            if (!obj) {
                return false;
            }
            obj.set({ visible: visible !== false });
            obj.dirty = true;
            this.requestCanvasRender();
            return true;
        },
        applyPageBackground() {
            if (!this.canvasCreator) {
                return false;
            }
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            const element = elems.find((el) => el && el.page_background === true);
            if (!element) {
                return true;
            }
            const existing = this.findCanvasObject(element.id);
            if (this.showTransparent === true) {
                if (existing) {
                    this.canvasCreator.remove(existing);
                    this.requestCanvasRender();
                }
                return true;
            }
            if (existing) {
                existing.set({ visible: true, dirty: true });
                this.requestCanvasRender();
                return true;
            }
            const rectObj = new fabric.Rect({
                left: element["x-pos"],
                top: element["y-pos"],
                width: element["width"],
                height: element["height"],
                fill: element["fill"] || "#000000",
                originX: element["originX"] || "left",
                originY: element["originY"] || "top",
                selectable: false,
                evented: false,
                canvasElementId: element.id,
            });
            this.canvasCreator.add(rectObj);
            this.canvasCreator.sendToBack(rectObj);
            this.requestCanvasRender();
            return true;
        },
        setShowTransparent(isTransparent) {
            this.showTransparent = isTransparent === true;
            if (!this.applyPageBackground()) {
                this.fillCanvas();
            }
        },
        setAngelHalo(checked) {
            this.userInput["angel_halo"] = checked;
            if (!this.setLayerVisible("angel_halo", checked)) {
                this.fillCanvas();
            }
        },
        setToggleableVisible(fieldId, checked) {
            this.userInput[fieldId] = checked;
            if (!this.setLayerVisible(fieldId, checked)) {
                this.fillCanvas();
            }
        },
        clearTextareaCanvasSyncTimer() {
            if (this.textareaCanvasSyncTimer != null) {
                clearTimeout(this.textareaCanvasSyncTimer);
                this.textareaCanvasSyncTimer = null;
            }
        },
        updateCanvasDetailsFromTextarea(event, fieldId) {
            const input = event && event.target ? event.target : null;
            if (input && typeof input.value === "string" && fieldId) {
                this.userInput[fieldId] = input.value;
            }
            this.updateCanvasDetails(input ? input.value : undefined, fieldId);
            this.clearTextareaCanvasSyncTimer();
            this.textareaCanvasSyncTimer = setTimeout(() => {
                this.textareaCanvasSyncTimer = null;
                const live = input && input.isConnected ? input : document.querySelector(".text-area-main");
                if (!live || typeof live.value !== "string" || !fieldId) {
                    return;
                }
                if (this.userInput[fieldId] === live.value) {
                    return;
                }
                this.userInput[fieldId] = live.value;
                this.updateCanvasDetails(live.value, fieldId);
            }, 350);
        },
        updateCanvasDetails(value, fieldId) {
            this.curSelectedSearchValue = value;
            const id = fieldId || this.curSelectedSearch;
            if (id === "location") {
                this.locationTitleManuallyEdited = true;
                this.locationEditSeq = (this.locationEditSeq || 0) + 1;
            }
            if (!this.patchTextField(id)) {
                this.fillCanvas();
            }
        },
        updateIconColor(value){
            this.userInput['icon_color'] = value;
            if (!this.applyIconColor()) {
                this.fillCanvas();
            }
        },
        updateIcon(key){
            this.userInput['icon_choice'] = key;
            this.fillCanvas();
        },
        updateWhiteText(){
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        updateMapColor() {
            this.fillInAddressNew();
        },
        async onCountryMapShowModeChange() {
            logCountryMapDebug("onCountryMapShowModeChange", {
                show_mode: this.userInput["show_mode"],
                state_code: this.userInput["state_code"],
                country: this.userInput["country"],
            });
            await this.refreshCountryMapRaster();
            const lat = parseFloat(this.globalLat);
            const lng = parseFloat(this.globalLon);
            const slug = this.getCountryMapUserIsoKey();
            if (!isNaN(lat) && !isNaN(lng) && slug) {
                const pct = this.latLngToCountryMarkerPercent(lat, lng, slug);
                this.userInput["marker_x"] = pct.x;
                this.userInput["marker_y"] = pct.y;
            }
            this.fillCanvas();
        },
        normalizeCountryMapHexForCompare(hex) {
            if (hex == null || String(hex).trim() === "") {
                return "";
            }
            let s = String(hex).trim().replace(/^#/i, "").toLowerCase();
            if (/^[0-9a-f]{3}$/.test(s)) {
                s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
            }
            return /^[0-9a-f]{6}$/.test(s) ? s : "";
        },
        countryMapColorPresetMatches(presetHex) {
            const a = this.normalizeCountryMapHexForCompare(this.userInput["country_color"]);
            const b = this.normalizeCountryMapHexForCompare(presetHex);
            return a !== "" && a === b;
        },
        async onCountryMapColorChange() {
            await this.refreshCountryMapRaster();
            this.fillCanvas();
        },
        async onCountryMapFillStyleChange() {
            await this.refreshCountryMapRaster();
            this.fillCanvas();
        },
        async onCountryMapTextureIdChange() {
            await this.refreshCountryMapRaster();
            this.fillCanvas();
        },
        updateOptionSelector(index,fieldID){
            const options = this.selectedProductType.styles[this.selectedProductTypeStyle].elements[index]["options"] || [];
            const option = options.find((item) => item && item["title"] === this.userInput[fieldID]);
            if (option && Array.isArray(option["values"])) {
                option["values"].forEach((values) => {
                    this.userInput[values["id"]] = Array.isArray(values["value"]) ? values["value"].slice() : values["value"];
                });
                if (fieldID === "angel_number" && !this.auraVisualsTouched) {
                    var preset = angelAuraConfig.presets[option["title"]];
                    if (preset && preset.recommended) {
                        this.userInput.aura_shape = preset.recommended.shape;
                        this.userInput.aura_palette = preset.recommended.palette;
                    }
                }
            }
            if (fieldID === "angel_number") {
                this.fillCanvas();
                return;
            }
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        updateOptionPicker(fieldId, value) {
            this.userInput[fieldId] = value;
            if (fieldId === "aura_shape" || fieldId === "aura_palette" || fieldId === "aura_panel") {
                this.auraVisualsTouched = true;
            }
            const picker = this.getStyleElementById(fieldId);
            if (picker && picker["sets_color"]) {
                this.userInput[picker["sets_color"]] = playingCardDefaultColor(value);
            }
            this.fillCanvas();
        },
        optionPickerSwatchStyle(option) {
            if (!option || option.swatch === "clear" || option.id === "clear") {
                return {};
            }
            if (option.colors && option.colors.length) {
                return { backgroundImage: "linear-gradient(135deg, " + option.colors.join(", ") + ")" };
            }
            if (option.color) {
                return { backgroundColor: option.color };
            }
            return {};
        },
        auraShapePreviewStyle() {
            const paletteId = this.userInput && this.userInput.aura_palette;
            const hex = auraPaletteHex(paletteId);
            const targetHue = this.hexColorToHueDegrees(hex);
            const angle = this.countryMapTextureHueRotateDegrees(targetHue, AURA_ARTWORK_BASE_HUE);
            return { filter: "hue-rotate(" + angle + "deg)" };
        },
        auraGradientPillStyle() {
            const paletteId = this.userInput && this.userInput.aura_palette;
            const hex = auraPaletteHex(paletteId);
            return {
                backgroundColor: hex,
                backgroundImage: "radial-gradient(circle at 38% 42%, #ffffff 0%, " + hex + " 58%)",
            };
        },
        getAuraKeywords() {
            if (!Array.isArray(this.userInput.keywords)) {
                this.userInput.keywords = [];
            }
            return this.userInput.keywords;
        },
        addAuraKeyword(fieldId) {
            const id = fieldId || "keywords";
            if (!Array.isArray(this.userInput[id])) {
                this.userInput[id] = [];
            }
            if (this.userInput[id].length >= 6) {
                return;
            }
            this.userInput[id].push("");
            if (!this.patchKeywordRow()) {
                this.fillCanvas();
            }
            this.$nextTick(() => {
                const inputs = this.$el && this.$el.querySelectorAll(".tag-input-pill input");
                if (inputs && inputs.length) {
                    inputs[inputs.length - 1].focus();
                }
            });
        },
        removeAuraKeyword(fieldId, tagIndex) {
            const id = fieldId || "keywords";
            if (!Array.isArray(this.userInput[id])) {
                return;
            }
            this.userInput[id].splice(tagIndex, 1);
            if (!this.patchKeywordRow()) {
                this.fillCanvas();
            }
        },
        updateAuraKeyword() {
            this.clearFillCanvasDebounceTimer();
            if (!this.patchKeywordRow()) {
                this.fillCanvas();
            }
        },
        currentAngelPreset() {
            const number = this.userInput && this.userInput.angel_number;
            return (number && angelAuraConfig.presets[number]) || null;
        },
        fieldSuggestionList(element) {
            const preset = this.currentAngelPreset();
            if (preset && element && element.id === "title" && Array.isArray(preset.titleSuggestions)) {
                return preset.titleSuggestions.slice();
            }
            if (element && Array.isArray(element.example_text)) {
                return element.example_text.slice();
            }
            return [];
        },
        openAuraWordSuggestions(fieldId, tagIndex) {
            const words = this.userInput && this.userInput[fieldId];
            const current = Array.isArray(words) && words[tagIndex] != null ? String(words[tagIndex]) : "";
            const preset = this.currentAngelPreset();
            const list = preset && Array.isArray(preset.wordSuggestions) ? preset.wordSuggestions.slice() : [];
            this.shuffleArray(list);
            this.exampleTextOptions = list;
            this.curSelectedSearch = fieldId + "-word";
            this.curSelectedSearchValue = current;
            this.auraWordSuggestionIndex = tagIndex;
        },
        applyAuraWordSuggestion(fieldId, text) {
            const index = this.auraWordSuggestionIndex;
            if (!Array.isArray(this.userInput[fieldId]) || index == null) {
                return;
            }
            this.userInput[fieldId].splice(index, 1, text);
            this.curSelectedSearchValue = text != null ? String(text) : "";
            this.curSelectedSearch = "";
            this.auraWordSuggestionIndex = null;
            if (!this.patchKeywordRow()) {
                this.fillCanvas();
            }
        },
        scrollToBottom() {
            const el = this.$refs.bottomOfApp;
            if (el) {
                el.scrollIntoView({behavior: 'smooth'});
            }
        },
        scrollToCanvas() {
            const el = this.$refs.canvasOuter;
            if (el) {
                el.scrollIntoView({behavior: 'smooth'});
            }
        },
        setupPreviewPillObserver() {
            const canvas = this.$refs.canvasOuter;
            const target = canvas && canvas.closest ? canvas.closest(".canvas-designer") : null;
            const el = target || canvas;
            if (!el || typeof IntersectionObserver === "undefined") {
                return;
            }
            this.teardownPreviewPillObserver();
            this.previewPillObserver = new IntersectionObserver((entries) => {
                const entry = entries[0];
                this.previewPillVisible = !(entry && entry.isIntersecting);
            }, { threshold: 0 });
            this.previewPillObserver.observe(el);
        },
        teardownPreviewPillObserver() {
            if (this.previewPillObserver) {
                this.previewPillObserver.disconnect();
                this.previewPillObserver = null;
            }
        },
        syncCartFromServer() {
            return refreshHeaderCartCount().then((cart) => {
                if (!cart) {
                    return;
                }
                const count = Number(cart.item_count);
                const itemCount = Number.isFinite(count) && count > 0 ? count : 0;
                this.cartItemCount = itemCount;
                this.cartEmpty = itemCount === 0;
            }).catch(() => {});
        },
        isStackedMobileLayout() {
            return window.matchMedia('(max-width: 800px)').matches;
        },
        onCanvasTapScrollToDetails() {
            if (!this.isStackedMobileLayout()) {
                return;
            }
            this.scrollToProductDetails();
        },
        scrollToProductDetails() {
            const el = this.$refs.productEditableSection;
            if (!el) {
                return;
            }

            const header = document.querySelector('#shopify-section-header, .header-mobile, header.header');
            let headerHeight = 0;
            if (header) {
                const headerRect = header.getBoundingClientRect();
                if (headerRect.bottom > 0 && headerRect.top < 80) {
                    headerHeight = Math.max(0, headerRect.bottom);
                }
            }
            const gap = 32;
            const top = el.getBoundingClientRect().top + window.pageYOffset - headerHeight - gap;

            window.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth'
            });
        },
        showOptimisticCartAdd() {
            if (typeof cartPopUp === "undefined" || typeof cartPopUp.showOptimisticCreateLines !== "function") return;
            cartPopUp.showOptimisticCreateLines();
        },
        removeOptimisticCartAdd() {
            if (typeof cartPopUp === "undefined" || typeof cartPopUp.removeOptimisticCreateLines !== "function") return;
            cartPopUp.removeOptimisticCreateLines();
        },
        cloneDesignValue(value) {
            if (value == null) {
                return value;
            }
            try {
                return JSON.parse(JSON.stringify(value));
            } catch (error) {
                return null;
            }
        },
        editingSameStyle() {
            if (!this.cartEditActive || this.cartEditProductType == null) {
                return false;
            }
            return this.cartEditProductType === this.productTypeSelected;
        },
        clearCartEdit() {
            this.cartEditActive = false;
            this.cartEditLineKey = "";
            this.cartEditDesignKey = "";
            this.cartEditProductType = null;
            this.cartEditStyle = null;
            this.cartEditMissing = false;
            try {
                const params = new URLSearchParams(window.location.search);
                params.delete("edit-cart");
                params.delete("design");
                const query = params.toString();
                window.history.replaceState(null, "", window.location.pathname + (query ? "?" + query : ""));
            } catch (error) {}
        },
        beginCartEdit(lineKey, designKey) {
            if (!lineKey || !designKey || this.cartEditRestoring) {
                return;
            }
            this.cartEditActive = true;
            this.cartEditMissing = false;
            this.cartEditLineKey = lineKey;
            this.cartEditDesignKey = designKey;
            this.cartEditRestoring = true;
            try {
                const params = new URLSearchParams(window.location.search);
                params.set("edit-cart", lineKey);
                params.set("design", designKey);
                window.history.replaceState(null, "", window.location.pathname + "?" + params.toString());
            } catch (error) {}
            this.scrollToCanvas();
            void this.restoreCartDesign();
        },
        readCartEditQuery() {
            try {
                const params = new URLSearchParams(window.location.search);
                const lineKey = params.get("edit-cart");
                const designKey = params.get("design");
                if (!lineKey || !designKey) {
                    return null;
                }
                return { lineKey: lineKey, designKey: designKey };
            } catch (error) {
                return null;
            }
        },
        buildCartDesignSnapshot() {
            const userInput = this.cloneDesignValue(this.userInput) || {};
            Object.keys(TT_DESIGN_GENERATED_INPUTS).forEach((key) => {
                delete userInput[key];
            });
            if (
                this.selectedProductType &&
                this.selectedProductType["has_country_map"] === true &&
                userInput["show_mode"] === "country"
            ) {
                userInput["state_code"] = "";
            }
            const photos = (this.multiUploadImagesArray || [])
                .filter((img) => img && (img.src || img.original))
                .map((img) => ({
                    src: img.src || "",
                    original: img.original || "",
                    removeBackground: img.removeBackground === true,
                    aiImageGeneration: img.aiImageGeneration ? this.cloneDesignValue(img.aiImageGeneration) : null,
                }));
            const skyDate = this.nightSkyDate instanceof Date && !isNaN(this.nightSkyDate.getTime())
                ? this.nightSkyDate.toISOString()
                : null;
            return {
                v: 1,
                productTypeSelected: this.productTypeSelected,
                productSizeSelected: this.productSizeSelected,
                selectedProductTypeStyle: this.selectedProductTypeStyle,
                userInput: userInput,
                multiUploadImagesArray: photos,
                selectedFont: this.selectedFont || "",
                curAutoAddressLocation: this.cloneDesignValue(this.curAutoAddressLocation),
                selectedMapMarker: this.selectedMapMarker,
                selectedMapMarkerColor: this.selectedMapMarkerColor,
                usingDefaultLocation: this.usingDefaultLocation,
                locationTitleManuallyEdited: this.locationTitleManuallyEdited,
                locationSelectionKind: this.locationSelectionKind,
                locationPlaceAnchor: this.cloneDesignValue(this.locationPlaceAnchor),
                mapboxMapSearchField: this.mapboxMapSearchField || "",
                routeEndLocation: this.cloneDesignValue(this.routeEndLocation),
                routeEndSearchField: this.routeEndSearchField || "",
                routeEndIsPreview: this.routeEndIsPreview === true,
                showRouteEndField: this.showRouteEndField,
                routeZoomOffset: this.routeZoomOffset,
                routeLoop: this.routeLoop,
                gpsRoute: this.cloneDesignValue(this.gpsRoute),
                gpsRouteIsPreview: this.gpsRouteIsPreview === true,
                routeStats: { ...this.routeStats },
                routeStatsEdited: { ...this.routeStatsEdited },
                selectedRouteColor: this.selectedRouteColor,
                distanceUnit: this.distanceUnit,
                nightSkyDate: skyDate,
                nightSkyGrid: this.nightSkyGrid,
                nightSkyMilkyWay: this.nightSkyMilkyWay,
                nightSkyConstellations: this.nightSkyConstellations,
                selectedSkyColor: this.selectedSkyColor || "#151726",
                timeSelector: this.cloneDesignValue(this.timeSelector),
                showDate: this.showDate,
                showTransparent: this.showTransparent,
                showMoment: this.showMoment,
                showLongLat: this.showLongLat,
                showExtraDate: this.showExtraDate,
                fullPrintEdgeToEdge: this.fullPrintEdgeToEdge,
                lineArtGeneratedUrlRaw: this.lineArtGeneratedUrlRaw || null,
                lineArtGenerationHistory: this.cloneDesignValue(this.lineArtGenerationHistory) || [],
                lineArtSelectedGenerationId: this.lineArtSelectedGenerationId,
                userDateFormat: this.userDateFormat || "",
                userSelectedDateFormat: this.userSelectedDateFormat || "",
                curFormattedDate: this.curFormattedDate || "",
            };
        },
        plaqueCartProperties(renderUrl, thumbUrl, renderId, isFallback, designKey) {
            const properties = {
                _render: renderUrl,
                _render_thumbnail: thumbUrl,
                _render_id: renderId,
                _product_canvas_bg: this.selectedProductTypeAndSize["atc-background-image-thumb"],
                _product_canvas_bg_mask: this.selectedProductTypeAndSize["atc-background-image-mask"],
                _fork: this.selectedProductType["styles"][this.selectedProductTypeStyle]["fork"],
            };
            if (isFallback) {
                properties._render_fallback = "true";
            }
            if (designKey) {
                properties._editor = "tt";
                properties._design_key = designKey;
                properties._edit_path = window.location.pathname;
            }
            const upsellList = this.selectedProductTypeAndSize && this.selectedProductTypeAndSize.upsell;
            const upsell = upsellList && upsellList[this.selectedProductTypeAndSizeUpsell];
            const upsellProduct = upsell && this.selectedProductType && this.selectedProductType.products
                ? this.selectedProductType.products[upsell.variant]
                : null;
            if (upsell && upsell.variant_id && upsellProduct) {
                properties._matching_upsell_variant_id = String(upsell.variant_id);
                properties._matching_upsell_message = upsell.message || "";
                properties._matching_upsell_bg = upsellProduct["atc-background-image-thumb"] || "";
                properties._matching_upsell_mask = upsellProduct["atc-background-image-mask"] || "";
            }
            if (this.routeActive() && this.gpsRouteMode()) {
                properties._route = this.gpsRoute.name || "GPS route";
                properties._route_id = this.gpsRoute.id;
                properties._route_source = this.gpsRoute.source || "";
                properties._route_distance = this.formatGpsRouteDistance(this.gpsRoute.distance_m);
            } else if (this.routeActive()) {
                const routeStart = this.curAutoAddressLocation.position;
                const routeEnd = this.routeEndLocation.position;
                properties._route = this.routeLocationLabel(this.curAutoAddressLocation) + " to " + this.routeLocationLabel(this.routeEndLocation);
                properties._route_coordinates = routeStart.lat + ", " + routeStart.lng + " to " + routeEnd.lat + ", " + routeEnd.lng;
                properties._route_loop = this.routeLoop ? "Yes" : "No";
            }
            if (this.getPhotoSourceElement()) {
                const cutoutSource = this.cutoutPhotoSource();
                if (cutoutSource === "sky") {
                    properties.Cutout = "Night Sky";
                } else if (cutoutSource === "moon") {
                    properties.Cutout = "Moon";
                }
            }
            properties._added_at = String(Date.now());
            return properties;
        },
        ornamentUpsellProperties() {
            const upsellList = this.selectedProductTypeAndSize && this.selectedProductTypeAndSize.upsell;
            const upsell = upsellList && upsellList[this.selectedProductTypeAndSizeUpsell];
            if (!upsell) {
                return null;
            }
            const product = this.selectedProductType.products[upsell.variant];
            const properties = {
                _render: this.curRenderedImage,
                _render_thumbnail: this.curRenderedImageThumb,
                _render_id: this.curRenderedImageID,
                _product_canvas_bg: product["atc-background-image-thumb"],
                _product_canvas_bg_mask: product["atc-background-image-mask"],
                _fork: this.selectedProductType.styles[this.selectedProductTypeStyle].fork,
            };
            const sourceKey = this.lastUpsellSourceDesignKey || this.cartEditDesignKey;
            if (sourceKey) {
                properties._upsell_source_design_key = sourceKey;
            }
            return properties;
        },
        chosenStandOption() {
            const addon = this.selectedProductTypeAndSize && this.selectedProductTypeAndSize.upsell_addon;
            if (!addon || !Array.isArray(addon.options)) {
                return null;
            }
            const option = addon.options[this.userInput.upsell_addon];
            if (!option || option.type !== "product" || !option.variant_id) {
                return null;
            }
            return option;
        },
        standCartProperties(designKey) {
            return {
                _atc_source: "upsell",
                _design_key: designKey,
            };
        },
        fetchCart() {
            return fetch("/cart.js", { headers: { Accept: "application/json" } }).then((response) => {
                if (!response.ok) {
                    throw new Error("Cart read failed: " + response.status);
                }
                return response.json();
            });
        },
        postCartJson(url, body) {
            return fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            }).then((response) => {
                if (!response.ok) {
                    throw new Error("Cart request failed: " + response.status);
                }
                return response.json();
            });
        },
        findCartPlaque(items) {
            const list = items || [];
            const byKey = list.find((item) => item && item.key === this.cartEditLineKey);
            if (byKey) {
                return byKey;
            }
            return list.find((item) => {
                const props = item && item.properties;
                return props && props._editor === "tt" && props._design_key === this.cartEditDesignKey;
            }) || null;
        },
        findLinkedStand(items, designKey) {
            return (items || []).find((item) => {
                const props = item && item.properties;
                return props && props._design_key === designKey && props._editor !== "tt";
            }) || null;
        },
        rememberEditedPlaque(cart) {
            const plaque = this.findCartPlaque(cart && cart.items ? cart.items : []);
            if (!plaque) {
                return;
            }
            this.cartEditLineKey = plaque.key;
            try {
                const params = new URLSearchParams(window.location.search);
                params.set("edit-cart", plaque.key);
                params.set("design", this.cartEditDesignKey);
                window.history.replaceState(null, "", window.location.pathname + "?" + params.toString());
            } catch (error) {}
        },
        async syncLinkedStand(designKey) {
            const cart = await this.fetchCart();
            const stand = this.findLinkedStand(cart.items || [], designKey);
            const option = this.chosenStandOption();
            if (!option) {
                if (stand) {
                    await this.postCartJson("/cart/change.js", { id: stand.key, quantity: 0 });
                }
                return;
            }
            const properties = this.standCartProperties(designKey);
            if (!stand) {
                await this.postCartJson("/cart/add.js", {
                    items: [{ id: option.variant_id, quantity: 1, properties: properties }],
                });
                return;
            }
            if (Number(stand.variant_id) === Number(option.variant_id)) {
                return;
            }
            await this.postCartJson("/cart/add.js", {
                items: [{
                    id: option.variant_id,
                    quantity: stand.quantity || 1,
                    properties: properties,
                }],
            });
            await this.postCartJson("/cart/change.js", { id: stand.key, quantity: 0 });
        },
        async syncUpsellRenders(designKey, oldRenderId, nextProperties) {
            if (!nextProperties || !nextProperties._render) {
                return;
            }
            const cart = await this.fetchCart();
            const oldId = oldRenderId != null && oldRenderId !== "" && String(oldRenderId) !== "fallback"
                ? String(oldRenderId)
                : "";
            const matches = (cart.items || []).filter((item) => {
                const props = item && item.properties;
                if (!props || props._editor === "tt") {
                    return false;
                }
                if (designKey && props._upsell_source_design_key === designKey) {
                    return true;
                }
                return !!(oldId && props._render_id != null && String(props._render_id) === oldId);
            });
            for (let i = 0; i < matches.length; i++) {
                const item = matches[i];
                const props = Object.assign({}, item.properties);
                props._render = nextProperties._render;
                props._render_thumbnail = nextProperties._render_thumbnail;
                props._render_id = nextProperties._render_id;
                if (designKey) {
                    props._upsell_source_design_key = designKey;
                }
                await this.postCartJson("/cart/change.js", {
                    id: item.key,
                    quantity: item.quantity || 1,
                    properties: props,
                });
            }
        },
        async saveEditedCartLines(properties) {
            const designKey = this.cartEditDesignKey;
            const cart = await this.fetchCart();
            const plaque = this.findCartPlaque(cart.items || []);
            const oldRenderId = plaque && plaque.properties ? plaque.properties._render_id : null;
            const variantId = this.selectedProductTypeAndSize.variant_id;
            if (plaque && plaque.properties && plaque.properties._line_id) {
                properties._line_id = plaque.properties._line_id;
            }
            if (plaque && plaque.properties && plaque.properties._added_at) {
                properties._added_at = String(plaque.properties._added_at);
            }
            if (!plaque) {
                const items = [];
                const option = this.chosenStandOption();
                if (option) {
                    items.push({
                        id: option.variant_id,
                        quantity: 1,
                        properties: this.standCartProperties(designKey),
                    });
                }
                items.push({
                    id: variantId,
                    quantity: this.quantity || 1,
                    properties: properties,
                });
                await this.postCartJson("/cart/add.js", { items: items });
            } else if (Number(plaque.variant_id) === Number(variantId)) {
                await this.postCartJson("/cart/change.js", {
                    id: plaque.key,
                    quantity: plaque.quantity || 1,
                    properties: properties,
                });
            } else {
                await this.postCartJson("/cart/add.js", {
                    items: [{
                        id: variantId,
                        quantity: plaque.quantity || 1,
                        properties: properties,
                    }],
                });
                await this.postCartJson("/cart/change.js", { id: plaque.key, quantity: 0 });
            }
            await this.syncLinkedStand(designKey);
            await this.syncUpsellRenders(designKey, oldRenderId, properties);
            this.rememberEditedPlaque(await this.fetchCart());
        },
        applyLinkedStandFromCart(cart) {
            const addon = this.selectedProductTypeAndSize && this.selectedProductTypeAndSize.upsell_addon;
            if (!addon || !Array.isArray(addon.options)) {
                return;
            }
            const stand = this.findLinkedStand(cart.items || [], this.cartEditDesignKey);
            if (!stand) {
                const blankIndex = addon.options.findIndex((option) => option && option.type === "blank");
                if (blankIndex !== -1) {
                    this.userInput.upsell_addon = blankIndex;
                }
                return;
            }
            const match = addon.options.findIndex((option) => option && Number(option.variant_id) === Number(stand.variant_id));
            if (match !== -1) {
                this.userInput.upsell_addon = match;
            }
        },
        bootBlankEditor() {
            this.setUpUserJson();
            if (this.selectedProductType && this.selectedProductType["has_country_map"] === true) {
                this.applyCountryMapProductDefaults();
                void this.fillInAddressNew();
            } else if (
                this.selectedProductType &&
                this.selectedProductType["has_map_field"] === true &&
                this.selectedProductType["has_country_map"] !== true
            ) {
                this.applyMapProductDefaults({ forkSwitch: false });
                void this.fillInAddressNew();
            }
            this.applyProductDateFormatDefault();
            this.reloadLineArtGenerationState();
            this.preloadCanvasFonts();
            if (this.canvasBuildVersion === 0) {
                if (this.getLineArtGeneratorElement()) {
                    if (!(this.lineArtGeneratedUrlRaw || this.lineArtGenerationHistory.length > 0)) {
                        void this.refreshLineArtTint();
                    }
                } else {
                    this.fillCanvas();
                }
            }
        },
        abandonCartEdit() {
            this.cartEditMissing = true;
            this.cartEditActive = false;
            this.cartEditLineKey = "";
            this.cartEditDesignKey = "";
            this.cartEditProductType = null;
            this.cartEditStyle = null;
            this.cartEditRestoring = false;
            this.atcAvailable = true;
            this.bootBlankEditor();
        },
        async drawRestoredDesign() {
            if (this.getLineArtGeneratorElement()) {
                await this.refreshLineArtTint();
                return;
            }
            const type = this.selectedProductType;
            if (type && (type["has_country_map"] === true || type["has_map_field"] === true)) {
                await this.fillInAddressNew();
                return;
            }
            if (this.wantsSky()) {
                this.updateNightSky();
                return;
            }
            if (this.wantsMoon()) {
                await this.updateMoonPhase();
                return;
            }
            this.fillCanvas();
        },
        async applyCartDesignRecord(record) {
            const types = this.productData && this.productData.types;
            const type = types && types[record.productTypeSelected];
            if (!type || !type.styles || !type.styles.length) {
                this.abandonCartEdit();
                return;
            }
            this.productTypeSelected = record.productTypeSelected;
            const products = type.products || [];
            let size = Number(record.productSizeSelected) || 0;
            if (type.type === "standard" || !products[size]) {
                size = 0;
            }
            this.productSizeSelected = size;
            let style = Number(record.selectedProductTypeStyle) || 0;
            if (!type.styles[style]) {
                style = 0;
            }
            this.selectedProductTypeStyle = style;
            this.cartEditProductType = this.productTypeSelected;
            this.cartEditStyle = this.selectedProductTypeStyle;
            this.setUpUserJson();
            const savedInput = record.userInput || {};
            Object.keys(savedInput).forEach((key) => {
                if (TT_DESIGN_GENERATED_INPUTS[key]) {
                    return;
                }
                this.userInput[key] = savedInput[key];
            });
            ["showDate", "showTransparent", "showMoment", "showLongLat", "showExtraDate", "fullPrintEdgeToEdge"].forEach((key) => {
                if (record[key] !== undefined) {
                    this[key] = record[key];
                }
            });
            if (this.fullPrintEdgeToEdge) {
                this.applyFullPrintEdgeLayout({ fill: false });
            }
            if ("curAutoAddressLocation" in record) {
                this.curAutoAddressLocation = record.curAutoAddressLocation;
            }
            if (record.selectedMapMarker != null) {
                this.selectedMapMarker = record.selectedMapMarker;
            }
            if (record.selectedMapMarkerColor != null) {
                this.selectedMapMarkerColor = record.selectedMapMarkerColor;
            }
            if (record.usingDefaultLocation !== undefined) {
                this.usingDefaultLocation = record.usingDefaultLocation === true;
            }
            if (record.locationTitleManuallyEdited !== undefined) {
                this.locationTitleManuallyEdited = record.locationTitleManuallyEdited === true;
            }
            if ("locationSelectionKind" in record) {
                this.locationSelectionKind = record.locationSelectionKind;
            }
            if ("locationPlaceAnchor" in record) {
                this.locationPlaceAnchor = record.locationPlaceAnchor;
            }
            if (record.mapboxMapSearchField != null) {
                this.mapboxMapSearchField = record.mapboxMapSearchField;
            }
            this.routeEndLocation = record.routeEndLocation || null;
            this.routeEndIsPreview = record.routeEndIsPreview === true && !!this.routeEndLocation;
            this.routeEndSearchField = record.routeEndSearchField || "";
            this.showRouteEndField = record.showRouteEndField === true || !!this.routeEndLocation;
            this.routeZoomOffset = Number(record.routeZoomOffset) || 0;
            this.routeLoop = record.routeLoop === true;
            this.gpsRoute = record.gpsRoute || null;
            this.gpsRouteIsPreview = record.gpsRouteIsPreview === true && !!this.gpsRoute;
            this.gpsRouteSearchField = this.gpsRouteIsPreview ? "" : ((this.gpsRoute && this.gpsRoute.name) || "");
            this.distanceUnit = record.distanceUnit === "mi" ? "mi" : "km";
            if (record.routeStats) {
                this.routeStats = { distance: "", time: "", pace: "", ...record.routeStats };
                this.routeStatsEdited = { distance: false, time: false, pace: false, ...(record.routeStatsEdited || {}) };
            } else if (this.gpsRoute) {
                this.fillRouteStats(this.gpsRoute);
            }
            if (record.selectedRouteColor) {
                this.selectedRouteColor = record.selectedRouteColor;
            }
            if (record.selectedSkyColor) {
                this.selectedSkyColor = record.selectedSkyColor;
                if (typeof nightSkyConfig !== "undefined" && nightSkyConfig.background) {
                    nightSkyConfig.background.fill = this.selectedSkyColor;
                }
            }
            if (record.nightSkyGrid !== undefined) {
                this.nightSkyGrid = record.nightSkyGrid === true;
            }
            if (record.nightSkyMilkyWay !== undefined) {
                this.nightSkyMilkyWay = record.nightSkyMilkyWay === true;
            }
            if (record.nightSkyConstellations !== undefined) {
                this.nightSkyConstellations = record.nightSkyConstellations === true;
            }
            if (record.nightSkyDate) {
                const parsedSkyDate = new Date(record.nightSkyDate);
                if (!isNaN(parsedSkyDate.getTime())) {
                    this.nightSkyDate = parsedSkyDate;
                }
            }
            if (record.timeSelector) {
                this.timeSelector = record.timeSelector;
            }
            if (record.userDateFormat) {
                this.userDateFormat = record.userDateFormat;
            }
            if (record.userSelectedDateFormat) {
                this.userSelectedDateFormat = record.userSelectedDateFormat;
            }
            if (record.curFormattedDate != null) {
                this.curFormattedDate = record.curFormattedDate;
            }
            const restoredDate = this.getDatePickerValue("date");
            if (restoredDate) {
                this.datePickerValue = restoredDate;
                this.dateFieldInstance = restoredDate;
            }
            this.selectedFont = record.selectedFont || "";
            this.multiUploadImagesArray = Array.isArray(record.multiUploadImagesArray)
                ? record.multiUploadImagesArray.slice()
                : [];
            this.multiImageUploader = this.multiUploadImagesArray.some((img) => img && (img.src || img.original));
            this.lineArtGeneratedUrlRaw = record.lineArtGeneratedUrlRaw || null;
            this.lineArtGenerationHistory = this.normalizeLineArtGenerationHistory(record.lineArtGenerationHistory || []);
            this.lineArtSelectedGenerationId = record.lineArtSelectedGenerationId || null;
            const lineArtElement = this.getLineArtGeneratorElement();
            this.loadLineArtGenerationCount(
                lineArtElement && lineArtElement["ai-image-generation"]
                    ? lineArtElement["ai-image-generation"]["generation-limit"]
                    : null
            );
            try {
                const cart = await this.fetchCart();
                this.applyLinkedStandFromCart(cart);
            } catch (error) {
                console.error("Could not read the cart stand:", error);
            }
            await this.$nextTick();
            this.rescale_canvas_if_needed();
            await this.preloadCanvasFonts();
            try {
                await this.drawRestoredDesign();
            } catch (error) {
                console.error("Could not draw the restored design:", error);
                this.fillCanvas();
            }
            this.cartEditRestoring = false;
            this.atcAvailable = true;
        },
        restoreCartDesign() {
            return ttGetDesign(this.cartEditDesignKey).then((record) => {
                if (!record) {
                    this.abandonCartEdit();
                    return;
                }
                return this.applyCartDesignRecord(record);
            }).catch((error) => {
                console.error("Could not restore cart design:", error);
                this.abandonCartEdit();
            });
        },
        async addCanvasItemToShopifyCart(renderUrl, thumbUrl, renderId, isFallback, attemptId, designSnapshot) {
            const cartStartedAt = performance.now();
            const loggedRenderId = isFallback ? null : renderId;
            this.curRenderedImage = renderUrl;
            this.curRenderedImageThumb = thumbUrl;
            this.curRenderedImageID = renderId;

            const editingSameStyle = this.cartEditActive && this.cartEditDesignKey && this.editingSameStyle();
            const designKey = editingSameStyle ? this.cartEditDesignKey : this.makeid(10);
            let designSaved = false;
            try {
                await ttPutDesign(designKey, designSnapshot || this.buildCartDesignSnapshot());
                designSaved = true;
            } catch (error) {
                console.error("Could not save design for cart edit:", error);
            }
            const lineDesignKey = editingSameStyle ? this.cartEditDesignKey : (designSaved ? designKey : null);
            this.lastUpsellSourceDesignKey = lineDesignKey || "";
            const mainProductProperties = this.plaqueCartProperties(renderUrl, thumbUrl, renderId, isFallback, lineDesignKey);

            if (editingSameStyle) {
                try {
                    await this.saveEditedCartLines(mainProductProperties);
                    ttLogRenderEvent(attemptId, "cart_added", { ms: performance.now() - cartStartedAt, detail: "edit" }, loggedRenderId);
                    this.uploadPercentage = 0;
                    this.atcInFlight = false;
                    this.atcAvailable = true;
                    this.syncCartFromServer();
                    if (typeof cartPopUp !== "undefined" && typeof cartPopUp.getCartThenOpen === "function") {
                        cartPopUp.getCartThenOpen(true);
                    } else {
                        console.error("cartPopUp is not initialized yet.");
                    }
                } catch (error) {
                    console.error("Error updating cart design:", error);
                    ttLogRenderEvent(attemptId, "cart_add_failed", { error: ttErrorText(error), detail: "edit" }, loggedRenderId);
                    this.uploadPercentage = 0;
                    this.atcInFlight = false;
                    this.atcAvailable = true;
                }
                return;
            }

            var shopifyItemsArray = [];

            var mainProductJson = {
                quantity: this.quantity,
                id: this.selectedProductTypeAndSize['variant_id'],
                properties: mainProductProperties
            }

            var standOption = this.chosenStandOption();
            if (standOption) {
                shopifyItemsArray.push({
                    quantity: 1,
                    id: standOption.variant_id,
                    properties: lineDesignKey ? this.standCartProperties(lineDesignKey) : { _atc_source: "upsell" }
                });
            }

            shopifyItemsArray.push(mainProductJson);

            var shopifyJson = {"items": shopifyItemsArray};

            this.showOptimisticCartAdd(shopifyItemsArray);

            return fetch("/cart/add.js", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(shopifyJson)
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Cart add failed: " + response.status);
                    }
                    return response.json();
                })
                .then(response => {
                    ttLogRenderEvent(attemptId, "cart_added", { ms: performance.now() - cartStartedAt, detail: isFallback ? "fallback" : "" }, loggedRenderId);
                    this.uploadPercentage = 0;
                    this.atcInFlight = false;
                    this.atcAvailable = true;
                    this.syncCartFromServer();
                    if (this.cartEditActive && !this.editingSameStyle()) {
                        this.clearCartEdit();
                    }

                    if (this.getLineArtGeneratorElement()) {
                        this.resetLineArtGenerationCount();
                    }

                    try {
                        if (typeof cartPopUp !== 'undefined' && typeof cartPopUp.getCartThenOpen === 'function') {
                            if(this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]){
                                var shopifyUpsellJson = {
                                    quantity: 1,
                                    id: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['variant_id'],
                                    properties: this.ornamentUpsellProperties()
                                }

                                var upsellDetails = {
                                    title: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['message'],
                                    price: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['price'][this.cartCurrency],
                                    save: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['compare'][this.cartCurrency]-this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['price'][this.cartCurrency],
                                    discount: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['discount'],
                                    compare: this.selectedProductTypeAndSize['upsell'][this.selectedProductTypeAndSizeUpsell]['compare'][this.cartCurrency]
                                };
                                setUpsell(shopifyUpsellJson,upsellDetails);
                            }else{
                                cartPopUp.getCartThenOpen(true);
                            }
                        } else {
                            console.error("cartPopUp is not initialized yet.");
                        }
                    } catch (cartOpenError) {
                        console.error("Error opening cart:", cartOpenError);
                    }

                }).catch(error => {
                    console.error("Error adding product to cart:", error);
                    ttLogRenderEvent(attemptId, "cart_add_failed", { error: ttErrorText(error) }, loggedRenderId);
                    this.removeOptimisticCartAdd();
                    this.atcInFlight = false;
                    this.atcAvailable = true;
            });
        },
        async reserveRender(renderMs, attemptId) {
            const startedAt = performance.now();
            let lastError = "unknown";
            let attempts = 0;

            while (attempts < TT_RENDER_RESERVE_ATTEMPTS) {
                attempts += 1;
                let retry = false;
                try {
                    const response = await ttFetchWithTimeout(TT_RENDER_API + "/renders", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Accept": "application/json" },
                        body: JSON.stringify({
                            content_type: "image/png",
                            user_id: this.userHash,
                            render_ms: Math.round(renderMs),
                            flow: String(this.productTypeSelected == null ? "" : this.productTypeSelected).slice(0, 48),
                        }),
                    }, TT_RENDER_RESERVE_TIMEOUT_MS);
                    if (!response.ok) {
                        lastError = "reserve status " + response.status;
                        retry = response.status === 429 || response.status >= 500;
                    } else {
                        const reservation = await response.json();
                        if (!reservation || !reservation.id || !reservation.upload_url || !reservation.file_url) {
                            ttLogRenderEvent(attemptId, "reserve_unavailable", { ms: performance.now() - startedAt });
                            return null;
                        }
                        ttLogRenderEvent(attemptId, "reserve_ok", { ms: performance.now() - startedAt }, reservation.id);
                        return reservation;
                    }
                } catch (error) {
                    lastError = ttErrorText(error);
                    retry = true;
                }

                if (!retry || attempts >= TT_RENDER_RESERVE_ATTEMPTS) {
                    break;
                }
                ttLogRenderEvent(attemptId, "reserve_retry", { error: lastError, detail: "attempt " + attempts });
                await ttDelay(800);
            }

            ttLogRenderEvent(attemptId, "reserve_failed", {
                ms: performance.now() - startedAt,
                error: lastError,
                detail: "attempts " + attempts,
            });
            return null;
        },
        legacyUploadRender(printed, config) {
            return axios.post(TT_RENDER_API + "/upload_image", {
                image: printed.image,
                user_id: this.userHash
            }, config || { headers: { "Content-Type": "application/json" }, timeout: TT_RENDER_UPLOAD_TIMEOUT_MS }).then((uploaded) => {
                const data = uploaded && uploaded.data;
                if (!data || !data.file_url) {
                    throw new Error("upload succeeded but no file URL was returned");
                }
                return data;
            });
        },
        /** Puts the print image on its reserved URL. Falls back to upload_image when direct upload is unavailable. */
        async uploadPrintedImage(printed) {
            const attemptId = this.makeid(16);
            const reservation = await this.reserveRender(0, attemptId);
            if (reservation) {
                const startedAt = performance.now();
                try {
                    const blob = await fetch(printed.image).then((response) => response.blob());
                    const response = await ttFetchWithTimeout(reservation.upload_url, {
                        method: "PUT",
                        headers: reservation.upload_headers || { "Content-Type": "image/png" },
                        body: blob,
                    }, TT_RENDER_UPLOAD_TIMEOUT_MS);
                    if (!response.ok) {
                        throw new Error("status " + response.status);
                    }
                    const status = await this.completeRender(reservation.id, performance.now() - startedAt, 1, attemptId);
                    if (status !== "failed") {
                        ttLogRenderEvent(attemptId, "upload_done", { ms: performance.now() - startedAt }, reservation.id);
                        return {
                            file_url: reservation.file_url,
                            thumbnail_file_url: reservation.thumbnail_file_url || reservation.file_url,
                            id: reservation.id,
                        };
                    }
                } catch (error) {
                    ttLogRenderEvent(attemptId, "upload_failed", { error: ttErrorText(error) }, reservation.id);
                }
            }

            ttLogRenderEvent(attemptId, "legacy_upload_started");
            return this.legacyUploadRender(printed);
        },
        completeRender(renderId, uploadMs, attempts, attemptId) {
            return ttFetchWithTimeout(TT_RENDER_API + "/renders/" + encodeURIComponent(renderId) + "/complete", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Accept": "application/json" },
                body: JSON.stringify({ upload_ms: Math.round(uploadMs), attempts: attempts }),
            }, 10000).then((response) => response.json().catch(() => ({})).then((data) => {
                const status = (data && data.status) || (response.ok ? "uploaded" : "unknown");
                ttLogRenderEvent(attemptId, response.ok ? "complete_ok" : "complete_failed", { detail: status, error: data && data.reason }, renderId);
                return status;
            })).catch((error) => {
                ttLogRenderEvent(attemptId, "complete_failed", { error: ttErrorText(error) }, renderId);
                return "unknown";
            });
        },
        /** PUTs the print image to its reserved URL; falls back to upload_image, then FALLBACK_DESIGN_URL, and patches the cart line. */
        async uploadReservedRender(reservation, printed, attemptId, cartAdded) {
            const renderId = reservation.id;
            const startedAt = performance.now();
            let attempts = 0;
            let lastError = "";
            let uploaded = false;

            ttLogRenderEvent(attemptId, "upload_started", null, renderId);
            try {
                const blob = await fetch(printed.image).then((response) => response.blob());
                while (!uploaded && attempts < TT_RENDER_UPLOAD_ATTEMPTS) {
                    attempts += 1;
                    try {
                        const response = await ttFetchWithTimeout(reservation.upload_url, {
                            method: "PUT",
                            headers: reservation.upload_headers || { "Content-Type": "image/png" },
                            body: blob,
                        }, TT_RENDER_UPLOAD_TIMEOUT_MS);
                        uploaded = response.ok;
                        if (!uploaded) lastError = "status " + response.status;
                    } catch (error) {
                        lastError = ttErrorText(error);
                    }
                    if (!uploaded && attempts < TT_RENDER_UPLOAD_ATTEMPTS) {
                        ttLogRenderEvent(attemptId, "upload_retry", { error: lastError, detail: "attempt " + attempts }, renderId);
                        await ttDelay(attempts * 1500);
                    }
                }
            } catch (error) {
                lastError = ttErrorText(error);
            }

            const uploadMs = performance.now() - startedAt;
            if (uploaded) {
                ttLogRenderEvent(attemptId, "upload_done", { ms: uploadMs, detail: "attempts " + attempts }, renderId);
                const status = await this.completeRender(renderId, uploadMs, attempts, attemptId);
                if (status !== "failed") {
                    ttFinishPendingRender(renderId);
                    return;
                }
                lastError = "rejected";
            } else {
                ttLogRenderEvent(attemptId, "upload_failed", { ms: uploadMs, error: lastError, detail: "attempts " + attempts }, renderId);
            }

            await Promise.resolve(cartAdded).catch(() => {});
            try {
                const legacy = await this.legacyUploadRender(printed);
                const legacyThumb = legacy.thumbnail_file_url || legacy.file_url;
                const preview = ttRenderUploads.previews[reservation.file_url];
                if (preview) {
                    ttRenderUploads.previews[legacy.file_url] = preview;
                    ttRenderUploads.previews[legacyThumb] = preview;
                    ttSaveRenderPreviews();
                }
                ttLogRenderEvent(attemptId, "legacy_upload_used", { ms: performance.now() - startedAt, detail: legacy.id }, renderId);
                await this.replaceCartRender(renderId, reservation.file_url, legacy.file_url, legacyThumb, legacy.id, false, attemptId);
            } catch (error) {
                ttLogRenderEvent(attemptId, "fallback_used", { ms: performance.now() - startedAt, error: ttErrorText(error) }, renderId);
                await this.replaceCartRender(renderId, reservation.file_url, FALLBACK_DESIGN_URL, FALLBACK_DESIGN_URL, "fallback", true, attemptId);
            }
            ttFinishPendingRender(renderId);
        },
        /** Points every cart line that uses a reserved render at its replacement image. */
        async replaceCartRender(renderId, renderUrl, nextUrl, nextThumbUrl, nextRenderId, isFallback, attemptId) {
            try {
                const cartFetch = window.__ttCartFetch || fetch;
                const cart = await cartFetch("/cart.js?render=" + Date.now(), null, 12000).then((response) => response.json());
                const lines = (cart && cart.items) || [];
                const changes = [];
                lines.forEach((line) => {
                    const props = line.properties || {};
                    if (String(props._render_id || "") !== renderId && props._render !== renderUrl) return;
                    const properties = Object.assign({}, props);
                    if (properties._render) properties._render = nextUrl;
                    if (properties._render_thumbnail) properties._render_thumbnail = nextThumbUrl;
                    if (String(props._render_id || "") === renderId) properties._render_id = nextRenderId;
                    if (isFallback) properties._render_fallback = "true";
                    changes.push({ kind: "change", key: line.key, quantity: line.quantity, properties: properties });
                });
                if (!changes.length) {
                    ttLogRenderEvent(attemptId, "cart_render_replace_failed", { error: "no matching cart line" }, renderId);
                    return;
                }
                if (typeof cartPopUp !== "undefined" && typeof cartPopUp.queueCartWrite === "function") {
                    changes.forEach((change) => cartPopUp.queueCartWrite(change));
                } else {
                    for (const change of changes) {
                        await cartFetch("/cart/change.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ id: change.key, quantity: change.quantity, properties: change.properties }),
                        }, 12000);
                    }
                }
                ttLogRenderEvent(attemptId, "cart_render_replaced", {
                    detail: String(changes.length) + (isFallback ? " fallback" : ""),
                }, renderId);
            } catch (error) {
                ttLogRenderEvent(attemptId, "cart_render_replace_failed", { error: ttErrorText(error) }, renderId);
            }
        },
        startEditingDesign() {
            this.showUpdateDesignPopUp = false;
        },
        cancelUpdateDesign() {
            window.location.href = '/';
        },
        openViewCurrentDesign() {
            this.showViewCurrentDesignPopUp = true;
            if (this.updateDesignAssetId && !this.updateDesignPreviewLoading) {
                this.fetchUpdateDesignPreview();
            }
        },
        confirmSaveOrderDesign() {
            if (this.validateRequiredFields() !== true && this.selectedProductTypeAndSize['type'] === 'canvas') {
                this.scrollToProductDetails();
                return;
            }
            this.showSaveDesignConfirmPopUp = true;
        },
        keepEditingDesign() {
            if (this.designSaveLoading) {
                return;
            }
            this.showSaveDesignConfirmPopUp = false;
        },
        confirmAndSaveOrderDesign() {
            this.saveOrderDesign();
        },
        fetchUpdateDesignPreview() {
            if (!this.updateDesignAssetId) {
                return;
            }

            this.updateDesignPreviewLoading = true;
            this.updateDesignPreviewError = '';

            axios.get('https://app.agentallen.ai/api/orders/line-item-design', {
                params: {
                    asset_id: this.updateDesignAssetId,
                    order_id: this.queryArray['order-id'],
                }
            }).then((res) => {
                const preview = res?.data || null;
                if (this.lastSavedDesignUrl && preview) {
                    this.updateDesignPreview = {
                        ...preview,
                        design_url: this.lastSavedDesignUrl,
                    };
                } else {
                    this.updateDesignPreview = preview;
                }
                this.designPreviewCacheKey = Date.now();
            }).catch((err) => {
                this.updateDesignPreview = null;
                this.updateDesignPreviewError =
                    err?.response?.data?.message || err?.message || 'Could not load existing design.';
            }).then(() => {
                this.updateDesignPreviewLoading = false;
            });
        },
        saveOrderDesign() {

            if(this.loadingCanvasImage === true || this.pendingCanvasAssets > 0){
                this.awaitingDesignUpdate = true;

                console.log("Waiting until canvas has fully loaded before adding to cart");

            }else{
                clearTimeout(this.designSaveHideTimer);
                this.designSaveVisible = true;
                this.designSaveLoading = true;
                this.designSaveType = "loading";
                this.designSaveMessage = "Uploading & updating design…";

                this.renderPrintImages().then((printed) => {
                    return this.uploadPrintedImage(printed);
                }).then((uploaded) => {
                    const fileUrl = uploaded?.file_url;
                    if (!fileUrl) {
                        return Promise.reject(new Error("Upload succeeded but no file URL was returned."));
                    }

                    const payload = this.updateDesignAssetId
                        ? {
                            asset_id: this.updateDesignAssetId,
                            new_design_url: fileUrl,
                        }
                        : {
                            order_id: this.queryArray["order-id"],
                            line_item_id: this.queryArray["order-line-item-id"],
                            line_unit_index: 0,
                            new_design_url: fileUrl,
                        };

                    const staffEdit = this.queryArray && this.queryArray["staff_edit"];
                    if (staffEdit) {
                        payload.staff_edit = staffEdit;
                    }

                    return axios.post("https://app.agentallen.ai/api/orders/line-item-design", payload).then(() => fileUrl);
                }).then((fileUrl) => {
                    this.designSaveLoading = false;
                    this.designSaveType = "success";
                    this.designSaveMessage = "Design updated successfully.";

                    if (fileUrl) {
                        this.lastSavedDesignUrl = fileUrl;
                        this.updateDesignPreview = {
                            ...(this.updateDesignPreview || {}),
                            design_url: fileUrl,
                        };
                        this.designPreviewCacheKey = Date.now();
                    }

                    this.designSaveHideTimer = setTimeout(() => {
                        this.designSaveVisible = false;
                    }, 6000);
                }).catch((error) => {
                    this.designSaveLoading = false;
                    this.designSaveType = "error";

                    const msg =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Something went wrong. Please try again.";

                    this.designSaveMessage = msg;

                    this.designSaveHideTimer = setTimeout(() => {
                        this.designSaveVisible = false;
                    }, 8000);
                });
            }
        },
        /** Eases the saving bar toward 90% until the add-to-cart modal closes. */
        startAtcProgress() {
            clearInterval(this._atcProgressTimer);
            this._atcProgressTimer = setInterval(() => {
                if (this.atcAvailable) {
                    clearInterval(this._atcProgressTimer);
                    this._atcProgressTimer = null;
                    return;
                }
                const remaining = 90 - this.uploadPercentage;
                if (remaining > 0) {
                    this.uploadPercentage += Math.max(1, Math.round(remaining * 0.05));
                }
            }, 150);
        },
        /** A long transform transition keeps animating off the main thread while the design export blocks the page. */
        setSavingBarGlide(enabled) {
            document.querySelectorAll(".saving-design-modal .saving-design-bar-fill").forEach((el) => {
                el.style.transition = enabled ? "transform 4s cubic-bezier(0.15, 0.6, 0.35, 1)" : "";
            });
        },
        waitForCanvasBuild(version) {
            return new Promise((resolve, reject) => {
                const started = Date.now();
                const tick = () => {
                    if (version != null && this.canvasBuildVersion !== version) {
                        reject(new Error("canvas rebuild was replaced"));
                        return;
                    }
                    if (!this.loadingCanvasImage && !(this.pendingCanvasAssets > 0)) {
                        resolve();
                        return;
                    }
                    if (Date.now() - started > 20000) {
                        reject(new Error("canvas rebuild timed out"));
                        return;
                    }
                    requestAnimationFrame(tick);
                };
                tick();
            });
        },
        async renderCartImagesFromDesignSnapshot() {
            const snapshot = this.buildCartDesignSnapshot();
            const countryMap = this.selectedProductType && this.selectedProductType["has_country_map"] === true && this.userInput;
            const previousStateCode = countryMap ? this.userInput["state_code"] : undefined;
            let appliedExportState = false;
            try {
                if (countryMap && snapshot.userInput) {
                    const nextMode = snapshot.userInput["show_mode"];
                    const nextState = snapshot.userInput["state_code"];
                    if (this.userInput["show_mode"] !== nextMode || String(this.userInput["state_code"] || "") !== String(nextState || "")) {
                        this.userInput["show_mode"] = nextMode;
                        this.userInput["state_code"] = nextState;
                        appliedExportState = true;
                        await this.refreshCountryMapRaster();
                        this.fillCanvas();
                        await this.waitForCanvasBuild(this.canvasBuildVersion);
                    }
                }
                const printed = await this.renderPrintImages();
                return { printed: printed, snapshot: snapshot };
            } finally {
                if (appliedExportState) {
                    this.userInput["state_code"] = previousStateCode;
                }
            }
        },
        addToCart() {
            if (this.atcInFlight || this.awaitingATC || this.cartEditRestoring) {
                return;
            }

            if (this.selectedProductTypeAndSize['type'] === 'canvas' && this.validateRequiredFields() !== true) {
                this.requiredValidationActive = true;
                this.scrollToProductDetails();
                return;
            }

            this.updateRandonUpsell();
            this.atcAvailable = false;
            this.uploadPercentage = 1;

            if (this.loadingCanvasImage === true || this.pendingCanvasAssets > 0) {
                this.awaitingATC = true;
                console.log("Waiting until canvas has fully loaded before adding to cart");
                return;
            }

            this.atcInFlight = true;

            if (this.selectedProductTypeAndSize['type'] !== 'canvas') {
                var standardItem = {
                    quantity: this.quantity,
                    id: this.selectedProductTypeAndSize['variant_id']
                };
                this.showOptimisticCartAdd([standardItem]);
                fetch("/cart/add.js", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify(standardItem)
                }).then(response => {
                    if (!response.ok) throw new Error("Cart add failed: " + response.status);
                    this.showSuccessBox = true;
                    this.atcAvailable = true;
                    this.syncCartFromServer();
                    if (typeof cartPopUp !== "undefined" && typeof cartPopUp.getCartThenOpen === "function") {
                        cartPopUp.getCartThenOpen(true);
                    }
                }).catch((error) => {
                    console.error("Error adding product to cart:", error);
                    this.removeOptimisticCartAdd();
                    this.atcAvailable = true;
                }).finally(() => {
                    this.atcInFlight = false;
                });
                return;
            }

            this.curRenderedImage = null;

            const config = {
                headers: { 'Content-Type': 'application/json' },
                timeout: 60000,
                onUploadProgress: (progressEvent) => {
                    const total = progressEvent.total;
                    if (!total) {
                        return;
                    }
                    const tempPercentage = Math.round((progressEvent.loaded * 100) / total);
                    this.uploadPercentage = Math.max(this.uploadPercentage, Math.min(tempPercentage, 93));
                }
            };

            const attemptId = this.makeid(16);
            const renderStartedAt = performance.now();
            ttLogRenderEvent(attemptId, "render_started");
            this.uploadPercentage = 8;
            this.startAtcProgress();

            ttNextPaint().then(() => {
                this.setSavingBarGlide(true);
                this.uploadPercentage = 60;
                return ttNextPaint();
            }).then(() => this.renderCartImagesFromDesignSnapshot()).then((exported) => {
                const printed = exported.printed;
                const designSnapshot = exported.snapshot;
                this.setSavingBarGlide(false);
                const renderMs = performance.now() - renderStartedAt;
                ttLogRenderEvent(attemptId, "render_done", { ms: renderMs });

                return this.reserveRender(renderMs, attemptId).then((reservation) => {
                    if (reservation) {
                        ttStartPendingRender(reservation.id, reservation.file_url, printed.image_thumb, attemptId);
                        const cartAdded = this.addCanvasItemToShopifyCart(reservation.file_url, reservation.thumbnail_file_url || reservation.file_url, reservation.id, false, attemptId, designSnapshot);
                        this.uploadReservedRender(reservation, printed, attemptId, cartAdded)
                            .catch((error) => {
                                ttLogRenderEvent(attemptId, "upload_failed", { error: ttErrorText(error), detail: "unexpected" }, reservation.id);
                            })
                            .finally(() => ttFinishPendingRender(reservation.id));
                        return;
                    }

                    ttLogRenderEvent(attemptId, "legacy_upload_started");
                    return this.legacyUploadRender(printed, config).then((data) => {
                        ttLogRenderEvent(attemptId, "legacy_upload_used", { ms: performance.now() - renderStartedAt, detail: data.id }, data.id);
                        this.addCanvasItemToShopifyCart(data.file_url, data.thumbnail_file_url || data.file_url, data.id, false, attemptId, designSnapshot);
                    }).catch((error) => {
                        console.error("Error uploading image:", error);
                        ttLogRenderEvent(attemptId, "fallback_used", { ms: performance.now() - renderStartedAt, error: ttErrorText(error) });
                        this.addCanvasItemToShopifyCart(FALLBACK_DESIGN_URL, FALLBACK_DESIGN_URL, 'fallback', true, attemptId, designSnapshot);
                    });
                });
            }).catch((renderError) => {
                this.setSavingBarGlide(false);
                console.error("Error rendering canvas for cart:", renderError);
                ttLogRenderEvent(attemptId, "render_failed", { ms: performance.now() - renderStartedAt, error: ttErrorText(renderError) });
                this.atcInFlight = false;
                this.atcAvailable = true;
            });
        },
        suggestionIconSvg(suggestion) {
            return locationSuggestionIconSvg(suggestion);
        },
        returnBoldSuggestion(title, str, search) {
            var splitStr = search.toString().toLowerCase().split(' ');
            for (var i = 0; i < splitStr.length; i++) {
                splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
            }
            var temp = splitStr.join(' ');

            if (title !== str) {
                var tempTitle = title + ", <span class='tt-suggest-secondary'>" + str.replace(title + ", ", "") + "</span>";
                return tempTitle.replace(temp, "<strong>" + temp + "</strong>");
            } else {
                return str.replace(temp, "<strong>" + temp + "</strong>");
            }
        },
        normalizeGooglePlaceDetailToHereShape(place) {
            if (!place || !place.location) {
                return null;
            }
            const lat = place.location.latitude;
            const lng = place.location.longitude;
            // POI title comes from Autocomplete suggestion (Essentials); avoid Place Details Pro displayName.
            const title = "";
            const label =
                place.formattedAddress ||
                place.shortFormattedAddress ||
                "";

            let city = "";
            let country = "";
            let district = "";
            let countryCode = "";
            let stateName = "";
            let stateCode = "";
            const components = place.addressComponents || [];
            for (let i = 0; i < components.length; i++) {
                const c = components[i];
                const typesArr = c.types || [];
                const longName = c.longText || c.long_name || "";
                if (typesArr.indexOf("locality") !== -1 && !city) {
                    city = longName;
                }
                if (typesArr.indexOf("postal_town") !== -1 && !city) {
                    city = longName;
                }
                if (typesArr.indexOf("administrative_area_level_1") !== -1) {
                    if (!district) {
                        district = longName;
                    }
                    if (!stateName) {
                        stateName = longName;
                    }
                    const shortSt = (c.shortText || c.short_name || "").trim();
                    if (
                        !stateCode &&
                        shortSt.length >= 2 &&
                        shortSt.length <= 3 &&
                        /^[A-Za-z]+$/.test(shortSt)
                    ) {
                        stateCode = shortSt.toUpperCase();
                    }
                }
                if (typesArr.indexOf("country") !== -1 && !country) {
                    country = longName;
                    countryCode = (c.shortText || c.short_name || "").toUpperCase();
                }
            }

            const types = place.types || [];
            let resultType = "place";
            if (types.indexOf("street_address") !== -1 || types.indexOf("premise") !== -1) {
                resultType = "street";
            } else if (
                types.some(function (t) {
                    return (
                        t === "locality" ||
                        t === "sublocality" ||
                        t === "sublocality_level_1" ||
                        t === "neighborhood"
                    );
                })
            ) {
                resultType = "locality";
            } else if (types.indexOf("country") !== -1) {
                resultType = "country";
            }

            return {
                position: { lat: lat, lng: lng },
                title: title,
                address: {
                    label: label,
                    city: city,
                    countryName: country,
                    district: district,
                    countryCode: countryCode,
                    state: stateName,
                    stateCode: stateCode,
                },
                resultType: resultType,
            };
        },

        /**
         * Place Details (New) for a place ID. Uses the same session token as Autocomplete until a
         * successful response; clearing that token ends the billable Places session (see
         * googlePlacesSearch / loadGooglePlacesLocationSearchSuggest).
         * @param {string} placeId
         * @returns {Promise<object|null>} Same shape as normalizeGooglePlaceDetailToHereShape (for curAutoAddressLocation).
         */
        async fetchGooglePlaceDetails(placeId) {
            if (!placeId || typeof placeId !== "string") {
                return null;
            }
            const sessionSnapshot = this.googlePlacesSearchSessionToken;
            if (!sessionSnapshot) {
                console.warn("fetchGooglePlaceDetails: missing googlePlacesSearchSessionToken");
                return null;
            }
            const apiKey = this.getGooglePlacesApiKey();
            const detailUrl =
                "https://places.googleapis.com/v1/places/" +
                encodeURIComponent(placeId) +
                "?sessionToken=" +
                encodeURIComponent(sessionSnapshot);
            try {
                const res = await fetch(detailUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Goog-Api-Key": apiKey,
                        "X-Goog-FieldMask":
                            "id,formattedAddress,location,addressComponents,types,shortFormattedAddress",
                    },
                });
                if (!res.ok) {
                    const errBody = await res.text().catch(function () {
                        return "";
                    });
                    console.warn("fetchGooglePlaceDetails failed", res.status, errBody);
                    return null;
                }
                const place = await res.json();
                const loc = this.normalizeGooglePlaceDetailToHereShape(place);
                // Successful Details completes Google's session-based billing for this token: all
                // prior Autocomplete calls with the same sessionToken are charged as one session.
                // Clear only on success so failed Details leaves the session open for retry.
                if (loc && this.googlePlacesSearchSessionToken === sessionSnapshot) {
                    this.resetGooglePlacesAutocompleteSession();
                }
                return loc;
            } catch (err) {
                console.warn("fetchGooglePlaceDetails", err);
                return null;
            }
        },

        /**
         * Google Places (New) requires a v4 UUID as `sessionToken`. One token is minted when the
         * user first hits the Autocomplete threshold, reused for every Autocomplete call while the
         * field stays active, then the same token is sent with Place Details — that is the
         * session-based billing unit. A new UUID only after clear, abandon, idle timeout, or
         * successful Details.
         */
        makeGooglePlacesSearchSessionToken() {
            if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
                return crypto.randomUUID();
            }
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },

        /**
         * Clear Google Places `sessionToken` and idle timer. Call when the billable session must
         * end (successful Details, cleared field, user abandon, idle).
         * Does not cancel a pending debounce — short-query resets must not kill a newer typed query.
         */
        resetGooglePlacesAutocompleteSession() {
            this.googlePlacesSearchSessionToken = null;
            this.googlePlacesSessionLastAutocompleteAt = 0;
        },

        clearGooglePlacesSearchDebounceTimer() {
            if (this.googlePlacesSearchDebounceTimer != null) {
                clearTimeout(this.googlePlacesSearchDebounceTimer);
                this.googlePlacesSearchDebounceTimer = null;
            }
        },

        /** User explicitly left the search (e.g. Escape): end Places session; hide suggestions. */
        abandonGooglePlacesLocationSearch() {
            this.clearGooglePlacesSearchDebounceTimer();
            this.resetGooglePlacesAutocompleteSession();
            this.showMapboxSearchFieldSuggestions = false;
        },

        /** Clear the location search text only. Does not reset the chosen map location. */
        clearLocationSearch() {
            this.mapboxMapSearchField = "";
            this.mapboxMapSearchFieldSuggestions = [];
            this.locationMapboxSearchLoading = false;
            this.abandonGooglePlacesLocationSearch();
            this.$nextTick(() => {
                const input = document.querySelector(".mapbox-location-search-mapbox-input");
                if (input) {
                    input.focus();
                }
            });
        },

        /** Clear the second-location search text only. Does not remove the chosen route end. */
        clearRouteEndSearch() {
            if (this.routeEndSearchTimer != null) {
                clearTimeout(this.routeEndSearchTimer);
                this.routeEndSearchTimer = null;
            }
            this.routeEndSearchRequestAt = Date.now();
            this.routeEndSearchField = "";
            this.routeEndSuggestions = [];
            this.showRouteEndSuggestions = false;
            this.routeEndSearchLoading = false;
            this.$nextTick(() => {
                const input = document.querySelector(".tt-route-end-input");
                if (input) {
                    input.focus();
                }
            });
        },

        /**
         * Debounced entry point for location Autocomplete (~350ms after typing pauses).
         * Syncs from the input DOM value so Android keyup/IME is not racing a stale v-model.
         * When the timer fires, re-read the DOM again — Gboard may append composed text without
         * further input/keyup events during the wait.
         */
        debouncedLoadGooglePlacesLocationSearchSuggest(event) {
            if (event && (event.key === "Enter" || event.keyCode === 13)) {
                return;
            }
            this.coordinateGeocodeSeq += 1;
            if (event && event.target && typeof event.target.value === "string") {
                this.mapboxMapSearchField = event.target.value;
            }
            this.clearGooglePlacesSearchDebounceTimer();
            this.googlePlacesSearchDebounceTimer = setTimeout(() => {
                this.googlePlacesSearchDebounceTimer = null;
                const input = document.querySelector(".mapbox-location-search-mapbox-input");
                if (input && typeof input.value === "string") {
                    this.mapboxMapSearchField = input.value;
                }
                this.loadGooglePlacesLocationSearchSuggest();
            }, 350);
        },

        /**
         * Google Places API (New): Autocomplete. Reuses the same sessionToken for every request
         * until Details succeeds, the field is cleared, the user abandons, or idle timeout
         * (see loadGooglePlacesLocationSearchSuggest).
         *
         * @param {string} query
         * @returns {Promise<{ suggestions: Array, getDetails: (placeId: string) => Promise<object|null> }>}
         */
        async googlePlacesSearch(query) {
            const apiKey = this.getGooglePlacesApiKey();
            const getDetails = (placeId) => this.fetchGooglePlaceDetails(placeId);

            const trimmed = query !== undefined && query !== null ? query.toString().trim() : "";
            if (trimmed.length < 3) {
                return { suggestions: [], getDetails: getDetails };
            }

            if (!this.googlePlacesSearchSessionToken) {
                this.googlePlacesSearchSessionToken = this.makeGooglePlacesSearchSessionToken();
            }
            const sessionToken = this.googlePlacesSearchSessionToken;

            const body = {
                input: trimmed,
                languageCode: "en",
                sessionToken: sessionToken,
            };
            if (typeof $cookies !== "undefined" && $cookies.get("444-loc") !== null) {
                const loc = $cookies.get("444-loc");
                const lat = Number(loc.latitude);
                const lon = Number(loc.longitude);
                if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
                    body.locationBias = {
                        circle: {
                            center: { latitude: lat, longitude: lon },
                            radius: 50000.0,
                        },
                    };
                }
            }

            this.googlePlacesSessionLastAutocompleteAt = Date.now();

            try {
                const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Goog-Api-Key": apiKey,
                        "X-Goog-FieldMask":
                            "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat,suggestions.placePrediction.types",
                    },
                    body: JSON.stringify(body),
                });
                if (!res.ok) {
                    const errBody = await res.text().catch(function () {
                        return "";
                    });
                    console.warn("googlePlacesSearch autocomplete failed", res.status, errBody);
                    return { suggestions: [], getDetails: getDetails };
                }
                const data = await res.json();
                const suggestions = [];
                const rows = data.suggestions || [];
                for (let i = 0; i < rows.length; i++) {
                    const p = rows[i].placePrediction;
                    if (!p || !p.placeId) {
                        continue;
                    }
                    const label = (p.text && p.text.text) || "";
                    const mainText =
                        p.structuredFormat &&
                        p.structuredFormat.mainText &&
                        p.structuredFormat.mainText.text;
                    const title =
                        mainText ||
                        (label.split(",")[0] || "").trim() ||
                        label ||
                        p.placeId;
                    suggestions.push({
                        source: "google",
                        google_place_id: p.placeId,
                        title: title,
                        types: p.types || [],
                        address: {
                            label: label || title,
                        },
                    });
                }
                return { suggestions: suggestions, getDetails: getDetails };
            } catch (err) {
                console.warn("googlePlacesSearch autocomplete", err);
                return { suggestions: [], getDetails: getDetails };
            }
        },

        async loadGooglePlacesLocationSearchSuggest() {
            const requestTimestamp = Date.now();
            this.mapboxLocationSearchLatestRequestAt = requestTimestamp;

            const trimmedField =
                this.mapboxMapSearchField !== undefined && this.mapboxMapSearchField !== null
                    ? this.mapboxMapSearchField.toString().trim()
                    : "";

            // User cleared the field (or only padding): end the Places session so the next threshold
            // crossing starts a fresh token (avoids orphan sessions and matches billing intent).
            if (
                this.mapboxMapSearchField === "" ||
                this.mapboxMapSearchField.length <= 2 ||
                trimmedField.length === 0 ||
                trimmedField.length < 3
            ) {
                this.locationMapboxSearchLoading = false;
                this.mapboxMapSearchFieldSuggestions = [];
                this.resetGooglePlacesAutocompleteSession();
                return;
            }

            const coordinateMatch = parseLocationCoordinates(trimmedField);
            if (coordinateMatch) {
                this.locationMapboxSearchLoading = false;
                this.resetGooglePlacesAutocompleteSession();
                const label = "Use coordinates: " + formatLocationCoordinatesLabel(coordinateMatch.lat, coordinateMatch.lng);
                this.mapboxMapSearchFieldSuggestions = [{
                    source: "coordinates",
                    title: label,
                    address: { label: label },
                    coordinates: { lat: coordinateMatch.lat, lng: coordinateMatch.lng },
                }];
                this.showMapboxSearchFieldSuggestions = true;
                this.lastSuccessfulMapboxSearchTimestamp = requestTimestamp;
                return;
            }

            // Idle timeout: if they stepped away, start a new session on return (stale tokens are not
            // useful for billing grouping and can confuse session accounting).
            const googlePlacesSessionIdleMs = 180000;
            if (
                this.googlePlacesSearchSessionToken &&
                this.googlePlacesSessionLastAutocompleteAt > 0 &&
                requestTimestamp - this.googlePlacesSessionLastAutocompleteAt > googlePlacesSessionIdleMs
            ) {
                this.resetGooglePlacesAutocompleteSession();
            }

            this.locationMapboxSearchLoading = true;
            this.showMapboxSearchFieldSuggestions = true;

            try {
                const { suggestions } = await this.googlePlacesSearch(this.mapboxMapSearchField);
                if (requestTimestamp !== this.mapboxLocationSearchLatestRequestAt) {
                    return;
                }
                if (this.mapboxMapSearchField === "" || this.mapboxMapSearchField.length <= 2) {
                    return;
                }
                if (
                    !this.lastSuccessfulMapboxSearchTimestamp ||
                    requestTimestamp > this.lastSuccessfulMapboxSearchTimestamp
                ) {
                    this.mapboxMapSearchFieldSuggestions = suggestions || [];
                    this.lastSuccessfulMapboxSearchTimestamp = requestTimestamp;
                }
            } catch (err) {
                console.error("loadGooglePlacesLocationSearchSuggest", err);
                if (requestTimestamp === this.mapboxLocationSearchLatestRequestAt) {
                    this.mapboxMapSearchFieldSuggestions = [];
                }
            } finally {
                if (requestTimestamp === this.mapboxLocationSearchLatestRequestAt) {
                    this.locationMapboxSearchLoading = false;
                }
            }
        },

        selectGooglePlaceLocationSuggestion(suggestion) {
            if (!suggestion) {
                return;
            }
            this.mapboxLocationSearchLatestRequestAt = Date.now();
            this.clearGooglePlacesSearchDebounceTimer();
            this.locationMapboxSearchLoading = false;
            this.showMapboxSearchFieldSuggestions = false;
            if (suggestion.source === "coordinates" && suggestion.coordinates) {
                this.selectCoordinateLocation(suggestion.coordinates);
                return;
            }
            if (suggestion.source === "google" && suggestion.google_place_id) {
                this.selectGooglePlaceForMapboxField(suggestion);
            }
        },
        confirmCoordinateLocationSuggestion(event) {
            if (!this.showMapboxSearchFieldSuggestions) {
                return;
            }
            const suggestions = this.mapboxMapSearchFieldSuggestions;
            if (!suggestions || suggestions.length !== 1) {
                return;
            }
            const suggestion = suggestions[0];
            if (!suggestion || suggestion.source !== "coordinates" || !suggestion.coordinates) {
                return;
            }
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            this.selectCoordinateLocation(suggestion.coordinates);
        },
        async selectGooglePlaceForMapboxField(suggestion) {
            if (!suggestion || !suggestion.google_place_id) {
                return;
            }
            this.clearGooglePlacesSearchDebounceTimer();
            this.showMapboxSearchFieldSuggestions = false;
            const suggestionTitle =
                suggestion.title !== undefined && suggestion.title !== null
                    ? String(suggestion.title).trim()
                    : "";
            const editedBeforePick = this.locationTitleManuallyEdited === true;
            if (suggestionTitle && !editedBeforePick && this.userInput) {
                this.userInput.location = this.clampLocationTitle(suggestionTitle);
                if (!this.patchTextField("location")) {
                    this.fillCanvas();
                }
            }
            const editSeq = this.locationEditSeq || 0;
            const loc = await this.fetchGooglePlaceDetails(suggestion.google_place_id);
            if (!loc) {
                return;
            }
            const shortLabel =
                loc.address && loc.address.label
                    ? String(loc.address.label).split(",")[0].trim()
                    : "";
            loc.title = suggestionTitle || shortLabel || "";
            this.curAutoAddressLocation = loc;
            this.usingDefaultLocation = false;
            this.defaultPreviewLocationLabel = "";
            const editedDuringLookup = (this.locationEditSeq || 0) !== editSeq;
            this.locationTitleManuallyEdited = editedBeforePick || editedDuringLookup;
            this.rememberSelectedPlaceAnchor(loc);
            await this.fillInAddressNew();
            this.mapboxMapSearchField = this.curAutoAddressLocation.address.label;
        },
        rememberSelectedPlaceAnchor(loc) {
            const title = loc && loc.title ? String(loc.title).trim() : "";
            const lat = loc && loc.position ? Number(loc.position.lat) : NaN;
            const lng = loc && loc.position ? Number(loc.position.lng) : NaN;
            if (loc && loc.resultType === "place" && title && Number.isFinite(lat) && Number.isFinite(lng)) {
                this.locationSelectionKind = "place";
                this.locationPlaceAnchor = { lat: lat, lng: lng };
                return;
            }
            this.locationSelectionKind = "area";
            this.locationPlaceAnchor = null;
        },
        shouldKeepBusinessTitle(lat, lng) {
            if (this.locationSelectionKind !== "place" || !this.locationPlaceAnchor) {
                return false;
            }
            const loc = this.curAutoAddressLocation;
            if (!loc || loc.resultType !== "place") {
                return false;
            }
            if (!loc.title || !String(loc.title).trim()) {
                return false;
            }
            return haversineMeters(this.locationPlaceAnchor.lat, this.locationPlaceAnchor.lng, lat, lng) <= LOCATION_PIN_KEEP_PLACE_METERS;
        },
        async reverseGeocodeLatLng(lat, lng) {
            try {
                await ensureGoogleMapsGeocoder(this.getGooglePlacesApiKey());
                const geocoder = new google.maps.Geocoder();
                const response = await geocoder.geocode({
                    location: { lat: Number(lat), lng: Number(lng) },
                    language: "en",
                });
                const results = response && Array.isArray(response.results) ? response.results : [];
                if (!results.length) {
                    return null;
                }
                return addressFieldsFromGeocodeResults(results);
            } catch (err) {
                console.warn("reverseGeocodeLatLng", err);
                return null;
            }
        },
        async selectCoordinateLocation(coordinates) {
            if (!coordinates || this.coordinateCommitInFlight) {
                return;
            }
            const lat = Number(coordinates.lat);
            const lng = Number(coordinates.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return;
            }
            const seq = ++this.coordinateGeocodeSeq;
            const submittedField = this.mapboxMapSearchField;
            this.coordinateCommitInFlight = true;
            this.clearGooglePlacesSearchDebounceTimer();
            this.showMapboxSearchFieldSuggestions = false;
            this.locationMapboxSearchLoading = true;
            this.resetGooglePlacesAutocompleteSession();
            let area = null;
            try {
                area = await this.reverseGeocodeLatLng(lat, lng);
            } catch (err) {
                console.warn("reverseGeocodeLatLng", err);
                area = null;
            }
            if (seq !== this.coordinateGeocodeSeq) {
                this.coordinateCommitInFlight = false;
                return;
            }
            const loc = buildCoordinateLocation(lat, lng, area);
            this.curAutoAddressLocation = loc;
            this.usingDefaultLocation = false;
            this.defaultPreviewLocationLabel = "";
            this.locationTitleManuallyEdited = false;
            this.locationSelectionKind = "coordinate";
            this.locationPlaceAnchor = null;
            try {
                await this.fillInAddressNew();
            } finally {
                this.coordinateCommitInFlight = false;
                if (seq === this.coordinateGeocodeSeq && this.mapboxMapSearchField === submittedField) {
                    this.mapboxMapSearchField = loc.title;
                    this.mapboxMapSearchFieldSuggestions = [];
                    this.showMapboxSearchFieldSuggestions = false;
                    this.locationMapboxSearchLoading = false;
                }
            }
        },
        routeActive() {
            const pt = this.selectedProductType;
            if (this.gpsRouteMode()) {
                return this.gpsRoutePath().length >= 2 && this.styleHasStaticMapImage();
            }
            return !!(
                this.routeEndLocation &&
                this.routeEndLocation.position &&
                this.curAutoAddressLocation &&
                this.curAutoAddressLocation.position &&
                pt &&
                pt["has_map_field"] === true &&
                pt["has_country_map"] !== true &&
                this.styleHasStaticMapImage()
            );
        },
        gpsRouteMode() {
            const pt = this.selectedProductType;
            return !!(pt && pt["has_gps_route"] === true);
        },
        gpsRoutePath() {
            const points = this.gpsRoute && Array.isArray(this.gpsRoute.points) ? this.gpsRoute.points : [];
            return points.map((p) => ({ lat: Number(p[0]), lng: Number(p[1]) }));
        },
        /** Line drawn on the route map: the GPS track, or the great circle between the two locations. */
        routePath() {
            if (this.gpsRouteMode()) {
                return this.gpsRoutePath();
            }
            const a = this.curAutoAddressLocation.position;
            const b = this.routeEndLocation.position;
            return TTRouteOverlay.greatCircle(
                { lat: Number(a.lat), lng: Number(a.lng) },
                { lat: Number(b.lat), lng: Number(b.lng) },
                128
            );
        },
        formatGpsRouteDistance(meters) {
            const unit = this.distanceUnit === "mi" ? "mi" : "km";
            const value = Number(meters || 0) / (unit === "mi" ? 1609.344 : 1000);
            return value.toFixed(value >= 100 ? 0 : 1) + " " + unit.toUpperCase();
        },
        setDistanceUnit(unit) {
            if (this.distanceUnit === unit) {
                return;
            }
            this.distanceUnit = unit;
            if (!this.gpsRoute) {
                return;
            }
            if (!this.routeStatsEdited.distance) {
                this.routeStats.distance = this.formatGpsRouteDistance(this.gpsRoute.distance_m);
            }
            if (!this.routeStatsEdited.pace) {
                this.routeStats.pace = this.autoRoutePace();
            }
            this.userInput["location"] = this.routeStatsText();
            this.refreshLocationVisual({ textOnly: true });
        },
        /** Fills distance, time and pace from a route; Strava runs carry a time, library and GPX routes leave it to the customer. */
        fillRouteStats(route) {
            const time = Number(route && route.time_s) || 0;
            this.routeStats = {
                distance: this.formatGpsRouteDistance(route && route.distance_m),
                time: time ? this.formatDuration(time) : "",
                pace: time ? this.formatRoutePace(route.distance_m, time, route.sport_type) : "",
            };
            this.routeStatsEdited = { distance: false, time: false, pace: false };
        },
        routeStatsText() {
            return [this.routeStats.distance, this.routeStats.time, this.routeStats.pace]
                .map((part) => String(part || "").trim())
                .filter(Boolean)
                .join(" · ");
        },
        onRouteStatInput(key) {
            this.routeStatsEdited[key] = true;
            if (key !== "pace" && !this.routeStatsEdited.pace) {
                this.routeStats.pace = this.autoRoutePace();
            }
            this.userInput["location"] = this.routeStatsText();
            this.updateCanvasDetails(this.userInput["location"], "location");
        },
        /** Pace from the distance and time boxes, or "" until both make sense. */
        autoRoutePace() {
            const seconds = this.parseDurationSeconds(this.routeStats.time);
            const meters = this.routeStatsDistanceMeters();
            if (!seconds || !meters) {
                return "";
            }
            return this.formatRoutePace(meters, seconds, this.gpsRoute && this.gpsRoute.sport_type);
        },
        routeStatsDistanceMeters() {
            if (!this.routeStatsEdited.distance) {
                return Number(this.gpsRoute && this.gpsRoute.distance_m) || 0;
            }
            const text = String(this.routeStats.distance || "");
            const value = parseFloat(text.replace(",", "."));
            if (!(value > 0)) {
                return 0;
            }
            const miles = /mi/i.test(text) || (!/km/i.test(text) && this.distanceUnit === "mi");
            return value * (miles ? 1609.344 : 1000);
        },
        /** "3:45:12", "45:12" or "45" (minutes) to seconds. */
        parseDurationSeconds(text) {
            const parts = String(text || "").trim().split(":").map((part) => Number(part));
            if (parts.length === 0 || parts.length > 3 || parts.some((part) => !Number.isFinite(part) || part < 0) || String(text || "").trim() === "") {
                return 0;
            }
            if (parts.length === 1) {
                return parts[0] * 60;
            }
            return parts.reduce((total, part) => total * 60 + part, 0);
        },
        routeColorOptions() {
            const options = [{ color: "#FC5200", stroke: "#c43f00" }];
            (this.mapMarkers || []).forEach((group) => {
                const color = group.route_color || group.color;
                if (color && !options.some((option) => option.color.toLowerCase() === String(color).toLowerCase())) {
                    options.push({ color: color, stroke: group.stroke || color });
                }
            });
            return options;
        },
        setRouteColor(color) {
            this.selectedRouteColor = color;
            this.fillInAddressNew();
        },
        defaultStyleIndex(typeIndex) {
            const type = this.productData && this.productData.types ? this.productData.types[typeIndex] : null;
            const index = Number(type && type["default_style_index"]);
            return type && Number.isInteger(index) && type.styles && type.styles[index] ? index : 0;
        },
        /** Shows the product's sample route (e.g. Gold Coast Marathon) until the customer picks their own. */
        async loadDefaultGpsRoute() {
            const name = this.selectedProductType && this.selectedProductType["default_gps_route"];
            if (!this.gpsRouteMode() || !name || this.gpsRoute || this.cartEditActive || (this.queryArray && this.queryArray["update-design"] === "true")) {
                return;
            }
            try {
                const search = await fetch(GPS_ROUTES_API_URL + "/search?q=" + encodeURIComponent(name));
                const results = await search.json();
                const match = (Array.isArray(results.data) ? results.data : []).find((result) => result && result.name === name);
                if (!match || this.gpsRoute) {
                    return;
                }
                const response = await fetch(GPS_ROUTES_API_URL + "/" + encodeURIComponent(match.id));
                const json = await response.json();
                if (!response.ok || !json.data || this.gpsRoute || !this.gpsRouteMode()) {
                    return;
                }
                await this.applyGpsRoute(json.data, { preview: true });
            } catch (err) {
                console.error("loadDefaultGpsRoute", err);
            }
        },
        focusGpsRouteSearch() {
            const query = String(this.gpsRouteSearchField || "").trim();
            if (query === "") {
                this.gpsRouteResults = [];
                this.showGpsRouteResults = false;
                return;
            }
            this.showGpsRouteResults = this.gpsRouteResults.length > 0;
        },
        debouncedGpsRouteSearch(event) {
            if (event && event.target && typeof event.target.value === "string") {
                this.gpsRouteSearchField = event.target.value;
            }
            if (this.gpsRouteSearchTimer != null) {
                clearTimeout(this.gpsRouteSearchTimer);
            }
            this.gpsRouteSearchTimer = setTimeout(() => {
                this.gpsRouteSearchTimer = null;
                this.loadGpsRouteResults();
            }, 300);
        },
        async loadGpsRouteResults() {
            const query = String(this.gpsRouteSearchField || "").trim();
            if (query === "") {
                this.gpsRouteRequestAt = Date.now();
                this.gpsRouteResults = [];
                this.showGpsRouteResults = false;
                this.gpsRouteLoading = false;
                return;
            }
            const requestAt = Date.now();
            this.gpsRouteRequestAt = requestAt;
            this.gpsRouteLoading = true;
            this.showGpsRouteResults = true;
            try {
                const response = await fetch(GPS_ROUTES_API_URL + "/search?q=" + encodeURIComponent(query));
                const json = await response.json();
                if (requestAt === this.gpsRouteRequestAt) {
                    this.gpsRouteResults = response.ok && Array.isArray(json.data) ? json.data : [];
                }
            } catch (err) {
                console.error("loadGpsRouteResults", err);
                if (requestAt === this.gpsRouteRequestAt) {
                    this.gpsRouteResults = [];
                }
            } finally {
                if (requestAt === this.gpsRouteRequestAt) {
                    this.gpsRouteLoading = false;
                }
            }
        },
        async selectGpsRoute(result) {
            if (!result || !result.id) {
                return;
            }
            this.showGpsRouteResults = false;
            this.gpsRouteError = "";
            this.gpsRouteLoading = true;
            try {
                const response = await fetch(GPS_ROUTES_API_URL + "/" + encodeURIComponent(result.id));
                const json = await response.json();
                if (!response.ok || !json.data) {
                    throw new Error("Route request failed with " + response.status);
                }
                await this.applyGpsRoute(json.data);
            } catch (err) {
                console.error("selectGpsRoute", err);
                this.gpsRouteError = "We couldn't load that route. Please try again.";
            } finally {
                this.gpsRouteLoading = false;
            }
        },
        async uploadGpxFile(event) {
            const input = event && event.target;
            const file = input && input.files && input.files[0];
            if (!file) {
                return;
            }
            this.gpsRouteError = "";
            this.gpsRouteLoading = true;
            try {
                const form = new FormData();
                form.append("file", file);
                const response = await fetch(GPS_ROUTES_API_URL + "/gpx", {
                    method: "POST",
                    headers: { Accept: "application/json" },
                    body: form,
                });
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json.data) {
                    const fieldError = json.errors && json.errors.file && json.errors.file[0];
                    if (response.status === 429) {
                        this.gpsRouteError = "Too many uploads in a row. Please wait a minute and try again.";
                    } else if (response.status >= 500) {
                        this.gpsRouteError = "Uploads aren't working right now. Please try again shortly.";
                    } else {
                        this.gpsRouteError = fieldError || "We couldn't read that GPX file. Please try another.";
                    }
                    return;
                }
                await this.applyGpsRoute(json.data);
            } catch (err) {
                console.error("uploadGpxFile", err);
                this.gpsRouteError = "We couldn't upload that GPX file. Please try again.";
            } finally {
                this.gpsRouteLoading = false;
                if (input) {
                    input.value = "";
                }
            }
        },
        openStravaTab() {
            this.gpsRouteTab = "strava";
            this.gpsRouteError = "";
            if (!this.stravaSession) {
                try {
                    this.stravaSession = sessionStorage.getItem("tt-strava-session") || "";
                } catch (err) {}
            }
            if (this.stravaSession && !this.stravaLoaded) {
                this.loadStravaActivities();
            } else if (!this.stravaSession) {
                this.loadStravaConnectUrl();
            }
        },
        /** The page Strava returns to: this design page with product, size and style kept. */
        stravaReturnUrl() {
            const url = new URL(window.location.href);
            const sizeCodes = ["A5", "A4", "A3", "Keychain", "Block", "Magnet", "Ornament"];
            url.hash = "";
            url.searchParams.set("product_type", "route-map");
            if (sizeCodes[this.productSizeSelected]) {
                url.searchParams.set("product_size", sizeCodes[this.productSizeSelected]);
            }
            url.searchParams.set("product_fork", String(this.selectedProductTypeStyle || 0));
            return url.toString();
        },
        async loadStravaConnectUrl() {
            if (this.stravaConnectUrl && Date.now() - this.stravaConnectUrlAt < 10 * 60 * 1000) {
                return;
            }
            this.stravaConnectUrl = "";
            const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
                || (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
            try {
                const response = await fetch(
                    DEFAULT_MAPS_API_BASE + "/api/strava/connect-url?return=" + encodeURIComponent(this.stravaReturnUrl()) + (mobile ? "&mobile=1" : ""),
                    { headers: { Accept: "application/json" } }
                );
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json.data || !json.data.url) {
                    this.gpsRouteError = "Strava isn't available right now. Please try again shortly.";
                    return;
                }
                this.stravaConnectUrl = json.data.url;
                this.stravaConnectUrlAt = Date.now();
            } catch (err) {
                console.error("loadStravaConnectUrl", err);
                this.gpsRouteError = "Strava isn't available right now. Please try again shortly.";
            }
        },
        /** Strava sends the customer back with #tt-strava=<session> or #tt-strava-error=<message>. */
        consumeStravaReturn() {
            const match = window.location.hash.match(/^#(tt-strava|tt-strava-error)=(.*)$/);
            if (!match) {
                return;
            }
            history.replaceState(null, "", window.location.pathname + window.location.search);
            let value = "";
            try {
                value = decodeURIComponent(match[2]);
            } catch (err) {}
            this.gpsRouteTab = "strava";
            if (match[1] === "tt-strava" && value) {
                this.stravaSession = value;
                try {
                    sessionStorage.setItem("tt-strava-session", value);
                } catch (err) {}
                this.loadStravaActivities();
            } else {
                this.gpsRouteError = value || "We couldn't connect Strava. Please try again.";
                this.loadStravaConnectUrl();
            }
            this.$nextTick(() => {
                const field = document.querySelector(".tt-gps-route");
                if (field) {
                    field.scrollIntoView({ block: "center" });
                }
            });
        },
        disconnectStrava() {
            this.stravaSession = "";
            this.stravaActivities = [];
            this.stravaLoaded = false;
            this.stravaPage = 0;
            this.stravaHasMore = false;
            this.stravaListOpen = false;
            try {
                sessionStorage.removeItem("tt-strava-session");
            } catch (err) {}
            this.loadStravaConnectUrl();
        },
        /** Shared error handling for the Strava endpoints; returns parsed JSON on success. */
        async stravaRequest(path, options) {
            const response = await fetch(DEFAULT_MAPS_API_BASE + "/api/strava" + path, {
                ...options,
                headers: { Accept: "application/json", "X-Strava-Session": this.stravaSession },
            });
            const json = await response.json().catch(() => ({}));
            if (response.ok && json.data) {
                return json;
            }
            if (response.status === 401) {
                this.disconnectStrava();
            }
            this.gpsRouteError = response.status === 429 && !json.message
                ? "Too many requests. Please wait a minute and try again."
                : json.message || "We couldn't reach Strava. Please try again.";
            return null;
        },
        /**
         * Loads the first page, or the next page of older activities when more is true.
         * Keeps loading while the list doesn't fill its box, but stops after a few pages
         * that add nothing (e.g. a history of swims) to protect the shared Strava rate limit.
         */
        async loadStravaActivities(more = false, emptyPages = 0) {
            if (more && (this.stravaLoadingMore || !this.stravaHasMore)) {
                return;
            }
            const page = more ? this.stravaPage + 1 : 1;
            this.gpsRouteError = "";
            if (more) {
                this.stravaLoadingMore = true;
            } else {
                this.gpsRouteLoading = true;
            }
            let added = 0;
            try {
                const json = await this.stravaRequest("/activities?page=" + page);
                if (json) {
                    added = json.data.length;
                    this.stravaActivities = more ? this.stravaActivities.concat(json.data) : json.data;
                    this.stravaPage = page;
                    this.stravaHasMore = !!(json.meta && json.meta.has_more);
                    this.stravaLoaded = true;
                }
            } catch (err) {
                console.error("loadStravaActivities", err);
                this.gpsRouteError = "We couldn't reach Strava. Please try again.";
                return;
            } finally {
                this.stravaLoadingMore = false;
                this.gpsRouteLoading = false;
            }
            const nextEmpty = added === 0 ? emptyPages + 1 : 0;
            if (!this.stravaHasMore || nextEmpty >= 3) {
                return;
            }
            await this.$nextTick();
            const list = document.querySelector(".tt-strava-activities");
            if (!list || list.scrollHeight <= list.clientHeight + 150) {
                this.loadStravaActivities(true, nextEmpty);
            }
        },
        onStravaListScroll(event) {
            const list = event && event.target;
            if (list && list.scrollTop + list.clientHeight >= list.scrollHeight - 150) {
                this.loadStravaActivities(true);
            }
        },
        /** The activity currently on the plaque, if it is in the loaded pages. */
        selectedStravaActivity() {
            const id = this.gpsRoute && this.gpsRoute.strava_activity_id;
            if (!id) {
                return null;
            }
            return this.stravaActivities.find((activity) => activity.id === id) || null;
        },
        /** Opens the run list as a dropdown, and fills it if the first page is shorter than the box. */
        toggleStravaList() {
            this.stravaListOpen = !this.stravaListOpen;
            if (!this.stravaListOpen) {
                return;
            }
            this.$nextTick(() => {
                const list = document.querySelector(".tt-strava-activities");
                if (list && list.scrollHeight <= list.clientHeight + 150) {
                    this.loadStravaActivities(true);
                }
            });
        },
        stravaSportGroup(sportType) {
            return /Ride$|Velomobile|Handcycle/.test(String(sportType || "")) ? "Ride" : "Foot";
        },
        /** Race time for races (matches chip time), moving time for everything else. */
        stravaActivityTime(activity) {
            return Number(activity && (activity.is_race ? activity.elapsed_time_s : activity.moving_time_s)) || 0;
        },
        formatDuration(seconds) {
            const total = Math.round(Number(seconds || 0));
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = total % 60;
            const pad = (n) => String(n).padStart(2, "0");
            return h > 0 ? h + ":" + pad(m) + ":" + pad(s) : m + ":" + pad(s);
        },
        /** Pace for foot sports (4:34/KM), speed for rides (28.5 KM/H). */
        formatRoutePace(meters, seconds, sportType) {
            const unitMeters = this.distanceUnit === "mi" ? 1609.344 : 1000;
            const units = Number(meters || 0) / unitMeters;
            const time = Number(seconds || 0);
            if (units <= 0 || time <= 0) {
                return "";
            }
            if (this.stravaSportGroup(sportType) === "Ride") {
                return (units / (time / 3600)).toFixed(1) + (this.distanceUnit === "mi" ? " MPH" : " KM/H");
            }
            return this.formatDuration(time / units) + "/" + (this.distanceUnit === "mi" ? "MI" : "KM");
        },
        stravaActivitySummary(activity) {
            const time = this.stravaActivityTime(activity);
            return [
                this.formatStravaDate(activity.event_date),
                this.formatGpsRouteDistance(activity.distance_m),
                time ? this.formatDuration(time) : "",
                this.formatRoutePace(activity.distance_m, time, activity.sport_type),
            ].filter(Boolean).join(" · ");
        },
        async selectStravaActivity(activity) {
            if (!activity || !activity.id || this.gpsRouteLoading) {
                return;
            }
            this.stravaListOpen = false;
            this.gpsRouteError = "";
            this.gpsRouteLoading = true;
            try {
                const json = await this.stravaRequest("/activities/" + encodeURIComponent(activity.id) + "/route", { method: "POST" });
                if (json) {
                    await this.applyGpsRoute({
                        ...json.data,
                        strava_activity_id: activity.id,
                        sport_type: activity.sport_type,
                        time_s: this.stravaActivityTime(activity),
                    });
                }
            } catch (err) {
                console.error("selectStravaActivity", err);
                this.gpsRouteError = "We couldn't load that run. Please try again.";
            } finally {
                this.gpsRouteLoading = false;
            }
        },
        formatStravaDate(date) {
            if (!date) {
                return "";
            }
            const parsed = new Date(date + "T00:00:00");
            return isNaN(parsed) ? date : parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        },
        /**
         * Puts a route from any source on the map and pre-fills title, distance, time, pace and date.
         * preview keeps the product's sample title and date; keepText redraws an already chosen route without touching the customer's text.
         * Library routes never set the date because their event_date can be the next race, not the customer's run.
         */
        async applyGpsRoute(route, options) {
            const opts = options || {};
            const points = route && Array.isArray(route.points) ? route.points : [];
            if (points.length < 2) {
                this.gpsRouteError = "That route doesn't have a GPS track.";
                return;
            }
            this.gpsRoute = route;
            this.gpsRouteIsPreview = opts.preview === true;
            if (!this.gpsRouteIsPreview) {
                this.gpsRouteSearchField = route.name || "";
            }
            this.gpsRouteResults = [];
            this.clearRouteEnd();
            this.curAutoAddressLocation = buildCoordinateLocation(Number(points[0][0]), Number(points[0][1]), null);
            this.usingDefaultLocation = false;
            this.locationTitleManuallyEdited = false;
            if (opts.keepText === true) {
                await this.fillInAddressNew();
                return;
            }
            const titleEl = this.getStyleElementById("title");
            if (route.name && titleEl && !this.gpsRouteIsPreview) {
                const max = Number(titleEl["max_char"]) || 0;
                this.userInput["title"] = max ? String(route.name).slice(0, max).trim() : route.name;
            }
            this.fillRouteStats(route);
            this.userInput["location"] = this.routeStatsText();
            const datedSource = route.source === "strava" || route.source === "gpx";
            const dateParts = datedSource && !this.gpsRouteIsPreview && route.event_date ? String(route.event_date).split("-").map(Number) : null;
            if (dateParts && dateParts.length === 3 && this.getStyleElementById("date")) {
                const eventDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                this.userInput["date"] = this.formatDate(eventDate);
                this.curFormattedDate = this.userInput["date"];
                this.datePickerValue = eventDate;
                this.dateFieldInstance = eventDate;
            }
            if (this.requiredValidationActive) {
                this.validateRequiredFields();
            }
            await this.fillInAddressNew();
        },
        routeLocationAvailable() {
            const pt = this.selectedProductType;
            return !!(
                pt &&
                pt["has_country_map"] !== true &&
                this.styleHasStaticMapImage()
            );
        },
        canAddRouteLocation() {
            return this.routeLocationAvailable();
        },
        refreshRoutePreviewCity() {
            if (!this.routeEndIsPreview) {
                return;
            }
            const preview = pickRoutePreviewCity(this.curAutoAddressLocation);
            if (preview) {
                this.routeEndLocation = preview;
            }
        },
        routeZoomMin() {
            return -Math.min(4, Math.max(0, Number(this.routeFitZoom) || 0));
        },
        routeLocationLabel(loc) {
            if (!loc) {
                return "";
            }
            const address = loc.address || {};
            const prefersCity = loc.resultType === "houseNumber" || loc.resultType === "street";
            const title = loc.title ? String(loc.title).trim() : "";
            const city = address.city ? String(address.city).trim() : "";
            const label = String(address.label || "").split(",")[0].trim();
            const text = (prefersCity && city) || title || city || label;
            return text.length > 28 ? text.slice(0, 28).trim() : text;
        },
        routeStyle() {
            const mapElement = this.getMapLocationSearchElement();
            const titleElement = this.getStyleElementById("title");
            const font = this.selectedFont || (titleElement && titleElement["font-family"]) || null;
            const jsonStyle = (mapElement && mapElement["route_style"]) || null;
            const style = TTRouteOverlay.resolveStyle(jsonStyle, font);
            if (this.gpsRouteMode() && this.selectedRouteColor) {
                style.color = this.selectedRouteColor;
            } else if (!jsonStyle || !jsonStyle.color) {
                const group = (this.mapMarkers && this.mapMarkers[this.selectedMapMarkerColor]) || {};
                style.color = group.route_color || group.color || TTRouteOverlay.defaultStyle.color;
            }
            style.loop = !this.gpsRouteMode() && this.routeLoop === true;
            return style;
        },
        setRouteLoop(on) {
            if (this.routeLoop === on) {
                return;
            }
            this.routeLoop = on;
            if (this.routeActive()) {
                this.fillInAddressNew();
            }
        },
        /** Mask image that shapes the current map slot (heart, circle, house, photo cutout), or null. */
        routeMaskUrl() {
            const el = this.getMapImageLayoutElement();
            if (!el) {
                return null;
            }
            if (el.type === "map_image_mask") {
                return el["clipping-mask-image"] || null;
            }
            if (el.type === "map_image_cutout") {
                const shape = el["use-photo-shape"] ? this.getSelectedPhotoShape() : null;
                return (shape && shape["map-mask"]) || el["clipping-mask-image"] || null;
            }
            return null;
        },
        loadRouteMask(url) {
            this._routeMaskGrids = this._routeMaskGrids || {};
            this._routeMaskLoads = this._routeMaskLoads || {};
            if (!url) {
                return Promise.resolve(null);
            }
            if (!this._routeMaskLoads[url]) {
                this._routeMaskLoads[url] = this.loadHtmlImage(url).then((img) => {
                    const grid = TTRouteOverlay.maskGrid(img, 128);
                    this._routeMaskGrids[url] = grid;
                    return grid;
                }).catch((err) => {
                    console.warn("Route mask failed to load; fitting to the slot instead.", err);
                    delete this._routeMaskLoads[url];
                    return null;
                });
            }
            return this._routeMaskLoads[url];
        },
        routeMaskGridSync() {
            const url = this.routeMaskUrl();
            return (url && this._routeMaskGrids && this._routeMaskGrids[url]) || null;
        },
        async computeRouteView(imageSize) {
            const path = this.routePath();
            const labels = this.gpsRouteMode()
                ? []
                : [this.routeLocationLabel(this.curAutoAddressLocation), this.routeLocationLabel(this.routeEndLocation)];
            const layout = this.getMapImageLayoutElement();
            const mask = await this.loadRouteMask(this.routeMaskUrl());
            const fit = TTRouteOverlay.fitRoute(path, imageSize.width, imageSize.height, {
                style: this.routeStyle(),
                slot: { left: layout["x-pos"], top: layout["y-pos"], width: layout["width"], height: layout["height"] },
                mask: mask,
                marker: this.routeMarkerSpec(imageSize.width),
                labels: labels,
                maxZoom: this.gpsRouteMode() ? 16 : Number(this.mapZoom) + 1,
            });
            this.routeFitZoom = fit.zoom;
            const offset = Math.max(this.routeZoomMin(), Math.min(0, Number(this.routeZoomOffset) || 0));
            return TTRouteOverlay.viewAt(fit.bounds, fit.zoom + offset, fit.target, imageSize.width, imageSize.height);
        },
        openRouteEndField() {
            this.showRouteEndField = true;
            if (!this.routeEndLocation) {
                const preview = pickRoutePreviewCity(this.curAutoAddressLocation);
                if (preview) {
                    this.routeEndLocation = preview;
                    this.routeEndIsPreview = true;
                    this.routeZoomOffset = 0;
                    this.fillInAddressNew();
                }
            }
            this.$nextTick(() => {
                const selector = this.usingDefaultLocation === false
                    ? ".tt-route-end-input"
                    : ".mapbox-location-search-mapbox-input";
                const input = document.querySelector(selector);
                if (input) {
                    input.focus();
                }
            });
        },
        clearRouteEnd() {
            if (this.routeEndSearchTimer != null) {
                clearTimeout(this.routeEndSearchTimer);
                this.routeEndSearchTimer = null;
            }
            this.routeEndLocation = null;
            this.routeEndIsPreview = false;
            this.routeEndSearchField = "";
            this.routeEndSuggestions = [];
            this.showRouteEndSuggestions = false;
            this.routeEndSearchLoading = false;
            this.routeEndSearchRequestAt = 0;
            this.showRouteEndField = false;
            this.routeZoomOffset = 0;
            this.routeView = null;
        },
        removeRouteEnd() {
            const hadRoute = this.routeActive();
            this.clearRouteEnd();
            if (hadRoute && this.usingDefaultLocation === true && !this.locationTitleManuallyEdited) {
                this.userInput['location'] = "";
            }
            if (hadRoute) {
                this.fillInAddressNew();
            }
        },
        async swapRouteEnds() {
            if (!this.routeActive() || !this.routeEndLocation || this.routeEndIsPreview) {
                return;
            }
            const first = this.curAutoAddressLocation;
            const firstText = this.mapboxMapSearchField;
            this.curAutoAddressLocation = this.routeEndLocation;
            this.mapboxMapSearchField = this.routeEndSearchField;
            this.routeEndLocation = first;
            this.routeEndSearchField = firstText;
            this.mapboxMapSearchFieldSuggestions = [];
            this.showMapboxSearchFieldSuggestions = false;
            this.routeEndSuggestions = [];
            this.showRouteEndSuggestions = false;
            this.rememberSelectedPlaceAnchor(this.curAutoAddressLocation);
            await this.fillInAddressNew();
        },
        debouncedRouteEndSearch(event) {
            if (event && (event.key === "Enter" || event.keyCode === 13)) {
                return;
            }
            if (event && event.target && typeof event.target.value === "string") {
                this.routeEndSearchField = event.target.value;
            }
            if (this.routeEndSearchTimer != null) {
                clearTimeout(this.routeEndSearchTimer);
            }
            this.routeEndSearchTimer = setTimeout(() => {
                this.routeEndSearchTimer = null;
                const input = document.querySelector(".tt-route-end-input");
                if (input && typeof input.value === "string") {
                    this.routeEndSearchField = input.value;
                }
                this.loadRouteEndSuggestions();
            }, 350);
        },
        async loadRouteEndSuggestions() {
            const requestAt = Date.now();
            this.routeEndSearchRequestAt = requestAt;
            const query = String(this.routeEndSearchField || "").trim();
            if (query.length < 3) {
                this.routeEndSuggestions = [];
                this.routeEndSearchLoading = false;
                return;
            }
            const coordinateMatch = parseLocationCoordinates(query);
            if (coordinateMatch) {
                const label = "Use coordinates: " + formatLocationCoordinatesLabel(coordinateMatch.lat, coordinateMatch.lng);
                this.routeEndSuggestions = [{
                    source: "coordinates",
                    title: label,
                    address: { label: label },
                    coordinates: { lat: coordinateMatch.lat, lng: coordinateMatch.lng },
                }];
                this.routeEndSearchLoading = false;
                this.showRouteEndSuggestions = true;
                return;
            }
            this.routeEndSearchLoading = true;
            this.showRouteEndSuggestions = true;
            try {
                const { suggestions } = await this.googlePlacesSearch(query);
                if (requestAt === this.routeEndSearchRequestAt) {
                    this.routeEndSuggestions = suggestions || [];
                }
            } catch (err) {
                console.error("loadRouteEndSuggestions", err);
                if (requestAt === this.routeEndSearchRequestAt) {
                    this.routeEndSuggestions = [];
                }
            } finally {
                if (requestAt === this.routeEndSearchRequestAt) {
                    this.routeEndSearchLoading = false;
                }
            }
        },
        confirmRouteEndCoordinates(event) {
            const suggestions = this.routeEndSuggestions;
            if (!this.showRouteEndSuggestions || !suggestions || suggestions.length !== 1 || suggestions[0].source !== "coordinates") {
                return;
            }
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            this.selectRouteEndSuggestion(suggestions[0]);
        },
        async selectRouteEndSuggestion(suggestion) {
            if (!suggestion) {
                return;
            }
            if (this.routeEndSearchTimer != null) {
                clearTimeout(this.routeEndSearchTimer);
                this.routeEndSearchTimer = null;
            }
            this.showRouteEndSuggestions = false;
            const requestAt = Date.now();
            this.routeEndSearchRequestAt = requestAt;
            this.routeEndSearchLoading = true;
            let loc = null;
            try {
                if (suggestion.source === "coordinates" && suggestion.coordinates) {
                    const lat = Number(suggestion.coordinates.lat);
                    const lng = Number(suggestion.coordinates.lng);
                    this.resetGooglePlacesAutocompleteSession();
                    loc = buildCoordinateLocation(lat, lng, await this.reverseGeocodeLatLng(lat, lng));
                } else if (suggestion.source === "google" && suggestion.google_place_id) {
                    loc = await this.fetchGooglePlaceDetails(suggestion.google_place_id);
                    if (loc) {
                        const title = suggestion.title != null ? String(suggestion.title).trim() : "";
                        const shortLabel = loc.address && loc.address.label ? String(loc.address.label).split(",")[0].trim() : "";
                        loc.title = title || shortLabel || "";
                    }
                }
            } finally {
                if (requestAt === this.routeEndSearchRequestAt) {
                    this.routeEndSearchLoading = false;
                }
            }
            if (requestAt !== this.routeEndSearchRequestAt || !loc || !loc.position) {
                return;
            }
            this.routeEndLocation = loc;
            this.routeEndIsPreview = false;
            this.routeEndSearchField = (loc.address && loc.address.label) || loc.title || "";
            this.routeEndSuggestions = [];
            this.routeZoomOffset = 0;
            this.locationTitleManuallyEdited = false;
            await this.fillInAddressNew();
        },
        /** Selected marker at the same logical size Mapbox uses for a single-address pin (84px tall, centred). */
        routeMarkerSpec(reqW) {
            if (this.gpsRouteMode()) {
                return { url: "", width: 0, height: 0, anchorY: "center", image: null };
            }
            const groups = this.mapMarkers || [];
            const group = groups[this.selectedMapMarkerColor] || groups[0] || {};
            const markers = group.markers || [];
            const entry = markers[this.selectedMapMarker] || markers[0] || null;
            const layout = this.getMapImageLayoutElement();
            const none = !entry || !entry.img || String(entry.img).indexOf("None-Pin") !== -1;
            const scale = layout && layout["width"] && reqW ? Number(layout["width"]) / Number(reqW) : 1;
            return {
                url: none ? "" : entry.img,
                width: none ? 0 : 100 * scale,
                height: none ? 0 : 84 * scale,
                anchorY: "center",
                image: null,
            };
        },
        /** Draws the route above `mapImg` once the label font and marker image are ready. */
        addRouteOverlay(element, mapImg, canvasId) {
            const view = this.routeView;
            if (!view || this.isDefaultMap === true || !this.routeActive()) {
                return;
            }
            const gps = this.gpsRouteMode();
            const path = this.routePath();
            const a = gps ? path[0] : this.curAutoAddressLocation.position;
            const b = gps ? path[path.length - 1] : this.routeEndLocation.position;
            const style = this.routeStyle();
            const clip = mapImg.clipPath;
            const opts = {
                start: { lat: Number(a.lat), lng: Number(a.lng), label: gps ? "" : this.routeLocationLabel(this.curAutoAddressLocation) },
                end: { lat: Number(b.lat), lng: Number(b.lng), label: gps ? "" : this.routeLocationLabel(this.routeEndLocation) },
                path: path,
                view: view,
                slot: { left: element["x-pos"], top: element["y-pos"], width: element["width"], height: element["height"] },
                style: style,
                clipElement: clip && clip.type === "image" && typeof clip.getElement === "function" ? clip.getElement() : null,
                mask: this.routeMaskGridSync(),
                marker: this.routeMarkerSpec(view.width),
            };
            this.beginCanvasAssetLoad(canvasId);
            Promise.all([
                this.waitForCanvasFont(style.labelFontFamily, false, false).catch(function () {}),
                opts.marker.url ? this.loadHtmlImage(opts.marker.url).catch(function () { return null; }) : Promise.resolve(null),
            ]).then((results) => {
                try {
                    if (canvasId !== this.canvasBuildVersion) {
                        return;
                    }
                    const index = this.canvasCreator.getObjects().indexOf(mapImg);
                    if (index === -1) {
                        return;
                    }
                    opts.marker.image = results[1];
                    this.canvasCreator.insertAt(TTRouteOverlay.build(fabric, opts), index + 1);
                } catch (err) {
                    console.error("Route overlay failed:", err);
                } finally {
                    this.finishCanvasAssetLoad(canvasId);
                }
            });
        },
        applyPinAdjustedLocation(lat, lng, area) {
            if (!this.curAutoAddressLocation || typeof this.curAutoAddressLocation !== "object") {
                this.curAutoAddressLocation = {
                    position: {},
                    address: {},
                    title: "",
                    resultType: "coordinate",
                };
            }
            const loc = this.curAutoAddressLocation;
            if (!loc.position || typeof loc.position !== "object") {
                loc.position = {};
            }
            if (!loc.address || typeof loc.address !== "object") {
                loc.address = {};
            }
            const keepBusinessName = this.shouldKeepBusinessTitle(lat, lng);
            const previousLabel = loc.address.label || "";
            loc.position.lat = lat;
            loc.position.lng = lng;

            if (area) {
                loc.address.city = area.city || "";
                loc.address.countryName = area.country || "";
                loc.address.district = area.stateName || "";
                loc.address.countryCode = area.countryCode || "";
                loc.address.state = area.stateName || "";
                loc.address.stateCode = area.stateCode || "";
            } else if (!keepBusinessName) {
                loc.address.city = "";
                loc.address.countryName = "";
                loc.address.district = "";
                loc.address.countryCode = "";
                loc.address.state = "";
                loc.address.stateCode = "";
            }

            if (!keepBusinessName) {
                const title = locationAreaTitle(loc.address, lat, lng);
                loc.title = title;
                loc.resultType = "coordinate";
                loc.address.label = title;
                this.locationSelectionKind = "coordinate";
                this.locationPlaceAnchor = null;
                this.mapboxMapSearchField = title;
            } else if (previousLabel) {
                loc.address.label = previousLabel;
            }

            this.usingDefaultLocation = false;
            this.defaultPreviewLocationLabel = "";
        },
        getMapImageLayoutElement() {
            const els = this.selectedProductType.styles[this.selectedProductTypeStyle].elements;
            const found = els.find(function (e) {
                return e["type"] === "map_image" || e["type"] === "country_map_image";
            });
            return found || els[1];
        },

        /**
         * Uniform scale so intrinsic SVG size fits inside the map slot (contain). Used when rasterising.
         * Prefer full map visibility over strict width-fill; may letterbox horizontally or vertically.
         */
        computeCountryMapContainRasterSize(slotW, slotH, intrinsicW, intrinsicH) {
            const sw = Number(slotW) > 0 ? Number(slotW) : 700;
            const sh = Number(slotH) > 0 ? Number(slotH) : 700;
            const nw = Number(intrinsicW) > 0 ? Number(intrinsicW) : 1;
            const nh = Number(intrinsicH) > 0 ? Number(intrinsicH) : 1;
            const scale = Math.min(sw / nw, sh / nh);
            return {
                w: Math.max(1, Math.round(nw * scale)),
                h: Math.max(1, Math.round(nh * scale)),
            };
        },

        /** Pixel box for marker_x/marker_y and pin_regions: centred fitted raster inside the country_map_image slot. */
        getCountryMapPinGeometry(mapBox, contentPixelW, contentPixelH) {
            const slotW = mapBox && mapBox["width"] ? Number(mapBox["width"]) : 700;
            const slotH = mapBox && mapBox["height"] ? Number(mapBox["height"]) : 700;
            const cw = Number(contentPixelW) > 0 ? Number(contentPixelW) : slotW;
            const ch = Number(contentPixelH) > 0 ? Number(contentPixelH) : slotH;
            const offsetX = (slotW - cw) / 2;
            const offsetY = (slotH - ch) / 2;
            return {
                contentLeft: mapBox["x-pos"] + offsetX,
                contentTop: mapBox["y-pos"] + offsetY,
                contentW: cw,
                contentH: ch,
                offsetX: offsetX,
                offsetY: offsetY,
            };
        },

        findCountryMapSvgElement() {
            const els = this.selectedProductType.styles[this.selectedProductTypeStyle].elements;
            return els.find(function (e) {
                return e["editable_type"] === "country-map-svg";
            }) || null;
        },

        /** Lowercase ISO 3166-1 alpha-2 from the location search; empty until a place resolves `countryCode`. */
        getCountryMapUserIsoKey() {
            const v = this.userInput["country"];
            if (v == null || String(v).trim() === "") {
                return "";
            }
            return String(v).trim().toLowerCase();
        },

        /**
         * Country row for map logic: optional match in product `countries[]`, else a synthetic `{ maps_iso }`
         * row so the CRM maps API drives geometry from `userInput.country`.
         */
        getCountryMapCountryRow(slug) {
            const cfg = this.findCountryMapSvgElement();
            if (!cfg) {
                return null;
            }
            const keyRaw = slug != null ? String(slug).trim().toLowerCase() : "";
            if (cfg.countries && cfg.countries.length) {
                const row = cfg.countries.find(function (c) {
                    if (!c) {
                        return false;
                    }
                    if (c.slug && String(c.slug).toLowerCase() === keyRaw) {
                        return true;
                    }
                    const mi = normalizeMapsIso(c.maps_iso);
                    return Boolean(mi && mi === normalizeMapsIso(keyRaw));
                });
                if (row) {
                    return row;
                }
            }
            const iso =
                (MAPS_SLUG_TO_ISO[keyRaw] || "") ||
                normalizeMapsIso(keyRaw) ||
                (keyRaw.length === 2 && /^[a-z]{2}$/.test(keyRaw) ? keyRaw : "");
            if (!iso) {
                return null;
            }
            return { slug: iso, maps_iso: iso };
        },

        /**
         * Resolves a subdivision code from Google Places (`stateCode`, `state`, etc.):
         * ISO 3166-2 suffix (`AU-QLD` → QLD), 2-letter (WA, CA), 3-letter (QLD, NSW, ACT).
         * Full US state names use US_STATE_NAME_TO_CODE.
         */
        deriveCountryMapStateCodeFromAddress(a) {
            if (!a || typeof a !== "object") {
                return "";
            }
            const countryIsoForNames =
                a.countryCode != null && String(a.countryCode).trim() !== ""
                    ? String(a.countryCode).trim().toUpperCase()
                    : "";
            const tryNameTables = function (raw) {
                if (!raw || String(raw).trim() === "") {
                    return "";
                }
                let t = String(raw).trim().replace(/\s+/g, " ");
                const isoSub = t.match(/^[a-z]{2}-([a-z0-9]{1,12})$/i);
                if (isoSub && isoSub[1]) {
                    t = isoSub[1];
                }
                if (/^[a-z]{2}$/i.test(t)) {
                    return t.toUpperCase();
                }
                if (/^[a-z]{3}$/i.test(t)) {
                    return t.toUpperCase();
                }
                if (t.length > 2) {
                    const lk = t.toLowerCase();
                    const us = US_STATE_NAME_TO_CODE[lk];
                    if (us) {
                        return us;
                    }
                    const auNm = AU_STATE_NAME_TO_CODE[lk];
                    if (auNm) {
                        if (countryIsoForNames === "AU" || lk !== "victoria") {
                            return auNm;
                        }
                    }
                }
                return "";
            };
            let cand = "";
            if (a.stateCode != null && String(a.stateCode).trim() !== "") {
                cand = String(a.stateCode).trim();
            } else if (a.state != null && String(a.state).trim() !== "") {
                cand = String(a.state).trim();
            }
            const primary = tryNameTables(cand);
            if (primary) {
                return primary;
            }
            const extraKeys = ["region", "district", "county"];
            for (let i = 0; i < extraKeys.length; i++) {
                const k = extraKeys[i];
                if (a[k] == null || String(a[k]).trim() === "") {
                    continue;
                }
                const fromExtra = tryNameTables(String(a[k]).trim());
                if (fromExtra) {
                    return fromExtra;
                }
            }
            return "";
        },

        getCountryMapResolvedShowMode() {
            const cfg = this.findCountryMapSvgElement();
            const def = (cfg && cfg.default_show_mode) || "country";
            const opts = cfg && cfg.show_mode_options;
            const raw =
                this.userInput && this.userInput["show_mode"] != null
                    ? String(this.userInput["show_mode"]).trim()
                    : "";
            if (!raw) {
                return def;
            }
            if (opts && opts.length && opts.indexOf(raw) === -1) {
                logCountryMapDebug("getCountryMapResolvedShowMode: invalid raw, using default", raw, "→", def);
                return def;
            }
            return raw;
        },

        getCountryMapEffectiveRow(slug, stateCodeUpper, showMode) {
            const countryRow = this.getCountryMapCountryRow(slug);
            if (!countryRow) {
                logCountryMapDebug("getCountryMapEffectiveRow: no country row for slug", slug);
                return { row: null, kind: "country", stateExcluded: false, countryRow: null };
            }
            const st = (stateCodeUpper && String(stateCodeUpper).trim().toUpperCase()) || "";
            const mode = showMode === "state" ? "state" : "country";
            logCountryMapDebug("getCountryMapEffectiveRow", {
                slug: slug,
                resolvedShowMode: showMode,
                mode: mode,
                stateCodeRaw: stateCodeUpper,
                stateCodeNorm: st || "(empty)",
                statesConfigured: countryRow.states ? countryRow.states.length : 0,
            });
            const excludedFn = function (code) {
                if (!code || !countryRow.excluded_state_codes) {
                    return false;
                }
                return countryRow.excluded_state_codes.some(function (c) {
                    return String(c).toUpperCase() === code;
                });
            };
            if (mode === "state" && st) {
                const mapsIsoForRow = resolveMapsIsoForCountryRow(countryRow, slug);
                const states = countryRow.states;
                if (states && states.length) {
                    const stateRow = states.find(function (s) {
                        return s && String(s.code || "").toUpperCase() === st;
                    });
                    if (
                        stateRow &&
                        (stateRow.svg || stateRow.svg_url || mapsIsoForRow)
                    ) {
                        logCountryMapDebug("→ using state SVG row", {
                            code: stateRow.code,
                            svg_url: stateRow.svg_url || stateRow.svg || "(CRM maps_api)",
                            maps_iso: mapsIsoForRow || "(none)",
                        });
                        return {
                            row: stateRow,
                            kind: "state",
                            stateExcluded: false,
                            countryRow: countryRow,
                        };
                    }
                    logCountryMapDebug(
                        "→ state mode: no matching state row or missing svg for code",
                        st,
                        "matchedStateRow:",
                        !!stateRow
                    );
                } else {
                    logCountryMapDebug("→ state mode: country.states[] empty");
                }
                const stateMapsSeg =
                    mapsIsoForRow && st ? normalizeMapsStatePathSegment(st, mapsIsoForRow) : "";
                if (mapsIsoForRow && stateMapsSeg) {
                    logCountryMapDebug("→ state via CRM maps API (no product states[] row)", {
                        mapsIso: mapsIsoForRow,
                        stateSeg: stateMapsSeg,
                        userStateCode: st,
                    });
                    return {
                        row: { code: st },
                        kind: "state",
                        stateExcluded: false,
                        countryRow: countryRow,
                    };
                }
                const ex = excludedFn(st);
                logCountryMapDebug("→ fallback to country row", { kind: "country", stateExcluded: ex });
                return {
                    row: countryRow,
                    kind: "country",
                    stateExcluded: ex,
                    countryRow: countryRow,
                };
            }
            if (mode === "state" && !st) {
                logCountryMapDebug(
                    "→ state mode but state_code is empty — need a searched US address with state; using country row"
                );
            }
            const exEnd = excludedFn(st);
            logCountryMapDebug("→ country mode row", { kind: "country", stateExcluded: exEnd });
            return {
                row: countryRow,
                kind: "country",
                stateExcluded: exEnd,
                countryRow: countryRow,
            };
        },

        getCountryMapMarkerGeometryRow(slug) {
            const showMode = this.getCountryMapResolvedShowMode();
            const st =
                this.userInput["state_code"] != null && String(this.userInput["state_code"]).trim() !== ""
                    ? String(this.userInput["state_code"]).trim().toUpperCase()
                    : "";
            const eff = this.getCountryMapEffectiveRow(slug, st, showMode);
            return eff.row;
        },

        updateCountryMapPinVisibility() {
            if (!this.selectedProductType || this.selectedProductType["has_country_map"] !== true) {
                return;
            }
            const slug = this.getCountryMapUserIsoKey();
            const showMode = this.getCountryMapResolvedShowMode();
            const st =
                this.userInput["state_code"] != null && String(this.userInput["state_code"]).trim() !== ""
                    ? String(this.userInput["state_code"]).trim().toUpperCase()
                    : "";
            const eff = this.getCountryMapEffectiveRow(slug, st, showMode);
            this.userInput["country_map_pin_visible"] = !(eff.kind === "country" && eff.stateExcluded);
        },

        getCountryBBoxForSlug(slug) {
            const keyRaw = slug != null ? String(slug).trim().toLowerCase() : "";
            const cfg = this.findCountryMapSvgElement();
            if (cfg && cfg.countries && cfg.countries.length) {
                const row = cfg.countries.find(function (c) {
                    if (!c) {
                        return false;
                    }
                    if (c.slug && String(c.slug).toLowerCase() === keyRaw) {
                        return true;
                    }
                    const mi = normalizeMapsIso(c.maps_iso);
                    return Boolean(mi && mi === normalizeMapsIso(keyRaw));
                });
                if (row && row.bbox) {
                    return row.bbox;
                }
            }
            if (keyRaw && COUNTRY_BBOX_FALLBACK[keyRaw]) {
                return COUNTRY_BBOX_FALLBACK[keyRaw];
            }
            const iso =
                (MAPS_SLUG_TO_ISO[keyRaw] || "") ||
                normalizeMapsIso(keyRaw) ||
                (keyRaw.length === 2 && /^[a-z]{2}$/.test(keyRaw) ? keyRaw : "");
            if (iso && COUNTRY_ISO_BBOX_FALLBACK[iso]) {
                return COUNTRY_ISO_BBOX_FALLBACK[iso];
            }
            return null;
        },

        /** Parsed `userInput.country_map_geo_bounds` (JSON from CRM maps `geo_bounds` after last raster refresh). */
        readCountryMapGeoBoundsFromUserInput() {
            const raw = this.userInput["country_map_geo_bounds"];
            if (raw == null) {
                return null;
            }
            if (typeof raw === "object" && raw !== null) {
                return normalizeMapsApiGeoBounds(raw);
            }
            const s = String(raw).trim();
            if (s === "") {
                return null;
            }
            try {
                return normalizeMapsApiGeoBounds(JSON.parse(s));
            } catch (e) {
                return null;
            }
        },

        /**
         * Maps WGS84 lat/lng to marker_x / marker_y (0–100) on the country map image.
         * Optional per-country `pin_regions` (see country-map-svg JSON): ordered list; first matching geographic `bbox`
         * maps linearly into that region's `rect` (percent of full image, top-left origin). Inset-style US artwork
         * requires pin_regions so Alaska/Hawaii insets align with searches.
         * If `pin_regions` is set but the point lies outside every region, falls back to the row's single `bbox` (same as countries without pin_regions).
         */
        latLngToCountryMarkerPercent(lat, lng, slug) {
            const lngF = parseFloat(lng);
            const latF = parseFloat(lat);
            const row = this.getCountryMapMarkerGeometryRow(slug);
            if (!row) {
                return { x: 50, y: 50 };
            }
            const clamp = function (v) {
                return Math.max(0, Math.min(100, v));
            };
            const mapLngLatToRectPercent = function (bbox, rect, lat0, lng0) {
                const spanX = bbox.east - bbox.west;
                const spanY = bbox.north - bbox.south;
                let nx = spanX > 0 ? (lng0 - bbox.west) / spanX : 0.5;
                let ny = spanY > 0 ? (bbox.north - lat0) / spanY : 0.5;
                nx = Math.max(0, Math.min(1, nx));
                ny = Math.max(0, Math.min(1, ny));
                return {
                    x: clamp(rect.x + nx * rect.w),
                    y: clamp(rect.y + ny * rect.h),
                };
            };
            if (row.pin_regions && row.pin_regions.length) {
                for (let i = 0; i < row.pin_regions.length; i++) {
                    const region = row.pin_regions[i];
                    if (!region || !region.bbox || !region.rect) {
                        continue;
                    }
                    const b = region.bbox;
                    if (
                        lngF >= b.west &&
                        lngF <= b.east &&
                        latF >= b.south &&
                        latF <= b.north
                    ) {
                        return mapLngLatToRectPercent(b, region.rect, latF, lngF);
                    }
                }
            }
            let bbox = row && row.bbox ? row.bbox : null;
            if (!bbox) {
                bbox = this.readCountryMapGeoBoundsFromUserInput();
            }
            if (!bbox) {
                bbox = this.getCountryBBoxForSlug(slug);
            }
            if (!bbox) {
                return { x: 50, y: 50 };
            }
            const spanX = bbox.east - bbox.west;
            const spanY = bbox.north - bbox.south;
            let xPct = spanX > 0 ? ((lngF - bbox.west) / spanX) * 100 : 50;
            let yPct = spanY > 0 ? ((bbox.north - latF) / spanY) * 100 : 50;
            return { x: clamp(xPct), y: clamp(yPct) };
        },

        bringCountryMapMarkerToFront() {
            const c = this.canvasCreator;
            if (!c || typeof c.getObjects !== "function") {
                return;
            }
            const objs = c.getObjects();
            for (let i = 0; i < objs.length; i++) {
                const o = objs[i];
                if (o && o.customType === "country_map_marker") {
                    c.bringToFront(o);
                }
            }
        },

        /** Overlays slot (outer) and pin_regions rects in content space when `naturalW`/`naturalH` of the map PNG are known. */
        addCountryMapDebugBoundsOverlay(mapLayoutElement, naturalW, naturalH) {
            if (!SHOW_COUNTRY_MAP_DEBUG_BOUNDS || !mapLayoutElement) {
                return;
            }
            const mapLeft = mapLayoutElement["x-pos"];
            const mapTop = mapLayoutElement["y-pos"];
            const mapW = mapLayoutElement["width"];
            const mapH = mapLayoutElement["height"];
            this.canvasCreator.add(
                new fabric.Rect({
                    left: mapLeft,
                    top: mapTop,
                    width: mapW,
                    height: mapH,
                    fill: "rgba(255, 170, 170, 0.08)",
                    stroke: "rgba(220, 70, 70, 0.9)",
                    strokeWidth: 2,
                    selectable: false,
                    evented: false,
                })
            );
            const nw = Number(naturalW) > 0 ? Number(naturalW) : mapW;
            const nh = Number(naturalH) > 0 ? Number(naturalH) : mapH;
            const geo = this.getCountryMapPinGeometry(mapLayoutElement, nw, nh);
            const slug = this.getCountryMapUserIsoKey();
            if (!slug) {
                return;
            }
            const row = this.getCountryMapMarkerGeometryRow(slug);
            if (!row || !row.pin_regions || !row.pin_regions.length) {
                return;
            }
            const self = this;
            row.pin_regions.forEach(function (region) {
                if (!region || !region.rect) {
                    return;
                }
                const r = region.rect;
                self.canvasCreator.add(
                    new fabric.Rect({
                        left: geo.contentLeft + (r.x / 100) * geo.contentW,
                        top: geo.contentTop + (r.y / 100) * geo.contentH,
                        width: (r.w / 100) * geo.contentW,
                        height: (r.h / 100) * geo.contentH,
                        fill: "rgba(255, 100, 100, 0.16)",
                        stroke: "rgba(190, 40, 40, 0.7)",
                        strokeWidth: 1,
                        selectable: false,
                        evented: false,
                    })
                );
            });
        },

        applyCountryColorToSvgString(svgStr, hex) {
            if (!svgStr || typeof svgStr !== "string") {
                return svgStr;
            }
            let out = svgStr;
            const hasClass =
                /class="[^"]*country-shape/.test(out) || /class='[^']*country-shape/.test(out);
            if (!hasClass) {
                out = out.replace(/<path(\s)/g, '<path class="country-shape"$1');
            }
            out = out.replace(/(<path\b[^>]*?)\sfill="url\([^"]*\)"/gi, "$1");
            out = out.replace(/(<path\b[^>]*?)\sfill='url\([^']*\)'/gi, "$1");
            const inject =
                "<style><![CDATA[.country-shape{fill:" +
                hex +
                "!important;stroke:none;}]]></style>";
            if (out.indexOf("</svg>") !== -1) {
                out = out.replace("</svg>", inject + "</svg>");
            }
            return out;
        },

        /** Hue angle in degrees [0,360) from #rrggbb for texture-mode `hue-rotate`. */
        hexColorToHueDegrees(hex) {
            const s = (hex && String(hex).trim()) || "#65bdb1";
            let r = 101,
                g = 189,
                b = 177;
            const m6 = /^#?([0-9a-f]{6})$/i.exec(s);
            const m3 = /^#?([0-9a-f]{3})$/i.exec(s);
            if (m6) {
                const n = parseInt(m6[1], 16);
                r = (n >> 16) & 255;
                g = (n >> 8) & 255;
                b = n & 255;
            } else if (m3) {
                const x = m3[1];
                r = parseInt(x[0] + x[0], 16);
                g = parseInt(x[1] + x[1], 16);
                b = parseInt(x[2] + x[2], 16);
            }
            const rn = r / 255;
            const gn = g / 255;
            const bn = b / 255;
            const max = Math.max(rn, gn, bn);
            const min = Math.min(rn, gn, bn);
            const d = max - min;
            let h = 0;
            if (d !== 0) {
                if (max === rn) {
                    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
                } else if (max === gn) {
                    h = ((bn - rn) / d + 2) / 6;
                } else {
                    h = ((rn - gn) / d + 4) / 6;
                }
            }
            return ((((h % 1) + 1) % 1) * 360);
        },

        /**
         * `hue-rotate` spins existing hues; for coloured PNG textures set JSON `texture_base_hue` (dominant hue of the asset in degrees).
         * Rotation applied = targetHue − baseHue so the result matches the user's picked hue.
         * If `textureBaseHueDeg` is omitted, uses absolute target hue (neutral / grayscale textures).
         */
        countryMapTextureHueRotateDegrees(targetHueDeg, textureBaseHueDeg) {
            const t = Number(targetHueDeg);
            const tn = ((t % 360) + 360) % 360;
            if (
                textureBaseHueDeg === undefined ||
                textureBaseHueDeg === null ||
                textureBaseHueDeg === ""
            ) {
                return tn;
            }
            const b = Number(textureBaseHueDeg);
            if (Number.isNaN(b)) {
                return tn;
            }
            return ((tn - b) % 360 + 360) % 360;
        },

        /** Pick `[{id,label,url}]` row from element config for current `country_map_texture_id`. */
        resolveCountryMapTextureEntry(cfg) {
            const textures = cfg && cfg.textures;
            if (!textures || !textures.length) {
                return null;
            }
            let id =
                this.userInput["country_map_texture_id"] != null
                    ? String(this.userInput["country_map_texture_id"]).trim()
                    : "";
            if (!id) {
                id =
                    cfg.default_texture_id != null && String(cfg.default_texture_id).trim() !== ""
                        ? String(cfg.default_texture_id).trim()
                        : textures[0].id;
            }
            const found = textures.find(function (t) {
                return t && String(t.id) === id;
            });
            return found || textures[0];
        },

        /**
         * SVG mask for texture compositing: white silhouette on transparent background (no user tint).
         */
        svgStringToAlphaMaskForCountryMap(svgStr) {
            if (!svgStr || typeof svgStr !== "string") {
                return svgStr;
            }
            let out = svgStr;
            const hasClass =
                /class="[^"]*country-shape/.test(out) || /class='[^']*country-shape/.test(out);
            if (!hasClass) {
                out = out.replace(/<path(\s)/g, '<path class="country-shape"$1');
            }
            out = out.replace(/(<path\b[^>]*?)\sfill="url\([^"]*\)"/gi, "$1");
            out = out.replace(/(<path\b[^>]*?)\sfill='url\([^']*\)'/gi, "$1");
            const inject =
                "<style><![CDATA[.country-shape{fill:#ffffff!important;stroke:none;}]]></style>";
            if (out.indexOf("</svg>") !== -1) {
                out = out.replace("</svg>", inject + "</svg>");
            }
            return out;
        },

        loadCountryMapImageAsync(src) {
            return new Promise(function (resolve, reject) {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = function () {
                    resolve(img);
                };
                img.onerror = function (e) {
                    reject(e);
                };
                img.src = src;
            });
        },

        /**
         * Texture PNG + hue + SVG alpha mask → same raster slot as solid mode.
         * @param {number|undefined|null} textureBaseHueDeg optional dominant hue of the PNG (see `textures[].texture_base_hue`).
         */
        async rasterCountryMapTextureMode(svgSource, textureUrl, slotW, slotH, colorHex, textureBaseHueDeg) {
            const maskSvg = this.svgStringToAlphaMaskForCountryMap(svgSource);
            const targetHue = this.hexColorToHueDegrees(colorHex);
            const hueDeg = this.countryMapTextureHueRotateDegrees(targetHue, textureBaseHueDeg);

            const maskImg = await new Promise(function (resolve, reject) {
                const img = new Image();
                const blob = new Blob([maskSvg], { type: "image/svg+xml;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                img.onload = function () {
                    URL.revokeObjectURL(url);
                    resolve(img);
                };
                img.onerror = function (e) {
                    URL.revokeObjectURL(url);
                    reject(e);
                };
                img.src = url;
            });

            const texImg = await this.loadCountryMapImageAsync(textureUrl);

            const nw = maskImg.naturalWidth > 0 ? maskImg.naturalWidth : 1;
            const nh = maskImg.naturalHeight > 0 ? maskImg.naturalHeight : 1;
            const out = this.computeCountryMapContainRasterSize(slotW, slotH, nw, nh);

            const canvas = document.createElement("canvas");
            canvas.width = out.w;
            canvas.height = out.h;
            const ctx = canvas.getContext("2d");
            const grayscale = isGrayscaleHexForCountryMap(colorHex);
            const useNativeFilter = countryMapDetectCanvasHueRotateFilterWorks();
            if (grayscale) {
                if (useNativeFilter) {
                    ctx.filter = "grayscale(1)";
                    ctx.drawImage(texImg, 0, 0, out.w, out.h);
                    ctx.filter = "none";
                } else {
                    ctx.drawImage(texImg, 0, 0, out.w, out.h);
                    try {
                        const imageData = ctx.getImageData(0, 0, out.w, out.h);
                        grayscaleCountryMapImageData(imageData);
                        ctx.putImageData(imageData, 0, 0);
                    } catch (err) {
                        logCountryMapDebug("rasterCountryMapTextureMode: pixel grayscale fallback failed", err);
                    }
                }
            } else if (useNativeFilter) {
                ctx.filter = "hue-rotate(" + hueDeg + "deg)";
                ctx.drawImage(texImg, 0, 0, out.w, out.h);
                ctx.filter = "none";
            } else {
                ctx.drawImage(texImg, 0, 0, out.w, out.h);
                try {
                    const imageData = ctx.getImageData(0, 0, out.w, out.h);
                    hueRotateCountryMapImageData(imageData, hueDeg);
                    ctx.putImageData(imageData, 0, 0);
                } catch (err) {
                    logCountryMapDebug("rasterCountryMapTextureMode: pixel hue fallback failed", err);
                }
            }
            ctx.globalCompositeOperation = "destination-in";
            ctx.drawImage(maskImg, 0, 0, out.w, out.h);
            ctx.globalCompositeOperation = "source-over";

            this.userInput["country-map-image"] = canvas.toDataURL("image/png");
            this.userInput["country-map-content-w"] = String(out.w);
            this.userInput["country-map-content-h"] = String(out.h);
        },

        /**
         * Rasterises country or state SVG into `userInput['country-map-image']` via CRM maps API (optional overrides from product `countries[]`).
         * Aspect ratio preserved; size is "contain" inside the map slot (see computeCountryMapContainRasterSize).
         */
        async refreshCountryMapRaster() {
            if (!this.selectedProductType || this.selectedProductType["has_country_map"] !== true) {
                return;
            }
            const slug = this.getCountryMapUserIsoKey();
            const cfg = this.findCountryMapSvgElement();
            if (!cfg) {
                console.warn("refreshCountryMapRaster: missing country-map-svg element");
                return;
            }
            if (!slug) {
                logCountryMapDebug("refreshCountryMapRaster: no country ISO from search yet");
                return;
            }
            const showMode = this.getCountryMapResolvedShowMode();
            const st =
                this.userInput["state_code"] != null && String(this.userInput["state_code"]).trim() !== ""
                    ? String(this.userInput["state_code"]).trim().toUpperCase()
                    : "";
            const eff = this.getCountryMapEffectiveRow(slug, st, showMode);
            const row = eff.row;
            if (!row) {
                console.warn("refreshCountryMapRaster: unknown slug or missing row", slug);
                return;
            }
            this.updateCountryMapPinVisibility();
            const countryRow = eff.countryRow;
            const mapsIso = resolveMapsIsoForCountryRow(countryRow, slug);
            logCountryMapDebug("refreshCountryMapRaster", {
                kind: eff.kind,
                stateExcluded: eff.stateExcluded,
                country_map_pin_visible: this.userInput["country_map_pin_visible"],
                rowLabel: row.code || row.label || slug,
                mapsIsoResolved: mapsIso || undefined,
                hasSvgUrl: !!row.svg_url,
                hasInlineSvg: !!row.svg,
            });
            const color =
                (this.userInput["country_color"] && String(this.userInput["country_color"]).trim()) ||
                "#65bdb1";
            const mapEl = this.getMapImageLayoutElement();
            const slotW = mapEl && mapEl["width"] ? Number(mapEl["width"]) : 700;
            const slotH = mapEl && mapEl["height"] ? Number(mapEl["height"]) : 700;
            let svgSource = "";
            let apiMapsMetaForGeo = null;
            if (mapsIso) {
                this.userInput["country_map_geo_bounds"] = "";
                const stateForApi =
                    eff.kind === "state" &&
                    row &&
                    row.code != null &&
                    String(row.code).trim() !== ""
                        ? String(row.code).trim()
                        : "";
                try {
                    const apiRes = await fetchCountryMapFromApi({
                        baseUrl: DEFAULT_MAPS_API_BASE,
                        countryIso: mapsIso,
                        stateCode: stateForApi || undefined,
                        slotWidth: slotW,
                    });
                    apiMapsMetaForGeo = apiRes.meta;
                    if (apiRes.svgString) {
                        svgSource = apiRes.svgString;
                    }
                } catch (e) {
                    logCountryMapDebug("refreshCountryMapRaster: maps API failed", e);
                    console.warn("refreshCountryMapRaster: maps API failed", e);
                }
            }
            const geoFromApi = normalizeMapsApiGeoBounds(apiMapsMetaForGeo);
            if (geoFromApi) {
                this.userInput["country_map_geo_bounds"] = JSON.stringify(geoFromApi);
            }
            if (!svgSource && row.svg) {
                svgSource = row.svg;
            }
            if (!svgSource && row.svg_url) {
                try {
                    const res = await fetch(row.svg_url, {
                        mode: "cors",
                        credentials: "omit",
                    });
                    if (!res.ok) {
                        console.warn("refreshCountryMapRaster: svg_url HTTP", res.status, row.svg_url);
                        return;
                    }
                    svgSource = await res.text();
                } catch (e) {
                    console.warn("refreshCountryMapRaster: fetch svg_url failed", e);
                    return;
                }
            }
            if (!svgSource) {
                return;
            }

            const fillStyleRaw = this.userInput["country_map_fill_style"];
            const fillStyle =
                fillStyleRaw != null && String(fillStyleRaw).trim() !== ""
                    ? String(fillStyleRaw).trim().toLowerCase()
                    : "solid";

            if (fillStyle === "texture" && cfg.textures && cfg.textures.length) {
                const tex = this.resolveCountryMapTextureEntry(cfg);
                if (tex && tex.url) {
                    try {
                        await this.rasterCountryMapTextureMode(
                            svgSource,
                            tex.url,
                            slotW,
                            slotH,
                            color,
                            tex.texture_base_hue
                        );
                        this.updateCountryMapPinVisibility();
                        return;
                    } catch (e) {
                        console.warn("refreshCountryMapRaster: texture mode failed, using solid fill", e);
                    }
                }
            }

            const tinted = this.applyCountryColorToSvgString(svgSource, color);
            const self = this;
            await new Promise(function (resolve, reject) {
                const img = new Image();
                const blob = new Blob([tinted], { type: "image/svg+xml;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                img.onload = function () {
                    try {
                        const nw = img.naturalWidth > 0 ? img.naturalWidth : 1;
                        const nh = img.naturalHeight > 0 ? img.naturalHeight : 1;
                        const out = self.computeCountryMapContainRasterSize(slotW, slotH, nw, nh);
                        const canvas = document.createElement("canvas");
                        canvas.width = out.w;
                        canvas.height = out.h;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0, out.w, out.h);
                        self.userInput["country-map-image"] = canvas.toDataURL("image/png");
                        self.userInput["country-map-content-w"] = String(out.w);
                        self.userInput["country-map-content-h"] = String(out.h);
                        self.updateCountryMapPinVisibility();
                        URL.revokeObjectURL(url);
                        resolve();
                    } catch (err) {
                        URL.revokeObjectURL(url);
                        reject(err);
                    }
                };
                img.onerror = function (e) {
                    URL.revokeObjectURL(url);
                    reject(e);
                };
                img.src = url;
            });
        },

        async fillInAddressNew() {
            this.refreshRoutePreviewCity();
            var place = "";
            var latlong = "";
            var longlat = "";

            if(this.curAutoAddressLocation === null){
                let usedProductJsonDefault = false;
                let locJsonFallback = null;
                if (
                    this.selectedProductType &&
                    this.selectedProductType["has_map_field"] === true &&
                    this.selectedProductType["has_country_map"] !== true
                ) {
                    const locJson = getMergedDefaultMapLocationFromStyleProduct(
                        this.selectedProductType,
                        this.selectedProductTypeStyle
                    );
                    if (locJson && locJson.position != null) {
                        locJsonFallback = locJson;
                        const latVal = locJson.position.lat;
                        const lngVal = locJson.position.lng;
                        latlong = latVal + ", " + lngVal;
                        longlat = lngVal + "," + latVal;
                        this.globalLat = latVal;
                        this.globalLon = lngVal;
                        usedProductJsonDefault = true;
                    }
                }
                if (!usedProductJsonDefault) {
                    const gc = getDefaultGoldCoastLatLngStrings();
                    latlong = gc.latlong;
                    longlat = gc.longlat;
                    this.globalLat = gc.latStr;
                    this.globalLon = gc.lngStr;
                } else if (locJsonFallback) {
                    this.userInput["long-lat"] = latlong;
                    const latNum = Number(locJsonFallback.position.lat);
                    const lngNum = Number(locJsonFallback.position.lng);
                    if (!isNaN(latNum)) {
                        this.userInput["lat"] = Math.abs(latNum.toFixed(3)).toString() + "°";
                        this.userInput["lat-e"] = latNum.toFixed(3) < 0 ? "S" : "N";
                    }
                    if (!isNaN(lngNum)) {
                        this.userInput["long"] = Math.abs(lngNum.toFixed(3)).toString() + "°";
                        this.userInput["long-pm"] = lngNum.toFixed(3) < 0 ? "W" : "E";
                    }
                    const addr = locJsonFallback.address || {};
                    const city = addr.city || "";
                    const country = addr.countryName || "";
                    var tempLocation = country;
                    if (city !== "") {
                        tempLocation = city + ", " + country;
                    }
                    const placeTile = locJsonFallback.title || "";
                    const rt = locJsonFallback.resultType || "";
                    if (this.usingDefaultLocation !== true && !this.locationTitleManuallyEdited && !this.gpsRouteMode()) {
                        if (placeTile !== "") {
                            if (rt === "houseNumber" || rt === "street" || rt === "locality") {
                                this.userInput["location"] = this.clampLocationTitle(tempLocation);
                            } else {
                                this.userInput["location"] = this.clampLocationTitle(placeTile);
                            }
                        } else {
                            this.userInput["location"] = this.clampLocationTitle(tempLocation);
                        }
                    }
                }

                this.isDefaultMap = true;
            }else{
                this.isDefaultMap = false;
                //console.log(place.name);
                latlong = this.curAutoAddressLocation['position']['lat'] + ", " + this.curAutoAddressLocation['position']['lng'];
                longlat = this.curAutoAddressLocation['position']['lng'] + "," + this.curAutoAddressLocation['position']['lat'];

                this.globalLat = this.curAutoAddressLocation['position']['lat'];
                this.globalLon = this.curAutoAddressLocation['position']['lng'];

                var city = "";
                var country = "";
                this.userInput['long-lat'] = latlong;

                this.userInput['lat'] =  Math.abs(this.curAutoAddressLocation['position']['lat'].toFixed(3)).toString()+'°';

                if(this.curAutoAddressLocation['position']['lat'].toFixed(3) < 0){
                    this.userInput['lat-e'] = "S";
                }else{
                    this.userInput['lat-e'] = "N";
                }

                this.userInput['long'] = Math.abs(this.curAutoAddressLocation['position']['lng'].toFixed(3)).toString()+'°';

                if(this.curAutoAddressLocation['position']['lng'].toFixed(3) < 0){
                    this.userInput['long-pm'] = "W";
                }else{
                    this.userInput['long-pm'] = "E";
                }

                var city = this.curAutoAddressLocation['address']['city'];
                var country = this.curAutoAddressLocation['address']['countryName'];
                var placeTile = this.curAutoAddressLocation['title'];
                var district = this.curAutoAddressLocation['address']['district'];

                var tempLocation = country;
                if (city != "") {
                    tempLocation = city + ", " + country;
                }

                if (this.usingDefaultLocation !== true && !this.locationTitleManuallyEdited && !this.gpsRouteMode()) {
                    if(placeTile !== ""){
                        if(this.curAutoAddressLocation['resultType'] === "houseNumber" || this.curAutoAddressLocation['resultType'] === "street" || this.curAutoAddressLocation['resultType'] === "locality"){
                            this.userInput['location'] = this.clampLocationTitle(tempLocation);
                        }else{
                            // if(district !== "" && district !== undefined){
                            //     this.userInput['location'] = placeTile+" "+district;
                            // }else{
                            //     this.userInput['location'] = placeTile;
                            // }

                            this.userInput['location'] = this.clampLocationTitle(placeTile);
                        }
                    }else{
                        this.userInput['location'] = this.clampLocationTitle(tempLocation);
                    }
                }

            }

            const mapLayout = this.getMapImageLayoutElement();
            var imageSizeTemp = this.resizeDimensions(
                mapLayout["width"],
                mapLayout["height"],
                maxDimension = 1280
            );

            if (this.selectedProductType["has_country_map"] === true) {
                if (
                    this.curAutoAddressLocation &&
                    this.curAutoAddressLocation["address"] &&
                    this.curAutoAddressLocation["address"]["countryCode"] != null &&
                    String(this.curAutoAddressLocation["address"]["countryCode"]).trim() !== ""
                ) {
                    const code = String(this.curAutoAddressLocation["address"]["countryCode"]).trim();
                    this.userInput["country"] = normalizeMapsIso(code) || "";
                } else if (
                    this.curAutoAddressLocation &&
                    this.curAutoAddressLocation.resultType === "coordinate"
                ) {
                    this.userInput["country"] = "";
                }
                if (this.curAutoAddressLocation && this.curAutoAddressLocation["address"]) {
                    const a = this.curAutoAddressLocation["address"];
                    logCountryMapDebug("fillInAddressNew: address fields", {
                        stateCode: a.stateCode,
                        state: a.state,
                        district: a.district,
                        region: a.region,
                        countryCode: a.countryCode,
                    });
                    const st = this.deriveCountryMapStateCodeFromAddress(a);
                    this.userInput["state_code"] = st;
                    logCountryMapDebug("fillInAddressNew: userInput.state_code =", st || "(empty)");
                } else {
                    this.userInput["state_code"] = "";
                    logCountryMapDebug("fillInAddressNew: no address object — state_code cleared");
                }
                if (!String(this.userInput["state_code"] || "").trim()) {
                    this.userInput["show_mode"] = "country";
                } else if (!String(this.userInput["show_mode"] || "").trim()) {
                    const cmCfg = this.findCountryMapSvgElement();
                    const dm =
                        cmCfg &&
                        cmCfg.default_show_mode != null &&
                        String(cmCfg.default_show_mode).trim() !== ""
                            ? String(cmCfg.default_show_mode).trim()
                            : "country";
                    const opts = cmCfg && cmCfg.show_mode_options;
                    this.userInput["show_mode"] =
                        opts && opts.length && opts.indexOf(dm) === -1 ? opts[0] : dm;
                }
                this.updateCountryMapPinVisibility();
                await this.refreshCountryMapRaster();
                if (!this.isDefaultMap && this.globalLat !== "" && this.globalLon !== "") {
                    const slug = this.getCountryMapUserIsoKey();
                    if (slug) {
                        const pct = this.latLngToCountryMarkerPercent(
                            parseFloat(this.globalLat),
                            parseFloat(this.globalLon),
                            slug
                        );
                        this.userInput["marker_x"] = pct.x;
                        this.userInput["marker_y"] = pct.y;
                    }
                }
                this.refreshLocationVisual();
                return;
            }

            this.mapDrawnAtHalfSize = this.mapRequestUsesHalfSize();
            var tempMapSrc;
            if (this.routeActive()) {
                this.routeView = await this.computeRouteView(imageSizeTemp);
                tempMapSrc = this.assignRouteStaticMapUrls(this.routeView, this.userInput['map_color']);
                if (!this.locationTitleManuallyEdited && !this.gpsRouteMode()) {
                    this.userInput['location'] = this.clampLocationTitle(
                        this.routeLocationLabel(this.curAutoAddressLocation) + " to " + this.routeLocationLabel(this.routeEndLocation)
                    );
                }
            } else {
                this.routeView = null;
                tempMapSrc = this.assignStaticMapUrls(longlat, this.userInput['map_color'], imageSizeTemp['width']+"x"+imageSizeTemp['height'], this.mapZoom);
            }

            if(this.isDefaultMap === true){
                this.defaultMap = tempMapSrc;
            }else{
                this.userInput['map-image'] = tempMapSrc;
            }

            // this.mapStyleThumbnails['dark-map'] = this.returnMapSRC(tempThumbMapSRC, "dark-map", "188x125", this.mapZoom - 2, false);
            // this.mapStyleThumbnails['standard'] = this.returnMapSRC(tempThumbMapSRC, "standard", "188x125", this.mapZoom - 2, false);
            // this.mapStyleThumbnails['satellite'] = this.returnMapSRC(tempThumbMapSRC, "satellite", "188x125", this.mapZoom - 2, false);
            // this.mapStyleThumbnails['light-map'] = this.returnMapSRC(tempThumbMapSRC, "light-map", "188x125", this.mapZoom - 2, false);

            this.refreshLocationVisual();

        },
        currentNightSkyKey() {
            const date = this.nightSkyDate instanceof Date ? this.nightSkyDate.getTime() : "";
            const background = (typeof nightSkyConfig !== "undefined" && nightSkyConfig.background)
                ? nightSkyConfig.background.fill
                : this.selectedSkyColor;
            return [
                date,
                this.globalLat,
                this.globalLon,
                this.nightSkyGrid ? 1 : 0,
                this.nightSkyConstellations ? 1 : 0,
                this.nightSkyMilkyWay ? 1 : 0,
                background || ""
            ].join("|");
        },
        updateSkyImage() {
            const runKey = this.currentNightSkyKey();
            const runId = (this.skyUpdateSeq = (this.skyUpdateSeq || 0) + 1);
            if (cachedNightSky && cachedNightSky.key === runKey && cachedNightSky.image) {
                this.clearNightSkyCaptureTimers();
                this.userInput.sky_map = cachedNightSky.image;
                this.fillCanvas();
                return;
            }
            const skyAlreadyShowing = typeof this.userInput.sky_map === "string" && this.userInput.sky_map.indexOf("data:image") === 0;
            const cutoutSky = this.cutoutPhotoSource() === "sky";
            if (!skyAlreadyShowing && !cutoutSky) {
                if (cachedNightSky && cachedNightSky.image) {
                    this.userInput.sky_map = cachedNightSky.image;
                }
                this.fillCanvas();
            }

            this.clearNightSkyCaptureTimers();
            this.nightSkyActiveRun = { runId: runId, runKey: runKey };
            ensureCelestial().then(() => {
                if (runId !== this.skyUpdateSeq) {
                    return;
                }
                if (!this.wantsSky()) {
                    return;
                }
                if (typeof Celestial === "undefined" || typeof Celestial.display !== "function") {
                    return;
                }
                // skyview is created inside display(). The first display must run before skyview.
                if (typeof Celestial.skyview !== "function") {
                    Celestial.display(nightSkyConfig);
                }
                Celestial.skyview(
                    {
                        date: this.nightSkyDate,
                        location:[this.globalLat, this.globalLon]
                    }
                );

                if (!this.nightSkyCallbackRegistered) {
                    this.nightSkyCallbackRegistered = true;
                    Celestial.addCallback(() => {
                        const run = this.nightSkyActiveRun;
                        if (!run || run.runId !== this.skyUpdateSeq || !this.wantsSky()) {
                            return;
                        }
                        this.replaceNightSkyImage(run.runId, run.runKey);
                    });
                }
                Celestial.display(nightSkyConfig);
                this.nightSkyFallbackTimer = setTimeout(() => {
                    this.nightSkyFallbackTimer = null;
                    this.captureNightSkyImage(runId, runKey);
                }, 2500);
            }).catch((err) => {
                console.error("Night sky library failed to load", err);
                if (runId === this.skyUpdateSeq) {
                    this.canvasLoader = false;
                }
            });
        },
        clearNightSkyCaptureTimers() {
            if (this.nightSkySettleTimer) {
                clearTimeout(this.nightSkySettleTimer);
                this.nightSkySettleTimer = null;
            }
            if (this.nightSkyFallbackTimer) {
                clearTimeout(this.nightSkyFallbackTimer);
                this.nightSkyFallbackTimer = null;
            }
        },
        replaceNightSkyImage(runId, runKey) {
            // Celestial fires a callback per data layer; capture once they stop arriving.
            if (this.nightSkySettleTimer) {
                clearTimeout(this.nightSkySettleTimer);
            }
            this.nightSkySettleTimer = setTimeout(() => {
                this.nightSkySettleTimer = null;
                this.captureNightSkyImage(runId, runKey);
            }, 400);
        },
        captureNightSkyImage(runId, runKey) {
            if (runId !== this.skyUpdateSeq || !this.wantsSky()) {
                return;
            }
            if (cachedNightSky && cachedNightSky.key === runKey && this.userInput.sky_map === cachedNightSky.image) {
                return;
            }
            const tempCanvas = document.querySelector('#nightSkyCanvas canvas');
            if (!tempCanvas) {
                console.warn("Canvas not found!");
                return;
            }
            this.clearNightSkyCaptureTimers();
            const image = tempCanvas.toDataURL('image/png');
            cachedNightSky = { key: runKey, image: image };
            this.userInput.sky_map = image;
            this.fillCanvas();
            this.repaintCanvasBuild(this.canvasBuildVersion);
        },
        showNightSkyTime(){
            const now = new Date();
            let hour = now.getHours();
            const minute = now.getMinutes();
            const period = hour >= 12 ? "PM" : "AM";

            // Convert to 12-hour format
            hour = hour % 12;
            hour = hour === 0 ? 12 : hour;

            this.timeSelector.hour = hour;
            this.timeSelector.minute = String(minute).padStart(2, "0");
            this.timeSelector.period = period;

            this.updateNightSky();
        },
        updateNightSky() {
            this.loadingCanvasImage = true;

            const cutoutSky = this.cutoutPhotoSource() === "sky";
            let dateSource = this.curFormattedDate;
            if (dateSource === "" && cutoutSky) {
                const dateEl = this.getStyleElementById("date");
                dateSource = (this.userInput && this.userInput.date) || (dateEl && dateEl.initial) || "";
            }

            if (dateSource !== "") {
                if (cutoutSky) {
                    const parsed = this.parseMoonPhaseLocalDate(dateSource);
                    if (parsed) {
                        this.nightSkyDate = new Date(parsed.getTime());
                    }
                } else {
                    //this.nightSkyDate = new Date(this.dateFieldInstance.parseDate(this.curFormattedDate, this.userSelectedDateFormat));
                    this.nightSkyDate = dateFns.parse(dateSource, this.userSelectedDateFormat, new Date());
                }
            }

            if (cutoutSky) {
                if (!(this.nightSkyDate instanceof Date) || isNaN(this.nightSkyDate.getTime())) {
                    this.nightSkyDate = new Date();
                }
                this.nightSkyDate.setHours(21, 0, 0, 0);
                if (typeof nightSkyConfig !== "undefined" && nightSkyConfig.background) {
                    nightSkyConfig.background.fill = "#151726";
                }
            } else if(this.timeSelector.showTime === true){
                const { hour, minute, period } = this.timeSelector;

                if (hour !== "" && minute !== "") {
                    let parsedHour = parseInt(hour, 10);
                    const parsedMinute = parseInt(minute, 10);

                    // Convert to 24-hour format
                    if (period === "PM" && parsedHour < 12) {
                        parsedHour += 12;
                    } else if (period === "AM" && parsedHour === 12) {
                        parsedHour = 0;
                    }

                    this.nightSkyDate.setHours(parsedHour);
                    this.nightSkyDate.setMinutes(parsedMinute);
                    this.nightSkyDate.setSeconds(0); // Optional: reset seconds
                    this.nightSkyDate.setMilliseconds(0); // Optional: reset milliseconds
                }
            }

            if (!cutoutSky && typeof nightSkyConfig !== "undefined" && nightSkyConfig.background && this.selectedSkyColor) {
                nightSkyConfig.background.fill = this.selectedSkyColor;
            }

            nightSkyConfig.lines.graticule.show = this.nightSkyGrid;
            nightSkyConfig.constellations.lines = this.nightSkyConstellations;
            nightSkyConfig.mw.show = this.nightSkyMilkyWay;

            this.updateSkyImage();
        },
        updateSkyColor(sky_color){
            this.selectedSkyColor = sky_color;
            nightSkyConfig.background.fill = this.selectedSkyColor;
            this.updateNightSky();
        },
        updateNightSkyConstellations(){
            this.nightSkyConstellations = !this.nightSkyConstellations;
            this.updateNightSky();
        },
        updateNightSkyMilkyWay(){
            this.nightSkyMilkyWay = !this.nightSkyMilkyWay;
            this.updateNightSky();
        },
        updateNightSkyGrid(){
            this.nightSkyGrid = !this.nightSkyGrid;
            this.updateNightSky();
        },
        refreshLocationVisual(options) {
            if (this.wantsSky()) {
                this.updateNightSky();
            } else if (this.wantsMoon()) {
                this.updateMoonPhase();
            } else if (options && options.textOnly === true) {
                const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
                const hasCalendar = elems.some((el) => el && el.type === "custom_calendar");
                if (hasCalendar || !this.refreshCanvasTextObjects()) {
                    this.fillCanvas();
                }
            } else {
                this.fillCanvas();
            }
        },
        onOptionalTimeToggle() {
            if (this.selectedProductType && this.selectedProductType["has_moon_field"] === true) {
                if (this.timeSelector.showTime) {
                    this.timeSelector.hour = 9;
                    this.timeSelector.minute = "00";
                    this.timeSelector.period = "PM";
                }
                this.updateMoonPhase();
                return;
            }
            this.showNightSkyTime();
        },
        onOptionalTimeChange() {
            this.refreshLocationVisual({ textOnly: true });
        },
        resolveMoonPhaseDate() {
            let dateStr = this.curFormattedDate || (this.userInput && this.userInput.date) || "";
            if (!String(dateStr).trim() && this.cutoutPhotoSource() === "moon") {
                const dateEl = this.getStyleElementById("date");
                dateStr = (dateEl && dateEl.initial) || "";
            }
            return this.parseMoonPhaseLocalDate(dateStr) || new Date();
        },
        parseMoonPhaseLocalDate(dateStr) {
            const raw = String(dateStr || "").trim();
            if (!raw) {
                return null;
            }
            const formats = [
                this.userSelectedDateFormat,
                this.userDateFormat,
                "dd/MM/yyyy",
                "d MMMM yyyy",
                "MMMM d, yyyy",
                "yyyy-MM-dd"
            ].filter(Boolean);
            for (let i = 0; i < formats.length; i++) {
                try {
                    const parsed = dateFns.parse(raw, formats[i], new Date());
                    if (parsed instanceof Date && !isNaN(parsed.getTime())) {
                        return parsed;
                    }
                } catch (e) {
                    // try next format
                }
            }
            const native = new Date(raw);
            return native instanceof Date && !isNaN(native.getTime()) ? native : null;
        },
        getMoonPhaseLocalClock() {
            if (
                this.timeSelector.showTime === true &&
                this.timeSelector.hour !== "" &&
                this.timeSelector.minute !== ""
            ) {
                let hour = parseInt(this.timeSelector.hour, 10);
                const minute = parseInt(this.timeSelector.minute, 10);
                const period = this.timeSelector.period;
                if (period === "PM" && hour < 12) {
                    hour += 12;
                } else if (period === "AM" && hour === 12) {
                    hour = 0;
                }
                return { hour: hour, minute: minute, usedDefaultTime: false };
            }
            return { hour: 21, minute: 0, usedDefaultTime: true };
        },
        async updateMoonPhase() {
            if (!this.wantsMoon()) {
                return;
            }
            try {
                await ensureMoonPhaseLibs();
            } catch (err) {
                console.error("Moon Phase modules failed to load", err);
                this.canvasLoader = false;
                this.loadingCanvasImage = false;
                return;
            }
            if (!this.wantsMoon()) {
                return;
            }
            if (typeof MoonPhaseAstronomy === "undefined" || typeof MoonPhaseRenderer === "undefined") {
                console.error("Moon Phase modules are not loaded");
                this.canvasLoader = false;
                this.loadingCanvasImage = false;
                return;
            }

            const lat = parseFloat(this.globalLat);
            const lng = parseFloat(this.globalLon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                console.error("Moon Phase is missing latitude/longitude");
                return;
            }

            const parsedDate = this.resolveMoonPhaseDate();
            if (!parsedDate) {
                console.error("Moon Phase could not parse the selected date:", this.curFormattedDate);
                return;
            }

            const clock = this.getMoonPhaseLocalClock();
            const seq = (this.moonPhaseUpdateSeq = (this.moonPhaseUpdateSeq || 0) + 1);
            this.loadingCanvasImage = true;
            this.canvasLoader = true;

            try {
                const phase = await MoonPhaseAstronomy.calculateMoonPhase({
                    latitude: lat,
                    longitude: lng,
                    year: parsedDate.getFullYear(),
                    month: parsedDate.getMonth() + 1,
                    day: parsedDate.getDate(),
                    hour: clock.hour,
                    minute: clock.minute,
                    usedDefaultTime: clock.usedDefaultTime
                });

                if (seq !== this.moonPhaseUpdateSeq) {
                    return;
                }

                const nextKey = [
                    phase.dateTimeUtc,
                    phase.illumination,
                    phase.waxing,
                    phase.orientationAngle,
                    MoonPhaseRenderer.PRINT_SIZE,
                    MoonPhaseRenderer.DARK_LEVEL
                ].join("|");

                this.userInput.moon_datetime_utc = phase.dateTimeUtc;
                this.userInput.moon_illumination = String(Math.round(phase.illumination * 10000) / 10000);
                this.userInput.moon_phase_name = phase.phaseName;
                this.userInput.timeZoneId = phase.timeZoneId;

                if (nextKey === this.lastMoonPhaseKey && this.userInput.moon_image && String(this.userInput.moon_image).indexOf("data:image") === 0) {
                    this.canvasLoader = false;
                    this.fillCanvas();
                    return;
                }

                const dataUrl = await MoonPhaseRenderer.renderMoonPhase(phase);
                if (seq !== this.moonPhaseUpdateSeq) {
                    return;
                }
                this.lastMoonPhaseKey = nextKey;
                this.userInput.moon_image = dataUrl;
                this.canvasLoader = false;
                this.fillCanvas();
                this.repaintCanvasBuild(this.canvasBuildVersion);
            } catch (error) {
                console.error("Moon Phase calculation failed:", error);
                this.canvasLoader = false;
            }
        },
        assignStaticMapUrls(longlat, color, size, zoom) {
            const markers = this.mapMarkers[this.selectedMapMarkerColor]["markers"];
            let marker = markers[this.selectedMapMarker];
            if (this.gpsRouteMode()) {
                const none = markers.find(function (entry) {
                    return entry && String(entry.img || "").indexOf("None-Pin") !== -1;
                });
                if (none) {
                    marker = none;
                }
            }
            const previewPin = encodeURIComponent(marker.imgPreview || marker.img);
            const printPin = encodeURIComponent(marker.img);
            this.mapPrintSrc = this.returnMapSRC(longlat, color, size, zoom, printPin, true, true);
            this.prefetchPrintMap(this.mapPrintSrc);
            return this.returnMapSRC(longlat, color, size, zoom, previewPin, true, false);
        },
        assignRouteStaticMapUrls(view, color) {
            this.mapPrintSrc = this.returnRouteMapSRC(view, color, true);
            this.prefetchPrintMap(this.mapPrintSrc);
            return this.returnRouteMapSRC(view, color, false);
        },
        /** Static map at the exact route view, with no pin. `?tt-route-debug=1` adds Mapbox pins at both ends to check alignment. */
        returnRouteMapSRC(view, color, retina) {
            if (!this.mapboxStaticStyleId(color) || !view || !Number.isFinite(Number(view.width))) {
                return "";
            }
            let overlay = "";
            if (this.queryArray && this.queryArray["tt-route-debug"] === "1" && this.routeActive()) {
                const path = this.gpsRouteMode() ? this.routePath() : null;
                const a = path ? path[0] : this.curAutoAddressLocation.position;
                const b = path ? path[path.length - 1] : this.routeEndLocation.position;
                overlay = "pin-s+0044ff(" + a.lng + "," + a.lat + "),pin-s+00aa44(" + b.lng + "," + b.lat + ")/";
            }
            return "https://api.mapbox.com/styles/v1/transparent-tracks/" + this.mapboxStaticStyleId(color) +
                "/static/" + overlay + view.lng + "," + view.lat + "," + view.zoom + ",0,0/" +
                view.width + "x" + view.height + (retina ? "@2x" : "") +
                "?access_token=" + this.getMapboxToken() + "&attribution=false&logo=false";
        },
        mapboxStaticStyleId(color) {
            var tempMapStyle = "";
            if (color === "shift-worker") {
                tempMapStyle = "cmuo31vvz002a01sp8vup1w07";
                //Thumb:
                //tempMapStyle = "clzgiklt500j101r19lte8l0f";
            } else if (color === "standard") {
                tempMapStyle = "cmuo32ibk002g01ss5nq663as";
                //Thumb:
                //tempMapStyle = "clzgifnxs00id01r29u5a92et";
            } else if (color === "satellite") {
                tempMapStyle = "cmuo31h9o002901sphl5lf7j3";
                //Thumb:
                //tempMapStyle = "clzgis2z900j201r1bznib1vi";
            } else if (color === "light-map") {
                tempMapStyle = "cmuo309gq002f01ss3tvkduz8";
                //Thumb:
                //tempMapStyle = "clzgiv54x00ih01r4hdw2az8b";
            } else if (color === "standard-blue-no-text") {
                tempMapStyle = "cmuo32vwn002b01sp0t210olc";
            } else if (color === "light-green") {
                tempMapStyle = "cmuo2zvgk002801sp4neu9iag";
            } else if (color === "faded-blue") {
                tempMapStyle = "cmuo2z2sr002q01smggzhhilo";
                //Thumb:
                //tempMapStyle = "clzgjw1gq00ij01r4g9vu9cjc";
            } else if (color === "red-hue") {
                tempMapStyle = "cmuo30ogz002301r7a45igeip";
                //Thumb:
                //tempMapStyle = "clzgiy7lz00ie01r20kjgblod";
            }
            return tempMapStyle;
        },
        returnMapSRC(longlat, color, size, zoom, marker, showLabels, retina) {

            var tempMapStyle = this.mapboxStaticStyleId(color);
            var mapWidth = parseFloat(String(size || "").split("x")[0]);
            if (!tempMapStyle || !Number.isFinite(mapWidth)) {
                return "";
            }
            var tempMapSize = "";

            // tempMapStyle = tempMapStyle+"&scale=2&style=feature:transit%7Cvisibility:off"
            tempMapSize = "&size=" + size;

            var key = "pk.eyJ1IjoidHJhbnNwYXJlbnQtdHJhY2tzIiwiYSI6ImNsemZhdDZpYzB5OWIyaW9zczU4cnJzM2EifQ.3EXobnlO4v2VK7O_lBh5Wg";

            // The *-2026 styles are built for one zoom level closer and twice the
            // pixel size, so this image covers the same ground as the old 640px map.
            var staticZoom = Number(zoom) + 1;

            // @2x doubles the image. The 168px pin matches the 84px preview on that image,
            // including 1280x1280@2x.
            const scale = retina !== false ? "@2x" : "";
            return "https://api.mapbox.com/styles/v1/transparent-tracks/"+tempMapStyle+"/static/url-"+marker+"("+longlat+")"+"/"+longlat+","+staticZoom+",0,0/"+size+scale+"?access_token="+key+"&attribution=false&logo=false";

            // if (showLabels === true) {
            //     return mapSRC + tempMapStyle + tempMapSize + "&zoom=" + zoom;
            // } else {
            //     return mapSRC + tempMapStyle + tempMapSize + "&zoom=" + zoom + "&style=element:labels%7Cvisibility:off";
            // }

        },
        getMapboxToken() {
            // You already use the same key in returnMapSRC() :contentReference[oaicite:6]{index=6}
            return "pk.eyJ1IjoidHJhbnNwYXJlbnQtdHJhY2tzIiwiYSI6ImNsemZhdDZpYzB5OWIyaW9zczU4cnJzM2EifQ.3EXobnlO4v2VK7O_lBh5Wg";
        },

        getGooglePlacesApiKey() {
            return "AIzaSyAWDWKKLS8hnej_FOBMEExVF2frP3jWsSY";
        },

        getMapboxStyleUrlFromColor(color) {
            // Mirror your returnMapSRC mapping :contentReference[oaicite:7]{index=7}
            let styleId = "clzfd8oby00hm01r18u2z36zx"; // standard default
            if (color === "shift-worker") styleId = "cma3chuqb000i01snektsf7fb";
            else if (color === "standard") styleId = "clzfd8oby00hm01r18u2z36zx";
            else if (color === "satellite") styleId = "clzfd9bv800g101ohd6350suy";
            else if (color === "light-map") styleId = "clzfd70l900g001oh5517egc6";
            else if (color === "standard-blue-no-text") styleId = "clzgegrjx00i201r24ifid9q6";
            else if (color === "light-green") styleId = "clzg7keki00gr01ohf7183i4a";
            else if (color === "faded-blue") styleId = "cm18jrpyc02t701rb9h2g3gjw";
            else if (color === "red-hue") styleId = "cma1zwyyi008e01sp1cxb5dkq";

            return `mapbox://styles/transparent-tracks/${styleId}`;
        },

        openPinAdjustModal() {
            // ensure we have a starting coordinate (fillInAddressNew sets these) :contentReference[oaicite:8]{index=8}
            const lat = parseFloat(this.globalLat);
            const lng = parseFloat(this.globalLon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            this.pinAdjustStartLat = lat;
            this.pinAdjustStartLng = lng;

            this.showPinAdjustModal = true;

            this.$nextTick(() => {
                ensureMapboxGl().then(() => {
                    if (!this.showPinAdjustModal) {
                        return;
                    }
                    this.initPinAdjustMap(lat, lng);
                }).catch((err) => {
                    console.error("Mapbox failed to load", err);
                });
            });
        },
        initPinAdjustMap(lat, lng) {
            mapboxgl.accessToken = this.getMapboxToken();

            // destroy old instance if reopening
            if (this.pinAdjustMap) {
                this.pinAdjustMap.remove();
                this.pinAdjustMap = null;
                this.pinAdjustMarker = null;
            }

            const styleUrl = this.getMapboxStyleUrlFromColor(this.userInput["map_color"]);

            this.pinAdjustMap = new mapboxgl.Map({
                container: "pinAdjustMap",
                style: styleUrl,
                center: [lng, lat],
                zoom: this.mapZoom || 12
            });

            this.pinAdjustMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

            // UX: disable rotation so it feels simpler on mobile
            this.pinAdjustMap.dragRotate.disable();
            this.pinAdjustMap.touchZoomRotate.disableRotation();

            const markerUrl =
                this.mapMarkers[this.selectedMapMarkerColor]
                    .markers[this.selectedMapMarker]
                    .img;

            const el = document.createElement("img");
            el.src = markerUrl;
            el.style.width = "42px";
            el.style.height = "42px";
            el.style.transform = "translate(-50%, -100%)"; // pin tip = location
            el.style.userSelect = "none";
            el.draggable = false;

            this.pinAdjustMarker = new mapboxgl.Marker({
                element: el,
                draggable: true
            })
                .setLngLat([lng, lat])
                .addTo(this.pinAdjustMap);

        },
        resetPinAdjustModal() {
            if (this.pinAdjustConfirmInFlight) return;
            if (!this.pinAdjustMarker || this.pinAdjustStartLat == null || this.pinAdjustStartLng == null) return;
            this.pinAdjustMarker.setLngLat([this.pinAdjustStartLng, this.pinAdjustStartLat]);
            this.pinAdjustMap?.flyTo({ center: [this.pinAdjustStartLng, this.pinAdjustStartLat], essential: true });
        },
        async confirmPinAdjustModal() {
            if (!this.pinAdjustMarker || this.pinAdjustConfirmInFlight) return;

            const { lng, lat } = this.pinAdjustMarker.getLngLat();
            const startLat = Number(this.pinAdjustStartLat);
            const startLng = Number(this.pinAdjustStartLng);
            const moved = !Number.isFinite(startLat)
                || !Number.isFinite(startLng)
                || haversineMeters(startLat, startLng, lat, lng) > LOCATION_PIN_MOVE_EPSILON_METERS;
            if (!moved) {
                this.closePinAdjustModal();
                return;
            }

            this.pinAdjustConfirmInFlight = true;
            let area = null;
            try {
                area = await this.reverseGeocodeLatLng(lat, lng);
            } catch (err) {
                console.warn("confirmPinAdjustModal reverse geocode", err);
                area = null;
            }
            if (!this.showPinAdjustModal) {
                this.pinAdjustConfirmInFlight = false;
                return;
            }

            try {
                this.applyPinAdjustedLocation(lat, lng, area);
                await this.fillInAddressNew();
                if (this.showPinAdjustModal) {
                    this.closePinAdjustModal();
                }
            } finally {
                this.pinAdjustConfirmInFlight = false;
            }
        },

        closePinAdjustModal() {
            this.showPinAdjustModal = false;

            if (this.pinAdjustMap) {
                this.pinAdjustMap.remove();
                this.pinAdjustMap = null;
                this.pinAdjustMarker = null;
            }
        },
        countVisibleSizes(products){
            var i = 0;

            for (const product of products) {
                if(product['display'] === true){
                    i = i+1;
                }
            }
            return i;
        },
        beginCanvasAssetLoad(canvasId) {
            if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                return;
            }
            this.pendingCanvasAssets += 1;
            this.loadingCanvasImage = true;
        },
        finishCanvasAssetLoad(canvasId) {
            if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                return;
            }
            this.pendingCanvasAssets = Math.max(0, this.pendingCanvasAssets - 1);
            if (this.pendingCanvasAssets > 0) {
                return;
            }
            this.settleCanvasThenComplete(canvasId);
        },
        collectCanvasFontRequests() {
            const elems = this.selectedProductType?.styles?.[this.selectedProductTypeStyle]?.elements || [];
            const requests = {};
            const typekitFamily = {
                expressway: true,
                "farnham-headline": true,
                "manufacturing-consent": true,
                "house-of-cards": true,
            };
            const mark = (family, italic, mustLoad) => {
                if (!family) {
                    return;
                }
                if (!requests[family]) {
                    requests[family] = { italic: false, mustLoad: false };
                }
                if (italic) {
                    requests[family].italic = true;
                }
                if (mustLoad) {
                    requests[family].mustLoad = true;
                }
            };
            let anyItalic = false;
            elems.forEach((el) => {
                if (!el) {
                    return;
                }
                const italic = el["font-style"] === "italic";
                if (italic) {
                    anyItalic = true;
                }
                if (el["lock_font"] === true && el["font-family"]) {
                    mark(el["font-family"], italic, true);
                } else if (!this.selectedFont && el["font-family"]) {
                    mark(el["font-family"], italic, !!typekitFamily[el["font-family"]]);
                }
            });
            if (this.selectedFont) {
                mark(this.selectedFont, anyItalic, !!typekitFamily[this.selectedFont]);
            }
            return Object.keys(requests).map((family) => ({
                family: family,
                italic: requests[family].italic,
                mustLoad: requests[family].mustLoad,
            }));
        },
        canvasFontFaceSpec(family, italic) {
            return (italic ? "italic " : "") + '80px "' + family + '"';
        },
        waitForCanvasFont(family, italic, mustLoad) {
            if (!document.fonts || typeof document.fonts.load !== "function") {
                return Promise.resolve();
            }
            const spec = this.canvasFontFaceSpec(family, italic);
            const started = Date.now();
            const attempt = () => document.fonts.load(spec).catch(function () {
                return [];
            }).then((faces) => {
                if (faces && faces.length) {
                    return;
                }
                // Typekit has not registered the family yet. load() resolves
                // with an empty list in that case, so a cold visit would
                // measure a fallback face and keep that position.
                if (!mustLoad || Date.now() - started > 5000) {
                    return;
                }
                return new Promise(function (resolve) {
                    setTimeout(resolve, 50);
                }).then(attempt);
            });
            return attempt();
        },
        preloadCanvasFonts() {
            const requests = this.collectCanvasFontRequests();
            const loaders = [];
            if (document.fonts && typeof document.fonts.load === "function") {
                requests.forEach((request) => {
                    loaders.push(this.waitForCanvasFont(request.family, false, request.mustLoad));
                    if (request.italic) {
                        loaders.push(this.waitForCanvasFont(request.family, true, request.mustLoad));
                    }
                });
            }
            return Promise.all(loaders);
        },
        clearCanvasFontMeasureCache() {
            if (typeof fabric !== "undefined" && fabric.charWidthsCache) {
                fabric.charWidthsCache = {};
            }
        },
        loadCanvasFontsForGeneration(canvasId) {
            this.beginCanvasAssetLoad(canvasId);
            const finish = () => {
                this.finishCanvasAssetLoad(canvasId);
            };
            this.preloadCanvasFonts().then(() => {
                if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                    finish();
                    return;
                }
                requestAnimationFrame(() => {
                    if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                        finish();
                        return;
                    }
                    this.clearCanvasFontMeasureCache();
                    this.refreshCanvasTextObjects();
                    finish();
                });
            }).catch(finish);
        },
        remeasureCanvasTextObjects() {
            if (!this.canvasCreator) {
                return;
            }
            this.canvasCreator.getObjects().forEach((obj) => {
                if (!obj || (obj.type !== "text" && obj.type !== "i-text" && obj.type !== "textbox")) {
                    return;
                }
                const left = obj.left;
                const top = obj.top;
                const originX = obj.originX;
                const originY = obj.originY;
                if (typeof obj.initDimensions === "function") {
                    obj.initDimensions();
                }
                obj.set({
                    left: left,
                    top: top,
                    originX: originX,
                    originY: originY
                });
                if (typeof obj.setCoords === "function") {
                    obj.setCoords();
                }
            });
        },
        watchCanvasImage(img, canvasId) {
            this.beginCanvasAssetLoad(canvasId);
            const prevLoad = img.onload;
            const prevErr = img.onerror;
            img.onload = (e) => {
                try {
                    if (typeof prevLoad === "function") {
                        prevLoad.call(img, e);
                    }
                } finally {
                    this.finishCanvasAssetLoad(canvasId);
                }
            };
            img.onerror = (e) => {
                try {
                    if (typeof prevErr === "function") {
                        prevErr.call(img, e);
                    }
                } finally {
                    this.finishCanvasAssetLoad(canvasId);
                }
            };
        },
        setCanvasImageSrc(img, src, canvasId) {
            if (src == null || src === "" || src === "null") {
                return;
            }
            this.watchCanvasImage(img, canvasId);
            img.src = src;
        },
        settleCanvasThenComplete(canvasId) {
            const run = () => {
                if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                    return;
                }
                if (this.pendingCanvasAssets > 0) {
                    return;
                }
                if (this.canvasCreator) {
                    this.canvasCreator.renderOnAddRemove = true;
                    this.remeasureCanvasTextObjects();
                    this.repaintCanvasBuild(canvasId);
                }
                requestAnimationFrame(() => {
                    if (canvasId != null && canvasId !== this.canvasBuildVersion) {
                        return;
                    }
                    if (this.pendingCanvasAssets > 0) {
                        return;
                    }
                    this.loadingCanvasImageComplete(canvasId);
                });
            };
            run();
        },
        repaintCanvasBuild(version) {
            const paint = () => {
                if (version != null && version !== this.canvasBuildVersion) {
                    return;
                }
                if (!this.canvasCreator) {
                    return;
                }
                if (typeof this.canvasCreator.requestRenderAll === "function") {
                    this.canvasCreator.requestRenderAll();
                } else {
                    this.canvasCreator.renderAll();
                }
            };
            paint();
            requestAnimationFrame(paint);
        },
        loadingCanvasImageComplete(version){
            if (version != null && version !== this.canvasBuildVersion) {
                return;
            }
            if (this.pendingCanvasAssets > 0) {
                return;
            }
            this.loadingCanvasImage = false;
            this.canvasLoader = false;
            this.rescale_canvas_if_needed();
            const settledVersion = version;
            requestAnimationFrame(() => {
                if (settledVersion != null && settledVersion !== this.canvasBuildVersion) {
                    return;
                }
                this.preLoadedImage();
                this.schedulePreviewWarmup();
            });
            if(this.awaitingATC === true){
                console.log("Loaded now add to cart");
                this.awaitingATC = false;
                this.addToCart();
            }
            if(this.awaitingDesignUpdate === true){
                console.log("Loaded now add to cart");
                this.saveOrderDesign();
                this.awaitingDesignUpdate = false;
            }
        },
        handleScroll() {
            const el = this.$refs.scrollArea;

            if (el) {
                this.showTopBlur = el.scrollTop > 10;

                const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
                this.showBottomBlur = !isAtBottom;

                // Hide scroll hint when user starts scrolling
                this.showScrollHint = el.scrollTop < 10;
            }
        },
        rescale_canvas_if_needed() {
            if (!this.canvasCreator) {
                return;
            }

            const canvasEl = this.$refs.canvas;
            const w = canvasEl ? canvasEl.clientWidth : 0;
            const baseH = (this.selectedProductType && this.selectedProductType['canvas_height']) || 1400;
            const scale = w / 1000;
            const vpt = this.canvasCreator.viewportTransform;
            const vptBroken = !vpt || vpt.some((n) => !isFinite(n));

            // setZoom(0) when the inner canvas is not laid out yet inverts to NaN
            // translates and every later draw goes off-canvas.
            if (!w || !isFinite(scale) || scale <= 0) {
                if (vptBroken) {
                    const z = this.canvasCreator.getZoom();
                    const safeZ = isFinite(z) && z > 0 ? z : 1;
                    this.canvasCreator.setViewportTransform([safeZ, 0, 0, safeZ, 0, 0]);
                    this.canvasCreator.renderAll();
                }
                return;
            }

            const printW = PRINT_EXPORT_WIDTH_PX;
            const printH = Math.round(baseH * printW / 1000);
            const zoom = printW / 1000;
            const canvas = this.canvasCreator;
            const cssW = w + "px";
            const cssH = (baseH * scale) + "px";
            const backingW = typeof canvas.getWidth === "function" ? canvas.getWidth() : 0;
            const backingH = typeof canvas.getHeight === "function" ? canvas.getHeight() : 0;
            const backingMatches = Math.round(backingW) === printW && Math.round(backingH) === printH;
            const zoomMatches = !vptBroken && vpt[0] === zoom && vpt[1] === 0 && vpt[2] === 0 && vpt[3] === zoom && vpt[4] === 0 && vpt[5] === 0;
            const cssTargets = [canvas.lowerCanvasEl, canvas.upperCanvasEl, canvas.wrapperEl];
            const cssMatches = cssTargets.every((el) => !el || (el.style.width === cssW && el.style.height === cssH));

            // Keyboard and size changes keep the print bitmap. Repainting it
            // only happens when the backing store or zoom still needs to be set.
            if (backingMatches && zoomMatches && cssMatches) {
                return;
            }
            if (backingMatches && zoomMatches) {
                cssTargets.forEach((el) => {
                    if (!el) {
                        return;
                    }
                    el.style.width = cssW;
                    el.style.height = cssH;
                });
                canvas.calcOffset();
                return;
            }

            canvas.enableRetinaScaling = false;
            canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
            if (typeof canvas.setDimensions === "function") {
                canvas.setDimensions({ width: printW, height: printH }, { backstoreOnly: true });
            } else {
                canvas.setWidth(printW);
                canvas.setHeight(printH);
            }
            cssTargets.forEach((el) => {
                if (!el) {
                    return;
                }
                el.style.width = cssW;
                el.style.height = cssH;
            });
            canvas.calcOffset();
            const previewCtx = typeof canvas.getContext === "function" ? canvas.getContext() : canvas.contextContainer;
            if (previewCtx) {
                previewCtx.imageSmoothingEnabled = true;
                previewCtx.imageSmoothingQuality = "high";
            }
            canvas.renderAll();
        },
        /** Multiplier that rasterizes the current canvas width to PRINT_EXPORT_WIDTH_PX (A5 portrait, 300 PPI). */
        getPrintExportMultiplier() {
            const w = this.canvasCreator ? this.canvasCreator.getWidth() : 0;
            if (!w || !isFinite(w)) {
                return 4;
            }
            if (Math.round(w) === PRINT_EXPORT_WIDTH_PX) {
                return 1;
            }
            // Canvas pixel size truncates toward zero. 1748/w can be a hair under
            // 1748 after float error (e.g. width 1440 → 1747). Half a pixel of
            // headroom still truncates to 1748, and height stays 2447.
            return (PRINT_EXPORT_WIDTH_PX + 0.5) / w;
        },
        getPreviewExportMultiplier() {
            const w = this.canvasCreator ? this.canvasCreator.getWidth() : 0;
            if (!w || w <= 1000) {
                return 1;
            }
            return 1000 / w;
        },
        loadHtmlImage(src) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error("print map failed"));
                img.src = src;
            });
        },
        prefetchPrintMap(src) {
            if (!src) {
                return;
            }
            const existing = this._printMapPrefetch;
            if (existing && existing.src === src && !existing.failed) {
                return;
            }
            const img = new Image();
            img.crossOrigin = "Anonymous";
            const record = { src: src, img: img, failed: false, pending: null };
            record.pending = new Promise((resolve, reject) => {
                img.onload = () => resolve(img);
                img.onerror = () => {
                    record.failed = true;
                    reject(new Error("print map failed"));
                };
            });
            record.pending.catch(() => {});
            this._printMapPrefetch = record;
            img.src = src;
        },
        takePrintMapImage(src) {
            this.prefetchPrintMap(src);
            const record = this._printMapPrefetch;
            if (!record || record.src !== src) {
                return this.loadHtmlImage(src);
            }
            return record.pending;
        },
        resampleMapForPrint(source, slotW, slotH, clip) {
            const baked = document.createElement("canvas");
            baked.width = slotW;
            baked.height = slotH;
            const ctx = baked.getContext("2d");
            if (!ctx) {
                return null;
            }
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(source, 0, 0, slotW, slotH);
            const maskEl = clip && (typeof clip.getElement === "function" ? clip.getElement() : clip._element);
            const maskWidth = maskEl && (maskEl.naturalWidth || maskEl.width);
            const maskHeight = maskEl && (maskEl.naturalHeight || maskEl.height);
            if (maskEl && maskWidth && maskHeight) {
                ctx.globalCompositeOperation = "destination-in";
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(maskEl, 0, 0, slotW, slotH);
                return { canvas: baked, clipped: true };
            }
            return { canvas: baked, clipped: false };
        },
        withDesignSizeCanvas(render) {
            const canvas = this.canvasCreator;
            if (!canvas) {
                return render();
            }
            const caches = (typeof canvas.getObjects === "function" ? canvas.getObjects() : []).map((obj) => ({
                obj: obj,
                objectCaching: obj.objectCaching,
            }));
            this._suppressExportRedraw = true;
            try {
                caches.forEach(({ obj }) => {
                    obj.objectCaching = false;
                    obj.dirty = true;
                    obj._removeCacheCanvas();
                });
                return render();
            } finally {
                this._suppressExportRedraw = false;
                caches.forEach(({ obj, objectCaching }) => {
                    obj.objectCaching = objectCaching;
                    obj.dirty = true;
                    obj._removeCacheCanvas();
                });
            }
        },
        applyMapBitmap(obj, bitmap, scaleX, scaleY) {
            if (typeof obj.setElement === "function") {
                obj.setElement(bitmap);
            } else {
                obj._element = bitmap;
                obj._originalElement = bitmap;
                obj.width = bitmap.naturalWidth || bitmap.width || obj.width;
                obj.height = bitmap.naturalHeight || bitmap.height || obj.height;
            }
            obj.scaleX = scaleX;
            obj.scaleY = scaleY;
            obj.dirty = true;
        },
        printExportZoom() {
            const raw = this.canvasCreator && this.canvasCreator.getZoom();
            if (isFinite(raw) && raw > 0) {
                return raw;
            }
            return PRINT_EXPORT_WIDTH_PX / 1000;
        },
        printSlotPixels(obj, zoom) {
            const displayWidth = obj.width * obj.scaleX;
            const displayHeight = obj.height * obj.scaleY;
            return {
                displayWidth: displayWidth,
                displayHeight: displayHeight,
                slotW: Math.max(1, Math.round(displayWidth * zoom)),
                slotH: Math.max(1, Math.round(displayHeight * zoom)),
            };
        },
        snapshotForPrintSwap(obj) {
            return {
                obj: obj,
                element: typeof obj.getElement === "function" ? obj.getElement() : obj._element,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                objectCaching: obj.objectCaching,
                imageSmoothing: obj.imageSmoothing,
                clipPath: obj.clipPath || null,
                cropX: obj.cropX || 0,
                cropY: obj.cropY || 0,
                width: obj.width,
                height: obj.height,
            };
        },
        restorePrintSwap(snap) {
            if (snap.element) {
                this.applyMapBitmap(snap.obj, snap.element, snap.scaleX, snap.scaleY);
            } else {
                snap.obj.scaleX = snap.scaleX;
                snap.obj.scaleY = snap.scaleY;
            }
            snap.obj.cropX = snap.cropX;
            snap.obj.cropY = snap.cropY;
            snap.obj.width = snap.width;
            snap.obj.height = snap.height;
            snap.obj.clipPath = snap.clipPath;
            snap.obj.objectCaching = snap.objectCaching;
            snap.obj.imageSmoothing = snap.imageSmoothing !== false;
            snap.obj.dirty = true;
            snap.obj._removeCacheCanvas();
        },
        /**
         * Cover-crop a photo into the print slot and punch its mask in canvas
         * space. Export then draws that bitmap with no Fabric clip, which is the
         * path that was leaving the circle empty.
         */
        bakeClippedPhotoForPrint(source, slotW, slotH, clip) {
            const srcW = source && (source.naturalWidth || source.width);
            const srcH = source && (source.naturalHeight || source.height);
            if (!source || !srcW || !srcH || !slotW || !slotH) {
                return null;
            }
            const maxSide = 4096;
            const down = Math.min(1, maxSide / Math.max(srcW, srcH));
            const midW = Math.max(1, Math.round(srcW * down));
            const midH = Math.max(1, Math.round(srcH * down));
            const mid = document.createElement("canvas");
            mid.width = midW;
            mid.height = midH;
            const midCtx = mid.getContext("2d");
            const baked = document.createElement("canvas");
            baked.width = slotW;
            baked.height = slotH;
            const ctx = baked.getContext("2d");
            if (!midCtx || !ctx) {
                return null;
            }
            try {
                midCtx.imageSmoothingEnabled = true;
                midCtx.imageSmoothingQuality = "high";
                midCtx.drawImage(source, 0, 0, midW, midH);

                const cover = Math.max(slotW / midW, slotH / midH);
                let cropW = slotW / cover;
                let cropH = slotH / cover;
                let sx = (midW - cropW) / 2;
                let sy = (midH - cropH) / 2;
                if (cropW > midW) {
                    cropW = midW;
                }
                if (cropH > midH) {
                    cropH = midH;
                }
                if (sx < 0) {
                    sx = 0;
                }
                if (sy < 0) {
                    sy = 0;
                }
                if (sx + cropW > midW) {
                    sx = midW - cropW;
                }
                if (sy + cropH > midH) {
                    sy = midH - cropH;
                }
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(mid, sx, sy, cropW, cropH, 0, 0, slotW, slotH);
            } catch (err) {
                console.error("Print photo bake failed:", err);
                return null;
            }

            if (clip && clip.type === "circle") {
                ctx.globalCompositeOperation = "destination-in";
                ctx.fillStyle = "#000";
                ctx.beginPath();
                ctx.arc(slotW / 2, slotH / 2, Math.min(slotW, slotH) / 2, 0, Math.PI * 2);
                ctx.fill();
                return baked;
            }

            const maskEl = clip && (typeof clip.getElement === "function" ? clip.getElement() : clip._element);
            const maskWidth = maskEl && (maskEl.naturalWidth || maskEl.width);
            const maskHeight = maskEl && (maskEl.naturalHeight || maskEl.height);
            if (!maskEl || !maskWidth || !maskHeight) {
                return null;
            }
            try {
                ctx.globalCompositeOperation = "destination-in";
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(maskEl, 0, 0, slotW, slotH);
            } catch (err) {
                console.error("Print photo mask failed:", err);
                return null;
            }
            return baked;
        },
        async withPrintMap(render) {
            const previous = this._printMapJob || Promise.resolve();
            const job = previous.catch(() => {}).then(() => this.swapPrintMap(render));
            this._printMapJob = job;
            return job;
        },
        async swapPrintMap(render) {
            const canvasObjects = (this.canvasCreator && typeof this.canvasCreator.getObjects === "function")
                ? this.canvasCreator.getObjects()
                : [];
            const objects = canvasObjects.filter((obj) => obj && obj.customType === "map_static");
            const clippedPhotos = canvasObjects.filter((obj) => {
                if (!obj || obj.customType === "map_static" || !obj.clipPath) {
                    return false;
                }
                const clipType = obj.clipPath.type;
                if (clipType !== "image" && clipType !== "circle") {
                    return false;
                }
                const el = typeof obj.getElement === "function" ? obj.getElement() : obj._element;
                const w = el && (el.naturalWidth || el.width);
                const h = el && (el.naturalHeight || el.height);
                return !!(w && h);
            });
            let printImg = null;
            if (this.mapPrintSrc && objects.length > 0) {
                try {
                    printImg = await this.takePrintMapImage(this.mapPrintSrc);
                } catch (err) {
                    console.error("Print map failed to load, exporting the preview:", err);
                    printImg = null;
                }
            }
            const printWidth = printImg && (printImg.naturalWidth || printImg.width);
            const printHeight = printImg && (printImg.naturalHeight || printImg.height);
            const usePrint = !!(printImg && printWidth && printHeight);
            const snapshots = [];
            const zoom = this.printExportZoom();

            try {
                if (usePrint) {
                    objects.forEach((obj) => {
                        const slot = this.printSlotPixels(obj, zoom);
                        snapshots.push(this.snapshotForPrintSwap(obj));
                        const fitted = this.resampleMapForPrint(printImg, slot.slotW, slot.slotH, obj.clipPath);
                        const bitmap = fitted ? fitted.canvas : printImg;
                        const bitmapWidth = bitmap.naturalWidth || bitmap.width;
                        const bitmapHeight = bitmap.naturalHeight || bitmap.height;
                        this.applyMapBitmap(obj, bitmap, slot.displayWidth / bitmapWidth, slot.displayHeight / bitmapHeight);
                        obj.objectCaching = false;
                        obj.imageSmoothing = false;
                        obj._removeCacheCanvas();
                        if (fitted && fitted.clipped) {
                            obj.clipPath = null;
                        }
                    });
                }
                clippedPhotos.forEach((obj) => {
                    const slot = this.printSlotPixels(obj, zoom);
                    const source = typeof obj.getElement === "function" ? obj.getElement() : obj._element;
                    const baked = this.bakeClippedPhotoForPrint(source, slot.slotW, slot.slotH, obj.clipPath);
                    if (!baked) {
                        return;
                    }
                    snapshots.push(this.snapshotForPrintSwap(obj));
                    this.applyMapBitmap(obj, baked, slot.displayWidth / baked.width, slot.displayHeight / baked.height);
                    obj.objectCaching = false;
                    obj.imageSmoothing = false;
                    obj._removeCacheCanvas();
                    obj.clipPath = null;
                    obj.cropX = 0;
                    obj.cropY = 0;
                });
                return this.withDesignSizeCanvas(render);
            } finally {
                snapshots.forEach((snap) => {
                    this.restorePrintSwap(snap);
                });
                this.requestCanvasRender();
            }
        },
        renderPrintImages() {
            return this.withPrintMap(() => ({
                image: this.renderCanvas("png", this.getPrintExportMultiplier()),
                image_thumb: this.renderCanvas("png", this.getPreviewExportMultiplier()),
            }));
        },
        renderCanvas(format, multiple) {

            if (this.selectedProductType && this.selectedProductType.has_aura && this.canvasCreator) {
                this.canvasCreator.getObjects().forEach(function (obj) {
                    obj.set({ objectCaching: false, dirty: true });
                    if (obj._objects) {
                        obj._objects.forEach(function (child) {
                            child.set({ objectCaching: false, dirty: true });
                        });
                    }
                });
                this.canvasCreator.renderAll();
            }

            if (format === "jpeg") {
                this.canvasCreator.setBackgroundColor('#fff', this.canvasCreator.renderAll.bind(this.canvasCreator));
            }

            var tempData = this.canvasCreator.toDataURL({format: format, multiplier: multiple});
            if (!this._suppressExportRedraw) {
                this.canvasCreator.setBackgroundColor(null, this.canvasCreator.renderAll.bind(this.canvasCreator));
            } else {
                this.canvasCreator.backgroundColor = null;
            }
            return tempData;
        },
        toggleLongLat(){
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        resizeDimensions(width, height, maxDimension = 640) {
            // Calculate the aspect ratio
            const aspectRatio = width / height;

            // Determine the scaling factor
            let newWidth, newHeight;
            if (width > height) {
                newWidth = maxDimension;
                newHeight = maxDimension / aspectRatio;
            } else {
                newHeight = maxDimension;
                newWidth = maxDimension * aspectRatio;
            }

            // If both dimensions are larger than the maximum, scale down both
            if (newWidth > maxDimension || newHeight > maxDimension) {
                if (newWidth > newHeight) {
                    newWidth = maxDimension;
                    newHeight = maxDimension / aspectRatio;
                } else {
                    newHeight = maxDimension;
                    newWidth = maxDimension * aspectRatio;
                }
            }

            if(this.mapRequestUsesHalfSize()){
                return {
                    width: Math.round(newWidth/2),
                    height: Math.round(newHeight/2)
                };
            }else{
                return {
                    width: Math.round(newWidth),
                    height: Math.round(newHeight)
                };
            }
        },
        browseExamples(){
            this.showBrowseExamples = true;
        },
        toggleDateCheckbox(elementId, isChecked) {
            const includeDate = typeof isChecked === 'boolean' ? isChecked : this.showDate;
            this.showDate = includeDate;
            if (includeDate) {
                // ✅ Checkbox turned ON → make this field required (if it's not already)
                if (!this.requiredProductFields.includes(elementId)) {
                    this.requiredProductFields.push(elementId);
                }
            } else {
                // ❌ Checkbox turned OFF → remove from required fields
                this.requiredProductFields = this.requiredProductFields.filter(
                    (id) => id !== elementId
                );

                // also clear any existing error for this field
                this.errorRequiredFields = this.errorRequiredFields.filter(
                    (id) => id !== elementId
                );
            }
            if (this.requiredValidationActive) {
                this.validateRequiredFields();
            }
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        toggleMemorialExtraDate() {
            if (!this.showExtraDate) {
                this.userInput['date_to'] = '';
            }
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        applyFullPrintEdgeLayout(options) {
            const shouldFill = !options || options.fill !== false;
            const types = this.productData && this.productData.types ? this.productData.types : [];
            const fullPrint = types.find((type) => type && type.title === "Full Print");
            const styles = fullPrint && fullPrint.styles ? fullPrint.styles : [];
            for (const style of styles) {
                const elems = style && style.elements ? style.elements : [];
                const imageEl = elems.find((el) => el && el.id === "post_url_image_0");
                const uploadEl = elems.find((el) => el && el.id === "multi_image_selector");
                if (imageEl) {
                    if (this.fullPrintEdgeToEdge) {
                        imageEl["x-pos"] = 0;
                        imageEl["y-pos"] = 0;
                        imageEl["width"] = 1000;
                        imageEl["height"] = 1400;
                    } else {
                        imageEl["x-pos"] = 90;
                        imageEl["y-pos"] = 90;
                        imageEl["width"] = 820;
                        imageEl["height"] = 1220;
                    }
                }
                if (uploadEl) {
                    uploadEl["aspect-ratio"] = this.fullPrintEdgeToEdge ? 5 / 7 : 41 / 61;
                }
            }
            if (shouldFill) {
                this.fillCanvas();
            }
        },
        toggleFullPrintEdgeToEdge() {
            this.applyFullPrintEdgeLayout();
        },
        toggleMomentCheckbox(elementId) {
            if (!this.showMoment) {
                if (this.userInput && this.userInput[elementId] !== undefined) {
                    this.userInput[elementId] = "";
                }
            }
            if (!this.refreshCanvasTextObjects()) {
                this.fillCanvas();
            }
        },
        makeid(length) {
            let result = '';
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            const charactersLength = characters.length;
            let counter = 0;
            while (counter < length) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
                counter += 1;
            }
            return result;
        },
        multiImageMove(evt){
            this.fillCanvas();
            return true;
        },
        /**
         * Returns a loaded HTMLImageElement with pixels converted to grayscale (luma NTSC-ish).
         * Falls back to `sourceImg` if dimensions are missing or decode fails.
         */
        htmlImageToGrayscaleDataUrlImage(sourceImg, callback) {
            const w = sourceImg.naturalWidth || sourceImg.width;
            const h = sourceImg.naturalHeight || sourceImg.height;
            if (!w || !h) {
                callback(sourceImg);
                return;
            }
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(sourceImg, 0, 0);
            const imageData = ctx.getImageData(0, 0, w, h);
            const d = imageData.data;
            for (let i = 0; i < d.length; i += 4) {
                const y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                d[i] = d[i + 1] = d[i + 2] = y;
            }
            ctx.putImageData(imageData, 0, 0);
            const out = new Image();
            out.crossOrigin = "Anonymous";
            out.onload = () => callback(out);
            out.onerror = () => callback(sourceImg);
            out.src = canvas.toDataURL("image/png");
        },
        htmlImageToFlatColorImage(sourceImg, hex, callback) {
            const w = sourceImg.naturalWidth || sourceImg.width;
            const h = sourceImg.naturalHeight || sourceImg.height;
            const color = typeof hex === "string" && hex.charAt(0) === "#" ? hex : PLAYING_CARD_RED;
            if (!w || !h) {
                callback(sourceImg);
                return;
            }
            try {
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(sourceImg, 0, 0);
                const imageData = ctx.getImageData(0, 0, w, h);
                const d = imageData.data;
                const r = parseInt(color.slice(1, 3), 16);
                const g = parseInt(color.slice(3, 5), 16);
                const b = parseInt(color.slice(5, 7), 16);
                for (let i = 0; i < d.length; i += 4) {
                    d[i] = r;
                    d[i + 1] = g;
                    d[i + 2] = b;
                }
                ctx.putImageData(imageData, 0, 0);
                const out = new Image();
                out.onload = () => callback(out);
                out.onerror = () => callback(sourceImg);
                out.src = canvas.toDataURL("image/png");
            } catch (err) {
                callback(sourceImg);
            }
        },
        /**
         * When any object has numeric `customZ` (from element `z_index`), restack the whole canvas
         * by ascending z (lower = further back). Untagged objects sort after tagged ones so text
         * and other legacy layers stay readable above decorative image stacks.
         */
        reorderCanvasByCustomZ() {
            const c = this.canvasCreator;
            if (!c) return;
            const objs = c.getObjects();
            if (!objs.some((o) => o && typeof o.customZ === "number" && !Number.isNaN(o.customZ))) return;

            const FRONT_BASE = 500000;
            const decorated = objs.map((o, idx) => ({
                o,
                idx,
                z: o && typeof o.customZ === "number" && !Number.isNaN(o.customZ) ? o.customZ : FRONT_BASE + idx * 1e-6,
            }));
            decorated.sort((a, b) => {
                if (a.z !== b.z) return a.z - b.z;
                return a.idx - b.idx;
            });
            decorated.forEach((d, i) => {
                c.moveTo(d.o, i);
            });

            const topObjs = c.getObjects();
            for (const o of topObjs) {
                if (o && o.customType === "icon") {
                    c.bringToFront(o);
                }
                if (o && o.customType === "country_map_marker") {
                    c.bringToFront(o);
                }
            }
            if (typeof c.requestRenderAll === "function") {
                c.requestRenderAll();
            }
        },
        clearFillCanvasDebounceTimer() {
            if (this.fillCanvasDebounceTimer) {
                clearTimeout(this.fillCanvasDebounceTimer);
                this.fillCanvasDebounceTimer = null;
            }
        },
        scheduleFillCanvas() {
            this.clearFillCanvasDebounceTimer();
            this.fillCanvasDebounceTimer = setTimeout(() => {
                this.fillCanvasDebounceTimer = null;
                this.fillCanvas();
            }, 350);
        },
        fillCanvas() {
            this.clearFillCanvasDebounceTimer();

            this.canvasBuildVersion += 1;
            const version = this.canvasBuildVersion;
            this.pendingCanvasAssets = 0;
            this.loadingCanvasImage = true;
            if (this.canvasCreator) {
                this.canvasCreator.renderOnAddRemove = false;
            }
            this.beginCanvasAssetLoad(version);
            this.buildCanvasContents(version);
        },
        buildCanvasContents(tempCanvasID) {
            if (tempCanvasID != null && tempCanvasID !== this.canvasBuildVersion) {
                this.finishCanvasAssetLoad(tempCanvasID);
                return;
            }

            try {
            this.canvasCreator.clear();
            this.selectedProductType["styles"][this.selectedProductTypeStyle]['elements'].forEach((element, key) => {

                var textValue = "";
                //console.log(element['image-loader']);
                if(element['image-loader'] === true){
                    this.canvasLoader = true;
                    this.canvasLoaderTopPos = element['image-loader-top-pos'];
                }

                const isToggleable = element['editable_type'] === "toggleable";
                const toggleableVisible = !isToggleable || this.userInput[element['id']] !== false; // default ON unless explicitly false
                value = this.resolveCanvasElementValue(element);

                if (element['type'] === "text") {
                    const spec = this.composeCanvasTextSpec(element);
                    const textObj = new fabric.Text(spec.text, {
                        fontFamily: spec.fontFamily,
                        fontWeight: spec.fontWeight,
                        fontStyle: spec.fontStyle,
                        left: spec.left,
                        top: spec.top,
                        fontSize: spec.fontSize,
                        textAlign: spec.textAlign,
                        originX: spec.originX,
                        originY: spec.originY,
                        fill: spec.fill,
                        skewY: spec.skewY,
                        skewX: spec.skewX,
                        scaleX: spec.scaleX,
                        angle: spec.angle,
                        lineHeight: spec.lineHeight != null ? spec.lineHeight : 1.16,
                        selectable: false,
                        charSpacing: spec.charSpacing,
                        canvasElementId: element['id'],
                    });

                    if (element['display'] === "block") {
                        this.canvasCreator.add(textObj);
                    }
                    if (element['pull-top'] !== undefined && element['pull-top'] === true){
                        this.canvasCreator.bringForward(textObj);
                    }
                } else if (element['type'] === "rect") {
                    let rectFill = element["fill"] || "#000000";
                    let skipPageBackground = false;
                    if (element['page_background'] === true && this.showTransparent === true) {
                        skipPageBackground = true;
                    }
                    const rectObj = new fabric.Rect({
                        left: element["x-pos"],
                        top: element["y-pos"],
                        width: element["width"],
                        height: element["height"],
                        fill: rectFill,
                        originX: element["originX"] || "left",
                        originY: element["originY"] || "top",
                        selectable: false,
                        evented: false,
                        canvasElementId: element['id'],
                    });
                    if (skipPageBackground) {
                        // Clear / transparent: do not print a page fill
                    } else if (element['display'] === "block" || element['display'] === undefined) {
                        this.canvasCreator.add(rectObj);
                    }
                } else if (element['type'] === "aura") {
                    if (element['display'] === "block" || element['display'] === undefined) {
                        const auraPalette = (this.userInput && this.userInput.aura_palette) || "love";
                        const shapeId = (this.userInput && this.userInput.aura_shape) || "circle";
                        const shapeFrame = angelAuraConfig.shapes[shapeId] || angelAuraConfig.shapes.circle;
                        if ((this.userInput && this.userInput.aura_panel) === "panel") {
                            placeTintedAuraImage(this, angelAuraConfig.background, auraPalette, tempCanvasID, "aura_panel");
                        }
                        placeTintedAuraImage(this, shapeFrame, auraPalette, tempCanvasID, "aura");
                    }
                } else if (element['type'] === "keyword_row") {
                    const keywordSpec = this.composeKeywordRowSpec(element);
                    if (keywordSpec) {
                        this.canvasCreator.add(new fabric.Text(keywordSpec.text, {
                            fontFamily: keywordSpec.fontFamily,
                            fontWeight: keywordSpec.fontWeight,
                            fontStyle: keywordSpec.fontStyle,
                            left: keywordSpec.left,
                            top: keywordSpec.top,
                            fontSize: keywordSpec.fontSize,
                            textAlign: keywordSpec.textAlign,
                            originX: keywordSpec.originX,
                            originY: keywordSpec.originY,
                            fill: keywordSpec.fill,
                            selectable: false,
                            canvasElementId: element["id"],
                        }));
                    }
                } else if (element['type'] === "text_box") {
                    var curFont = "";
                    if (this.selectedFont != "") {
                        curFont = this.selectedFont;
                    } else {
                        curFont = element['font-family'];
                    }

                    var letterPlace = this.userInput['profile_username'];
                    if (letterPlace === "") {
                        letterPlace = "username";
                    }
                    var letterCount = letterPlace.length;

                    const letterBreakingTextBox = new fabric.Textbox(letterPlace + " " + value, {
                        fontFamily: curFont,
                        fontWeight: element['font-weight'],
                        left: element["x-pos"],
                        top: element["y-pos"],
                        fontSize: element['font-size'],
                        textAlign: element['text-align'],
                        originX: element['text-align'],
                        fill: element['text-color'],
                        selectable: false,
                        width: 750,
                        canvasElementId: element['id'],
                    });

                    letterBreakingTextBox.setSelectionStyles({fontWeight: "bold"}, 0, letterCount);
                    this.canvasCreator.add(letterBreakingTextBox);
                }else if (element['type'] === "phonetic-spelling-word-class") {
                    const phonetic = this.phoneticCanvasLayout(element);
                    const letterBreakingTextBox = new fabric.Textbox(phonetic.text, {
                        fontFamily: phonetic.fontFamily,
                        fontWeight: element['font-weight'],
                        left: phonetic.left,
                        top: phonetic.top,
                        fontSize: phonetic.fontSize,
                        textAlign: element['text-align'],
                        originX: element['text-align'],
                        fill: element['text-color'],
                        selectable: false,
                        width: 750,
                        canvasElementId: element['id'],
                    });

                    this.canvasCreator.add(letterBreakingTextBox);
                } else if (element['type'] === "text_box2") {
                    var curFont = "";
                    if (this.selectedFont != "") {
                        curFont = this.selectedFont;
                    } else {
                        curFont = element['font-family'];
                    }

                    const letterBreakingTextBox = new fabric.Textbox(value, {
                        fontFamily: curFont,
                        fontWeight: element['font-weight'],
                        left: element["x-pos"],
                        top: element["y-pos"],
                        fontSize: element['font-size'],
                        textAlign: element['text-align'],
                        originX: element['text-align'],
                        fill: element['text-color'],
                        selectable: false,
                        width: 750,
                        canvasElementId: element['id'],
                    });

                    this.canvasCreator.add(letterBreakingTextBox);
                }  else if (element['type'] === 'post_image') {
                    var outerImg = new Image();
                    outerImg.onload = (img) => {
                        if(tempCanvasID === this.canvasBuildVersion) {
                            var img = new fabric.Image(outerImg, {
                                left: element["x-pos"],
                                top: element["y-pos"],
                                scaleX: element["width"] / outerImg.width,
                                scaleY: element["height"] / outerImg.width,
                                selectable: false,
                            });
                            let rect = new fabric.Rect({
                                left: element["x-pos"],
                                top: element["y-pos"],
                                width: element["width"],
                                height: element["height"],
                                absolutePositioned: true,
                            });
                            img.clipPath = rect;
                            this.canvasCreator.add(img);
                        }
                    };
                    outerImg.crossOrigin = "Anonymous";
                    this.setCanvasImageSrc(outerImg, value, tempCanvasID);
                } else if (element['type'] === 'profile_image') {
                    var outerImg = new Image();

                    outerImg.onload = (img) => {
                        if(tempCanvasID === this.canvasBuildVersion) {
                            var img = new fabric.Image(outerImg, {
                                left: element["x-pos"],
                                top: element["y-pos"],
                                scaleX: element["width"] / outerImg.width,
                                scaleY: element["height"] / outerImg.width,
                                selectable: false,
                            });
                            let rect = new fabric.Circle({
                                left: element["x-pos"],
                                top: element["y-pos"],
                                radius: element["width"] / 2,
                                absolutePositioned: true
                            });
                            img.clipPath = rect;
                            this.canvasCreator.add(img);
                        }
                    };
                    outerImg.crossOrigin = "Anonymous";
                    this.setCanvasImageSrc(outerImg, value, tempCanvasID);

                } else if (element['type'] === 'hidden_map_image') {
                    console.log("This product has no map image");
                }else if (element['type'] === 'map_image') {
                    if(value !== "null"){
                        if(this.isDefaultMap === true){
                            if(this.defaultMap === ""){
                                value = "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/default-map.png?v=1723156349";
                            }else{
                                value = this.defaultMap;
                            }
                        }

                        console.log("Loading Image");

                        var outerImg = new Image();
                        outerImg.onload = (img) => {
                            if(tempCanvasID === this.canvasBuildVersion) {
                                var img = new fabric.Image(outerImg, {
                                    left: element["x-pos"],
                                    top: element["y-pos"],
                                    scaleX: element["width"] / outerImg.width,
                                    scaleY: element["height"] / outerImg.height,
                                    selectable: false
                                });
                                img.customType = "map_static";

                                // let rect = new fabric.Rect({
                                //     left: element["x-pos"],
                                //     top: element["y-pos"],
                                //     width: element["width"],
                                //     height: element["height"],
                                //     absolutePositioned: true
                                // });
                                //
                                // img.clipPath = rect;
                                this.canvasCreator.add(img);
                                this.addRouteOverlay(element, img, tempCanvasID);
                            }
                        };
                        outerImg.crossOrigin = "Anonymous";
                        this.setCanvasImageSrc(outerImg, value, tempCanvasID);
                    }

                } else if (element["type"] === "country_map_image") {
                    if (value !== "null" && value !== "" && value !== undefined) {
                        var outerCountryImg = new Image();
                        outerCountryImg.onload = (img) => {
                            if (tempCanvasID === this.canvasBuildVersion) {
                                const geo = this.getCountryMapPinGeometry(
                                    element,
                                    outerCountryImg.naturalWidth,
                                    outerCountryImg.naturalHeight
                                );
                                var countryFabricImg = new fabric.Image(outerCountryImg, {
                                    left: geo.contentLeft,
                                    top: geo.contentTop,
                                    scaleX: geo.contentW / outerCountryImg.width,
                                    scaleY: geo.contentH / outerCountryImg.height,
                                    selectable: false,
                                });
                                this.canvasCreator.add(countryFabricImg);
                                this.addCountryMapDebugBoundsOverlay(
                                    element,
                                    outerCountryImg.naturalWidth,
                                    outerCountryImg.naturalHeight
                                );
                                this.bringCountryMapMarkerToFront();
                            }
                        };
                        outerCountryImg.crossOrigin = "Anonymous";
                        this.setCanvasImageSrc(outerCountryImg, value, tempCanvasID);
                    }
                } else if (element["type"] === "country_map_marker") {
                    const pv = this.userInput["country_map_pin_visible"];
                    if (
                        pv === false ||
                        pv === "false" ||
                        pv === 0 ||
                        pv === "0" ||
                        String(pv).toLowerCase() === "no"
                    ) {
                        return;
                    }
                    const els = this.selectedProductType.styles[this.selectedProductTypeStyle].elements;
                    const mapBox = els.find(function (e) {
                        return e["type"] === "country_map_image";
                    });
                    if (!mapBox) {
                        return;
                    }
                    const mx = parseFloat(this.userInput["marker_x"]);
                    const my = parseFloat(this.userInput["marker_y"]);
                    const xPct = isNaN(mx) ? 50 : mx;
                    const yPct = isNaN(my) ? 50 : my;
                    const markerUrl =
                        this.mapMarkers[this.selectedMapMarkerColor]["markers"][this.selectedMapMarker]["img"];
                    const markerImgEl = new Image();
                    markerImgEl.crossOrigin = "Anonymous";
                    markerImgEl.onload = () => {
                        if (tempCanvasID !== this.canvasBuildVersion) {
                            return;
                        }
                        const mapW = mapBox["width"];
                        const mapH = mapBox["height"];
                        let cw = parseFloat(this.userInput["country-map-content-w"]);
                        let ch = parseFloat(this.userInput["country-map-content-h"]);
                        if (isNaN(cw) || isNaN(ch) || cw <= 0 || ch <= 0) {
                            cw = mapW;
                            ch = mapH;
                        }
                        const geo = this.getCountryMapPinGeometry(mapBox, cw, ch);
                        const natW = markerImgEl.width || 1;
                        const natH = markerImgEl.height || 1;
                        const pinH = element["height"];
                        const scale = pinH / natH;
                        const pinW = natW * scale;
                        const pinLeft =
                            geo.contentLeft + (xPct / 100) * geo.contentW - pinW / 2;
                        const pinTop =
                            geo.contentTop + (yPct / 100) * geo.contentH - pinH;
                        const pinFabric = new fabric.Image(markerImgEl, {
                            left: pinLeft,
                            top: pinTop,
                            scaleX: scale,
                            scaleY: scale,
                            selectable: false,
                        });
                        pinFabric.customType = "country_map_marker";
                        this.canvasCreator.add(pinFabric);
                        this.canvasCreator.bringToFront(pinFabric);
                        this.bringCountryMapMarkerToFront();
                    };
                    this.setCanvasImageSrc(markerImgEl, markerUrl, tempCanvasID);
                }else if (element['type'] === 'map_image_mask') {

                    if(value !== "null") {

                        if (this.isDefaultMap === true) {
                            if (this.defaultMap === "") {
                                value = "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/default-map.png?v=1723156349";
                            } else {
                                value = this.defaultMap;
                            }
                        }

                        console.log("Loading Image");

                        var outerImg = new Image();
                        outerImg.onload = (img) => {
                            if (tempCanvasID === this.canvasBuildVersion) {
                                var img = new fabric.Image(outerImg, {
                                    left: element["x-pos"],
                                    top: element["y-pos"],
                                    scaleX: element["width"] / outerImg.width,
                                    scaleY: element["height"] / outerImg.height,
                                    selectable: false
                                });
                                img.customType = "map_static";

                                // Load the mask image
                                var maskImg = new Image();
                                maskImg.onload = () => {
                                    var mask = new fabric.Image(maskImg, {
                                        left: 0,
                                        top: 0,
                                        originX: 'center',
                                        originY: 'center',
                                        scaleX: element["width"] / outerImg.width,
                                        scaleY: element["height"] / outerImg.height,
                                        selectable: false
                                    });

                                    // Setting the clipPath options
                                    img.clipPath = mask;
                                    img.clipPath.left = 0;
                                    img.clipPath.top = 0;
                                    img.clipPath.originX = 'center';
                                    img.clipPath.originY = 'center';

                                    img.clipPath.scaleX = outerImg.width / maskImg.width;
                                    img.clipPath.scaleY = outerImg.height / maskImg.height;

                                    this.canvasCreator.add(img);
                                    this.addRouteOverlay(element, img, tempCanvasID);
                                };
                                maskImg.crossOrigin = "Anonymous";
                                this.setCanvasImageSrc(maskImg, element["clipping-mask-image"], tempCanvasID);
                            }
                        };
                        outerImg.crossOrigin = "Anonymous";
                        this.setCanvasImageSrc(outerImg, value, tempCanvasID);
                    }
                } else if (element['type'] === 'map_image_cutout') {
                    if (value !== "null") {
                        if (this.isDefaultMap === true) {
                            if (this.defaultMap === "") {
                                value = "https://cdn.shopify.com/s/files/1/0431/4156/2528/files/default-map.png?v=1723156349";
                            } else {
                                value = this.defaultMap;
                            }
                        }

                        const shape = element['use-photo-shape'] ? this.getSelectedPhotoShape() : null;
                        const maskUrl = (shape && shape['map-mask']) || element['clipping-mask-image'];

                        var outerCutoutImg = new Image();
                        outerCutoutImg.onload = () => {
                            if (tempCanvasID !== this.canvasBuildVersion) {
                                return;
                            }
                            var cutoutImg = new fabric.Image(outerCutoutImg, {
                                left: element["x-pos"],
                                top: element["y-pos"],
                                scaleX: element["width"] / outerCutoutImg.width,
                                scaleY: element["height"] / outerCutoutImg.height,
                                selectable: false
                            });
                            cutoutImg.customType = "map_static";

                            if (!maskUrl) {
                                this.canvasCreator.add(cutoutImg);
                                this.addRouteOverlay(element, cutoutImg, tempCanvasID);
                                return;
                            }

                            var cutoutMaskImg = new Image();
                            cutoutMaskImg.crossOrigin = "Anonymous";
                            cutoutMaskImg.onload = () => {
                                if (tempCanvasID !== this.canvasBuildVersion) {
                                    return;
                                }
                                cutoutImg.clipPath = new fabric.Image(cutoutMaskImg, {
                                    left: element["x-pos"] + element["width"] / 2,
                                    top: element["y-pos"] + element["height"] / 2,
                                    originX: 'center',
                                    originY: 'center',
                                    absolutePositioned: true,
                                    scaleX: element["width"] / cutoutMaskImg.width,
                                    scaleY: element["height"] / cutoutMaskImg.height,
                                    selectable: false,
                                    evented: false
                                });
                                this.canvasCreator.add(cutoutImg);
                                this.addRouteOverlay(element, cutoutImg, tempCanvasID);
                            };
                            this.setCanvasImageSrc(cutoutMaskImg, maskUrl, tempCanvasID);
                        };
                        outerCutoutImg.crossOrigin = "Anonymous";
                        this.setCanvasImageSrc(outerCutoutImg, value, tempCanvasID);
                    }
                } else if (element['type'] === 'image') {
                    var outerImg = new Image();
                    outerImg.onload = (img) => {
                        if(tempCanvasID === this.canvasBuildVersion) {
                            const placeImage = (photoImg) => {
                                if (tempCanvasID !== this.canvasBuildVersion) {
                                    return;
                                }
                                const angle = Number(element["angle"] || 0);
                                const width = Number(element["width"]);
                                const height = Number(element["height"]);
                                const zManaged =
                                    element["z_index"] !== undefined &&
                                    element["z_index"] !== null &&
                                    !Number.isNaN(Number(element["z_index"]));
                                const image = new fabric.Image(photoImg, {
                                    left: angle ? Number(element["x-pos"]) + width / 2 : element["x-pos"],
                                    top: angle ? Number(element["y-pos"]) + height / 2 : element["y-pos"],
                                    originX: angle ? "center" : "left",
                                    originY: angle ? "center" : "top",
                                    scaleX: width / (photoImg.width || 1),
                                    scaleY: height / (photoImg.height || 1),
                                    angle: angle,
                                    selectable: false,
                                    canvasElementId: element["id"],
                                });
                                if (zManaged) {
                                    image.customZ = Number(element["z_index"]);
                                }
                                this.canvasCreator.add(image);
                                if (zManaged) {
                                    this.reorderCanvasByCustomZ();
                                }
                            };
                            if (element["recolor"]) {
                                this.htmlImageToFlatColorImage(
                                    outerImg,
                                    playingCardColorHex(this.userInput && this.userInput[element["recolor"]]),
                                    placeImage
                                );
                            } else {
                                placeImage(outerImg);
                            }
                        }
                    };
                    outerImg.crossOrigin = "Anonymous";
                    this.setCanvasImageSrc(outerImg, value, tempCanvasID);

                }else if (element['type'] === 'image_bg') {
                    if (element['editable_type'] === "toggleable" && toggleableVisible === false) {
                        return;
                    }
                    const zManagedBg =
                        element["z_index"] !== undefined &&
                        element["z_index"] !== null &&
                        !Number.isNaN(Number(element["z_index"]));
                    const zBg = zManagedBg ? Number(element["z_index"]) : null;
                    var outerImg = new Image();
                    outerImg.onload = (img) => {
                        if(tempCanvasID === this.canvasBuildVersion) {
                            const image = new fabric.Image(outerImg, {
                                left: element["x-pos"],
                                top: element["y-pos"],
                                scaleX: element["width"] / outerImg.width,
                                scaleY: element["height"] / outerImg.height,
                                selectable: false,
                                canvasElementId: element["id"],
                            });
                            if (zManagedBg) {
                                image.customZ = zBg;
                            }
                            this.canvasCreator.add(image);
                            if (zManagedBg) {
                                this.reorderCanvasByCustomZ();
                            } else {
                                this.canvasCreator.sendToBack(image);
                            }
                        }
                    };
                    outerImg.crossOrigin = "Anonymous";
                    this.setCanvasImageSrc(outerImg, value, tempCanvasID);

                }else if (element['type'] === 'image_mid_layer') {
                    var outerImg = new Image();
                    outerImg.onload = (img) => {
                        if(tempCanvasID === this.canvasBuildVersion) {
                            const image = new fabric.Image(outerImg, {
                                left: element["x-pos"],
                                top: element["y-pos"],
                                scaleX: element["width"] / outerImg.width,
                                scaleY: element["height"] / outerImg.height,
                                selectable: false,
                            });
                            this.canvasCreator.add(image);
                            this.canvasCreator.sendBackwards(image);
                        }
                    };
                    outerImg.crossOrigin = "Anonymous";
                    this.setCanvasImageSrc(outerImg, value, tempCanvasID);

                }else if (element['type'] === 'multi_image' || element['type'] === 'line_art_image') {
                    if (element['use-photo-shape'] && element['type'] === 'multi_image') {
                        const shape = this.getSelectedPhotoShape();
                        if (shape) {
                            element = Object.assign({}, element, {
                                "x-pos": shape["photo-x-pos"],
                                "y-pos": shape["photo-y-pos"],
                                "width": shape["photo-width"],
                                "height": shape["photo-height"],
                                "clipping-mask-image": shape["photo-mask"],
                            });
                        }
                    }

                    let tmp;
                    let generatedCutout = false;
                    const cutoutSource = (element['use-photo-shape'] && element['type'] === 'multi_image')
                        ? this.cutoutPhotoSource()
                        : "upload";
                    if (element['type'] === 'line_art_image') {
                        tmp = this.lineArtTintedUrl || this.getLineArtActiveSourceUrl() || element['initial'];
                    } else if (
                        cutoutSource === "sky" &&
                        typeof this.userInput.sky_map === "string" &&
                        this.userInput.sky_map.indexOf("data:image") === 0
                    ) {
                        tmp = this.userInput.sky_map;
                        generatedCutout = true;
                    } else if (
                        cutoutSource === "moon" &&
                        typeof this.userInput.moon_image === "string" &&
                        this.userInput.moon_image.indexOf("data:image") === 0
                    ) {
                        tmp = this.userInput.moon_image;
                        generatedCutout = true;
                    } else {
                        const upload = this.multiUploadImagesArray[element["multi_image_id"]];
                        tmp = (upload === undefined) ? element['initial'] : upload["src"];
                    }

                    const outerImg = new Image();
                    // data/blob URLs and sample artwork are canvas-safe with CORS.
                    // Remote AI generation hosts often block CORS; loading those with
                    // crossOrigin=Anonymous fails and previously fell back to the sample.
                    if (element['type'] === 'line_art_image') {
                        const safeUrl = this.isLineArtDataOrBlobUrl(tmp);
                        const sampleUrl = !this.lineArtGeneratedUrlRaw || tmp === element['initial'];
                        if (safeUrl || sampleUrl) {
                            outerImg.crossOrigin = "Anonymous";
                        }
                    } else {
                        outerImg.crossOrigin = "Anonymous";
                    }
                    outerImg.onload = () => {
                        if (tempCanvasID !== this.canvasBuildVersion) return;

                        const addMultiImageFromPhoto = (photoImg) => {
                            if (tempCanvasID !== this.canvasBuildVersion) return;

                            // ---- container, center, rotation, z-order intent ----
                            const containerWidth  = Number(element["width"]);
                            const containerHeight = Number(element["height"]);
                            const cx = Number(element["x-pos"]) + containerWidth  / 2;
                            const cy = Number(element["y-pos"]) + containerHeight / 2;
                            const ANG = Number(element['angle'] ?? 0);
                            const ON_TOP = !!element['on-top']; // bring this slot above other images/multi_images
                            const zManagedMi =
                                element["z_index"] !== undefined &&
                                element["z_index"] !== null &&
                                !Number.isNaN(Number(element["z_index"]));
                            const zBaseMi = zManagedMi ? Number(element["z_index"]) : NaN;
                            const useZ = zManagedMi && !Number.isNaN(zBaseMi);

                            const bringIconsToFront = () => {
                                const objs = this.canvasCreator.getObjects();
                                for (const o of objs) {
                                    if (o && o.customType === 'icon') this.canvasCreator.bringToFront(o);
                                }
                            };

                            const makeCutoutBacking = (clipPath) => {
                                if (!generatedCutout) return null;
                                const backing = new fabric.Rect({
                                    left: cx,
                                    top: cy,
                                    originX: 'center',
                                    originY: 'center',
                                    width: containerWidth,
                                    height: containerHeight,
                                    fill: '#151726',
                                    angle: ANG,
                                    selectable: false,
                                    evented: false,
                                    objectCaching: false,
                                });
                                if (clipPath) {
                                    backing.clipPath = clipPath;
                                }
                                if (useZ) {
                                    backing.customZ = zBaseMi - 0.25;
                                }
                                return backing;
                            };

                            if (element['type'] === 'line_art_image') {
                                const scaleFactor = Math.min(
                                    containerWidth / photoImg.width,
                                    containerHeight / photoImg.height
                                );
                                const image = new fabric.Image(photoImg, {
                                    left: cx,
                                    top: cy,
                                    originX: 'center',
                                    originY: 'center',
                                    scaleX: scaleFactor,
                                    scaleY: scaleFactor,
                                    angle: ANG,
                                    selectable: false,
                                });
                                if (useZ) {
                                    image.customZ = zBaseMi;
                                }
                                this.canvasCreator.add(image);
                                if (useZ) {
                                    this.reorderCanvasByCustomZ();
                                } else if (ON_TOP) {
                                    this.canvasCreator.bringToFront(image);
                                } else {
                                    this.canvasCreator.sendToBack(image);
                                }
                                if (!useZ) {
                                    bringIconsToFront();
                                }
                                return;
                            }

                            // ---- crop-to-fill (default) or contain ----
                            let scaleFactor, cropX = 0, cropY = 0;
                            let cropWidth = photoImg.width;
                            let cropHeight = photoImg.height;

                            if (element['image-fit'] === 'contain') {
                                scaleFactor = Math.min(
                                    containerWidth / photoImg.width,
                                    containerHeight / photoImg.height
                                );
                            } else {
                                const imgAR = photoImg.width / photoImg.height;
                                const boxAR = containerWidth / containerHeight;

                                if (imgAR > boxAR) {
                                    scaleFactor = containerHeight / photoImg.height;
                                    cropX = (photoImg.width - containerWidth / scaleFactor) / 2;
                                } else {
                                    scaleFactor = containerWidth / photoImg.width;
                                    cropY = (photoImg.height - containerHeight / scaleFactor) / 2;
                                }
                                cropWidth = photoImg.width - 2 * cropX;
                                cropHeight = photoImg.height - 2 * cropY;
                            }

                            // Photo layer centered so rotation pivots around middle of the box
                            const image = new fabric.Image(photoImg, {
                                left: cx,
                                top:  cy,
                                originX: 'center',
                                originY: 'center',
                                scaleX: scaleFactor,
                                scaleY: scaleFactor,
                                angle: ANG,
                                selectable: false,
                                cropX, cropY,
                                width:  cropWidth,
                                height: cropHeight,
                                objectCaching: !generatedCutout
                            });

                            if (useZ) {
                                image.customZ = zBaseMi;
                            }

                            // ---- Circle cutout ----
                            // If requested, clip the photo to a centered circle (perfect circle using the smaller dimension).
                            // If a PNG mask is provided, the mask takes precedence.
                            if (!!element['circle-cutout'] && !element['clipping-mask-image']) {
                                const radius = Math.min(containerWidth, containerHeight) / 2;
                                image.clipPath = new fabric.Circle({
                                    left: cx,
                                    top: cy,
                                    originX: 'center',
                                    originY: 'center',
                                    radius,
                                    absolutePositioned: true,
                                    angle: ANG,
                                    selectable: false,
                                    evented: false,
                                });
                            }

                            // ---- PNG clipping mask (alpha) ----
                            if (element['clipping-mask-image']) {
                                const maskImg = new Image();
                                maskImg.crossOrigin = "Anonymous";
                                maskImg.onload = () => {
                                    const wantsStroke = !!element['stroke_mask'];
                                    const STROKE = Number(element.mask_stroke_width ?? 5);

                                    let strokeUnderlay = null;

                                    if (wantsStroke) {
                                        // White "stroke" underlay = the mask bitmap itself, slightly larger
                                        strokeUnderlay = new fabric.Image(maskImg, {
                                            left: cx,
                                            top:  cy,
                                            originX: 'center',
                                            originY: 'center',
                                            absolutePositioned: true,
                                            scaleX: (containerWidth  + STROKE * 2) / maskImg.width,
                                            scaleY: (containerHeight + STROKE * 2) / maskImg.height,
                                            angle: ANG,
                                            selectable: false,
                                            evented: false
                                        });
                                        if (useZ) {
                                            strokeUnderlay.customZ = zBaseMi - 0.5;
                                        }
                                        this.canvasCreator.add(strokeUnderlay);
                                    }

                                    // Normal-sized mask as the clipPath for the photo (also rotated)
                                    const maskTop = new fabric.Image(maskImg, {
                                        left: cx,
                                        top:  cy,
                                        originX: 'center',
                                        originY: 'center',
                                        absolutePositioned: true,
                                        scaleX: containerWidth  / maskImg.width,
                                        scaleY: containerHeight / maskImg.height,
                                        angle: ANG,
                                        selectable: false,
                                        evented: false,
                                        objectCaching: !generatedCutout
                                    });
                                    image.clipPath = maskTop;

                                    const backing = makeCutoutBacking(new fabric.Image(maskImg, {
                                        left: cx,
                                        top:  cy,
                                        originX: 'center',
                                        originY: 'center',
                                        absolutePositioned: true,
                                        scaleX: containerWidth  / maskImg.width,
                                        scaleY: containerHeight / maskImg.height,
                                        angle: ANG,
                                        selectable: false,
                                        evented: false,
                                        objectCaching: false
                                    }));

                                    // Dark fill sits behind a night sky or moon so transparent edges stay dark.
                                    if (backing) this.canvasCreator.add(backing);
                                    this.canvasCreator.add(image);

                                    // ---- stacking order for this slot ----
                                    if (useZ) {
                                        this.reorderCanvasByCustomZ();
                                    } else if (ON_TOP) {
                                        if (strokeUnderlay) this.canvasCreator.bringToFront(strokeUnderlay);
                                        if (backing) this.canvasCreator.bringToFront(backing);
                                        this.canvasCreator.bringToFront(image);
                                    } else {
                                        this.canvasCreator.sendToBack(image);
                                        if (backing) this.canvasCreator.sendToBack(backing);
                                        if (strokeUnderlay) this.canvasCreator.sendToBack(strokeUnderlay);
                                    }

                                    if (!useZ) {
                                        bringIconsToFront();
                                    }
                                };
                                this.setCanvasImageSrc(maskImg, element['clipping-mask-image'], tempCanvasID);
                            } else {
                                // No mask provided
                                let backingClip = null;
                                if (generatedCutout && !!element['circle-cutout']) {
                                    const radius = Math.min(containerWidth, containerHeight) / 2;
                                    backingClip = new fabric.Circle({
                                        left: cx,
                                        top: cy,
                                        originX: 'center',
                                        originY: 'center',
                                        radius,
                                        absolutePositioned: true,
                                        angle: ANG,
                                        selectable: false,
                                        evented: false,
                                    });
                                }
                                const backing = makeCutoutBacking(backingClip);
                                if (backing) this.canvasCreator.add(backing);
                                this.canvasCreator.add(image);
                                if (useZ) {
                                    this.reorderCanvasByCustomZ();
                                } else if (ON_TOP) {
                                    if (backing) this.canvasCreator.bringToFront(backing);
                                    this.canvasCreator.bringToFront(image);
                                } else {
                                    this.canvasCreator.sendToBack(image);
                                    if (backing) this.canvasCreator.sendToBack(backing);
                                }
                                if (!useZ) {
                                    bringIconsToFront();
                                }
                            }
                        };

                        if (this.showMultiImageGrayscale && element['type'] !== 'line_art_image' && !generatedCutout) {
                            this.htmlImageToGrayscaleDataUrlImage(outerImg, (photoImg) => {
                                addMultiImageFromPhoto(photoImg);
                            });
                        } else {
                            addMultiImageFromPhoto(outerImg);
                        }
                    };
                    if (element['type'] === 'line_art_image') {
                        outerImg.onerror = () => {
                            if (tempCanvasID !== this.canvasBuildVersion) {
                                return;
                            }
                            // If a data URL somehow fails, or CORS was set, retry once without it.
                            // Never replace a generation URL with the sample "initial" artwork.
                            if (!outerImg._lineArtCorsRetry && tmp && !this.isLineArtDataOrBlobUrl(tmp)) {
                                outerImg._lineArtCorsRetry = true;
                                try {
                                    outerImg.crossOrigin = null;
                                    outerImg.removeAttribute('crossOrigin');
                                } catch (e) {
                                    // ignore
                                }
                                console.warn('Line art load failed, retrying without crossOrigin:', tmp);
                                outerImg.src = '';
                                this.setCanvasImageSrc(outerImg, tmp, tempCanvasID);
                                return;
                            }
                            console.warn('Line art image failed to load:', tmp);
                        };
                    }
                    this.setCanvasImageSrc(outerImg, tmp, tempCanvasID);
                } else if (element['type'] === "custom_calendar") {
                    // The JSON "initial" value is authored in dd/MM/yyyy, but the user-entered
                    // value is stored in whatever format matches the shopper's locale
                    // (see handleDate / userSelectedDateFormat). Parsing the stored string
                    // with a hard-coded dd/MM/yyyy split previously caused US/other-locale
                    // customers to see the wrong month (e.g. June rendered for March).
                    const rawUserDate = this.userInput[element['id']];
                    const rawDate = rawUserDate || element['initial'] || "15/05/2025";

                    const tryParse = (str, fmt) => {
                        try {
                            const d = dateFns.parse(str, fmt, new Date());
                            return (d instanceof Date && !isNaN(d)) ? d : null;
                        } catch (e) {
                            return null;
                        }
                    };

                    let parsedDate = null;
                    if (rawUserDate) {
                        // First try the user's currently selected format
                        parsedDate = tryParse(rawUserDate, this.userSelectedDateFormat)
                                  || tryParse(rawUserDate, this.userDateFormat);
                    }
                    // Fallbacks: initial values and any legacy strings are dd/MM/yyyy
                    if (!parsedDate) parsedDate = tryParse(rawDate, "dd/MM/yyyy");
                    if (!parsedDate) parsedDate = tryParse(rawDate, "MM/dd/yyyy");
                    if (!parsedDate) parsedDate = tryParse(rawDate, "yyyy/MM/dd");
                    if (!parsedDate) parsedDate = tryParse(rawDate, "MM-dd-yyyy");
                    if (!parsedDate) parsedDate = tryParse(rawDate, "dd-MM-yyyy");
                    if (!parsedDate) parsedDate = new Date(2025, 4, 15); // final safety net

                    const day = parsedDate.getDate();
                    const month = parsedDate.getMonth() + 1; // keep 1-indexed for the rest of this block
                    const year = parsedDate.getFullYear();

                    // Canvas dimensions
                    const canvasWidth = 1000;
                    const canvasHeight = 1400;

                    // Cell layout
                    const cellSize = 84;
                    const cellPadding = 30;
                    const columns = 7;

                    const daysInMonth = new Date(year, month, 0).getDate();
                    const firstDay = new Date(year, month - 1, 1).getDay();
                    const totalGridCells = firstDay + daysInMonth;
                    const gridRows = Math.ceil(totalGridCells / columns);

                    const gridWidth = columns * (cellSize + cellPadding);
                    const gridHeight = gridRows * (cellSize + cellPadding);

                    // Layout anchors
                    const calendarCenterX = canvasWidth / 2;
                    const gridStartX = calendarCenterX - gridWidth / 2;
                    const baseGridStartY = 410;
                    const gridStartY = gridRows === 4 ? baseGridStartY + 60 : baseGridStartY;
                    const titleTop = 180;

                    const whiteTextEnabled = this.userInput['white_text_toggle'] === true;
                    const textColor = whiteTextEnabled ? "#ffffff" : "#000000";

                    // ✅ Get icon color to use for year and highlight
                    const iconColor = this.userInput['icon_color'] || "#d6bfa0";

                    // MONTH – serif, bold, black
                    const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(year, month - 1));
                    this.canvasCreator.add(new fabric.Text(monthName.toUpperCase(), {
                        fontSize: 60,
                        fontFamily: "Playfair Display",
                        fontWeight: "normal",
                        top: 120,
                        left: canvasWidth / 2,
                        originX: 'center',
                        fill: textColor,  // ✅ dynamic color
                        selectable: false,
                        charSpacing: 30,
                    }));

                    // ✅ YEAR – same color as icon
                    this.canvasCreator.add(new fabric.Text(year.toString(), {
                        fontSize: 175,
                        fontFamily: "Poppins",
                        fontWeight: "600",
                        top: 190,
                        left: canvasWidth / 2,
                        originX: 'center',
                        fill: iconColor,
                        selectable: false,
                        charSpacing: 80,
                    }));

                    // Grid of day numbers
                    Array.from({ length: daysInMonth }, (_, i) => i + 1).forEach((d, idx) => {
                        const gridIndex = firstDay + idx;
                        const col = gridIndex % columns;
                        const row = Math.floor(gridIndex / columns);

                        const left = gridStartX + col * (cellSize + cellPadding);
                        const top = gridStartY + row * (cellSize + cellPadding);
                        const centerX = left + cellSize / 2;
                        const centerY = top + cellSize / 2;
                        const isSelected = d === day;

                        if (isSelected) {
                            this.canvasCreator.add(new fabric.Circle({
                                radius: cellSize / 2,
                                fill: iconColor,
                                left: centerX,
                                top: centerY,
                                originX: 'center',
                                originY: 'center',
                                selectable: false
                            }));
                        }

                        this.canvasCreator.add(new fabric.Text(d.toString().padStart(2, '0'), {
                            fontSize: 36,
                            fontFamily: "Arial",
                            fill: textColor,
                            left: centerX,
                            top: centerY + 2,
                            originX: 'center',
                            originY: 'center',
                            selectable: false,
                            charSpacing: 100
                        }));
                    });
                } else if (element['type'] === 'icon') {
                // Default icon selection if not already chosen
                if (!this.userInput[element['id']]) {
                    const firstKey = Object.keys(element['icon_options'] || {})[0];
                    if (firstKey) this.userInput[element['id']] = firstKey;
                }

                // Default color assignment
                if (!this.userInput['icon_color'] && element['default_color']) {
                    this.userInput['icon_color'] = element['default_color'];
                }

                const selectedIconKey = this.userInput[element['id']];
                const iconURL = element['icon_options']?.[selectedIconKey];
                if (!iconURL) return;

                // Background on/off & colors
                const bgEnabled = !!element['background_color_picker_enabled'];
                const selectedColor = this.userInput['icon_color'] || element['default_color'] || "#000000";

                let iconColor = "#ffffff";
                if (bgEnabled === true) {
                    iconColor = (selectedColor === "#f9f9f9") ? "#000" : "#ffffff";
                } else {
                    iconColor = selectedColor;
                }

                // Pull JSON sizes & padding (strictly from JSON)
                const ELEM_W = Number(element['width'])  || Number(element['height']) || 0;
                const ELEM_H = Number(element['height']) || Number(element['width'])  || 0;
                const PAD     = 25;

                this.beginCanvasAssetLoad(tempCanvasID);
                let iconSettled = false;
                const settleIconLoad = () => {
                    if (iconSettled) {
                        return;
                    }
                    iconSettled = true;
                    this.finishCanvasAssetLoad(tempCanvasID);
                };
                fetch(iconURL)
                    .then((res) => {
                        if (!res.ok) {
                            throw new Error("Icon SVG HTTP " + res.status);
                        }
                        return res.text();
                    })
                    .then((svgText) => {
                        fabric.loadSVGFromString(svgText, (objects, options) => {
                    try {
                    if (!objects || !objects.length) {
                        return;
                    }
                    // Colorize the SVG (no geometry-based sizing)
                    objects.forEach(obj => {
                        if (obj.fill !== 'none') obj.set({ fill: iconColor });
                        if (obj.stroke && obj.stroke !== 'none') {
                            obj.set({ stroke: iconColor, strokeUniform: true });
                        }
                    });

                    // ---- OPTION A: measure tight content height from raw objects (ignore viewBox whitespace)
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                    objects.forEach(o => {
                        // Use each object's own bounds (not grouped yet, so no group transform involved)
                        // getBoundingRect(true) = include stroke; if you prefer excluding stroke, pass false
                        const br = o.getBoundingRect(true);
                        if (br.left < minX) minX = br.left;
                        if (br.top  < minY) minY = br.top;
                        if (br.left + br.width  > maxX) maxX = br.left + br.width;
                        if (br.top  + br.height > maxY) maxY = br.top  + br.height;
                    });
                    const rawContentH = Math.max(1, (isFinite(maxY - minY) ? (maxY - minY) : 1));

                    // Build the actual group with options (applies viewBox/x/y transforms properly)
                    const iconGroup = fabric.util.groupSVGElements(objects, options);

                    // Scale ICON using the **tight content height** so visual sizes match across icons
                    const targetIconHeight = bgEnabled
                        ? Math.max(1, ELEM_H - PAD)   // shrink to fit inside padded bg
                        : Math.max(1, ELEM_H);        // no bg: use full JSON height
                    const scale = targetIconHeight / rawContentH;

                    iconGroup.set({
                        left: 0,
                        top: 0,
                        originX: 'center',
                        originY: 'center',
                        scaleX: scale,
                        scaleY: scale,
                        selectable: false,
                        evented: false
                    });

                    if (bgEnabled) {
                        const shape  = (element['background_shape'] || 'circle').toLowerCase();
                        const bgColor = selectedColor;
                        let bgShape;

                        if (shape === 'rect') {
                            // Rect strictly from JSON size + padding ALL around
                            const rectW = ELEM_W + PAD * 2;
                            const rectH = ELEM_H + PAD * 2;
                            const corner = Math.min(rectW, rectH) / 2; // rounded/pill
                            bgShape = new fabric.Rect({
                                width: rectW,
                                height: rectH,
                                rx: corner,
                                ry: corner,
                                fill: bgColor,
                                left: 0,
                                top: 0,
                                originX: 'center',
                                originY: 'center',
                                selectable: false,
                                evented: false
                            });
                        } else {
                            // Circle strictly from JSON:
                            // diameter = max(width, height) + 2*PAD  => padding on all sides
                            const diameter = Math.max(ELEM_W, ELEM_H) + PAD * 2;
                            const radius = diameter / 2;
                            bgShape = new fabric.Circle({
                                radius,
                                fill: bgColor,
                                left: 0,
                                top: 0,
                                originX: 'center',
                                originY: 'center',
                                selectable: false,
                                evented: false
                            });
                        }

                        // Group bg + icon
                        const combo = new fabric.Group([bgShape, iconGroup], {
                            left: element['x-pos'],
                            top: element['y-pos'],
                            originX: 'center',
                            originY: 'center',
                            selectable: false,
                            evented: false
                        });

                        // Tag as icon and pull above photos/multi-images
                        combo.set({ customType: 'icon' });
                        this.canvasCreator.add(combo);
                        this.canvasCreator.bringToFront(combo);

                    } else {
                        // No background: just place the icon
                        iconGroup.set({
                            left: element['x-pos'],
                            top: element['y-pos'],
                            originX: 'center',
                            originY: 'center'
                        });

                        // Tag as icon and pull above photos/multi-images
                        iconGroup.set({ customType: 'icon' });
                        this.canvasCreator.add(iconGroup);
                        this.canvasCreator.bringToFront(iconGroup);
                    }
                    } catch (iconError) {
                        console.error("Icon SVG processing failed", iconError);
                    } finally {
                        settleIconLoad();
                    }
                        });
                    })
                    .catch((iconLoadError) => {
                        console.error("Icon SVG load failed", iconLoadError);
                        settleIconLoad();
                    });
            }

            });
            this.loadCanvasFontsForGeneration(tempCanvasID);
            if (tempCanvasID === this.canvasBuildVersion && this.canvasCreator) {
                this.remeasureCanvasTextObjects();
                if (typeof this.canvasCreator.requestRenderAll === "function") {
                    this.canvasCreator.requestRenderAll();
                } else {
                    this.canvasCreator.renderAll();
                }
            }
            } catch (fillError) {
                console.error("fillCanvas failed", fillError);
            } finally {
                this.finishCanvasAssetLoad(tempCanvasID);
            }
        },
    }
}).mount('#app');
window.ttBeginCartEdit = function (lineKey, designKey) {
    if (createPage && typeof createPage.beginCartEdit === "function") {
        createPage.beginCartEdit(lineKey, designKey);
    }
};
}).catch(function (err) {
    console.error("Failed to load product data", err);
});
